/**
 * @fileoverview Shared types between backend and frontend.
 *
 * This is the CANONICAL SOURCE for these types.
 * The frontend copies this file via `pnpm sync:types`.
 *
 * To add a new shared type:
 * 1. Add it here
 * 2. Run `pnpm sync:types` in frontend (or it runs automatically on dev/build)
 * 3. Import from '@/types/shared' in frontend
 */

/** Trusted public media origins used by browser imports and backend storage. */
export const KNOWN_CDN_ORIGINS = [
  'https://shots.martini.video',
  'https://staging.martini.video',
  'https://cdn.martini.film',
  'https://staging-cdn.martini.film',
]

/**
 * Shot rating - mutually exclusive approval/rejection status.
 * A shot cannot be both favorite and reject at the same time.
 */
export type ShotRating = 'favorite' | 'reject'

/** Independent 1-5 review score. Undefined means no star rating. */
export type StarRating = 1 | 2 | 3 | 4 | 5

/** Max option rows a serialized model input keeps (spec.json, MCP get_models). */
export const MAX_SERIALIZED_INPUT_OPTIONS = 40

/**
 * Returns a copy of `inputs` where every `{ options: [...] }` input longer than
 * `max` keeps its default option first plus the first `max - 1` others, and a
 * map of input name → original length for the ones that were cut. Validation
 * always runs against the handler's full list, never against this copy.
 */
export function compactModelInputs<T extends Record<string, unknown>>(
  inputs: T,
  max = MAX_SERIALIZED_INPUT_OPTIONS,
): { inputs: T; truncated: Record<string, number> } {
  const compacted = { ...inputs }
  const truncated: Record<string, number> = {}

  for (const [key, value] of Object.entries(inputs)) {
    if (!value || typeof value !== 'object') continue

    const input = value as Record<string, unknown>
    if (!Array.isArray(input.options) || input.options.length <= max) continue

    const defaultOption = input.options.find(option => {
      if (!option || typeof option !== 'object') return false
      const candidate = option as Record<string, unknown>
      return candidate.default === true || candidate.value === input.default
    })
    const remaining = defaultOption ? input.options.filter(option => option !== defaultOption) : input.options
    const kept = (defaultOption ? [defaultOption, ...remaining] : remaining).slice(0, max)

    compacted[key as keyof T] = { ...input, options: kept } as T[keyof T]
    truncated[key] = input.options.length
  }

  return { inputs: compacted, truncated }
}

/**
 * Shot color label - for custom organizational workflows.
 * Only one color label can be applied at a time.
 */
export type ShotColorLabel = 'green' | 'yellow' | 'red' | 'blue' | 'purple'

export type ColorLabelNames = Partial<Record<ShotColorLabel, string>>

export const DEFAULT_COLOR_LABEL_NAMES: Record<ShotColorLabel, string> = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
  blue: 'Blue',
  purple: 'Purple',
}

export function resolveColorLabelName(names: ColorLabelNames | undefined, color: ShotColorLabel): string {
  return names?.[color]?.trim() || DEFAULT_COLOR_LABEL_NAMES[color]
}

/**
 * Asset operation type - determines display noun and behavior.
 * Add new operations here as video models evolve.
 *
 * Root-level takes (t2v, i2v, upload, generate-image) share sibling numbering.
 * Child-level (extend, variation, offshoot) use per-operation counters.
 */
export type AssetOperation =
  | 't2v' // text-to-video (root take)
  | 'i2v' // image-to-video (root take)
  | 'extend' // video extension
  | 'variation' // same prompt, different seed (sibling in same row)
  | 'offshoot' // derived from existing shot via "Reuse Prompt" (child in new row)
  | 'v2v' // video-to-video (edit)
  | 'upload' // user-uploaded video (root take)
  | 'generate-image' // image generation (root take)
  | 'upscale' // upscaled version of an existing asset
  | 'i2s' // image-to-set (creates 3D set from image)
  | 'set-screenshot' // screenshot captured from 3D set
  | 'generate-audio' // audio generation (root take)
  | 'inpaint-audio' // audio inpaint (regenerates a region of an existing audio asset)
  | 'video-frame' // still frame extracted from a video asset
  | 'camera-motion' // frontend-authored camera motion draft / rendered motion
  | 'motion-graphic' // model-authored HTML/CSS/JS baked to a video asset
  | 'crop' // deterministic derivation: rect crop of a parent image (child op, never root)
  | 'crop-adjust' // crop child promoted by its first angle or colour adjustment; carries edit/editRevisions instead of cropRect/cropRevisions

// ---------------------------------------------------------------------------
// Asset lineage — the derivation record (docs/asset-lineage-design.md, ADR-0016)
// ---------------------------------------------------------------------------

/**
 * What a record on a board is. Only 'asset' has writers today; the rest are
 * named so keys never need re-shaping when notes, variables or renders join.
 */
export type LineageEntityKind = 'asset' | 'note' | 'variable' | 'subject' | 'render' | (string & {})

/**
 * Lineage's operation accepts every `AssetOperation` plus the verbs that have a
 * writer, and any lower-cased free text ('photoshop', 'pdf-extract') for what
 * Martini records but never interprets. The union is documentation and schema
 * suggestion, not a gate.
 */
export type LineageOperation =
  | AssetOperation
  | 'inpaint' // region edit of an image
  | 'trim' // time window of a clip
  | 'concat' // N clips → 1
  | 'mix' // N audio → 1
  | 'annotate' // markup
  | 'transcode'
  | 'copy' // new node, same bytes — the database marks it isCopy
  | 'import' // library capture, external URL mirror, a file extracted from a document
  | 'write' // a text note written from other nodes (ADR-0010)
  | 'set' // a variable's value set from other nodes
  | (string & {})

/** How a derivation is known. Set by the code path, never by the caller. */
export type LineageEvidence = 'recorded' | 'declared' | 'matched' | 'inferred'

/**
 * What a row is, read off its shape at read time (backend `lineage/category.ts`):
 * a model made it anew, a model changed a source, a tool changed a source, it
 * came from outside the board, or it is the same bytes on a new card. The
 * operation stays the precise verb underneath; this is the word a person reads.
 */
export type LineageCategory = 'ai-generated' | 'ai-edited' | 'edited' | 'imported' | 'copied'

/** Where the actor came through. */
export type LineageOrigin = 'ui' | 'mcp' | 'api' | 'workflow' | 'system'

/** What an input was for. Maps onto a C2PA ingredient relationship. */
export type DerivationInputRole =
  | 'source'
  | 'start-frame'
  | 'end-frame'
  | 'reference'
  | 'element'
  | 'style'
  | 'audio'
  | 'video'
  | 'mask'
  | 'prompt-mention'
  | (string & {})

/** The part of an input that was used: a timestamp, a window, a rect in natural pixels of the input's stored original. */
export interface DerivationRegion {
  timeSec?: number
  startSec?: number
  endSec?: number
  rect?: { x: number; y: number; width: number; height: number }
}

export interface DerivationInput {
  /** Defaults to 'asset'. A non-asset input is keyed on its nodeId. */
  kind?: LineageEntityKind
  /** Durable identity of the bytes consumed (the R2 folder). */
  storageAssetId?: string
  /** The board record the actor referenced (a copy, a version member, …). */
  nodeId?: string
  /** The project `nodeId` lives in when it is not the row's own — a cross-project edge (Duplicate Project, snapshot restore). Context, never access. */
  projectId?: string
  /** Bytes that came from outside Martini. */
  external?: { url?: string; filename?: string }
  /**
   * A workspace Library item the operation used (a reference, an element, a video). The Library is
   * the organisation's and its ids are short text, so the input names the item and is never walked.
   */
  library?: LineageLibraryRef
  role: DerivationInputRole
  region?: DerivationRegion
  /** Set on reads when a redaction hides this input: default reads keep only the role, the redacted export keeps everything. */
  redacted?: boolean
}

/**
 * A Library item as a lineage input. The item and the version picked (`versionId`, pinned at pick
 * time) say what the person chose; `url` is the file the operation read — an immutable copy, so the
 * row still shows what went in after the item gets a new version, is renamed or is deleted.
 */
export type LineageLibraryRef = LibraryRef & {
  /** The file the operation read, as the recipe names it. Never a walk key. */
  url?: string
}

/**
 * A Library item captured from a board asset, as the per-asset lineage answer reports it: read
 * from `library_assets.origin_asset_id`, not the ledger (an organisation's item is on no board).
 */
export interface LineageLibraryCapture {
  collectionId: string
  assetId: string
  name: string
}

export interface DerivationTool {
  kind: 'model' | 'martini' | 'external'
  id: string
  version?: string
  provider?: string
}

export interface DerivationActor {
  userId?: string
  origin: LineageOrigin
  /**
   * The client the row came through: an agent's self-reported name or its kind;
   * for the UI, the app family and platform ("Chrome on macOS", "Martini Desktop 0.1.4"),
   * never the raw user agent. Absent on rows written before the channel was kept.
   */
  agent?: string
  /** The app an API key was made for (`martini connect`): its key's label, so the ledger names the tool, not the door. */
  app?: string
}

export interface DerivationOutput {
  kind: LineageEntityKind
  nodeId: string
  /** Present exactly when kind is 'asset'. */
  storageAssetId?: string
  /** The asset's bare-hex SHA-256 checksum, when known. */
  contentHash?: string
  /** SHA-256 of the bytes with container metadata stripped, when the processing job computed it (design §13). */
  essenceHash?: string
}

export interface DerivationRefs {
  generationId?: string
  jobId?: string
  enhancementOperationId?: string
  /** The snapshot a restored project was rebuilt from. */
  snapshotId?: string
}

/** One operation that produced a board record from zero or more inputs. Append-only. */
export interface Derivation {
  /** Client-generated UUID; the idempotency key. */
  id: string
  /** Null only on the skeleton rows a project purge keeps. */
  projectId: string | null
  output: DerivationOutput
  operation: LineageOperation
  /** Computed at read time from the row's shape; never stored. */
  category: LineageCategory
  tool?: DerivationTool
  inputs: DerivationInput[]
  actor: DerivationActor
  refs?: DerivationRefs
  /** Free text for humans, or the user's reason when withheld. */
  note?: string
  /** True when the caller withheld lineage on the user's explicit instruction; the reason is in `note`. */
  withheld: boolean
  evidence: LineageEvidence
  /** Computed by the database: the output's key is among the inputs' keys. Never traversed. */
  isCopy: boolean
  /** When the ledger learned of it — the order rows reduce in. */
  createdAt: string
  /** When the operation happened, as far as the writer knew (a backfill passes the asset's creation time). */
  performedAt: string
  /** The project's organisation when the row was written. Absent on purge skeletons. */
  organizationId?: string
  /** Present only on the redacted export: this row is hidden from default reads. */
  redacted?: boolean
}

// ---------------------------------------------------------------------------
// Asset lineage rework — assets, origins, placements (ADR-0021, docs/asset-lineage-rework-plan.md)
// ---------------------------------------------------------------------------
// The closed vocabularies of the new ledger. Each list is mirrored by a CHECK
// constraint in supabase/schemas/41_asset_lineage.sql; a text-read drift guard
// (backend/src/lineage/ledgerVocabulary.test.ts) keeps the two equal, so a new
// member here is a schema change and the other way round.

/**
 * Who attests to an origin: the code that produced the bytes, as it happened
 * (`producer`); a content match to a known asset (`content_match`); a person's or
 * agent's statement (`statement`); the records the rebuild read (`records`). An
 * origin with an event id is the record of that event, whatever attests to it —
 * one per event; a statement carries none.
 */
export const LINEAGE_ATTESTED_BY = ['producer', 'content_match', 'statement', 'records'] as const
export type LineageAttestedBy = (typeof LINEAGE_ATTESTED_BY)[number]

/** The kinds of note a placement carries: a private remark, a local hide, or a withheld source with its reason. */
export const LINEAGE_NOTE_KINDS = ['note', 'hide', 'withheld'] as const
export type LineageNoteKind = (typeof LINEAGE_NOTE_KINDS)[number]

/**
 * Whether an asset's source is accounted for: `sourced` (Martini's own production, or bytes
 * from outside with inputs, a source reference or a statement), `withheld` (the person asked
 * that it not be recorded, with a reason), `omitted` (an upload, import or external work with
 * no inputs, no source reference and no statement — nobody said anything). Derived from the
 * rows (backend/src/lineage/sourceState.ts), never stored.
 */
export const LINEAGE_SOURCE_STATES = ['sourced', 'withheld', 'omitted'] as const
export type LineageSourceState = (typeof LINEAGE_SOURCE_STATES)[number]

/**
 * What an origin's operation may be: every `AssetOperation`, the verbs with a
 * writer, and `external` for work done outside Martini (the tool names the app).
 * A copy is never an origin — it is a placement.
 */
export const LINEAGE_ORIGIN_OPERATIONS = [
  't2v',
  'i2v',
  'extend',
  'variation',
  'offshoot',
  'v2v',
  'upload',
  'generate-image',
  'upscale',
  'i2s',
  'set-screenshot',
  'generate-audio',
  'inpaint-audio',
  'video-frame',
  'camera-motion',
  'crop',
  'crop-adjust',
  'inpaint',
  'trim',
  'concat',
  'mix',
  'annotate',
  'transcode',
  'import',
  'write',
  'set',
  'enhance',
  'replace-audio',
  'render',
  'external',
] as const
export type LineageOriginOperation = (typeof LINEAGE_ORIGIN_OPERATIONS)[number]

/** A placement's state: the bytes are verified only by the processing step that hashed them. */
export const LINEAGE_PLACEMENT_STATES = ['pending', 'placed', 'failed', 'unavailable'] as const
export type LineagePlacementState = (typeof LINEAGE_PLACEMENT_STATES)[number]

/** The kind of the event that placed an asset where it is (the placement's `eventId` names the event). */
export const LINEAGE_PLACEMENT_EVENT_KINDS = [
  'generated',
  'uploaded',
  'pasted',
  'duplicated',
  'restored',
  'captured',
  'placed',
  'imported',
] as const
export type LineagePlacementEventKind = (typeof LINEAGE_PLACEMENT_EVENT_KINDS)[number]

/**
 * What a placement's target is. `panel-upload` is a file brought into a generation without a board
 * node — a start frame, keyframe, reference or mask dropped into the Shot Details panel or sent by
 * URL — placed as the pipeline's copy under the output's folder; it is never on the board and has no
 * asset number (Long, 2026-10-23).
 */
export const LINEAGE_NODE_KINDS = [
  'asset',
  'note',
  'variable',
  'subject',
  'render',
  'library-version',
  'panel-upload',
] as const
export type LineageNodeKind = (typeof LINEAGE_NODE_KINDS)[number]

/** Where an origin or placement belongs. Origins may also belong nowhere. */
export const LINEAGE_SCOPE_KINDS = ['project', 'library'] as const
export type LineageScopeKind = (typeof LINEAGE_SCOPE_KINDS)[number]

/** The media an asset is. */
export const LINEAGE_MEDIA_KINDS = ['image', 'video', 'audio', 'set', 'file'] as const
export type LineageMediaKind = (typeof LINEAGE_MEDIA_KINDS)[number]

/** Where an actor came through — the same surfaces as `LineageOrigin`, closed. */
export const LINEAGE_ACTOR_ORIGINS = ['ui', 'mcp', 'api', 'workflow', 'system'] as const

/**
 * The client an actor came through, as a fixed vocabulary: the browser or the
 * desktop app for the UI, an API client, Martini itself, or one of the MCP
 * agent kinds. Never a free-text client name (erasure must leave nothing
 * personal behind).
 */
export const LINEAGE_AGENT_KINDS = [
  'browser',
  'desktop',
  'api-client',
  'martini',
  'claude-code',
  'claude-desktop',
  'cursor',
  'windsurf',
  'cline',
  'continue',
  'zed',
  'gemini-cli',
  'codex',
  'chatgpt',
  'martini-chat',
  'martini-cli',
  'martini-workflow',
  'ncp-agent-worker',
  'unknown',
] as const
export type LineageAgentKind = (typeof LINEAGE_AGENT_KINDS)[number]

/** Who did it and through what; the person is a separate column so erasure is one update. */
export interface LineageActor {
  origin: LineageOrigin
  agentKind: LineageAgentKind
  userId?: string
  /** The app whose key acted, when the actor came through one (`martini connect` names the key after it). */
  app?: string
}

/** An input of an origin: the asset the person chose, by its verified content hash. */
export interface LineageOriginInput {
  hash: string
  role: DerivationInputRole
  region?: DerivationRegion
  kind?: LineageNodeKind
  /** The hash of the copy the pipeline actually read, when it changed the bytes (resize, conform, trim). */
  preparedFrom?: { hash: string; preparation: string }
}

// ---------------------------------------------------------------------------
// The story a node tells — the wire shape of GET /assets/:assetId/story (ADR-0021;
// docs/asset-lineage-rework-plan.md §2 `storyOf`, §4). Built by backend/src/lineage/story.ts,
// read by the details panel and the lineage view. The project is the reading boundary: a
// placement, a person or a source appears only where the viewer may read its scope.
// ---------------------------------------------------------------------------

/** Where an origin or placement belongs: a project, or an organisation's Library. */
export interface LineageScope {
  kind: LineageScopeKind
  /** The project id, or the organisation id for the Library. */
  id: string
}

/** Where a placement's bytes can be previewed from: the storage folder, and the file when known. */
export interface LineagePlacementLocator {
  storageAssetId: string
  filename?: string
  previewKey?: string
}

/** Where a copy came from: a node of a project (or a snapshot of one), or an item of a Library. */
export interface LineagePlacementSource {
  nodeId?: string
  projectId?: string
  snapshotId?: string
  itemId?: string
  versionId?: string
  /** The Library the item belongs to, when the source is a Library item; the reader checks access against it. */
  organizationId?: string
  /** The URL the bytes were fetched from, for an import; settlement copies it onto the origin's refs as `sourceUrl`. */
  url?: string
}

/** An asset on a node or a Library version, as the viewer may see it. */
export interface LineagePlacementView {
  id: string
  scope: LineageScope
  nodeId: string | null
  /** What the target is: a board node (`asset`, …), a Library version, or a `panel-upload` with no node on the board. */
  nodeKind: LineageNodeKind
  libraryItemId: string | null
  libraryVersionId: string | null
  collectionId: string | null
  eventKind: LineagePlacementEventKind
  state: LineagePlacementState
  eventId: string
  label: string | null
  shotNumber: number | null
  mediaKind: LineageMediaKind | null
  locator: LineagePlacementLocator | null
  /** Where the copy came from, only when the viewer can read that source scope; else null. */
  source: LineagePlacementSource | null
  performedAt: string
  /** The person, when the viewer can read this placement's scope. */
  actor: LineageActor | null
}

/** How an asset came to be, as one record says it. */
export interface LineageOriginView {
  id: string
  /** A hide (for everyone, or this scope's own) covers the whole origin: a mark, and nothing else below is shown. */
  hidden: false | 'everyone' | 'local'
  attestedBy: LineageAttestedBy | null
  operation: LineageOriginOperation | null
  tool: DerivationTool | null
  eventId: string | null
  scope: LineageScope | null
  supersedes: string | null
  performedAt: string | null
  /** The person, when the viewer can read this origin's scope. */
  actor: LineageActor | null
  /**
   * What the origin names outside the ledger — the URL or file a source outside Martini was, the
   * file name a suggestion was read from — shown, like the person, only where the viewer can
   * read the origin's scope: a private drive link is as personal as a name.
   */
  refs?: LineageOriginRefsView
}

/** The references an origin's view carries; each bounded, none a hash. */
export interface LineageOriginRefsView {
  sourceUrl?: string
  sourceFilename?: string
  matchedFilename?: string
}

/** An input of an origin, with the placement of it the viewer may discover. */
export interface LineageInputView {
  ordinal: number
  /** The input's content hash, only when the viewer may discover a placement of it; null says "an asset not in this project". */
  hash: string | null
  /** Null when a hide covers the input: a hidden input is a mark and its position, nothing more. */
  role: DerivationInputRole | null
  region?: DerivationRegion
  preparedFrom?: { hash: string; preparation: string }
  /** A hide (for everyone, or this scope's own) covers this input: shown as a mark, nothing else. */
  hidden: false | 'everyone' | 'local'
  /** A placement the viewer may discover, else null: "an asset not in this project". */
  placement: LineagePlacementView | null
  /** The input asset's own origin, by operation and tool only — what Content Credentials would show anyone. */
  madeBy: { operation: LineageOriginOperation; tool: DerivationTool | null } | null
}

/** An attributed alternative: another origin of the asset with what it says the asset was made from. */
export interface LineageAlternativeOriginView extends LineageOriginView {
  inputs: LineageInputView[]
}

/** An origin that consumed the asset, with the placements of its output the viewer may discover. */
export interface LineageUsedInView {
  origin: LineageOriginView
  /** The input role this asset played in that origin. */
  role: DerivationInputRole
  placements: LineagePlacementView[]
}

export interface LineageStoryNote {
  id: string
  /** A private remark, or the person's reason that the source not be recorded. */
  kind: Exclude<LineageNoteKind, 'hide'>
  text: string
  actor: LineageActor | null
  createdAt: string
}

export interface LineageStory {
  placement: LineagePlacementView | null
  /** How the placement was chosen: by the node's `lineageEvent`, or the newest of the node's own project when the node carries none. */
  placementMatchedBy: 'event' | 'latest' | null
  asset: { contentHash: string } | null
  /** The producer origin of the placement's event; for a copy, the asset's production origin. */
  origin: LineageOriginView | null
  /**
   * `event`: the origin of the event that placed this node; `source`: a copy, the production origin reached
   * through its recorded source; `asset`: a copy with no recorded source, the newest production origin of the bytes.
   */
  originMatchedBy: 'event' | 'source' | 'asset' | null
  /** Every other origin of the asset with its visible inputs, never promoted over the primary; superseded ones dropped. */
  otherOrigins: LineageAlternativeOriginView[]
  inputs: LineageInputView[]
  usedIn: LineageUsedInView[]
  copies: LineagePlacementView[]
  notes: LineageStoryNote[]
  /** Whether the asset's source is accounted for (sourceState.ts); null when nothing is recorded about the bytes. */
  sourceState: LineageSourceState | null
}

/** `GET /assets/:assetId/story?projectId=`: the story and the names it may show. */
export interface AssetStoryAnswer {
  asset: {
    nodeId: string
    /** The placement marker the board record carries, when it is one; null reads the node's newest placement. */
    lineageEvent: string | null
    /** The node is no longer on the board; the answer is what the ledger kept of it. */
    removed?: boolean
  }
  story: LineageStory
  /** The names people in the story go by, keyed by user id; only people the viewer may see are in the story at all. */
  people: Record<string, string>
  /** The names of the other projects the story's placements are in, keyed by project id; every one passed discovery. */
  projects: Record<string, string>
}

/**
 * One input of a row's origin, as the project's reader may see it: the hash only when it is one
 * of this project's own placements (the reading boundary of ADR-0021 — a hash from elsewhere
 * reads as "an asset not in this project"), nothing but the position when a hide covers it.
 */
export interface LineageProjectPlacementInput {
  ordinal: number
  hash: string | null
  role: DerivationInputRole | null
  hidden: false | 'everyone' | 'local'
}

/** One row of the Lineage page: a placement and the producer origin of the event that made it. */
export interface LineageProjectPlacementRow {
  /** True only when joined to the current board revision; absent on older exports. */
  onCanvas?: boolean
  placement: LineagePlacementView
  /** The producer origin of the placing event; a copy's event (paste, duplicate, restore, capture, import) produced nothing. */
  origin: LineageOriginView | null
  /** The asset the placement shows, by content hash; null while processing has not verified the bytes. */
  contentHash: string | null
  /** What the origin was made from, so the page can draw families; empty for a copy or a hidden origin. */
  inputs: LineageProjectPlacementInput[]
  /**
   * The picture a tile shows, as a path under `/storage/` (`assets/{folder}/{file}`), resolved by the
   * backend from the locator or the folder's listing (lineage/previews.ts); null when there is none
   * to show — audio, a file still processing, a folder past the answer's lookup cap. Absent on rows
   * the export builds, which show no pictures.
   */
  preview?: string | null
  /** Whether the asset's source is accounted for; null for a copy whose production origin the rows do not hold. */
  sourceState: LineageSourceState | null
  /** The withheld note on this placement, when the person asked that the source not be recorded. */
  withheld: { reason: string; actor: LineageActor | null; createdAt: string } | null
}

/**
 * `GET /projects/:projectId/lineage/placements`: every placement of a project
 * as the Lineage page's table shows it — oldest first; a node placed more than
 * once (an enhancement, a restore) is one row per placement.
 */
export interface LineageProjectPlacementsAnswer {
  projectId: string
  generatedAt: string
  placements: LineageProjectPlacementRow[]
  /** The names people in the rows go by, keyed by user id. */
  people: Record<string, string>
  /** The names of other projects the rows' copy sources are in, keyed by project id. */
  projects: Record<string, string>
  /** The project has more placements than `limit`; the rows are the oldest `limit` of them. */
  truncated: boolean
  limit: number
}

/**
 * The columns of the placements CSV. The Lineage page's download and the
 * export's `derivations.csv` on the reworked ledger write the same file, one
 * row per placement; both builders fill the cells in this order.
 */
export const LINEAGE_PLACEMENTS_CSV_COLUMNS = [
  'placementId',
  'nodeId',
  'label',
  'shot',
  'eventKind',
  'state',
  'mediaKind',
  'eventId',
  'operation',
  'toolKind',
  'toolId',
  'attestedBy',
  'originId',
  'hidden',
  'sourceState',
  'actor',
  'actorKind',
  'actorUserId',
  'sourceNodeId',
  'sourceProjectId',
  'sourceItemId',
  'performedAt',
] as const

/**
 * A hide on an origin behind one of a project's placements, as the owner's
 * redacted export lists it. The row the hide covers keeps its mark; what the
 * hide covered rides here, and only when this project made the hide — a hide
 * another scope made for everyone keeps its reason with that scope.
 */
export interface LineageLedgerHide {
  id: string
  originId: string
  /** Null hides the whole origin; otherwise one input by its position. */
  inputOrdinal: number | null
  /** For everyone who holds the asset, or within one placement of this project. */
  scope: 'everyone' | 'local'
  /** The placement a local hide sits on; null for a hide for everyone. */
  placementId: string | null
  /** The reason given, only when this project made the hide. */
  reason: string | null
  /** The person, only when this project made the hide. */
  actor: LineageActor | null
  createdAt: string
  /** The origin as the viewer may otherwise see it, only when this project made the hide and the rows hold the origin. */
  origin: LineageOriginView | null
}

/**
 * The reworked ledger inside an export (ADR-0021; docs/asset-lineage-rework-plan.md §4):
 * `manifest.json` carries it, `derivations.csv` and the Placements section of
 * `index.html` are rendered from it. One row per placement with the origin of
 * its bytes; inputs are read per asset in Martini, not listed here.
 */
export interface LineageLedgerExport {
  /** Oldest first, ties on id — the order the content hash depends on. */
  placements: LineageProjectPlacementRow[]
  /** The names people in the rows go by, keyed by user id. */
  people: Record<string, string>
  /** The names of other projects the rows' copy sources are in, keyed by project id. */
  projects: Record<string, string>
  /** The project has more placements than `limit`; the rows are the oldest `limit` of them. */
  truncated: boolean
  limit: number
  /** Only on the owner's redacted export: every hide on the rows' origins, oldest first. */
  hides?: LineageLedgerHide[]
}

/**
 * A redaction hides a ledger row, or one of its inputs, from default reads
 * without editing or deleting it (design §4.5, D6). Insert-only, like the row.
 */
export interface DerivationRedaction {
  id: string
  derivationId: string
  /** Null hides the whole row; otherwise the index of one input in the row's inputs. */
  inputOrdinal: number | null
  reason: string
  actor: DerivationActor
  createdAt: string
}

/**
 * What a caller may send when it creates an asset (the `lineage` block on the
 * upload tools, `set_asset_lineage`, `POST /projects/:id/derivations`). The
 * backend fills the rest from the session and the board.
 */
export interface DerivationDeclaration {
  /** Defaults to 'upload' on uploads. */
  operation?: LineageOperation
  tool?: DerivationTool
  /** Empty means "nothing known" — the explicit, recorded blank. */
  inputs?: DerivationInput[]
  note?: string
  refs?: DerivationRefs
  /** Only when the user explicitly asked not to record lineage for this upload; the reason is quoted back and kept as a `withheld` note on the placement. */
  withheld?: { reason: string }
}

export type MotionGraphicAspectRatio = '16:9' | '9:16' | '1:1'

export type MotionGraphicBackgroundMode = 'opaque' | 'transparent'

export type MotionGraphicSourceMode = 'model-authored' | 'template-fallback' | 'user-edited' | 'stub' | 'agent-imported'

/** Stable binding exposed to generated source through the trusted Martini runtime. */
export interface MotionGraphicAssetSlot {
  slotId: string
  assetId: string
  displayName: string
}

export type MotionAnchorType = 'text' | 'color'

/**
 * A small, bounded editable knob the model exposes on a composition — the surface a user edits
 * instead of raw code. A `text` anchor binds to the element tagged `data-anchor="<id>"` (the runtime
 * sets its textContent); a `color` anchor binds to the CSS custom property `--anchor-<id>` (the
 * runtime sets it on :root). Editing an anchor updates `value` here and re-applies over the source —
 * the HTML/CSS/JS is never mutated — so preview == bake and version diffs stay legible.
 */
export interface MotionAnchor {
  id: string
  label: string
  type: MotionAnchorType
  value: string
}

/**
 * Versioned transport envelope for freely model-authored motion-graphic code.
 * This is deliberately not a layer grammar: HTML, CSS and JavaScript remain
 * source code authored by the model (or directly by the user).
 */
export interface MotionSourceV1 {
  schemaVersion: 1
  html: string
  css: string
  js: string
  durationMs: number
  aspectRatio: MotionGraphicAspectRatio
  /** Transparent sources bake to an alpha-capable video rather than opaque H.264. */
  backgroundMode: MotionGraphicBackgroundMode
  assetSlots: MotionGraphicAssetSlot[]
  /** Bounded editable knobs the editor surfaces as controls; applied over the source at build time. */
  anchors?: MotionAnchor[]
  /** The prompt/instruction that produced this source — surfaced per take ("prompt → output"). */
  prompt?: string
  scaffoldId?: string
  /**
   * Multi-scene sources (ENG-960). Each scene renders on its own clock (t = 0 at the scene's start)
   * into its own segment, and a bake stitches the segments in order, so an edit re-renders only the
   * scenes it changed. When present, the top-level `css` is shared by every scene, the top-level
   * `html` and `js` are empty, and `durationMs` is the sum of the scene durations. Absent means the
   * whole source is one scene.
   */
  scenes?: MotionScene[]
}

export interface MotionScene {
  /** Stable across edits, so revisions can target a scene by id. */
  id: string
  /** Short human label ("Title", "Feature 2") for progress and revision UI. */
  name?: string
  durationMs: number
  html: string
  css: string
  js: string
}

/**
 * The machine a render ran on (services/motion-renderer/host.mjs). The renderer profile pins the
 * image, not the host, and Modal's fleet mixes CPU generations whose SIMD paths can change pixels, so
 * every real render records these facts and drift can be traced to the host that made it (ENG-967).
 */
export interface MotionRenderHost {
  cpuModel: string
  cpuVendor: string | null
  /** `family/model/stepping`, which tells two CPUs with one marketing name apart. */
  cpuSignature: string
  cpuCount: number
  /** The SIMD extensions Skia and SwiftShader dispatch on that this CPU has. */
  dispatchFlags: string[]
  /** A short hash of every CPU flag. */
  flagsSha256: string | null
  kernel: string
  /** The NVIDIA driver version on a GPU host; null on the software profile. */
  gpuDriver: string | null
}

/** A rendered scene segment, recorded on the asset so reuse can be verified and progress reported. */
export interface MotionSegmentRecord {
  sceneId: string
  fingerprint: string
  sha256: string
  frames: number
  /**
   * The host the segment was rendered on, on the immutable R2 segment record only. Absent for stub
   * and local renders, for segments from before ENG-967, and always on the asset's metadata: the
   * Yjs doc carries no per-segment telemetry (the job row records the bake's hosts).
   */
  host?: MotionRenderHost
}

/**
 * How much authoring a generation buys. Draft writes each scene once. Polish also renders a keyframe
 * contact sheet, has the model review it, and revises the scenes it flags, within a round and cost cap.
 */
export type MotionAuthorTier = 'draft' | 'polish'

/** `fixing`: its render failed and the model is repairing it once. */
export type MotionAuthoringSceneStatus = 'writing' | 'fixing' | 'rendering' | 'accepted' | 'revising' | 'failed'

/** Live authoring progress on the asset, so the node can show each scene as it is accepted. */
export interface MotionAuthoringProgress {
  /** ISO timestamp when this authoring run began. Absent on older runs. */
  startedAt?: string
  tier: MotionAuthorTier
  phase: 'planning' | 'writing' | 'reviewing' | 'revising' | 'done' | 'failed'
  /** Polish review round, 1-based; 0 while the draft is being written. */
  round: number
  maxRounds: number
  scenes: Array<{ id: string; name?: string; durationMs: number; status: MotionAuthoringSceneStatus }>
  /** A valid source whose unaccepted scenes are placeholders, for the live preview. */
  previewSource?: { storageKey: string; hash: string }
  /** Scenes whose polish revision failed its checks, so the accepted version was kept. */
  keptScenes?: string[]
  /** What the run is expected to cost, its hard cap, and what it has cost so far (ENG-963). */
  budget?: MotionAuthoringBudget
}

/** The olive estimate and hard cap ("up to N olives") of one in-app Motion Graphic run. */
export interface MotionRunQuote {
  /** What a typical run of this size costs: tokens and render compute at cost + margin. */
  estimateOlives: number
  /** The most the run can cost: it stops before any step that could take it past this. */
  capOlives: number
}

export interface MotionAuthoringBudget extends MotionRunQuote {
  /**
   * Cost + margin of the model calls that answered and the render compute so far. A call that failed
   * counts toward the cap at its reservation but not here; the job's `agentBilling` receipt is the charge.
   */
  spentOlives: number
  /** The run stopped because its next step could have passed the cap; finished scenes were kept. */
  stoppedAtCap?: boolean
}

/** `GET /projects/:projectId/motion-graphics/:assetId/estimate`: the quote for each kind of run. */
export interface MotionGraphicEstimate {
  durationMs: number
  /** A new piece (`POST …/generate` with `tier`). */
  draft: MotionRunQuote
  polish: MotionRunQuote
  /**
   * A revision of the current source (`POST …/revise`) or a save of it (`POST …/render`, which may
   * repair a model-authored source once). Absent before a first take.
   */
  revision?: MotionRunQuote
  /**
   * A targeted revision (`POST …/revise-scene`, ENG-962): one scene rewritten in a small context, then
   * a bake of the scenes it changed. Priced for the largest scene; absent before a first take.
   */
  sceneRevision?: MotionRunQuote
  /** The margin the quote is priced at: the billing organization's agent rule, else the platform default. */
  pricing: { markupPercent: number; source: 'organization' | 'platform' }
  /**
   * Whether the olives are actually taken. False while agent-work billing is not launched for new
   * work, or the meter is not verified: the run is then free, the cap still stops it.
   */
  charged: { models: boolean; compute: boolean }
}

/**
 * Where a targeted revision points (ENG-962): a moment (a timestamped comment on the take, or the
 * playhead), a span, or a scene. Times are on the take's own clock, in ms from its start.
 */
export type MotionRevisionTarget =
  | { kind: 'time'; timeMs: number }
  | { kind: 'range'; startMs: number; endMs: number }
  | { kind: 'scene'; sceneId: string }

/** One scene's place on the take's clock. */
export interface MotionSceneSpan {
  sceneId: string
  name?: string
  index: number
  startMs: number
  endMs: number
}

/**
 * Where each scene plays in the take. With the bake's segment records (in scene order), a scene spans
 * the whole frames its segment rendered, which is where the stitched video really cuts; without them,
 * the declared durations.
 */
export function motionSceneSpans(
  source: Pick<MotionSourceV1, 'durationMs' | 'scenes'>,
  segments?: ReadonlyArray<{ sceneId: string; frames: number }>,
): MotionSceneSpan[] {
  const scenes: Array<Pick<MotionScene, 'id' | 'name' | 'durationMs'>> = source.scenes?.length
    ? source.scenes
    : [{ id: 'scene-1', durationMs: source.durationMs }]
  const byFrames =
    segments?.length === scenes.length && segments.every((segment, index) => segment.sceneId === scenes[index].id)
  let startMs = 0
  return scenes.map((scene, index) => {
    const frames = byFrames ? segments?.[index]?.frames : undefined
    const lengthMs = frames === undefined ? scene.durationMs : (frames * 1_000) / TIMELINE_FPS
    const span: MotionSceneSpan = {
      sceneId: scene.id,
      ...(scene.name ? { name: scene.name } : {}),
      index,
      startMs,
      endMs: startMs + lengthMs,
    }
    startMs += lengthMs
    return span
  })
}

/** A target resolved to its scene, and the moment the revision looks at (`atMs` on the take's clock). */
export type ResolvedMotionRevisionTarget =
  | { ok: true; span: MotionSceneSpan; atMs: number; offsetMs: number }
  | { ok: false; error: 'invalid_target' | 'target_out_of_range' | 'scene_not_found' }

/**
 * The scene a targeted revision edits. A moment picks the scene playing then (the last scene owns the
 * take's final instant); a span picks the scene it overlaps most, the earliest on a tie, and looks at
 * the middle of that overlap; a scene is looked at in its middle.
 */
export function resolveMotionRevisionTarget(
  spans: MotionSceneSpan[],
  target: MotionRevisionTarget,
): ResolvedMotionRevisionTarget {
  const totalMs = spans.at(-1)?.endMs ?? 0
  const at = (span: MotionSceneSpan, atMs: number): ResolvedMotionRevisionTarget => {
    // A moment is a frame inside the scene: never past its last frame.
    const offsetMs = Math.max(0, Math.min(atMs - span.startMs, span.endMs - span.startMs - 1_000 / TIMELINE_FPS))
    return { ok: true, span, atMs: span.startMs + offsetMs, offsetMs }
  }
  if (target.kind === 'scene') {
    const span = spans.find(value => value.sceneId === target.sceneId)
    return span ? at(span, (span.startMs + span.endMs) / 2) : { ok: false, error: 'scene_not_found' }
  }
  if (target.kind === 'time') {
    if (!Number.isFinite(target.timeMs)) return { ok: false, error: 'invalid_target' }
    if (target.timeMs < 0 || target.timeMs > totalMs) return { ok: false, error: 'target_out_of_range' }
    const span = spans.find(value => target.timeMs < value.endMs) ?? spans.at(-1)
    return span ? at(span, target.timeMs) : { ok: false, error: 'target_out_of_range' }
  }
  if (!Number.isFinite(target.startMs) || !Number.isFinite(target.endMs) || target.endMs <= target.startMs) {
    return { ok: false, error: 'invalid_target' }
  }
  let best: { span: MotionSceneSpan; overlap: number; from: number; to: number } | undefined
  for (const span of spans) {
    const from = Math.max(span.startMs, target.startMs)
    const to = Math.min(span.endMs, target.endMs)
    if (to > from && (!best || to - from > best.overlap)) best = { span, overlap: to - from, from, to }
  }
  return best ? at(best.span, (best.from + best.to) / 2) : { ok: false, error: 'target_out_of_range' }
}

/** How much of a piece a bake renders: the scenes with no segment rendered yet, of all its scenes. */
export interface MotionRerenderPlan {
  sceneIds: string[]
  total: number
}

/**
 * One run on a Graphic, read back from its job row: the brief or revision instruction as written,
 * who asked, what it cost and which take it produced. Edits saved from Code or Text/Style are runs
 * with no instruction (`action: 'render'`); an agent's push carries its own description as the
 * instruction.
 */
export interface MotionHistoryEntry {
  id: string
  action: 'generate' | 'revise' | 'revise-render' | 'revise-scene' | 'render' | 'graphic-render'
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  createdAt: string
  userId: string | null
  /** The brief or instruction as the model read it: `@image-N` for each image it was given. */
  instruction?: string
  /** The images the run was given, so `@image-N` can be shown by name. */
  images: MotionGraphicAssetSlot[]
  /** A one-scene revision's scene. */
  sceneId?: string
  tier?: string
  sourceMode?: MotionGraphicSourceMode
  /** The take a completed run produced. */
  revision?: number
  chargedOlives?: number
  /** An imported Graphic's render: its first take, a new take pushed over it, or an Adjust render. */
  reason?: 'created' | 'updated' | 'overrides'
  /** Asked for from outside the inspector: the Assistant, a workflow, or an agent over MCP (its kind). */
  via?: { assistant?: true; workflow?: true; agent?: McpAgentKind }
}

/** Ingredient identity retained in Yjs; ingredient bytes and source stay in R2. */
export interface MotionIngredient extends MotionGraphicAssetSlot {
  storageKey: string
  mimeType?: 'image/png' | 'image/webp'
  width?: number
  height?: number
  contentHash?: string
}

/** Lightweight asset metadata. Raw model-authored source must never be stored here. */
export interface GraphicPoster {
  key: string
  atSeconds: number
  frameIndex: number
  version: 1
  outputKey: string
}

export interface MotionGraphicMetadata {
  poster?: GraphicPoster
  schemaVersion: 1
  revision: number
  renderStatus: 'draft' | 'authoring' | 'rendering' | 'ready' | 'failed'
  source?: {
    storageKey: string
    hash: string
    mode: MotionGraphicSourceMode
    runtimeVersion: string
  }
  bakedSource?: {
    storageKey: string
    hash: string
  }
  ingredients: MotionIngredient[]
  activeGenerationId?: string
  renderer?: {
    /** `anypage` for an artifact rendered in the renderer's any-page mode (same profile, own capture loop). */
    name: 'hyperframes' | 'anypage'
    version: string
    chromeRevision: string
    /** Renderer profile id the bake ran on (`stub` for stub bakes). Absent on bakes from before profiles. */
    profile?: string
    /**
     * The host an imported Graphic's render ran on, as a short label (`<cpu model> [<signature>]`).
     * The full host record is on the render job's row, not in the Yjs doc.
     */
    hostLabel?: string
  }
  /** The scene segments the current bake was stitched from, in order. */
  segments?: MotionSegmentRecord[]
  authoring?: MotionAuthoringProgress
  /** The bake a Polish take replaced, kept so the draft stays reachable. */
  previousTake?: {
    /** Length of the replaced take, ticks; lets a later push refit untrimmed clips. */
    durationTicks?: number
    revision: number
    source: { storageKey: string; hash: string }
    uri: string
    thumbnailUri?: string
    /** What replaced it: a Polish pass over its draft (the default), or a targeted revision (ENG-962). */
    replacedBy?: 'polish' | 'revision'
  }
  /**
   * Set when the Graphic came in as a HyperFrames project from outside Martini (an agent's
   * `create_graphic` upload) instead of being authored here. Such a source renders natively (no
   * Martini wrapper document); `source` above then points at its GraphicProjectSourceV1 JSON.
   */
  origin?: 'authored' | 'imported'
  /**
   * Edits made in Martini on top of the immutable source (Adjust controls, timeline retimes). Stored
   * in Yjs so they are multiplayer and undoable; compiled into the render, never written into the
   * source files. A pull (`get_graphic_source`) returns the files with these applied.
   */
  overrides?: GraphicOverrides
  /** Private R2 key of the 2x2 keyframe sheet cut from the current render (imported Graphics). */
  contactSheetKey?: string
  /** The overrides the current render compiled in; differs from `overrides` while edits are unrendered. */
  renderedOverrides?: GraphicOverrides
  /** The render job whose output the asset shows; every publish changes it, a failure never does. */
  renderedGenerationId?: string
  lastError?: {
    code: string
    message: string
    /** Imported Graphics: what the render gate found, one entry per reason, written for the agent. */
    issues?: GraphicRenderIssue[]
  }
}

/**
 * Whether an asset is a Graphic: the `motion-graphic` operation and the record together. A Graphic's
 * rendered take pasted where its files could not follow (another project, a failed copy) keeps the
 * operation, which playback reads for transparency, but has nothing to edit or render, so every
 * surface that offers the Graphic (panel, badge, agent tools, routes) asks this, not the operation.
 */
export function isGraphic<T extends { operation?: AssetOperation; motionGraphic?: MotionGraphicMetadata }>(
  asset: T | null | undefined,
): asset is T & { operation: 'motion-graphic'; motionGraphic: MotionGraphicMetadata } {
  return asset?.operation === 'motion-graphic' && !!asset.motionGraphic
}

/** A render-gate finding on an imported Graphic, for the agent that built it: where, what, how to fix. */
export interface GraphicRenderIssue {
  code: string
  message: string
  /** One concrete sentence telling the agent what to change. */
  fix: string
}

// ---------------------------------------------------------------------------------------------
// Graphics imported from outside Martini (door 2). A user's own Claude/ChatGPT builds a HyperFrames
// project and pushes it over MCP; it becomes a Graphic: a `video` asset whose source is code.
// ---------------------------------------------------------------------------------------------

/** HyperFrames variable declaration (`data-composition-variables` on <html>), verbatim shape. */
export interface GraphicVariable {
  /** Scene-local declaration identity; public id is scoped to its file. */
  declared?: { file: string; id: string }
  /** Set by ingest for text it found undeclared: applied at render by selector. */
  derived?: { selector: string; file?: string; lineBreaks?: boolean }
  id: string
  type: 'string' | 'number' | 'color' | 'boolean' | 'enum'
  label: string
  default: string | number | boolean
  options?: Array<{ value: string; label: string }>
  min?: number
  max?: number
  step?: number
  unit?: string
  placeholder?: string
  maxLength?: number
  /**
   * Set at ingest when a sub-composition instance pins its own value (`data-variable-values`). A
   * render-time value overrides that pin, so here even a value equal to `default` changes frames.
   */
  pinned?: boolean
}

/** Variable values keyed by id — the `--variables` / `data-variable-values` shape. */
export type GraphicVariableValues = Record<string, string | number | boolean>

/**
 * One timed element of the entry composition, read at ingest (never executed): a direct child of
 * the root that carries `data-start`. Seconds, as HyperFrames writes them.
 */
export interface GraphicPiece {
  /** The element id (HyperFrames requires ids on media; ingest assigns `piece-N` when absent). */
  id: string
  kind: 'sub-composition' | 'video' | 'audio' | 'image' | 'element'
  start: number
  duration: number
  trackIndex?: number
  /** Project-relative path for sub-compositions and media. */
  src?: string
  /**
   * True when retiming this piece keeps its animation attached: a sub-composition host (its timeline
   * is nested at the host's start) or media. Plain elements animated by the root timeline are not.
   */
  movable: boolean
}

export type GraphicProjectFileRole = 'text' | 'media' | 'font' | 'vendor'

/** A file of an imported project. Text lives in private R2; media is also a Martini asset (lineage). */
export interface GraphicProjectFile {
  /** Project-relative POSIX path, no leading slash, no `..`. */
  path: string
  role: GraphicProjectFileRole
  mimeType: string
  bytes: number
  sha256: string
  /** Private R2 key (text, fonts, vendored libraries). */
  storageKey?: string
  /** Martini asset the file is backed by (media). */
  assetId?: string
}

/** A CDN library tag rewritten at ingest to a pinned, vendored copy (the sealed box has no network). */
export interface GraphicVendoredLibrary {
  library: string
  version: string
  /** Project path the tag now points at, e.g. `_vendor/gsap/3.15.0/gsap.min.js`. */
  path: string
  /** The URL the author wrote. */
  originalUrl: string
}

/** Stored as JSON in private R2; immutable per take. */
export interface GraphicProjectSourceV1 {
  schemaVersion: 1
  kind: 'hyperframes-project'
  entry: 'index.html'
  files: GraphicProjectFile[]
  durationMs: number
  width: number
  height: number
  backgroundMode: MotionGraphicBackgroundMode
  variables: GraphicVariable[]
  pieces: GraphicPiece[]
  vendored: GraphicVendoredLibrary[]
  /** Which animation runtimes the project uses, as detected at ingest. */
  runtimes: Array<'gsap' | 'animejs' | 'css' | 'waapi' | 'lottie' | 'three' | 'hf-seek' | 'clock'>
  /**
   * The page seeks itself (data-clock on the entry root): the renderer calls `window[seek](t)` for
   * every frame and awaits it, after awaiting `window[ready]` once. See docs/graphics/custom-clock.md.
   */
  clock?: { seek: string; ready?: string }
  /** What the agent said it made; shown per take like an authored prompt. */
  description?: string
}

/**
 * A plain web page imported as a Graphic (an "any page" artifact from claude.ai / ChatGPT: CSS
 * animations, framer-motion, rAF loops, timers). It is not seekable, so it renders in the renderer's
 * any-page mode: a virtual clock stepped one frame at a time, forward only, checked by rendering the
 * whole clip twice. Additive to GraphicProjectSourceV1; `kind` tells the two apart.
 */
export interface GraphicArtifactSourceV1 {
  schemaVersion: 1
  kind: 'anypage-artifact'
  entry: 'index.html'
  /**
   * The sealed render input (`index.html`, `anypage.json`, referenced media) plus the author's
   * originals under `source/` (kept for pulls, never sent to the renderer).
   */
  files: GraphicProjectFile[]
  durationMs: number
  width: number
  height: number
  backgroundMode: MotionGraphicBackgroundMode
  /** Click the page centre at t=0 for artifacts that start on input. */
  autoStart: boolean
  /** Present when the author sent React: what was bundled at import, offline, against a fixed allowlist. */
  build?: { tool: 'esbuild'; entry: string; allowlist: string[]; tailwind: boolean }
  /** What the page animates with, as detected at import (a report, not a gate). */
  runtimes: Array<'css' | 'waapi' | 'smil' | 'raf' | 'timers' | 'date' | 'react' | 'framer-motion' | 'recharts'>
  description?: string
}

/** Any imported Graphic source; `kind` selects the renderer path. */
export type GraphicSourceV1 = GraphicProjectSourceV1 | GraphicArtifactSourceV1

/** Martini-side edits over an immutable source (see MotionGraphicMetadata.overrides). */
export interface GraphicOverrides {
  variables?: GraphicVariableValues
  styles?: GraphicStyleOverrides
  /** Per-piece retimes, keyed by GraphicPiece.id. Seconds. */
  pieces?: Record<string, { start?: number; duration?: number }>
  /** The take these overrides were made against, so a newer push can carry them over by piece id. */
  baseRevision: number
}

/**
 * One Adjust value, a record of the `graphicVariableOverrides` Yjs map keyed by
 * `graphicVariableOverrideKey`. The app writes one record per edited variable, so two
 * collaborators changing different variables at once both land; the whole-object
 * `MotionGraphicMetadata.overrides` is what the server writes (carry-over on a push) and what
 * documents from before this map hold. `effectiveGraphicOverrides` reads the two together.
 */
export interface GraphicVariableOverrideRecord {
  assetId: string
  variableId: string
  /** Omitted for text/value records; one independent LWW register per style property. */
  property?: GraphicStyleProperty
  /** New records outrank migration-free legacy records within the same revision. */
  qualified?: true
  /** Reset this style property to source, masking any carried take value. */
  reset?: true
  value: string | number | boolean
  /** The take the value was set against; records from another take are ignored. */
  baseRevision: number
}

/** Longest variable id ingest accepts in `data-composition-variables`. */
export const GRAPHIC_VARIABLE_ID_MAX_LENGTH = 64

/**
 * Longest Adjust string value the board holds: room for a paragraph of copy, never a data URL. Every
 * record lives in the shared Yjs document, so an unbounded value costs every collaborator's load.
 */
export const GRAPHIC_VARIABLE_VALUE_MAX_LENGTH = 2000

/**
 * Most variables a composition declares (ingest refuses more), and so the most Adjust records the app
 * writes against one take: every declared variable always has room.
 */
export const GRAPHIC_VARIABLE_OVERRIDES_MAX = 100

/**
 * Most Adjust values read for one Graphic: a take's values (its carried-over overrides and its own
 * records, all declared), with headroom for legacy carried ids. Styles have their own property
 * bound; neither budget consumes the other.
 */
export const GRAPHIC_VARIABLE_OVERRIDES_READ_MAX = 2 * GRAPHIC_VARIABLE_OVERRIDES_MAX

/**
 * An Adjust value the board may hold: an id no longer than a declared variable's and a bounded
 * primitive. The app refuses to write anything else and every reader ignores it, so a client that
 * writes around the app cannot put an oversized value into a render, a copy or a carried-over take.
 * Ids the take does not declare are the render's to drop (`renderingGraphicVariables`).
 */
export function isGraphicVariableOverride(variableId: unknown, value: unknown): value is string | number | boolean {
  if (typeof variableId !== 'string' || !variableId || variableId.length > GRAPHIC_VARIABLE_ID_MAX_LENGTH) return false
  if (typeof value === 'string') return value.length <= GRAPHIC_VARIABLE_VALUE_MAX_LENGTH
  if (typeof value === 'number') return Number.isFinite(value)
  return typeof value === 'boolean'
}

export type GraphicStyleProperty = 'color' | 'fontSize' | 'fontWeight'
export type GraphicStyleOverrides = Record<string, Partial<Record<GraphicStyleProperty, string | number>>>
/** Input-only null deletes the property register; stored styles remain non-null. */
export type GraphicStyleOverrideInput = Record<string, Partial<Record<GraphicStyleProperty, string | number | null>>>
export const GRAPHIC_STYLE_OVERRIDES_MAX = 300
export const GRAPHIC_STYLE_OVERRIDES_READ_MAX = 2 * GRAPHIC_STYLE_OVERRIDES_MAX

export function isGraphicStyleValue(property: unknown, value: unknown): value is string | number {
  if (property === 'color') return typeof value === 'string' && /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(value)
  if (typeof value !== 'number' || !Number.isFinite(value)) return false
  if (property === 'fontSize') return value >= 1 && value <= 2000
  return property === 'fontWeight' && value >= 100 && value <= 900 && value % 100 === 0
}

/** Validates without silently discarding malformed style properties. Budgets count properties. */
export function isGraphicStyleOverrides(
  value: unknown,
  limit = GRAPHIC_STYLE_OVERRIDES_READ_MAX,
): value is GraphicStyleOverrides {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  let count = 0
  return Object.entries(value).every(([id, style]) => {
    if (!isGraphicVariableOverride(id, '') || !style || typeof style !== 'object' || Array.isArray(style)) return false
    const entries = Object.entries(style)
    count += entries.length
    return count <= limit && entries.length > 0 && entries.every(([property, v]) => isGraphicStyleValue(property, v))
  })
}

/** Legacy two-argument form is read-compatible only. New writes always supply the revision.
 * Encoded segments prevent a variable id containing slash/# from colliding with another register.
 * Yjs resolves concurrent writes to the same register with its deterministic last-write-wins rule.
 */
export function graphicVariableOverrideKey(
  assetId: string,
  variableId: string,
  baseRevision?: number,
  property?: GraphicStyleProperty,
): string {
  return baseRevision === undefined
    ? `${graphicVariableOverrideKeyPrefix(assetId)}${variableId}`
    : `${graphicVariableOverrideKeyPrefix(assetId)}r${baseRevision}/${encodeURIComponent(variableId)}/${property ?? 'value'}`
}

export function graphicVariableOverrideKeyPrefix(assetId: string): string {
  return `${assetId}/`
}

/** Carry happens once, by id, in the push snapshot. Never read previous-revision registers:
 * an offline edit made against an old take must not change a new take on reconnection.
 * Legacy unqualified records remain readable without migration, beneath qualified records.
 */
export function effectiveGraphicOverrides(
  assetId: string,
  metadata: Pick<MotionGraphicMetadata, 'revision' | 'overrides' | 'previousTake'> | undefined,
  records: Iterable<GraphicVariableOverrideRecord>,
): GraphicOverrides | undefined {
  if (!metadata) return undefined
  const { revision } = metadata
  const base = metadata.overrides?.baseRevision === revision ? metadata.overrides : undefined
  const variables: GraphicVariableValues = Object.fromEntries(
    Object.entries(base?.variables ?? {}).filter(([id, value]) => isGraphicVariableOverride(id, value)),
  )
  const styles: GraphicStyleOverrides = Object.create(null)
  for (const [id, style] of Object.entries(base?.styles ?? {})) {
    if (!style || typeof style !== 'object' || Array.isArray(style)) continue
    for (const [property, value] of Object.entries(style))
      if (isGraphicVariableOverride(id, '') && isGraphicStyleValue(property, value))
        (styles[id] ??= {})[property as GraphicStyleProperty] = value
  }
  let found = false
  const applicable = Array.from(records).filter(r => r && r.assetId === assetId && r.baseRevision === revision)
  for (const record of applicable.sort((a, b) => Number(!!a.qualified) - Number(!!b.qualified))) {
    if (!isGraphicVariableOverride(record.variableId, record.value)) continue
    if (record.property !== undefined) {
      if (record.reset === true && ['color', 'fontSize', 'fontWeight'].includes(record.property)) {
        delete styles[record.variableId]?.[record.property]
        found = true
        continue
      }
      if (!isGraphicStyleValue(record.property, record.value)) continue
      ;(styles[record.variableId] ??= {})[record.property] = record.value
    } else variables[record.variableId] = record.value
    found = true
  }
  if (!base && !found) return undefined
  const result: GraphicOverrides = { ...base, baseRevision: revision }
  const bounded = Object.entries(variables).slice(0, GRAPHIC_VARIABLE_OVERRIDES_READ_MAX)
  if (bounded.length) result.variables = Object.fromEntries(bounded)
  else delete result.variables
  let count = 0
  const boundedStyles: GraphicStyleOverrides = Object.create(null)
  for (const [id, style] of Object.entries(styles))
    for (const [property, value] of Object.entries(style)) {
      if (count++ < GRAPHIC_STYLE_OVERRIDES_READ_MAX)
        (boundedStyles[id] ??= {})[property as GraphicStyleProperty] = value
    }
  if (Object.keys(boundedStyles).length) result.styles = { ...boundedStyles }
  else delete result.styles
  return result
}

/** Canonical rendering state, independent of revision and insertion order. Use for identity,
 * expect, renderedOverrides, preview docId and the frontend dirty check. Styles apply only to
 * string/derived text; declared text targets every data-var-text binding in its declaring file,
 * including template fragments and all repeated instances. Pieces retain validated timing fields.
 */
export function renderingGraphicOverrides(
  overrides: GraphicOverrides | undefined,
  declared: readonly GraphicVariable[],
): Required<Pick<GraphicOverrides, 'variables' | 'styles' | 'pieces'>> {
  const variables = Object.fromEntries(
    Object.entries(renderingGraphicVariables(overrides?.variables, declared)).sort(([a], [b]) =>
      a < b ? -1 : a > b ? 1 : 0,
    ),
  )
  const styles: GraphicStyleOverrides = Object.create(null)
  for (const [id, style] of Object.entries(overrides?.styles ?? {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
    if (
      !style ||
      typeof style !== 'object' ||
      Array.isArray(style) ||
      !declared.some(v => v.id === id && (v.derived || v.type === 'string'))
    )
      continue
    for (const [property, value] of Object.entries(style).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      if (isGraphicStyleValue(property, value))
        (styles[id] ??= {})[property as GraphicStyleProperty] = typeof value === 'string' ? value.toLowerCase() : value
  }
  const pieces: NonNullable<GraphicOverrides['pieces']> = Object.create(null)
  for (const [id, retime] of Object.entries(overrides?.pieces ?? {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
    if (!retime || typeof retime !== 'object') continue
    for (const property of ['duration', 'start'] as const) {
      const value = retime[property]
      if (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= 0 &&
        value <= 90 &&
        (property !== 'duration' || value > 0)
      )
        (pieces[id] ??= {})[property] = value
    }
  }
  return { variables, styles: { ...styles }, pieces: { ...pieces } }
}

/** Same variable ids with the same values, ignoring key order. */
export function sameGraphicVariableValues(
  a: GraphicVariableValues | undefined,
  b: GraphicVariableValues | undefined,
): boolean {
  const left = a ?? {}
  const right = b ?? {}
  return (
    Object.keys(left).length === Object.keys(right).length &&
    Object.keys(left).every(key => Object.hasOwn(right, key) && left[key] === right[key])
  )
}

/**
 * The variable values that change a render: declared ids only, minus any set back to its declared
 * default (that renders what no value at all renders). A pinned variable keeps even its default:
 * there the value overrides an instance's own. Render identity, the sealed bundle and the app's
 * "not rendered yet" read values through this, so resetting a value never costs a render.
 */
export function renderingGraphicVariables(
  values: GraphicVariableValues | undefined,
  declared: readonly Pick<GraphicVariable, 'id' | 'default' | 'pinned'>[],
): GraphicVariableValues {
  const byId = new Map(declared.map(variable => [variable.id, variable]))
  const kept: GraphicVariableValues = Object.create(null)
  for (const [id, value] of Object.entries(values ?? {})) {
    const variable = byId.get(id)
    if (variable && (variable.pinned || variable.default !== value)) kept[id] = value
  }
  return { ...kept }
}

/**
 * Inset (in natural sheet pixels) applied to each cell of a panel-grid crop so
 * the extracted panels exclude the grid's divider lines. Canonical home
 * (contract C18): the onboarding 2×2 storyboard crops and `sheetQuadrantCss`
 * both derive from this value so quadrant CSS previews and the real crop
 * children render near-identical pixels.
 *
 * NOTE: the v9/v10 character-sheet extractors keep a private copy of this
 * constant until T-WATCH's separator-band hoist consolidates them here.
 */
export const PANEL_GRID_INSET_PX = 4

/**
 * Export job status - used for Premiere Pro XML export progress tracking.
 */
export type ExportStatus =
  'pending' | 'validating' | 'downloading' | 'generating' | 'bundling' | 'uploading' | 'completed' | 'failed'

/**
 * Render job status - used for video rendering progress tracking.
 */
export type RenderStatus =
  | 'pending'
  | 'queued' // Waiting for render slot (concurrency limit)
  | 'validating'
  | 'downloading'
  | 'generating' // Preparing render project
  | 'rendering' // Rendering video
  | 'uploading'
  | 'completed'
  | 'failed'

/** Named export destinations exposed by the web and MCP render surfaces. */
export type RenderPreset = 'youtube' | 'short-form' | 'square' | 'feed'
export type RenderResolution = '1080p' | '4k'
export type RenderFit = 'contain' | 'cover'

/**
 * Numeric rank for a resolution option value so dropdowns list low → high
 * (720p then 1080p then 4K; 1K then 2K then 4K). Handlers disagree on notation
 * (`4k` vs `4K` vs `2160p`), so this normalizes before comparing.
 */
export function resolutionSortRank(value: string): number {
  const v = value.trim().toLowerCase()
  if (v === 'sd') return 480
  if (v === 'hd') return 1080
  if (v === 'uhd' || v === '4k') return 2160
  if (v === '8k') return 4320
  const pMatch = /^(\d+)p$/.exec(v)
  if (pMatch) return Number(pMatch[1])
  const kMatch = /^(\d+)k$/.exec(v)
  if (kMatch) return Number(kMatch[1]) * 1024
  return Number.POSITIVE_INFINITY
}

/**
 * Maps a resolution label (e.g. "1080p", "4K") to its pixel height so per-second
 * models with `resolutionTiers` land on the right rung.
 *
 * Canonical for BOTH the quote and the charge — the frontend estimate and
 * `calculateGenerationCost` import this one function, because two copies that
 * drift by a single case arm silently bill a different rung than the button
 * showed. An unmatched label returns `undefined`, which skips `resolutionTiers`
 * and falls back to the model's BASE rate; a handler that rewrites unsupported
 * labels must clamp to a rung this function agrees with (ENG-761).
 *
 * Distinct from `resolutionSortRank` — that ranks options for a dropdown and
 * reads "2K" as 2048; this is real pixel height, so "2K" is 1440.
 */
export function resolutionStringToHeight(resolution?: string | null): number | undefined {
  if (!resolution) return undefined
  switch (resolution.trim().toLowerCase()) {
    case '360p':
      return 360
    case '480p':
      return 480
    case '540p':
      return 540
    case '720p':
      return 720
    case '768p':
      return 768
    case '1080p':
      return 1080
    case '1440p':
    case '2k':
      return 1440
    case '2160p':
    case '4k':
      return 2160
    default:
      return undefined
  }
}

/**
 * Resolves an orientation-neutral video resolution tier. A portrait
 * 1080×1920 output is still 1080p, so explicit geometry uses the short edge.
 */
export function resolveVideoResolutionTierHeight(input: {
  outputWidth?: number
  outputHeight?: number
  sourceWidth?: number
  sourceHeight?: number
  scaleFactor?: number
  resolution?: string | null
}): number | undefined {
  const isPositiveFinite = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0

  if (isPositiveFinite(input.outputWidth) && isPositiveFinite(input.outputHeight)) {
    return Math.min(input.outputWidth, input.outputHeight)
  }
  if (
    isPositiveFinite(input.sourceWidth) &&
    isPositiveFinite(input.sourceHeight) &&
    isPositiveFinite(input.scaleFactor)
  ) {
    return Math.min(input.sourceWidth, input.sourceHeight) * input.scaleFactor
  }
  return resolutionStringToHeight(input.resolution)
}

export function compareResolutionValues(a: string, b: string): number {
  const diff = resolutionSortRank(a) - resolutionSortRank(b)
  return diff !== 0 ? diff : a < b ? -1 : a > b ? 1 : 0
}

export function sortResolutionOptions<T extends { value: string | number }>(options: readonly T[]): T[] {
  return [...options].sort((a, b) => compareResolutionValues(String(a.value), String(b.value)))
}

/** Explicit output geometry for an MP4 render. */
export interface RenderTargetFrame {
  width: number
  height: number
  fit: RenderFit
}

export const DEFAULT_RENDER_TARGET_FRAME: Readonly<RenderTargetFrame> = {
  width: 1920,
  height: 1080,
  fit: 'contain',
}

const MAX_RENDER_TARGET_DIMENSION = 3840
const MAX_RENDER_TARGET_PIXELS = 3840 * 2160

export function isValidRenderTargetFrame(frame: RenderTargetFrame | undefined): frame is RenderTargetFrame {
  if (!frame) return false
  const { width, height, fit } = frame
  return (
    Number.isInteger(width) &&
    Number.isInteger(height) &&
    width >= 2 &&
    height >= 2 &&
    width <= MAX_RENDER_TARGET_DIMENSION &&
    height <= MAX_RENDER_TARGET_DIMENSION &&
    width % 2 === 0 &&
    height % 2 === 0 &&
    width * height <= MAX_RENDER_TARGET_PIXELS &&
    (fit === 'contain' || fit === 'cover')
  )
}

/** Canonical dimensions for each named export destination. */
export const RENDER_PRESET_FRAMES = {
  youtube: {
    aspectRatio: '16:9',
    resolutions: {
      '1080p': { width: 1920, height: 1080 },
      '4k': { width: 3840, height: 2160 },
    },
  },
  'short-form': {
    aspectRatio: '9:16',
    resolutions: {
      '1080p': { width: 1080, height: 1920 },
      '4k': { width: 2160, height: 3840 },
    },
  },
  square: {
    aspectRatio: '1:1',
    resolutions: {
      '1080p': { width: 1080, height: 1080 },
      '4k': { width: 2160, height: 2160 },
    },
  },
  feed: {
    aspectRatio: '4:5',
    resolutions: {
      '1080p': { width: 1080, height: 1350 },
      '4k': { width: 2160, height: 2700 },
    },
  },
} as const satisfies Record<
  RenderPreset,
  {
    aspectRatio: string
    resolutions: Record<RenderResolution, { width: number; height: number }>
  }
>

export function resolveRenderPresetFrame(
  preset: RenderPreset,
  resolution: RenderResolution = '1080p',
  fit: RenderFit = 'contain',
): RenderTargetFrame {
  return { ...RENDER_PRESET_FRAMES[preset].resolutions[resolution], fit }
}

/** Video codec of a timeline render. ProRes renders are 10-bit 4:2:2 `.mov` masters with PCM audio. */
export const RENDER_CODECS = ['h264', 'prores_422', 'prores_422_hq'] as const
export type RenderCodec = (typeof RENDER_CODECS)[number]
export const DEFAULT_RENDER_CODEC: RenderCodec = 'h264'

/**
 * Output frame rates of a timeline render. The timeline itself is edited on the
 * `TIMELINE_FPS` grid; a render at another rate places each edit on the nearest
 * output frame and keeps the duration in seconds.
 */
export const RENDER_FPS_OPTIONS = [24, 25, 29.97, 30, 50, 59.94, 60] as const
export type RenderFps = (typeof RENDER_FPS_OPTIONS)[number]
export const DEFAULT_RENDER_FPS: RenderFps = 24

export function isRenderCodec(value: unknown): value is RenderCodec {
  return (RENDER_CODECS as readonly unknown[]).includes(value)
}

export function isRenderFps(value: unknown): value is RenderFps {
  return (RENDER_FPS_OPTIONS as readonly unknown[]).includes(value)
}

export function isProResCodec(codec: RenderCodec): boolean {
  return codec !== 'h264'
}

/** Exact rational rate: 29.97 and 59.94 are the NTSC rates 30000/1001 and 60000/1001. */
export function renderFrameRate(fps: RenderFps): { num: number; den: number } {
  return Number.isInteger(fps) ? { num: fps, den: 1 } : { num: Math.round(fps * 1001), den: 1001 }
}

export function renderCodecContainer(codec: RenderCodec): { extension: 'mp4' | 'mov'; contentType: string } {
  return isProResCodec(codec)
    ? { extension: 'mov', contentType: 'video/quicktime' }
    : { extension: 'mp4', contentType: 'video/mp4' }
}

// Apple's ProRes target rates at 1920x1080 29.97 (ProRes white paper): 147 Mb/s for 422 and
// 220 Mb/s for 422 HQ. Scaled per pixel and frame, 10 s of 4K at 24 fps is about 0.88 GB of HQ.
const HD_NTSC_PIXELS_PER_SECOND = 1920 * 1080 * (30000 / 1001)
const PRORES_BITS_PER_PIXEL: Record<Exclude<RenderCodec, 'h264'>, number> = {
  prores_422: 147e6 / HD_NTSC_PIXELS_PER_SECOND,
  prores_422_hq: 220e6 / HD_NTSC_PIXELS_PER_SECOND,
}
/** 48 kHz stereo 24-bit PCM. */
const PRORES_AUDIO_BITS_PER_SECOND = 48_000 * 2 * 24

/**
 * Render scratch space is the machine's root disk (8 GB on Fly, no volume), which also holds the
 * image and the downloaded sources, and the loudness pass writes a second copy of the output. A
 * ProRes render whose estimate passes this is refused. Very large masters are ENG-1095's.
 */
export const PRORES_MAX_ESTIMATED_BYTES = 2 * 1024 ** 3

/** Expected bytes of a ProRes render at its target rate; null for H.264, whose size depends on the content. */
export function estimateRenderBytes(input: {
  codec: RenderCodec
  width: number
  height: number
  fps: RenderFps
  durationSeconds: number
}): number | null {
  if (input.codec === 'h264') return null
  const { num, den } = renderFrameRate(input.fps)
  const videoBitsPerSecond = PRORES_BITS_PER_PIXEL[input.codec] * input.width * input.height * (num / den)
  return Math.round(((videoBitsPerSecond + PRORES_AUDIO_BITS_PER_SECOND) * Math.max(0, input.durationSeconds)) / 8)
}

/** Scale a sequence's working frame for export while staying inside the render service limits. */
export function resolveSequenceRenderFrame(
  storedFrame: RenderTargetFrame | undefined,
  resolution: RenderResolution = '1080p',
  requestedFit?: RenderFit,
): RenderTargetFrame {
  const frame = isValidRenderTargetFrame(storedFrame) ? storedFrame : DEFAULT_RENDER_TARGET_FRAME
  const fit = requestedFit ?? frame.fit
  if (resolution === '1080p') return { ...frame, fit }

  const dimensionScale = MAX_RENDER_TARGET_DIMENSION / Math.max(frame.width, frame.height)
  const pixelScale = Math.sqrt(MAX_RENDER_TARGET_PIXELS / (frame.width * frame.height))
  const scale = Math.min(2, dimensionScale, pixelScale)
  // Round inward: nearest-even rounding can push an otherwise valid frame
  // back over the pixel cap after scaling (for example 1082×1918 at 4K).
  const roundEvenWithinLimit = (value: number) => Math.max(2, Math.floor(value / 2) * 2)

  return {
    width: roundEvenWithinLimit(frame.width * scale),
    height: roundEvenWithinLimit(frame.height * scale),
    fit,
  }
}

/**
 * Fit a clip's fade-in and fade-out inside its duration. When the two fades
 * together exceed the clip, both are scaled by the same factor so they meet at
 * the point they would have crossed, preserving the ratio the editor chose.
 * Units are the caller's (ticks, seconds, frames) — the rule is unit-agnostic,
 * and MCP, the browser preview, MLT, and XMEML all resolve fades through it.
 */
export function clampAudioFades(
  fadeIn: number = 0,
  fadeOut: number = 0,
  duration: number,
): { fadeIn: number; fadeOut: number } {
  const positiveFadeIn = Math.max(0, fadeIn)
  const positiveFadeOut = Math.max(0, fadeOut)
  const total = positiveFadeIn + positiveFadeOut
  if (total <= duration || total === 0) return { fadeIn: positiveFadeIn, fadeOut: positiveFadeOut }
  const scale = Math.max(0, duration) / total
  return { fadeIn: positiveFadeIn * scale, fadeOut: positiveFadeOut * scale }
}

// =============================================================================
// Video transitions
// =============================================================================

/** Visual transitions a cut can carry. A plain cut is the absence of a transition. */
export const CLIP_TRANSITION_TYPES = ['crossDissolve', 'dipToBlack'] as const
export type ClipTransitionType = (typeof CLIP_TRANSITION_TYPES)[number]

/**
 * A transition stored on the outgoing clip (`transitionOut`) for the cut at its
 * end. It is centered on the cut: half its duration plays before the cut, half
 * after, and no clip moves.
 */
export interface ClipTransition {
  type: ClipTransitionType
  /** Total length in timeline ticks. */
  duration: number
}

/** One second, the default in every mainstream editor. */
export const DEFAULT_CLIP_TRANSITION_DURATION_TICKS = 48000
/**
 * Two clips meet at a cut only when one ends exactly where the next starts
 * (one tick absorbs float noise). Exactly meeting ticks always round to the
 * same frame, so the render, preview and export agree on every cut; a sub-frame
 * gap renders as a black frame and leaves the transition dormant.
 */
export const CLIP_TRANSITION_ADJACENCY_TICKS = 1

export function isClipTransition(value: unknown): value is ClipTransition {
  if (!value || typeof value !== 'object') return false
  const { type, duration } = value as Record<string, unknown>
  return (
    CLIP_TRANSITION_TYPES.includes(type as ClipTransitionType) &&
    typeof duration === 'number' &&
    Number.isFinite(duration) &&
    duration > 0
  )
}

/** The fields {@link resolveClipTransitions} reads from a clip. */
export interface TransitionTrackClip {
  id: string
  startOnTimeline: number
  duration: number
  transitionOut?: ClipTransition
  /** Nested-sequence clips never take part in a transition. */
  nestedSequenceId?: string
}

/** A transition that plays, in the timeline coordinates of the clips it was resolved from. */
export interface ResolvedClipTransition {
  type: ClipTransitionType
  outgoingClipId: string
  incomingClipId: string
  /** Where the outgoing clip ends and the incoming one starts. */
  cut: number
  /** `cut` minus half the played duration. */
  start: number
  /** `cut` plus half the played duration. */
  end: number
}

/**
 * The transitions that play on one track. A clip's `transitionOut` plays only
 * while the next clip on the track starts where it ends (within
 * {@link CLIP_TRANSITION_ADJACENCY_TICKS}); otherwise it is dormant and the cut
 * stays hard. Neither side may be a nested sequence.
 *
 * Each half is capped at half of each neighbour, so a clip with transitions at
 * both ends always keeps them apart. Like fades, the stored duration is clamped
 * where it is read, never rewritten, and the render, the browser preview, MCP
 * and XMEML export all resolve transitions through this one function.
 *
 * @param clips - Clips of a single track, in any order.
 */
export function resolveClipTransitions(clips: readonly TransitionTrackClip[]): ResolvedClipTransition[] {
  const sorted = [...clips].sort((a, b) => a.startOnTimeline - b.startOnTimeline)
  const resolved: ResolvedClipTransition[] = []
  for (let index = 0; index < sorted.length - 1; index++) {
    const outgoing = sorted[index]
    const incoming = sorted[index + 1]
    const transition = outgoing.transitionOut
    if (!isClipTransition(transition) || outgoing.nestedSequenceId || incoming.nestedSequenceId) continue
    const cut = incoming.startOnTimeline
    if (Math.abs(outgoing.startOnTimeline + outgoing.duration - cut) > CLIP_TRANSITION_ADJACENCY_TICKS) continue
    const half = Math.min(transition.duration, outgoing.duration, incoming.duration) / 2
    if (!(half > 0)) continue
    resolved.push({
      type: transition.type,
      outgoingClipId: outgoing.id,
      incomingClipId: incoming.id,
      cut,
      start: cut - half,
      end: cut + half,
    })
  }
  return resolved
}

/**
 * How visible each side of a resolved transition is at `time`, from 0 to 1.
 *
 * - Cross dissolve: the outgoing picture stays fully drawn underneath while the
 *   incoming one is laid over it at `incoming`, so the blend is
 *   `incoming * B + (1 - incoming) * A` across the whole window.
 * - Dip to black: the outgoing picture fades to black by the cut, then the
 *   incoming one fades up from black. Only one side is drawn at a time.
 *
 * Progress is linear in time; MLT's luma dissolve uses the same ramp.
 */
export function clipTransitionMix(
  transition: Pick<ResolvedClipTransition, 'type' | 'cut' | 'start' | 'end'>,
  time: number,
): { outgoing: number; incoming: number } {
  const clamp01 = (value: number) => Math.min(1, Math.max(0, value))
  if (transition.type === 'crossDissolve') {
    const span = transition.end - transition.start
    return { outgoing: 1, incoming: span > 0 ? clamp01((time - transition.start) / span) : 1 }
  }
  if (time < transition.cut) {
    const span = transition.cut - transition.start
    return { outgoing: span > 0 ? clamp01((transition.cut - time) / span) : 0, incoming: 0 }
  }
  const span = transition.end - transition.cut
  return { outgoing: 0, incoming: span > 0 ? clamp01((time - transition.cut) / span) : 1 }
}

/**
 * URLs returned from backend after synchronous frame upload.
 * These are R2 URLs safe to store in Yjs (small strings, not base64).
 *
 * Used by:
 * - /generate endpoint response (backend returns uploaded frame URLs)
 * - /upload/frame endpoint response
 * - Frontend to store in generationParams for regeneration
 */
export interface FrameUrls {
  imageUrl?: string
  endFrameUrl?: string
  middleFrameUrl?: string
  /** Inpainting mask (model: flux-pro-fill), uploaded to R2 like the other frames. */
  maskUrl?: string
  imageReferences?: string[]
  elements?: {
    frontalImageUrl: string
    referenceImageUrls?: string[]
  }[]
}

export interface TtsTimestampSpan {
  text: string
  /** Seconds. */
  start: number
  /** Seconds. */
  end: number
}

/**
 * The TTS vendor's own word timing for a generated voice line, stored
 * unchanged (vendors can misplace a word; consumers sanity-check it). Times are
 * seconds on the delivered file's clock: vendor time + `offsetSeconds` = time
 * from sample 0 of the asset's original file.
 */
export interface TtsTimestamps {
  source: 'typecast/with-timestamps'
  clock: 'delivered-file'
  offsetSeconds: number
  audioDuration?: number
  /** Words only: the vendor's per-character spans would bloat the shared doc and every agent read. */
  words: TtsTimestampSpan[]
}

/**
 * Source video color metadata captured at transcode time via ffprobe.
 *
 * Powers honest HDR/SDR preview labeling across the canvas, the expanded
 * modal, and spec badges. Absent on legacy assets transcoded before this was
 * tracked (treat missing as "unknown / assume SDR").
 */
export interface SourceColorMetadata {
  /** Bits per color sample (8, 10, 12). */
  bitDepth?: number
  /** Normalized color transfer characteristic — e.g. 'smpte2084' (PQ), 'arib-std-b67' (HLG), 'bt709'. */
  transfer?: string
  /** Normalized color primaries — e.g. 'bt2020', 'bt709'. */
  primaries?: string
  /** True when the source is HDR (PQ/HLG transfer). */
  isHdr: boolean
}

/**
 * Supported video container formats.
 * This is the SINGLE SOURCE OF TRUTH for what we accept.
 *
 * Note: FFmpeg handles decoding all common video/audio codecs.
 * This list is about CONTAINERS, not codecs.
 */
export const SUPPORTED_VIDEO_FORMATS = {
  // Standard formats
  'video/mp4': { extension: '.mp4', label: 'MP4' },
  'video/quicktime': { extension: '.mov', label: 'MOV' },
  'video/webm': { extension: '.webm', label: 'WebM' },
  'video/x-matroska': { extension: '.mkv', label: 'MKV' },
  // Broadcast/legacy formats
  'video/mp2t': { extension: '.ts', label: 'TS' },
  'video/x-msvideo': { extension: '.avi', label: 'AVI' },
} as const

export type SupportedVideoMimeType = keyof typeof SUPPORTED_VIDEO_FORMATS

/** All supported extensions (for validation). */
export const SUPPORTED_VIDEO_EXTENSIONS = Object.values(SUPPORTED_VIDEO_FORMATS).map(f => f.extension)

/** Human-readable format list (for error messages). */
export const SUPPORTED_VIDEO_LABELS = Object.values(SUPPORTED_VIDEO_FORMATS).map(f => f.label)

// ============================================
// Model Pricing (Single Source of Truth)
// ============================================

/** Value of one olive in USD. */
/**
 * A media reference the pipeline can persist and submit: an uploaded http(s)
 * URL — including local-dev filesystem storage — as opposed to a
 * lifecycle-temporary reference (`pending:<uuid>` upload markers, `blob:`
 * object URLs, `data:` URIs). This is the ONE definition of "durable"; three
 * independent copies of it (frontend readiness, frontend submission, backend
 * validation) each independently required `https:` and each independently
 * broke local dev. Provider fetchability of loopback URLs is a separate
 * concern handled at submission time (providerFetchableInput).
 */
export function isDurableMediaUrl(value: string): boolean {
  return /^https?:\/\//i.test(value)
}

export const OLIVE_VALUE_USD = 0.1

// ============================================
// Subscription plan ids
// ============================================
// A plan id names a family (olive plans, or fal-key plans that run generation on
// the org's own fal key), a tier, whether it is complimentary, and its billing
// interval: `byok_` prefix, then the tier, then `_comp` or `_annual`. There is
// no `byok_free`: fal-key Free is the `default` plan plus a stored fal key, a
// difference read from the key state, never from the plan id
// (docs/byok-billing-plan.md → Plan model). Gates and display lists derive from
// parsePlanId instead of enumerating ids.

export type PlanFamily = 'olives' | 'byok'
export type PlanTier = 'free' | 'pro' | 'studio' | 'workshop' | 'enterprise'
export type PlanInterval = 'month' | 'year'

export interface ParsedPlanId {
  family: PlanFamily
  tier: PlanTier
  comp: boolean
  interval: PlanInterval
}

/** Every subscription plan id Martini knows. Anything else parses as an unknown free plan. */
export const KNOWN_PLAN_IDS = [
  'default',
  'pro',
  'pro_comp',
  'studio',
  'studio_comp',
  'workshop_comp',
  'enterprise',
  'enterprise_comp',
  'byok_pro',
  'byok_pro_comp',
  'byok_pro_annual',
  'byok_studio',
  'byok_studio_comp',
  'byok_studio_annual',
] as const

export type KnownPlanId = (typeof KNOWN_PLAN_IDS)[number]

const KNOWN_PLAN_ID_SET: ReadonlySet<string> = new Set(KNOWN_PLAN_IDS)

/** Plan rank by tier. Workshop ranks with Studio but never lifts a member's tier. */
export const PLAN_TIER_RANK: Record<PlanTier, number> = {
  free: 0,
  pro: 1,
  studio: 2,
  workshop: 2,
  enterprise: 3,
}

/**
 * Family, tier, comp flag and interval of a plan id. An unknown id (or none)
 * is an olive free plan, as before; its comp flag still follows the `_comp`
 * suffix so `isCompPlan` keeps answering the same for ids we don't list.
 */
export function parsePlanId(plan: string | null | undefined): ParsedPlanId {
  if (!plan || !KNOWN_PLAN_ID_SET.has(plan) || plan === 'default') {
    return { family: 'olives', tier: 'free', comp: Boolean(plan?.endsWith('_comp')), interval: 'month' }
  }
  let rest = plan
  const family: PlanFamily = rest.startsWith('byok_') ? 'byok' : 'olives'
  if (family === 'byok') rest = rest.slice('byok_'.length)
  const comp = rest.endsWith('_comp')
  if (comp) rest = rest.slice(0, -'_comp'.length)
  const interval: PlanInterval = rest.endsWith('_annual') ? 'year' : 'month'
  if (interval === 'year') rest = rest.slice(0, -'_annual'.length)
  // Only known ids reach here, and the parser test pins each one's tier.
  return { family, tier: rest as PlanTier, comp, interval }
}

/** Rank of a plan id's tier: free/unknown 0, pro 1, studio and workshop 2, enterprise 3. */
export function getPlanTierRank(plan: string | null | undefined): number {
  return PLAN_TIER_RANK[parsePlanId(plan).tier]
}

const PLAN_TIER_LABEL: Record<PlanTier, string> = {
  free: 'Indie',
  pro: 'Pro',
  studio: 'Studio',
  workshop: 'Workshop',
  enterprise: 'Enterprise',
}

/**
 * The plan's tier name ("Pro", "Studio"), the same in both families.
 * Unknown ids (and no plan) return null.
 */
export function planTierLabel(plan: string | null | undefined): string | null {
  if (!plan || !KNOWN_PLAN_ID_SET.has(plan)) return null
  return PLAN_TIER_LABEL[parsePlanId(plan).tier]
}

/**
 * Customer-facing plan name: "Pro", "Studio", in both families, as the Autumn
 * plans are named; the plan id, not the name, marks a fal-key plan. Comp and
 * annual variants share their plan's name. Unknown ids return null so callers
 * keep their own fallback.
 */
export function planDisplayName(plan: string | null | undefined): string | null {
  return planTierLabel(plan)
}

/** Statuses in which a fal-key plan carries its entitlements. These plans have no legacy null-status rows. */
const LIVE_BYOK_PLAN_STATUSES: ReadonlySet<string> = new Set(['active', 'trialing', 'past_due'])

/**
 * A fal-key plan (`byok_*`) in force: active, trialing, or past due. An open
 * checkout (`pending_checkout` / `pending_payment`) or a lapsed subscription is
 * not the plan yet: until Autumn activates it the org is on fal-key Free (or on
 * olives, without a key), so neither the plan family nor the allowance may
 * follow the plan id alone.
 */
export function hasLiveByokPlan(plan: string | null | undefined, status: string | null | undefined): boolean {
  return parsePlanId(plan).family === 'byok' && !!status && LIVE_BYOK_PLAN_STATUSES.has(status)
}

/** The plan state GET /billing reports on each billing org, personal and team. */
export interface BillingOrgPlanState {
  subscriptionPlan: string | null
  subscriptionStatus: string | null
  /** `byok` on a live fal-key plan, or once a fal key is in use (fal-key Free is `default` + key); else `olives`. */
  planFamily?: PlanFamily
  /**
   * When the free trial ends, as Autumn reports it (`organizations.subscription_trial_ends_at`,
   * written by the plan sync); null without a trial. ISO 8601.
   */
  trialEndsAt?: string | null
}

/**
 * GET /billing/pricing `trialAvailable`: whether the signed-in person's next fal-key checkout would carry the
 * 30-day free trial (their `billing_trial_redemptions` claim and those of accounts on the same device or inbox).
 * Absent when the ledger can't be read; the plan cards then keep the trial copy, and the checkout's own preview
 * decides the trial either way.
 */
export interface FalTrialEligibility {
  trialAvailable?: boolean
}

/** What follows a fal-key plan's name wherever a customer reads which plan an org is on. */
export const FAL_KEY_PLAN_SUFFIX = ' · your fal key'

/**
 * The plan id an org's billing page reads it as being on. The checkout routes
 * record the plan being bought on the row with `pending_checkout` /
 * `pending_payment` (the confirm step reads it from there), so an open or
 * abandoned checkout is not the plan yet: it reads as `default`, the free plan
 * the org is still on. Backstage shows this id next to the recorded one.
 */
export function effectiveBillingPlan(plan: string | null | undefined, status: string | null | undefined): string {
  const openCheckout = status === 'pending_checkout' || status === 'pending_payment'
  return openCheckout || !plan ? 'default' : plan
}

/**
 * The plan name the billing pages show for an org, with the fal-key family
 * marked: "Free · your fal key" for the `default` plan with a fal key in use
 * (planFamily `byok`), "Pro · your fal key" / "Studio · your fal key" on a
 * fal-key plan. An open checkout (`pending_checkout` / `pending_payment`)
 * reads as the free plan the org is still on. Every other plan keeps
 * planDisplayName ("Indie", "Pro", "Studio"); an unknown id returns null so the
 * caller keeps its own fallback. Invoices keep planDisplayName: they bill the
 * plan, not the key.
 */
export function billingPlanLabel(org: {
  plan: string | null | undefined
  status: string | null | undefined
  /** GET /billing `planFamily`; omitted where unknown, which reads as olives. */
  family?: PlanFamily | null
}): string | null {
  const plan = effectiveBillingPlan(org.plan, org.status)
  if (parsePlanId(plan).family === 'byok') return `${planDisplayName(plan)}${FAL_KEY_PLAN_SUFFIX}`
  if (plan === 'default' && org.family === 'byok') return `Free${FAL_KEY_PLAN_SUFFIX}`
  return planDisplayName(plan)
}

// ============================================
// The org's own provider keys (BYOK)
// ============================================

/** A provider an organization can bring its own key for. `fal.ai` also covers Google- and xAI-routed models. */
export type ByokProviderId = 'fal.ai' | 'enhancor' | 'elevenlabs' | 'typecast'

/** Whose key paid a generation: Martini's (charged in olives) or the org's own (billed by the provider). */
export type ProviderKeySource = 'martini' | 'byok'

/**
 * How the org's own fal key last failed a generation: fal refused the key
 * (`rejected`), or fal locked the account for want of balance or over its
 * spending limit (`balance_exhausted`).
 */
export type FalKeyErrorCode = 'rejected' | 'balance_exhausted'

/**
 * The fal key's last failure, kept until a generation succeeds on the key or
 * the key is saved again (`organization_provider_billing_settings.last_key_error`).
 * `at` is ISO 8601.
 */
export interface FalKeyError {
  code: FalKeyErrorCode
  at: string
}

// ============================================
// Processing-seconds meter
// ============================================
// Minutes of video and audio, generated or uploaded, with an image counting as
// 10 seconds (docs/byok-billing-plan.md → Processing-seconds meter). Only the
// fal-key plans carry an allowance; it is a soft cap: over it the billing page
// and a banner say so, and nothing is blocked.

/** What produced a `processing_usage` row. */
export type ProcessingUsageSource = 'generation' | 'upload' | 'render'

/** Processing seconds a month included with each fal-key tier. */
export const PROCESSING_ALLOWANCE_SECONDS = {
  /** fal-key Free: the `default` plan with a stored fal key (150 minutes). */
  free: 9_000,
  pro: 90_000,
  studio: 900_000,
} as const

/**
 * Processing seconds a month the org's plan includes, or null when the plan
 * has no allowance (uncapped, never bannered). Only fal-key orgs have one:
 * `default` with a stored key is fal-key Free, and the `byok_pro` /
 * `byok_studio` families (comp and annual variants included) carry their
 * tier's while the plan is live (hasLiveByokPlan). A fal-key plan whose
 * checkout is still open, or that lapsed, counts as the `default` plan the org
 * has not left. Olive plans, and any other plan an org with a key is on, have
 * none.
 */
export function processingAllowanceSeconds(
  plan: string | null | undefined,
  family: PlanFamily,
  status: string | null | undefined,
): number | null {
  if (family !== 'byok') return null
  const parsed = parsePlanId(plan)
  if (parsed.family === 'byok') {
    if (!hasLiveByokPlan(plan, status)) return PROCESSING_ALLOWANCE_SECONDS.free
    if (parsed.tier === 'pro') return PROCESSING_ALLOWANCE_SECONDS.pro
    if (parsed.tier === 'studio') return PROCESSING_ALLOWANCE_SECONDS.studio
    return null
  }
  return !plan || plan === 'default' ? PROCESSING_ALLOWANCE_SECONDS.free : null
}

/**
 * An org's processing seconds for its current period, as GET /billing reports
 * it on every fal-key org (`processing`; null for olive-family orgs).
 */
export interface ProcessingMeter {
  usedSeconds: number
  /** The plan's allowance; null when the plan has none (uncapped). */
  includedSeconds: number | null
  /** ISO start of the period the sum covers (inclusive). */
  periodStart: string
  /** ISO end of the period (exclusive). */
  periodEnd: string
  /** When the allowance starts over: the period end. */
  resetAt: string
  bySource: Record<ProcessingUsageSource, number>
  /** Seconds past the allowance; 0 within it or without one. */
  overBy: number
}

/** The code a generation or upload answers when the (dark) hard stop refuses it. */
export const PROCESSING_LIMIT_REACHED_CODE = 'PROCESSING_LIMIT_REACHED'

// ============================================
// Top-up bonus (Pricing v2) — subscribers only
// ============================================
// Two stacked incentives, surfaced as one "+N bonus" per pack:
//  1. Loyalty %: flat per paid tier (Pro/Studio). FREE users get NO bonus.
//  2. Volume %: +1% per step UP the plan's own pack ladder, so bigger presets
//     visibly reward more — the brackets ARE the tiles users see, so a buyer
//     who does the math sees "bigger is better".
// The credited bonus is rounded to the NEAREST 5 olives so totals stay clean.
// Founder call 2026-07-06.

/**
 * Loyalty bonus % by paid plan tier (parsePlanId: comp, annual and fal-key
 * variants share their tier's rate; free/unknown = 0 = none).
 * Same rate for both tiers — it's never printed as a %, so there's no need to
 * differentiate, and it keeps the grant simple.
 */
export const TOPUP_LOYALTY_BONUS_PCT: Record<string, number> = { pro: 3, studio: 3 }

/**
 * Plan pack ladders by tier — the top-up presets shown to each tier. Canonical
 * here so the volume-bonus brackets stay aligned with the tiles users actually
 * see (TopUpCard imports these).
 */
export const TOPUP_PACK_LADDER: Record<string, readonly number[]> = {
  pro: [300, 750, 2000, 5000],
  studio: [750, 2000, 5000, 10000],
}

/** Extra volume bonus % by ladder position (1st preset +0 … 4th preset +3). */
const TOPUP_VOLUME_BONUS_PCT_BY_TIER = [0, 1, 2, 3]

/**
 * Bonus olives on a top-up: loyalty% (paid tier) + volume% (position on that
 * plan's pack ladder), rounded to the nearest 5 olives. Free/unknown plans get
 * 0. Used by the backend grant AND the frontend display so they match by
 * construction.
 */
export function computeTopupBonus(
  plan: string | null | undefined,
  faceOlives: number,
): { bonusOlives: number; pct: number } {
  const { tier } = parsePlanId(plan)
  const loyaltyPct = TOPUP_LOYALTY_BONUS_PCT[tier] ?? 0
  if (loyaltyPct === 0 || faceOlives <= 0) return { bonusOlives: 0, pct: 0 }
  const ladder = TOPUP_PACK_LADDER[tier] ?? []
  let volumePct = 0
  for (let i = 0; i < ladder.length; i++) {
    if (faceOlives >= ladder[i]) volumePct = TOPUP_VOLUME_BONUS_PCT_BY_TIER[i]
  }
  const pct = loyaltyPct + volumePct
  const bonusOlives = Math.round((faceOlives * pct) / 100 / 5) * 5
  return { bonusOlives, pct }
}

/**
 * Olives a card top-up gives back as the payment fee: the organization's rate
 * (`organizations.card_payment_fee_rate`, a fraction) of the top-up's face
 * olives, rounded to a whole olive. Used by the backend deduction AND the
 * Backstage preview so they match by construction.
 */
export function computePaymentFeeOlives(topupOlives: number, rate: number): number {
  return Math.round(topupOlives * rate)
}

/** A fee rate as a percentage for copy: 0.044 → `4.4%`. The column holds at most five decimals. */
export function formatPaymentFeeRate(rate: number): string {
  return `${Number((rate * 100).toFixed(3))}%`
}

// ============================================
// Auto top-up (Autumn-native billing control)
// ============================================
// Autumn watches the olive balance server-side and charges the org's saved
// card when it drops below the threshold — we only store the config on the
// Autumn customer (billing_controls.auto_topups) and mirror it in the UI.
// Tier bonuses do NOT apply to auto top-ups (v1 decision, see
// .context/plan-auto-topup.md).

/** Auto top-up settings for one org, as shown/edited on the billing page. */
/**
 * Post-paid terms on an organization's olives, as the app's billing surfaces show them
 * (`GET /billing`, `GET /projects/:id/billing`). Null when the organization pays up
 * front. The balance beside it is what a charge can draw on: the granted olives plus
 * what the credit line still admits. Set per organization in Backstage (Olives →
 * Post-paid); see docs/post-paid-billing.md.
 */
export interface PostPaidTerms {
  /** Olives of unpaid overage the organization may run up before charges are refused; null when no line is set. */
  creditLimit: number | null
  /** Olives consumed beyond the granted balance and not yet settled by an invoice payment. */
  outstanding: number
}

export interface AutoTopupConfig {
  enabled: boolean
  /** Balance at/below which Autumn buys the refill. */
  thresholdOlives: number
  /** Olives purchased per auto top-up (face value, $0.10/olive). */
  refillOlives: number
}

/** "Refill when below" choices offered in the UI. */
export const AUTO_TOPUP_THRESHOLD_OPTIONS = [25, 50, 100, 250, 500, 1000] as const

/** Upper bound for a custom "Refill when below" value (lower bound is 1). */
export const AUTO_TOPUP_THRESHOLD_MAX_OLIVES = 100000

/**
 * Plan-based defaults so enabling is one click. Personal defaults key off the
 * subscription plan's tier (parsePlanId: comp, annual and fal-key variants
 * share it); teams get team-sized numbers regardless of plan.
 */
export function autoTopupDefaults(plan: string | null | undefined, orgType: 'personal' | 'team'): AutoTopupConfig {
  if (orgType === 'team') {
    return { enabled: true, thresholdOlives: 500, refillOlives: 5000 }
  }
  const { tier } = parsePlanId(plan)
  if (tier === 'studio') return { enabled: true, thresholdOlives: 100, refillOlives: 2000 }
  if (tier === 'pro') return { enabled: true, thresholdOlives: 50, refillOlives: 750 }
  return { enabled: true, thresholdOlives: 50, refillOlives: 300 }
}

/** Olive cost configuration for a single model. */
export interface ModelOliveCost {
  /** Cost in olives per second (video models). */
  olivesPerSecond?: number
  /**
   * Cost per combined input/output second when one or more video references are
   * present. This is an exact rate and takes precedence over videoInputDiscount.
   */
  videoInputOlivesPerSecond?: number
  /**
   * Floor on the seconds a reference-clip take bills, as a multiple of the output
   * length: fal's Kling 4 bills input plus output seconds with a minimum of 2× the
   * output, so a 5 s take with a 2 s clip bills 10 s, not 7. Applied only when
   * video references are present and the card bills them.
   */
  videoInputMinOutputMultiple?: number
  /** Additional olives per second when audio is enabled. */
  audioSurchargeOlives?: number
  /** Additional olives per second when voice control increases upstream audio pricing. */
  voiceSurchargeOlives?: number
  /** Cost in olives per asset (image/fixed-price models). */
  olivesPerAsset?: number
  /**
   * Cost in olives per output minute, rounded UP to the next whole minute.
   * Used by providers (e.g. ElevenLabs Music) that bill in 1-minute increments.
   * 10 s → 1 minute → `olivesPerMinuteCeil`. 61 s → 2 minutes → 2 × `olivesPerMinuteCeil`.
   */
  olivesPerMinuteCeil?: number
  /**
   * Cost in olives per 1,000 characters of input text (TTS / voiceover models).
   * The character count is the prompt length, known before submit, so TTS bills
   * upfront with no hold/refund. Pair with `minimumOlives` (defaults to 0.1) for
   * the per-generation floor so a very short line never bills to ~0.
   */
  olivesPerThousandChars?: number
  /** Floor for ceil-rounded billing — guarantees at least N olives per generation. */
  minimumOlives?: number
  /** Resolution-based pricing tiers (checked in order; first matching tier wins). */
  resolutionTiers?: Array<{
    maxHeight: number
    olivesPerSecond: number
    audioSurchargeOlives?: number
    videoInputOlivesPerSecond?: number
  }>
  /** Explicit per-image resolution prices. */
  imageResolutionTiers?: Array<{
    resolution: string
    olivesPerAsset: number
    qualityOlivesPerAsset?: Record<string, number>
  }>
  /** Duration-based pricing tiers for fixed-price models (checked in order; first matching tier wins). */
  durationTiers?: Array<{ maxDuration: number; olivesPerAsset: number }>
  /** Output-megapixel-based pricing tiers for per-image models (checked in order; first matching tier wins). */
  megapixelTiers?: Array<{ maxMP: number; olivesPerAsset: number }>
  /**
   * Per-quality multipliers for per-image models that expose a quality tier
   * (e.g., GPT Image 2 low/medium/high). When absent, no multiplier is applied
   * — this is the gate that prevents quality discounts from leaking onto
   * models that don't expose the parameter.
   */
  qualityMultipliers?: Record<string, number>
  /**
   * Exact per-second rate cards keyed by a video render-quality selector.
   * Unlike image `qualityMultipliers`, these tiers may carry their own
   * resolution matrix (for example FLUX.3 Draft is fixed 720p while Full has
   * distinct 720p/1080p prices).
   */
  perSecondQualityTiers?: Record<
    string,
    {
      olivesPerSecond: number
      resolutionTiers?: Array<{ maxHeight: number; olivesPerSecond: number }>
    }
  >
  /**
   * Multiplier applied to the per-second cost when the request includes one or more
   * video references (e.g., FAL Seedance 2.0 uses a 0.9× conservative reference rate).
   * Billing applies this to generated output seconds plus known trimmed video-reference seconds;
   * absent means no discount/input-duration pricing.
   */
  videoInputDiscount?: number
  /**
   * Flat per-image charge for reference images beyond an included allowance
   * (founder pricing decision 2026-08-31, H3 first). Billed on the DISTINCT
   * images the provider actually receives — handlers dedupe repeated URLs and
   * flatten element frontals into the same list, and a start-frame request
   * strips references entirely, so count with
   * `countBillableReferenceImages()`, never a raw array length. The charge is
   * per generation (not per second) and lands on the canonical cost, so every
   * surface — UI estimate, UI charge, MCP, public API, negotiated multipliers
   * — inherits it from this one declaration.
   */
  referenceImageSurcharge?: {
    /** Reference images included per request at no extra charge. */
    includedImages: number
    /** Olives per distinct billable image beyond `includedImages`. */
    olivesPerImage: number
    /** Ceiling on billable images — mirrors the handler's cap on what it sends. */
    maxImages: number
  }
  /** Absolute list price per 1,000 reference-image tokens, pro-rata. */
  referenceTokenSurcharge?: {
    includedTokens: number
    olivesPer1kTokens: number
    processedShortEdge: number
    dimensionStep: number
    pixelsPerToken: number
    /** Estimate-only assumption for assets awaiting dimensions (1824 = 16:9); never used for billing. */
    estimateTokensPerUnmeasuredImage: number
    maxImages: number
    /**
     * Reference audio is tokenized against the same allowance, per second of the clip the
     * provider receives (H3 Max: 80/s, measured on fal's billing events 2026-09-22). Absent
     * when the card does not bill audio.
     */
    tokensPerAudioSecond?: number
    /** Estimate-only assumption for a clip whose length is unknown; the model's combined cap. */
    estimateSecondsPerUnmeasuredAudio?: number
    /**
     * Reference video is sampled at this fraction of its source frames (H3 Max: 0.3, i.e.
     * 7.2 samples per second of a 24 fps clip) and each sample costs the tokens of a frame
     * at the OUTPUT resolution, not the clip's (fal, 2026-09-22). Absent when the card does
     * not bill video.
     */
    videoSamplesPerFrame?: number
    /** Tokens per sampled frame by output height; the last tier covers anything taller. */
    videoTokensPerSampleTiers?: Array<{ maxHeight: number; tokensPerSample: number }>
    /** Estimate-only assumptions for a clip that could not be probed: the model's cap, at 24 fps. */
    estimateSecondsPerUnmeasuredVideo?: number
    estimateFramesPerSecondForUnmeasuredVideo?: number
  }
}

/**
 * Fal-route Seedance 2.5 rate card, ~5% under Fal list price — launch-pricing
 * exception: Martini's 5% FAL discount makes these rates approximately
 * break-even (native BytePlus uses the MODEL_OLIVE_COSTS base card). Two
 * consumers: the fal-provider override in `getProviderAwareModelOliveCost`,
 * and `seedance-2.5-custom`, which routes natively but is deliberately priced
 * identically to the fal-routed `seedance-2.5`.
 */
const SEEDANCE_25_FAL_CARD: ModelOliveCost = {
  olivesPerSecond: 4.5,
  videoInputOlivesPerSecond: 2.7,
  resolutionTiers: [
    { maxHeight: 480, olivesPerSecond: 2.1, videoInputOlivesPerSecond: 1.3 },
    { maxHeight: 720, olivesPerSecond: 4.5, videoInputOlivesPerSecond: 2.7 },
    { maxHeight: 1080, olivesPerSecond: 11.1, videoInputOlivesPerSecond: 6.7 },
  ],
}

/**
 * Fal-routed Seedance 2.5 edit card: an edit processes ~one source second plus one
 * output second, both at the video-input rate, so each tier is 2× the fal card's
 * `videoInputOlivesPerSecond`. Shared by `seedance-2.5-edit` on the fal route and
 * by `seedance-2.5-custom-edit`, which pins the native route but bills these rates
 * so the choice is never price-steered (same policy as `seedance-2.5-custom`).
 */
const SEEDANCE_25_EDIT_FAL_CARD: ModelOliveCost = {
  olivesPerSecond: 5.4,
  resolutionTiers: [
    { maxHeight: 480, olivesPerSecond: 2.6 },
    { maxHeight: 720, olivesPerSecond: 5.4 },
    { maxHeight: 1080, olivesPerSecond: 13.4 },
  ],
}

/**
 * H3 Max reference-token terms, shared by every H3 Max route that takes references.
 */
const H3_MAX_REFERENCE_TOKEN_SURCHARGE = {
  includedTokens: 4096,
  olivesPer1kTokens: 0.2,
  processedShortEdge: 1024,
  dimensionStep: 32,
  pixelsPerToken: 1024,
  estimateTokensPerUnmeasuredImage: 1824,
  maxImages: 9,
  tokensPerAudioSecond: 80,
  estimateSecondsPerUnmeasuredAudio: 15,
  // Reference video: 0.3 samples per source frame, 1,008 tokens a sample at 768P/1080P
  // and 390 at 480P — a 124-frame "5 s" clip is the 37,296 tokens fal's page quotes.
  // Measured on eleven invoiced requests (2026-09-04 → 09-22).
  videoSamplesPerFrame: 0.3,
  videoTokensPerSampleTiers: [
    { maxHeight: 480, tokensPerSample: 390 },
    { maxHeight: 1080, tokensPerSample: 1008 },
  ],
  estimateSecondsPerUnmeasuredVideo: 15,
  estimateFramesPerSecondForUnmeasuredVideo: 24,
} satisfies NonNullable<ModelOliveCost['referenceTokenSurcharge']>

/**
 * Centralized olive costs for all generation models.
 *
 * This is the SINGLE SOURCE OF TRUTH for user-facing pricing.
 * Backend handlers and the frontend pricing page both read from here.
 */
export const MODEL_OLIVE_COSTS = {
  // Video generation
  'veo3.1': {
    olivesPerSecond: 1.8,
    audioSurchargeOlives: 1.8,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 1.8, audioSurchargeOlives: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6, audioSurchargeOlives: 1.8 },
    ],
  },
  'veo3.1-fast': {
    olivesPerSecond: 0.9,
    audioSurchargeOlives: 0.5,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 0.9, audioSurchargeOlives: 0.5 },
      { maxHeight: 2160, olivesPerSecond: 2.7, audioSurchargeOlives: 0.5 },
    ],
  },
  // Kling 4: fal lists $0.157/s at 720p and $0.235/s at 1080p. Pricing v2 +20%:
  // 0.188 → 1.9 olives, 0.282 → 2.8 olives. Base is the 1080p default.
  // With reference videos fal switches to $0.094/s (720p) / $0.141/s (1080p) on input
  // plus output seconds, minimum 2× the output (its model page, 2026-10-23): +20% →
  // 1.2 / 1.7 olives per billed second, the same floor.
  'kling-v4': {
    olivesPerSecond: 2.8,
    videoInputOlivesPerSecond: 1.7,
    videoInputMinOutputMultiple: 2,
    resolutionTiers: [
      { maxHeight: 720, olivesPerSecond: 1.9, videoInputOlivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 2.8, videoInputOlivesPerSecond: 1.7 },
    ],
  },
  // Kling 4 on each::labs: measured $0.14/s (2026-10-22), 720p only, so it bills the
  // kling-v4 card's 720p rung flat (1.9 olives/s, 26% margin).
  'kling-v4-eachlabs': { olivesPerSecond: 1.9 },
  'kling-o3': { olivesPerSecond: 1.0, audioSurchargeOlives: 0.3 },
  'kling-v3': { olivesPerSecond: 1.0, audioSurchargeOlives: 0.5, voiceSurchargeOlives: 0.3 },
  'kling-2.6': { olivesPerSecond: 0.6, audioSurchargeOlives: 0.7 },
  'kling-o1': { olivesPerSecond: 1.0 },
  'hailuo-02': { olivesPerSecond: 0.7 },
  // H3 is a DELIBERATE EXCEPTION to Pricing v2's +15-25% markup: Koh's call
  // (2026-08-21) is to price it ~5% UNDER fal's published list, H3 only, so
  // onboarding can hand out cheap/free H3 generations. Fal lists $0.05/$0.06/
  // $0.13/$0.16 per second at 480P/768P/2K/4K; -5% rounded to the nearest 0.1
  // olive lands at parity on the two cheap rungs (0.475 and 0.57 both round up)
  // and 6-8% under on 2K/4K. That used to be roughly break-even; the fal order
  // form of 2026-09-16 discounts the H3 family 15%, so the same card now earns
  // +15/+15/+7.9/+9.3% across the rungs — see docs/pricing-margins.md.
  // Base is the 2K default, for requests with no resolvable resolution.
  // Per fal, 2K and 4K upscale a 768P render rather than generating natively.
  'hailuo-03': {
    olivesPerSecond: 1.2,
    // fal bills a reference clip's seconds at the output resolution's rate, on top of the
    // output seconds (115 invoiced reference-to-video requests joined to fal's billing
    // events, 2026-09-02..23: quantity = take seconds + clip seconds, no separate input
    // rate). Reference audio is not billed. The card charges the clip at the same rate,
    // so the input term is the tier's own rate (`videoInputOlivesPerSecond`).
    videoInputOlivesPerSecond: 1.2,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.5, videoInputOlivesPerSecond: 0.5 },
      { maxHeight: 768, olivesPerSecond: 0.6, videoInputOlivesPerSecond: 0.6 },
      { maxHeight: 1440, olivesPerSecond: 1.2, videoInputOlivesPerSecond: 1.2 },
      { maxHeight: 2160, olivesPerSecond: 1.5, videoInputOlivesPerSecond: 1.5 },
    ],
    // First 5 reference images ride free; each further distinct image is $0.08.
    // maxImages mirrors HAILUO_03_MAX_IMAGE_REFERENCES — the handler never
    // sends more than 9, so billing must not count more than 9 either.
    referenceImageSurcharge: { includedImages: 5, olivesPerImage: 0.8, maxImages: 9 },
  },
  // H3 Max: fal's regular card is $0.05/$0.08/$0.16 per second at 480P/768P/1080P (the launch
  // promo through 2026-09-14 discounts it 75%; priced on the regular card so nothing
  // needs repricing when it ends). Pricing v2 +20-25%; 1080P doubles the 768P rate. The
  // H3 under-list onboarding exception above is H3-only. Base is the 768P default.
  'hailuo-03-max': {
    olivesPerSecond: 1.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 768, olivesPerSecond: 1.0 },
      { maxHeight: 1080, olivesPerSecond: 2.0 },
    ],
    // Every reference token passes through at fal cost — images, audio (80 per second of the
    // clip) and video (0.3 samples per source frame at the output grid) share one allowance,
    // and the surcharge below is charged on the pooled total (Long, 2026-09-23: the card no
    // longer absorbs the clips). Measured on fal's billing events, 2026-09-04 → 09-22.
    referenceTokenSurcharge: H3_MAX_REFERENCE_TOKEN_SURCHARGE,
  },
  // H3 Max Extend: fal's card is $0.05/$0.08/$0.16/$0.32 per generated second at
  // 480P/768P/1080P/2K — H3 Max's ladder plus a 2K rung at twice 1080P — so it takes
  // H3 Max's olive card (Pricing v2 +20-25%) and doubles 1080P for 2K. The source clip
  // is not billed: fal's billable units for a 5 s and an 11 s source were the requested
  // seconds alone (2026-09-25), where the same clip as a reference-to-video input billed
  // its tokens. fal bills the requested seconds, not the slightly longer clip it returns.
  'hailuo-03-max-extend': {
    olivesPerSecond: 1.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 768, olivesPerSecond: 1.0 },
      { maxHeight: 1080, olivesPerSecond: 2.0 },
      { maxHeight: 1440, olivesPerSecond: 4.0 },
    ],
  },
  // H3 Max Insert: fal's card is $0.05/$0.06 per second of the generated scene at 480P/768P,
  // billed on the new scene alone, not the full spliced output (fal.ai, 2026-10-02). Pricing v2
  // +20% gives 0.6/0.7. Reference images and videos tokenize against the same 4,096-token
  // allowance and $0.02/1k rate as H3 Max, so it shares that surcharge. Base is the 768P default.
  'hailuo-03-max-insert': {
    olivesPerSecond: 0.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 768, olivesPerSecond: 0.7 },
    ],
    referenceTokenSurcharge: H3_MAX_REFERENCE_TOKEN_SURCHARGE,
  },
  // H3 Max Recast: fal's card is $0.30 / $0.45 per output second at 768P / 1080P,
  // reference photos free. Launched 2026-10-01, after the fal order form, so it is
  // priced off list at Pricing v2 +20%. The output is as long as the source clip, so
  // /generate bills the trusted source duration (resolveH3MaxRecastBillingDuration).
  'hailuo-03-max-recast': {
    olivesPerSecond: 5.4,
    resolutionTiers: [
      { maxHeight: 768, olivesPerSecond: 3.6 },
      { maxHeight: 1080, olivesPerSecond: 5.4 },
    ],
  },
  // H3 Max Turbo (preview) is charged AT fal's list price (decided by Long,
  // 2026-09-21): a $0.025 unit at 1 / 1.6 / 3.2 units per second for 480P /
  // 768P / 1080P, half of H3 Max. The billing events this was first priced from
  // were fal's launch promo (50% off, later 40% and 35%, extended to 2026-10-31),
  // so the card ignores the promo and holds list. The 15% contract discount
  // is the margin. No reference route, so no surcharge. Base is the 768P default.
  'hailuo-03-max-turbo': {
    olivesPerSecond: 0.4,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.25 },
      { maxHeight: 768, olivesPerSecond: 0.4 },
      { maxHeight: 1080, olivesPerSecond: 0.8 },
    ],
  },
  'sora-2': {
    olivesPerSecond: 4.5,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 2.7 }],
  },
  'sora-2-standard': { olivesPerSecond: 0.9 },
  'seedance-1.5': {
    olivesPerSecond: 0.5,
    audioSurchargeOlives: 0.5,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.2, audioSurchargeOlives: 0.1 },
      { maxHeight: 720, olivesPerSecond: 0.2, audioSurchargeOlives: 0.3 },
    ],
  },
  'seedance-2.0': {
    // Native BytePlus ModelArk rates use official token pricing +20%, rounded to 0.1 olive.
    // Base stays the 1080p default; 4K remains an explicit opt-in.
    olivesPerSecond: 4.5,
    videoInputOlivesPerSecond: 2.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.8, videoInputOlivesPerSecond: 0.5 },
      { maxHeight: 720, olivesPerSecond: 1.8, videoInputOlivesPerSecond: 1.1 },
      { maxHeight: 1080, olivesPerSecond: 4.5, videoInputOlivesPerSecond: 2.7 },
      { maxHeight: 2160, olivesPerSecond: 9.3, videoInputOlivesPerSecond: 5.6 },
    ],
  },
  'seedance-2.0-enhancor': {
    olivesPerSecond: 6.1,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.2 },
      { maxHeight: 720, olivesPerSecond: 2.7 },
    ],
    videoInputDiscount: 0.9,
  },
  'seedance-2.0-fast': {
    olivesPerSecond: 1.4,
    videoInputOlivesPerSecond: 0.8,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.7, videoInputOlivesPerSecond: 0.4 }],
  },
  'seedance-2.0-mini': {
    olivesPerSecond: 0.9,
    videoInputOlivesPerSecond: 0.5,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.4, videoInputOlivesPerSecond: 0.3 }],
  },
  // Seedance 2.5 native BytePlus rates use official token pricing +20%, rounded
  // to 0.1 olive. The Fal rollback card below preserves its higher launch rates.
  'seedance-2.5': {
    // Base stays the 720p default; 1080p is an explicit opt-in.
    olivesPerSecond: 2.8,
    videoInputOlivesPerSecond: 1.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.2, videoInputOlivesPerSecond: 0.7 },
      { maxHeight: 720, olivesPerSecond: 2.8, videoInputOlivesPerSecond: 1.7 },
      { maxHeight: 1080, olivesPerSecond: 6.8, videoInputOlivesPerSecond: 4.1 },
    ],
  },
  'seedance-2.5-edit': {
    olivesPerSecond: 3.4,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.4 },
      { maxHeight: 720, olivesPerSecond: 3.4 },
      { maxHeight: 1080, olivesPerSecond: 8.2 },
    ],
  },
  // Custom pins the native route but bills the same rates as the fal-routed
  // seedance-2.5 (deliberate — see SEEDANCE_25_FAL_CARD).
  'seedance-2.5-custom': SEEDANCE_25_FAL_CARD,
  'seedance-2.5-custom-edit': SEEDANCE_25_EDIT_FAL_CARD,
  // fal's US-hosted endpoints: Pricing v2 (cost +20%, 0.1-olive steps) on fal's US list,
  // which is 1.2× the regular endpoints and outside the order form's Seedance discount.
  // Always fal-routed, so this is the only card (no SEEDANCE_FAL_OLIVE_COSTS entry).
  'seedance-2.5-us': {
    olivesPerSecond: 6.7,
    videoInputOlivesPerSecond: 4.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 3.0, videoInputOlivesPerSecond: 1.8 },
      { maxHeight: 720, olivesPerSecond: 6.7, videoInputOlivesPerSecond: 4.0 },
      { maxHeight: 1080, olivesPerSecond: 16.4, videoInputOlivesPerSecond: 9.8 },
    ],
  },
  // An edit bills ~one source second plus one output second at the video-input rate
  // (see SEEDANCE_25_EDIT_FAL_CARD), priced here per Pricing v2 on that US-list cost
  // (2× the US video-input rate, +20%) rather than doubling the rounded olives.
  'seedance-2.5-us-edit': {
    olivesPerSecond: 8.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 3.6 },
      { maxHeight: 720, olivesPerSecond: 8.0 },
      { maxHeight: 1080, olivesPerSecond: 19.7 },
    ],
  },
  'grok-imagine': {
    olivesPerSecond: 0.6,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.5 }],
  },
  // Grok Imagine 1.5: fal and xAI list $0.08/$0.14/$0.25 per second (480p/720p/1080p);
  // ~10% over list (Long, 2026-10-23), rounded to 0.1 olive; 720p rounds up because
  // reference clips run there and the +$0.01 per input image is absorbed.
  // fal's pricing API reports `unit_price: 0.01, unit: "seconds"`, but the unit is a cent:
  // a 1080p second bills 25 of them. Reading it as $0.01/s priced this model at 0.1 olive/s.
  'grok-imagine-1.5': {
    olivesPerSecond: 2.8,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.9 },
      { maxHeight: 720, olivesPerSecond: 1.6 },
    ],
  },
  // Grok Imagine 1.5 Lite: fal and xAI list $0.02/$0.03/$0.14 per second (480p/720p/1080p);
  // Pricing v2 +20%, rounded to 0.1 olive. The +$0.01 input-image fee on i2v is absorbed.
  'grok-imagine-1.5-lite': {
    olivesPerSecond: 1.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.2 },
      { maxHeight: 720, olivesPerSecond: 0.4 },
    ],
  },
  'pixverse-v6': {
    olivesPerSecond: 0.8,
    audioSurchargeOlives: 0.2,
    resolutionTiers: [
      { maxHeight: 360, olivesPerSecond: 0.2, audioSurchargeOlives: 0.1 },
      { maxHeight: 540, olivesPerSecond: 0.3, audioSurchargeOlives: 0.1 },
      { maxHeight: 720, olivesPerSecond: 0.4, audioSurchargeOlives: 0.1 },
      { maxHeight: 1080, olivesPerSecond: 0.8, audioSurchargeOlives: 0.2 },
    ],
  },
  // Boreal: fal $0.01/$0.03/$0.12 per second; Pricing v2 +20%, rounded to 0.1 olive.
  boreal: {
    olivesPerSecond: 1.4,
    resolutionTiers: [
      { maxHeight: 720, olivesPerSecond: 0.1 },
      { maxHeight: 1080, olivesPerSecond: 0.4 },
      { maxHeight: 1440, olivesPerSecond: 1.4 },
    ],
  },
  'happy-horse': {
    // Default falls through to 1080p pricing when resolution isn't passed.
    olivesPerSecond: 2.5,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 1.3 }],
  },
  // Wan 3.0: fal $0.05/$0.10/$0.20 per second at 480p/720p/1080p, audio included.
  // Pricing v2 +20% midpoint, rounded to 0.1 olive. Base is the 1080p default.
  'wan-3.0': {
    olivesPerSecond: 2.4,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 720, olivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 2.4 },
    ],
  },
  // Wan 3.0 Prime: fal $0.068/$0.14/$0.28 per second at 480p/720p/1080p.
  // Explicit FAL-parity exception: keep the exact 480p rate instead of rounding to 0.1 olive.
  'wan-3.0-prime': {
    olivesPerSecond: 2.8,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.68 },
      { maxHeight: 720, olivesPerSecond: 1.4 },
      { maxHeight: 1080, olivesPerSecond: 2.8 },
    ],
  },
  'ltx-2.3': {
    // Default falls through to 1080p pricing when resolution isn't resolvable
    // (e.g., caller omits resolution from the request body). Safer than
    // defaulting to the 4K rate.
    olivesPerSecond: 0.7,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 0.7 },
      { maxHeight: 1440, olivesPerSecond: 1.4 },
      { maxHeight: 2160, olivesPerSecond: 2.9 },
    ],
  },
  'luma-ray-3.2-v2v': {
    // Video-to-video that surfaces in the Create Shot tab (capability
    // generate-video). FAL prices per resolution: 540p $0.144/s, 720p
    // $0.216/s, 1080p $0.432/s → ~10% below, rounded to 0.1 olive.
    // Fallback matches the handler's 540p default (LumaRay32V2VHandler
    // prepareInput) so API/MCP callers that omit `resolution` are billed
    // for the resolution they actually receive instead of the 1080p rate.
    olivesPerSecond: 1.3,
    resolutionTiers: [
      { maxHeight: 540, olivesPerSecond: 1.3 },
      { maxHeight: 720, olivesPerSecond: 1.9 },
      { maxHeight: 1080, olivesPerSecond: 3.9 },
    ],
  },
  // FLUX.3 full: fal $0.17/s at 720p and $0.29/s at 1080p. Draft: $0.06/s
  // at fixed 720p. Customer rates target ~10% below fal and round to 0.1 olive.
  'flux-3': {
    olivesPerSecond: 2.6,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 1.5 }],
    perSecondQualityTiers: {
      draft: { olivesPerSecond: 0.5 },
      full: {
        olivesPerSecond: 2.6,
        resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 1.5 }],
      },
    },
  },
  // FLUX.3 Max: fal list $0.17/s at 720p and $0.29/s at 1080p, audio included,
  // no Draft. Pricing v2 +20% (the launch promo is ignored, so nothing needs
  // repricing when it ends): 0.204 → 2.0, 0.348 → 3.5. The base rate is the
  // 1080p rate, so the handler must persist its 720p default before billing.
  'flux-3-max': {
    olivesPerSecond: 3.5,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 2.0 }],
  },
  // Gemini Omni Flash (Google) — text/image/reference-to-video with native audio.
  // fal token-bills it (~$0.125/sec t2v, ~$0.13/sec i2v/reference) → ~10% below, flat
  // 1.2 olives/sec across all three modes. Duration 3–10s, no resolution tiers.
  'gemini-omni-flash': {
    // 720p keeps its original 1.2 rate — Pricing v2 never reprices an existing model upward,
    // and at v1.1's $0.10/sec fal cost that lands at +20% anyway. The rungs v1.1 added are new
    // tiers, so they take the Pricing v2 +15-25% band: cost x1.2 rounded to 0.1 olive. 360p
    // rounds up to 0.4 because 0.3 would price the draft rung exactly at cost.
    olivesPerSecond: 1.2,
    resolutionTiers: [
      { maxHeight: 360, olivesPerSecond: 0.4 },
      { maxHeight: 720, olivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6 },
    ],
  },

  // Video extend
  'veo3.1-extend': {
    olivesPerSecond: 1.8,
    audioSurchargeOlives: 1.8,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 1.8, audioSurchargeOlives: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6, audioSurchargeOlives: 1.8 },
    ],
  },
  'kling-o1-extend': { olivesPerSecond: 1.0 },
  // FLUX.3 Extend full: fal $0.41/s at 720p and $0.53/s at 1080p. Draft:
  // $0.12/s at fixed 720p. Rates follow the same ~10%-below target.
  'flux-3-extend': {
    olivesPerSecond: 4.8,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 3.7 }],
    perSecondQualityTiers: {
      draft: { olivesPerSecond: 1.1 },
      full: {
        olivesPerSecond: 4.8,
        resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 3.7 }],
      },
    },
  },

  // Draft Enhance always produces a full-quality 1080p render. fal charges
  // $0.29/s; the request has no prompt, resolution, or audio knobs. Unlike
  // create/extend (~10% below fal), enhance targets ~5% below: it is a
  // deliberate second render of a take the user already chose, so it carries
  // less discount. $0.29 × 0.95 = $0.2755 → 2.8 olives at $0.10/olive.
  'flux-3-draft-enhance': { olivesPerSecond: 2.8 },
  'seedance-2.5-draft-enhance': { olivesPerSecond: 6.8, videoInputOlivesPerSecond: 4.1 },
  'seedance-2.5-edit-draft-enhance': { olivesPerSecond: 8.2 },
  'seedance-2.5-custom-draft-enhance': { olivesPerSecond: 11.1, videoInputOlivesPerSecond: 6.7 },
  'seedance-2.5-custom-edit-draft-enhance': { olivesPerSecond: 13.4 },

  // Video edit
  'kling-o3-edit': { olivesPerSecond: 1.5 },
  // Kling 4 edit: fal bills $0.094/s (720p) / $0.141/s (1080p) on input plus output seconds, and the
  // output matches the source, so each billed source second costs 2× the rate. Pricing v2 +20%:
  // 0.188 × 1.2 → 2.3 olives, 0.282 × 1.2 → 3.4 olives, on the trusted source-clip duration.
  'kling-v4-edit': {
    olivesPerSecond: 3.4,
    resolutionTiers: [
      { maxHeight: 720, olivesPerSecond: 2.3 },
      { maxHeight: 1080, olivesPerSecond: 3.4 },
    ],
  },
  // FLUX.3 Edit: fal bills $0.205 per source second (no draft/resolution knobs).
  // Pricing v2 +20% midpoint: $0.205 × 1.2 = $0.246 → 2.5 olives, billed on the
  // trusted source-clip duration (max 15s) like the other edit models.
  'flux-3-edit': { olivesPerSecond: 2.5 },
  'kling-o1-edit': { olivesPerSecond: 1.5 },
  'kling-o1-restyle': { olivesPerSecond: 1.5 },
  'luma-ray-2-modify': { olivesPerSecond: 3.2 },
  // Gemini Omni Flash video edit — instruction-only v2v (fal token-bills ~$0.13/sec →
  // ~10% below → 1.2 olives/sec, billed on the source clip's duration like the Kling edits).
  // Same v1.1 rate card as generate: the edit endpoint bills per output second by resolution
  // too, on the processed source clip's duration.
  'gemini-omni-flash-edit': {
    // 720p keeps its original 1.2 rate — Pricing v2 never reprices an existing model upward,
    // and at v1.1's $0.10/sec fal cost that lands at +20% anyway. The rungs v1.1 added are new
    // tiers, so they take the Pricing v2 +15-25% band: cost x1.2 rounded to 0.1 olive. 360p
    // rounds up to 0.4 because 0.3 would price the draft rung exactly at cost.
    olivesPerSecond: 1.2,
    resolutionTiers: [
      { maxHeight: 360, olivesPerSecond: 0.4 },
      { maxHeight: 720, olivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6 },
    ],
  },
  // VEED Lip Sync 2.0 — FAL $0.07/output sec; +20% target rounds to $0.08.
  'veed-lipsync-v2': { olivesPerSecond: 0.8 },

  // Image generation
  'flux-2-max': {
    olivesPerAsset: 0.6,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.6 },
      { resolution: '2K', olivesPerAsset: 0.9 },
      { resolution: '4K', olivesPerAsset: 1.4 },
    ],
  },
  // FLUX.1 [pro] Fill (inpainting) — FAL bills $0.05/MP, rounded up to the nearest
  // MP. Inpaint output matches the source dims (no resolution selector), so a flat
  // per-asset olive sized for a typical ~1 MP edit. 0.5 olives ($0.05) is FAL parity
  // at the 0.1-olive floor for a 1 MP edit (the ~10% target rounds to the same tier
  // — see grok-imagine), and runs below FAL for the common >1 MP edit since FAL
  // rounds megapixels up while Martini stays flat. See docs/pricing-margins.md.
  'flux-pro-fill': {
    olivesPerAsset: 0.5,
  },
  // Extend Image (ENG-1105): Bria Expand over a padded canvas, in passes that
  // each grow the picture at most 2.4x. Here `olivesPerAsset` is per pass, not
  // per output: fal lists Bria Expand at a flat $0.04 per call at any size, and
  // Pricing v2 adds 15-25% ($0.048 rounds to 0.5 olives, +25%). A 1:1 still
  // widened to a 4.89:1 wall is two passes, 1.0 olive; a 21:9 still is one, 0.5.
  // See calculateImageExtendOlives and docs/pricing-margins.md.
  'bria-expand-extend': {
    olivesPerAsset: 0.5,
  },
  'nano-banana-pro-2': {
    olivesPerAsset: 1.4,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.4 },
      { resolution: '2K', olivesPerAsset: 1.4 },
      { resolution: '4K', olivesPerAsset: 2.7 },
    ],
  },
  'nano-banana-2': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.7 },
      { resolution: '2K', olivesPerAsset: 1.1 },
      { resolution: '4K', olivesPerAsset: 1.4 },
    ],
  },
  // Nano Banana 2.1 — fal list $0.08 per 1K image, with 2K and 4K at 1.5x and 2x
  // ($0.12 / $0.16), Nano Banana 2's multipliers; fal's 2.1 page shows only the base rate.
  // Launched after the fal order form, so it bills at list. Pricing v2 midpoint (+20%),
  // rounded to 0.1 olive: $0.096 → 1.0, $0.144 → 1.4, $0.192 → 1.9. Web search is absorbed.
  'nano-banana-2.1': {
    olivesPerAsset: 1.0,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.0 },
      { resolution: '2K', olivesPerAsset: 1.4 },
      { resolution: '4K', olivesPerAsset: 1.9 },
    ],
  },
  // Nano Banana 2 Lite — Google's fastest/cheapest image model. fal token-meters it
  // (~$0.048/image at a fixed 1K output) → ~10% below → 0.4 olives/image. No resolution
  // tiers (fixed 1K) and no web search, unlike nano-banana-2. VERIFY the effective per-image
  // charge in the fal dashboard, since token metering varies slightly with prompt/complexity.
  'nano-banana-2-lite': { olivesPerAsset: 0.4 },
  // Seedream 5 Lite — ByteDance's fast/cheap image model. FAL bills a flat $0.035/image
  // regardless of resolution → ~10% below → 0.3 olives/image. Natively 2K–4K (no 1K
  // output). Explicit flat tiers are REQUIRED: without imageResolutionTiers, the shared
  // resolveImageOlivesPerAsset applies the global 2K=1.5×/4K=2× multiplier, which would
  // overcharge a model whose FAL cost doesn't move with resolution. See docs/pricing-margins.md.
  'seedream-v5-lite': {
    olivesPerAsset: 0.3,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 0.3 },
      { resolution: '4K', olivesPerAsset: 0.3 },
    ],
  },
  // Seedream 5 Pro — ByteDance's flagship image model. Unlike Lite (flat $0.035), FAL prices
  // by output size in two tiers: ≤1536×1536 (≤2.36 MP) = $0.0675, larger up to 4.19 MP = $0.135.
  // Priced at FAL parity, rounded to the nearest 0.1 olive: $0.0675 → 0.7, $0.135 → 1.4.
  // The 1K/2K knob maps to those two tiers (the handler emits dims/presets that stay in-bracket),
  // so explicit tiers are REQUIRED — otherwise the global 2K=1.5×/4K=2× multiplier would misprice.
  // ORDER MATTERS: 2K is listed FIRST because resolveImageOlivesPerAsset falls back to
  // imageResolutionTiers[0] when a request omits `resolution` (API/MCP/stale clients), and the
  // handler defaults omitted-resolution output to 2K. First tier must be the 2K default so those
  // requests are charged 1.4 (not 0.7) — do NOT reorder to 1K-first. /edit's ~$0.0045/extra-ref
  // surcharge is absorbed. See docs/pricing-margins.md.
  'seedream-v5-pro': {
    olivesPerAsset: 1.4,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 1.4 },
      { resolution: '1K', olivesPerAsset: 0.7 },
    ],
  },
  'gpt-image-2': {
    olivesPerAsset: 1.4,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.4, qualityOlivesPerAsset: { low: 0.1, medium: 0.4, high: 1.4 } },
      { resolution: '2K', olivesPerAsset: 2.0, qualityOlivesPerAsset: { low: 0.1, medium: 0.5, high: 2.0 } },
      { resolution: '4K', olivesPerAsset: 3.6, qualityOlivesPerAsset: { low: 0.1, medium: 0.9, high: 3.6 } },
    ],
  },
  // GPT Image 2.5 Flare / Sunburst — OpenAI token-bills both variants at the SAME rates as
  // GPT Image 2 ($30/1M output tokens), so the per-image provider cost ladder is GPT Image 2's
  // (1K/2K/4K × low/medium/high ≈ $0.005–$0.401, see docs/pricing-margins.md) and identical for
  // the two variants. Unlike gpt-image-2 (legacy ~10% below FAL), these are NEW models and take
  // the Pricing v2 band: cost × 1.2, rounded to 0.1 olive, 0.1 floor (low tiers). VERIFY the
  // effective per-image charge in the fal dashboard after the first live runs — OpenAI states the
  // GPT Image 2 token calculator does not estimate 2.5 consumption.
  // ORDER MATTERS: 1K first — resolveImageOlivesPerAsset falls back to imageResolutionTiers[0]
  // when a request omits `resolution`, and the handler defaults omitted resolution to 1K.
  'gpt-image-2.5-flare': {
    olivesPerAsset: 1.9,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.9, qualityOlivesPerAsset: { low: 0.1, medium: 0.5, high: 1.9 } },
      { resolution: '2K', olivesPerAsset: 2.7, qualityOlivesPerAsset: { low: 0.1, medium: 0.7, high: 2.7 } },
      { resolution: '4K', olivesPerAsset: 4.8, qualityOlivesPerAsset: { low: 0.1, medium: 1.2, high: 4.8 } },
    ],
  },
  'gpt-image-2.5-sunburst': {
    olivesPerAsset: 1.9,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.9, qualityOlivesPerAsset: { low: 0.1, medium: 0.5, high: 1.9 } },
      { resolution: '2K', olivesPerAsset: 2.7, qualityOlivesPerAsset: { low: 0.1, medium: 0.7, high: 2.7 } },
      { resolution: '4K', olivesPerAsset: 4.8, qualityOlivesPerAsset: { low: 0.1, medium: 1.2, high: 4.8 } },
    ],
  },
  // Qwen Image 3.0 Pro — Fal bills $0.04 at 1K and $0.075 at 2K for text generation
  // or editing. Pricing v2 midpoint: $0.04 * 1.20 = $0.048 → 0.5 olives;
  // $0.075 * 1.20 = $0.09 → 0.9 olives. ORDER MATTERS: the handler defaults omitted
  // or stale resolution state to 2K, so the fallback first tier must also be 2K.
  'qwen-image-3': {
    olivesPerAsset: 0.9,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 0.9 },
      { resolution: '1K', olivesPerAsset: 0.5 },
    ],
  },
  // Grok Imagine Image 2.0 — xAI's image model on Fal, priced per output image by
  // resolution AND quality: 1K low $0.04 / 1K medium $0.06 / 2K low $0.06 / 2K medium $0.08.
  // Pricing v2 midpoint (+20%), rounded to 0.1 olive: $0.048 → 0.5, $0.072 → 0.7,
  // $0.072 → 0.7, $0.096 → 1.0. Explicit tiers are REQUIRED so the global 2K=1.5×
  // multiplier never applies to a model whose 2K step is smaller than that.
  // ORDER MATTERS: 1K is listed FIRST because resolveImageOlivesPerAsset falls back to
  // imageResolutionTiers[0] when a request omits `resolution` (API/MCP/stale clients), and
  // the handler defaults omitted resolution to 1K — the two must agree.
  // /edit's +$0.01-per-input-image surcharge (max 3 refs) is absorbed, as with
  // seedream-v5-pro. See docs/pricing-margins.md.
  'grok-imagine-image-2': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.7, qualityOlivesPerAsset: { low: 0.5, medium: 0.7 } },
      { resolution: '2K', olivesPerAsset: 1.0, qualityOlivesPerAsset: { low: 0.7, medium: 1.0 } },
    ],
  },
  // FLUX.3 Image — BFL's image model on Fal, priced per output image by resolution:
  // 1K $0.048 / 2K $0.10 / 4K $0.607 (BFL list; fal's 1K matches). Priced on that list,
  // not fal's 50%-off launch promo that ends 2026-10-08. Pricing v2 midpoint (+20%),
  // rounded to 0.1 olive: $0.0576 → 0.6, $0.12 → 1.2, $0.7284 → 7.3.
  // ORDER MATTERS: 2K is listed FIRST because resolveImageOlivesPerAsset falls back to
  // imageResolutionTiers[0] when a request omits `resolution`, and the handler defaults
  // omitted resolution to 2K. Any per-reference input charge on /edit-image is absorbed;
  // VERIFY against the first fal invoice (BFL lists $0.015 per input megapixel).
  'flux-3-image': {
    olivesPerAsset: 1.2,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 1.2 },
      { resolution: '1K', olivesPerAsset: 0.6 },
      { resolution: '4K', olivesPerAsset: 7.3 },
    ],
  },
  // Reve 2.1 — every text, edit, and remix request renders native 4K (~16 MP) and
  // costs $0.25/image on Fal. Pricing v2 midpoint: $0.25 * 1.20 = $0.30 = 3.0 olives.
  // One explicit 4K tier prevents stale resolution state from invoking global multipliers.
  reve: {
    olivesPerAsset: 3.0,
    imageResolutionTiers: [{ resolution: '4K', olivesPerAsset: 3.0 }],
  },
  // Ideogram 4.0 — FAL bills per output megapixel by rendering speed
  // ($0.03/$0.06/$0.10 per MP for Turbo/Balanced/Quality) PLUS a flat +$0.03/image for
  // prompt expansion (always on; required to avoid garbage-text output). Single tier (no
  // user-facing resolution selector); rendering speed maps to `quality`.
  // Handler sends explicit dimensions capped at 1536 on the long/square edge (verified honored
  // by fal: 1:1 -> 1536x1536 = 2.36 MP, the largest exposed output). Priced against that max,
  // incl. the +$0.03 expansion surcharge, ~10% below FAL:
  // turbo 2.36*$0.03+$0.03≈$0.101, balanced ≈$0.172, quality ≈$0.266.
  'ideogram-v4': {
    olivesPerAsset: 1.5,
    imageResolutionTiers: [
      {
        resolution: '1K',
        olivesPerAsset: 1.5,
        qualityOlivesPerAsset: { turbo: 0.9, balanced: 1.5, quality: 2.4 },
      },
    ],
  },
  // Ideogram 4.5 — fal list per image by quality, size-independent: text-to-image and edit
  // both $0.03 low / $0.06 medium / $0.22 high; edit adds $0.008 very_low. Pricing v2
  // midpoint (+20%, nearest 0.1, 0.1 floor): 0.1 / 0.4 / 0.7 / 2.6. Medium is fal's default.
  // Resolution is not an input, so the single tier is the fallback for every request.
  'ideogram-v4.5': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.7, qualityOlivesPerAsset: { low: 0.4, medium: 0.7, high: 2.6 } },
    ],
  },
  'ideogram-v4.5-fill': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      {
        resolution: '1K',
        olivesPerAsset: 0.7,
        qualityOlivesPerAsset: { very_low: 0.1, low: 0.4, medium: 0.7, high: 2.6 },
      },
    ],
  },

  // Upscale (Topaz) — image
  // GAN models (Standard V2, Low Res V2, High Fidelity V2, CGI): 24 MP/credit
  'topaz-image-enhance': {
    olivesPerAsset: 2.0, // default for >4K output
    megapixelTiers: [
      { maxMP: 9, olivesPerAsset: 1.0 }, // up to ~4K
    ],
  },
  // Generative models (Standard MAX, Recovery V2, Wonder, Redefine, Wonder 3): 4 MP/credit
  // (Wonder 3 is billed by Topaz at 8 MP/credit — cheaper than the rest of this tier)
  'topaz-image-enhance-gen': {
    olivesPerAsset: 10.0, // default for >4K output
    megapixelTiers: [
      { maxMP: 2, olivesPerAsset: 1.5 }, // up to ~HD
      { maxMP: 9, olivesPerAsset: 3.0 }, // up to ~4K
    ],
  },
  // Bloom models (Bloom Realism): 2 MP/credit — 2× the per-MP cost of the other
  // generative models, so they get their own tier (Topaz credit ≈ $0.08–0.10)
  'topaz-image-enhance-bloom': {
    olivesPerAsset: 20.0, // default for >4K output (8K ≈ 33 MP ≈ 17 credits)
    megapixelTiers: [
      { maxMP: 2, olivesPerAsset: 1.5 }, // up to ~HD (~1 credit)
      { maxMP: 9, olivesPerAsset: 6.0 }, // up to ~4K (~5 credits)
    ],
  },

  // Upscale (Topaz) — video
  'topaz-video-enhance': {
    olivesPerSecond: 0.5,
    resolutionTiers: [
      { maxHeight: 1440, olivesPerSecond: 0.2 }, // 2K and below
    ],
  },
  'topaz-video-enhance-starlight': {
    olivesPerSecond: 3.0,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 1.5 }],
  },
  'topaz-video-enhance-starlight-precise1': {
    olivesPerSecond: 6.0,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 4.0 }],
  },
  'topaz-video-enhance-starlight-fast': {
    olivesPerSecond: 1.5,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 0.8 }],
  },
  // Hyperion 2 / 2.5 (SDR→HDR). Topaz bills these per frame — ~13 frames/credit at
  // 1080p and ~6 at 4K ($0.18 / $0.40 per second at 30fps) — so they cannot share
  // the standard tier above. Cost-plus +20%, rounded to 0.1 olive. Hyperion 1 is
  // duration-billed (2 credits / 10s) and stays on the standard tier.
  'topaz-video-enhance-hyperion': {
    olivesPerSecond: 4.8,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 2.2 }],
  },

  // Upscale (FAL / Black Forest Labs) — video.
  // FAL bills per delivered output second and prices on BOTH output resolution and
  // the creativity switch, so the two modes are separate rate cards rather than a
  // flat surcharge (the creative premium widens from +0.7 to +2.9 olives/sec across
  // the ladder). Cost-plus +20%, rounded to 0.1 olive:
  //   precise  1080p $0.14 → 1.7 | 2K $0.25 → 3.0 | 4K $0.55 → 6.6
  //   creative 1080p $0.20 → 2.4 | 2K $0.35 → 4.2 | 4K $0.79 → 9.5
  // The top-level rate is the 4K card: a 1.5-3x factor can push output past 2160p,
  // and FAL has no published tier above 4K, so anything taller bills at the 4K rate.
  'flux-video-upscale': {
    olivesPerSecond: 6.6,
    // The base card mirrors `precise`, which is the default this handler sends when a
    // caller omits the switch (MCP, direct API) — NOT FAL's own default of creativity=1.
    // Keep these two in lockstep: if the handler's default flips, this card flips with
    // it, or an omitting caller is billed for a mode it never ran.
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 1.7 },
      { maxHeight: 1440, olivesPerSecond: 3.0 },
    ],
    perSecondQualityTiers: {
      precise: {
        olivesPerSecond: 6.6,
        resolutionTiers: [
          { maxHeight: 1080, olivesPerSecond: 1.7 },
          { maxHeight: 1440, olivesPerSecond: 3.0 },
        ],
      },
      creative: {
        olivesPerSecond: 9.5,
        resolutionTiers: [
          { maxHeight: 1080, olivesPerSecond: 2.4 },
          { maxHeight: 1440, olivesPerSecond: 4.2 },
        ],
      },
    },
  },

  // 3D set generation
  'worldlabs-marble-v1': { olivesPerAsset: 20 },

  // V9 shot pipeline (internal, per-asset flat cost covering transition + Kling + Nano Banana)
  'v9-shot': { olivesPerAsset: 14 },

  // V10 shot pipeline (same cost as V9 initially)
  'v10-shot': { olivesPerAsset: 14 },

  // Audio generation
  'elevenlabs-sfx-v2': {
    olivesPerAsset: 0.4,
    durationTiers: [
      { maxDuration: 5, olivesPerAsset: 0.1 },
      { maxDuration: 10, olivesPerAsset: 0.2 },
      { maxDuration: 15, olivesPerAsset: 0.3 },
      { maxDuration: 20, olivesPerAsset: 0.4 },
    ],
  },
  'beatoven-sfx': {
    olivesPerAsset: 0.9,
  },
  'cassetteai-sfx': {
    olivesPerAsset: 0.1,
  },
  // Sonilo 1.1: fal $0.0018/sec text or $0.009/sec video. Pricing v2's
  // 0.1-olive floor applies to text; video rounds to the same customer rate.
  'sonilo-sfx': { olivesPerSecond: 0.1 },
  // Sonilo Music: text $0.0025/s ×1.2 = $0.003 → 0.1-olive floor;
  // video $0.009/s ×1.2 = $0.0108 → rounds to 0.1 (+11% over cost), same customer rate as SFX.
  'sonilo-music': { olivesPerSecond: 0.1 },
  'minimax-music': { olivesPerAsset: 1.4 },
  // MiniMax Music 3: unlike v2.6 (a partner API at a flat $0.15/track), fal
  // self-hosts v3 and bills GPU **compute seconds** at $0.00125. Measured over
  // four real generations (requested → delivered → compute s):
  //   120 →  19.73 →  71.58    60 →  56.67 → 191.81
  //   300 → 194.85 → 409.51   300 → 217.44 → 395.91
  // Compute tracks the DELIVERED track length, not the requested ceiling — the
  // 120 s request cost less than the 60 s one because its lyrics ran out after
  // 19.73 s. Least-squares fit: `USD ≈ 0.085 + 0.00204 × delivered_s`, i.e. a
  // ~$0.09 fixed overhead plus a shallow slope. Neither flat shape works:
  // `olivesPerSecond` underprices short tracks (it can't recover the overhead)
  // and `olivesPerMinuteCeil` overcharges long ones, so the tiers below track
  // the fitted curve at cost +20%, rounded to 0.1 olive. The fit is noisy at
  // the short end (±40% at 20 s), which the overhead-dominated 60 s tier
  // absorbs.
  // Because cost follows the delivered length while `duration` is only a
  // ceiling, the charge is held at the requested tier and refunded down to the
  // delivered tier on completion (see minimaxMusic3Billing.ts).
  // 2026-10-21: fal's list moved to $0.002 per second (the handler's cost card
  // follows it). At the tier ceilings that is $0.12 / $0.24 / $0.36 / $0.48 /
  // $0.60, so the tiers below now sit at roughly +108% / +67% / +50% / +44% /
  // +40% over cost instead of +20%. They are left as they are until a billing
  // event shows whether fal's second is the delivered or the requested one;
  // re-tier from that measurement, not from this note.
  'minimax-music-3': {
    olivesPerAsset: 8.4,
    durationTiers: [
      { maxDuration: 60, olivesPerAsset: 2.5 },
      { maxDuration: 120, olivesPerAsset: 4.0 },
      { maxDuration: 180, olivesPerAsset: 5.4 },
      { maxDuration: 240, olivesPerAsset: 6.9 },
      { maxDuration: 300, olivesPerAsset: 8.4 },
    ],
  },
  lyria2: { olivesPerAsset: 0.9 },
  // Lyria 3 (ENG-599) replaces the dropped Lyria 2, priced at fal cost +25%.
  // Base: fal $0.04/track (fixed 30s). Pro: fal $0.08/track (full songs up to ~3 min).
  lyria3: { olivesPerAsset: 0.5 },
  'lyria-3-pro': { olivesPerAsset: 1.0 },
  // Lyria 3.5 (ENG-906): fal list $0.10/track (full songs up to ~3 min), Pricing v2 +20%.
  // Google models carry no fal contract discount, so list is the cost basis.
  'lyria-3.5': { olivesPerAsset: 1.2 },
  // ElevenLabs Music (ENG-867): fal now lists $0.60/output min (was $0.80);
  // legacy 10%-below-FAL basis -> 5.4. The direct ElevenLabs route costs
  // $0.15/min but fal stays the fallback, so fal remains the pricing floor.
  'elevenlabs-music': { olivesPerMinuteCeil: 5.4 },
  'stable-audio-25': { olivesPerAsset: 1.8 },
  'stable-audio-25-inpaint': { olivesPerAsset: 1.8 },
  // DeepFilterNet 3 speech enhancement (ENG-841): fal $0.001/sec = $0.06/min,
  // x1.2 = $0.072/min -> 0.7 olives per started minute of source audio.
  deepfilternet3: { olivesPerMinuteCeil: 0.7 },
  // VEED Clean Audio speech denoising (ENG-981): fal list $0.0125 per started minute
  // of input audio, x1.2 = $0.015 -> 0.15, rounded half-up to 0.2 (0.1 is below cost).
  'veed-clean-audio': { olivesPerMinuteCeil: 0.2 },
  // ByteDance Seed Audio 1.0: FAL bills $0.1875 per minute of generated audio.
  // The API has no duration parameter, so we hold the 2-minute maximum upfront
  // and refund the difference after completion based on actual output duration.
  'seed-audio-1.0': { olivesPerMinuteCeil: 1.7, minimumOlives: 3.4 },

  // Audio generation: TTS / voiceover. Billed per 1,000 characters of input text
  // (FAL bills these models per character; text length is known before submit).
  // FAL $/1k chars → +~20% → rounded to 0.1 olive (Pricing v2). 0.1-olive floor applies.
  'elevenlabs-tts-v3': { olivesPerThousandChars: 1.2 }, // FAL $0.10/1k → 1.2 olives/1k
  'elevenlabs-tts-v4': { olivesPerThousandChars: 1 }, // FAL $0.08/1k ×1.2 = $0.096 → 1.0 olives/1k
  'elevenlabs-tts-v4-turbo': { olivesPerThousandChars: 0.5 }, // FAL $0.04/1k ×1.2 = $0.048 → 0.5 olives/1k
  // BYOK-only: bills the team's ElevenLabs account; olives are never charged.
  // minimumOlives 0 opts out of the 0.1-olive floor so every estimate reads 0.
  'elevenlabs-tts-byok': { olivesPerThousandChars: 0, minimumOlives: 0 },
  'minimax-speech-02-hd': { olivesPerThousandChars: 1.2 }, // FAL $0.10/1k → 1.2 olives/1k
  'typecast-tts': { olivesPerThousandChars: 1.2 }, // Typecast ≈ $0.08/1k (1 credit/char) → 1.2 olives/1k
  // Gemini 3.8 TTS: priced off fal list; the direct Google route costs less per line.
  'gemini-3.8-flash-tts': { olivesPerThousandChars: 0.5 }, // FAL $0.045/1k ×1.2 = $0.054 → 0.5 olives/1k
  'gemini-3.8-flash-lite-tts': { olivesPerThousandChars: 0.4 }, // FAL $0.03/1k ×1.2 = $0.036 → 0.4 olives/1k

  // Mock (testing only)
  'mock-test': { olivesPerSecond: 3 },
  'mock-test-edit': { olivesPerSecond: 3 },
  'mock-test-image': { olivesPerAsset: 1 },
  'mock-test-set': { olivesPerAsset: 1 },
  'mock-test-audio': { olivesPerAsset: 1 },
  'mock-upscale-image': { olivesPerAsset: 1 },
  // Same per-pass card as bria-expand-extend, so the mock quotes what the real run would.
  'mock-extend-image': { olivesPerAsset: 0.5 },
  'mock-upscale-video': { olivesPerSecond: 1 },
} satisfies Record<string, ModelOliveCost>

const SEEDANCE_FAL_OLIVE_COSTS = {
  'seedance-2.0': {
    olivesPerSecond: 6.1,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.2 },
      { maxHeight: 720, olivesPerSecond: 2.7 },
      { maxHeight: 1080, olivesPerSecond: 6.1 },
      { maxHeight: 2160, olivesPerSecond: 14.0 },
    ],
    videoInputDiscount: 0.9,
  },
  'seedance-2.0-fast': {
    olivesPerSecond: 2.2,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 1.0 }],
    videoInputDiscount: 0.9,
  },
  'seedance-2.0-mini': {
    olivesPerSecond: 1.4,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.6 }],
    videoInputDiscount: 0.9,
  },
  'seedance-2.5': SEEDANCE_25_FAL_CARD,
  'seedance-2.5-edit': SEEDANCE_25_EDIT_FAL_CARD,
  // A draft made on fal completes on fal (`bytedance/seedance-2.5/draft/complete`)
  // and bills the fal card's 1080p tier; the custom IDs already publish these figures.
  'seedance-2.5-draft-enhance': { olivesPerSecond: 11.1, videoInputOlivesPerSecond: 6.7 },
  'seedance-2.5-edit-draft-enhance': { olivesPerSecond: 13.4 },
} satisfies Record<string, ModelOliveCost>

/**
 * Resolves the active provider's user rate card.
 *
 * Note the naming is historical and reads backwards against how production is
 * configured: `MODEL_OLIVE_COSTS` holds the native BytePlus rates, but with
 * `SEEDANCE_20_PROVIDER=fal` those reach only the orgs allowlisted in
 * seedance20ProviderConfig (`resolveSeedanceRoutePlan`). Everyone else — every
 * public customer — routes to Fal and is billed the Fal card below, which sits
 * ~5% under Fal's list price. Marketing surfaces must quote the Fal card for
 * that reason; see `publicOliveCost` in frontend/src/lib/marketing-config.ts.
 */
export function getProviderAwareModelOliveCost(id: keyof typeof MODEL_OLIVE_COSTS, provider: string): ModelOliveCost {
  if (provider === 'fal.ai' && id in SEEDANCE_FAL_OLIVE_COSTS) {
    return SEEDANCE_FAL_OLIVE_COSTS[id as keyof typeof SEEDANCE_FAL_OLIVE_COSTS]
  }
  return MODEL_OLIVE_COSTS[id]
}

/**
 * Replaces EVERY rate-card field of `pricing` with `card`'s, keeping the
 * non-card fields (type, display, provider costs). A plain spread is wrong
 * here: the fal Seedance cards express reference pricing as a relative
 * `videoInputDiscount` while the native cards use an absolute
 * `videoInputOlivesPerSecond`, so a field absent from the new card must be
 * cleared, not inherited.
 */
export function replaceModelOliveCost<T extends ModelOliveCost>(pricing: T, card: ModelOliveCost): T {
  return {
    ...pricing,
    olivesPerSecond: card.olivesPerSecond,
    videoInputOlivesPerSecond: card.videoInputOlivesPerSecond,
    audioSurchargeOlives: card.audioSurchargeOlives,
    voiceSurchargeOlives: card.voiceSurchargeOlives,
    olivesPerAsset: card.olivesPerAsset,
    olivesPerMinuteCeil: card.olivesPerMinuteCeil,
    olivesPerThousandChars: card.olivesPerThousandChars,
    minimumOlives: card.minimumOlives,
    resolutionTiers: card.resolutionTiers,
    imageResolutionTiers: card.imageResolutionTiers,
    durationTiers: card.durationTiers,
    megapixelTiers: card.megapixelTiers,
    qualityMultipliers: card.qualityMultipliers,
    perSecondQualityTiers: card.perSecondQualityTiers,
    videoInputDiscount: card.videoInputDiscount,
  }
}

/**
 * The visual inputs of a Seedance request that decide its provider under
 * route-split (ENG-755). Field names match `GenerationParams`, so a full
 * params object satisfies this shape structurally.
 */
export interface SeedanceMediaInputSignals {
  imageUrl?: string
  endFrameUrl?: string
  imageReferences?: readonly string[]
  videoReferences?: readonly unknown[]
  audioReferences?: readonly unknown[]
  elements?: readonly unknown[]
}

/**
 * Single source of truth for what counts as a "media input" on a route-split
 * Seedance request (ENG-755). BytePlus direct moderates every media input —
 * start/end frames and audio references as much as visual references — and rejects faces far more often
 * than fal does, so ALL of them route the request to fal. The backend routing
 * decision and the frontend price estimate MUST both use this predicate; if
 * they ever disagree, the quoted price and the charged price diverge.
 */
export function seedanceHasMediaInputs(signals: SeedanceMediaInputSignals | undefined): boolean {
  if (!signals) return false
  return Boolean(
    signals.imageUrl?.trim() ||
    signals.endFrameUrl?.trim() ||
    signals.imageReferences?.length ||
    signals.videoReferences?.length ||
    signals.audioReferences?.length ||
    signals.elements?.length,
  )
}

/**
 * Topaz image models routed through the generative endpoint (image/enhance-gen/async).
 * Membership here decides ROUTING only, not price: models bill on the
 * 'topaz-image-enhance-gen' tier unless they are also in TOPAZ_BLOOM_IMAGE_MODELS,
 * which overrides to 'topaz-image-enhance-bloom' (billing and the frontend estimate
 * check the Bloom list first).
 */
export const TOPAZ_GENERATIVE_IMAGE_MODELS: string[] = [
  'Standard MAX',
  'Recovery V2',
  'Wonder',
  'Wonder 3',
  'Redefine',
  'Bloom Realism',
]

/**
 * Subset of TOPAZ_GENERATIVE_IMAGE_MODELS billed on the pricier Bloom tier (2 MP/credit,
 * 2× the other generative models). A new Bloom model must be added to BOTH lists:
 * the one above for endpoint routing, this one for pricing.
 */
export const TOPAZ_BLOOM_IMAGE_MODELS: string[] = ['Bloom Realism']

/**
 * Maximum INPUT megapixels the Topaz Image API accepts per model. Submitting a
 * source above the cap is rejected with HTTP 413 before any processing starts,
 * so the provider downscales to fit rather than failing the job (ENG-710).
 *
 * These are *input* limits and are unrelated to the output `megapixelTiers`
 * used for pricing. Values come from Topaz's model docs
 * (developer.topazlabs.com/image-models/…); `Recovery V2` is the limit the API
 * itself reported in the 413 that prompted this. Models Topaz doesn't document
 * fall back to the family default below.
 */
export const TOPAZ_IMAGE_INPUT_MP_LIMITS: Record<string, number> = {
  'Standard V2': 512,
  'Low Resolution V2': 512,
  'Text Refine': 512,
  CGI: 512,
  'Standard MAX': 24,
  'Recovery V2': 24,
  Wonder: 128,
  Redefine: 256,
}

/** Fallback input cap for GAN (image/enhance) models Topaz doesn't document. */
export const TOPAZ_GAN_IMAGE_INPUT_MP_LIMIT = 512

/**
 * Fallback input cap for generative (image/enhance-gen) models Topaz doesn't
 * document. Deliberately the conservative 24 MP the documented generative
 * models enforce — downscaling a rare oversized source costs a little detail;
 * guessing high costs the whole job.
 */
export const TOPAZ_GENERATIVE_IMAGE_INPUT_MP_LIMIT = 24

/**
 * Resolves the input megapixel cap for a Topaz image model. Unknown models get
 * their endpoint family's default, so adding a Topaz model never requires a
 * provider change — only an entry here when its documented limit differs.
 */
export function getTopazImageInputMegapixelLimit(model: string): number {
  const declared = TOPAZ_IMAGE_INPUT_MP_LIMITS[model]
  if (declared != null) return declared
  return TOPAZ_GENERATIVE_IMAGE_MODELS.includes(model)
    ? TOPAZ_GENERATIVE_IMAGE_INPUT_MP_LIMIT
    : TOPAZ_GAN_IMAGE_INPUT_MP_LIMIT
}

/** Set creation costs by mode (olives). */
export const SET_CREATION_COST_FAST = 20
export const SET_CREATION_COST_QUALITY = 45
export const SET_SUPPLEMENTARY_IMAGE_SURCHARGE = 5

export function calculateSetCreationCost(mode: SetCreationMode, hasSupplementaryImages: boolean = false): number {
  const baseCost = mode === 'quality' ? SET_CREATION_COST_QUALITY : SET_CREATION_COST_FAST
  return hasSupplementaryImages ? baseCost + SET_SUPPLEMENTARY_IMAGE_SURCHARGE : baseCost
}

/** Union of all model IDs that have pricing entries. */
export type PricedModelId = keyof typeof MODEL_OLIVE_COSTS

/**
 * A provider shutdown with an announced date. The model is offered normally
 * until `sunsetAt` and unlisted from that instant on — editor picker, `/models`,
 * MCP `get_models`, model-name resolution, and the marketing catalog all read
 * this one schedule, so retirement day needs no deploy.
 */
export interface ModelSunset {
  /** ISO-8601 instant the model stops being offered. */
  sunsetAt: string
  /**
   * Why the model is gone, in copy a customer can read: it is the body of the
   * error an API or MCP caller gets for a retired model id.
   */
  reason: string
}

const SORA_SUNSET_AT = '2026-09-24T00:00:00Z'
const REVE_SUNSET_AT = '2026-10-29T00:00:00Z'

/**
 * Scheduled provider shutdowns, keyed by Martini model id.
 *
 * A retired model keeps its handler and its `MODEL_OLIVE_COSTS` entry so
 * provenance on existing clips still resolves and historical billing still
 * prices; delete all three together once nothing references the id anymore.
 *
 * OpenAI's final notice: the Sora Videos API (`sora-2`, `sora-2-pro`) shuts
 * down on September 24, 2026. Unlisted at 00:00 UTC that day — OpenAI did not
 * name a timezone, and a generation that starts hours before the upstream
 * goes dark can only fail, so err early rather than late.
 *
 * Reve 2.1 (`reve`) left fal without notice: its `reve/2.1/*` endpoints answer
 * 404 "Application 2.1 not found" and fal's catalog no longer lists Reve. Every
 * request since August 25, 2026 failed, so it is retired the day it was found.
 */
export const MODEL_SUNSETS: Readonly<Record<string, ModelSunset>> = {
  'sora-2': {
    sunsetAt: SORA_SUNSET_AT,
    reason:
      'Sora 2 Pro was retired on September 24, 2026, when OpenAI shut down the Sora API. Choose another video model.',
  },
  'sora-2-standard': {
    sunsetAt: SORA_SUNSET_AT,
    reason: 'Sora 2 was retired on September 24, 2026, when OpenAI shut down the Sora API. Choose another video model.',
  },
  reve: {
    sunsetAt: REVE_SUNSET_AT,
    reason:
      'Reve 2.1 was retired on October 29, 2026, after its provider stopped hosting it. Choose another image model.',
  },
}

/** The sunset scheduled for a model, or undefined when none is. */
export function getModelSunset(modelId: string): ModelSunset | undefined {
  return MODEL_SUNSETS[modelId]
}

/**
 * True once `now` (epoch ms, defaults to the wall clock) has reached the
 * model's scheduled sunset. Models with no schedule are never sunset.
 */
export function isModelSunset(modelId: string, now: number = Date.now()): boolean {
  const sunset = MODEL_SUNSETS[modelId]
  return sunset !== undefined && now >= Date.parse(sunset.sunsetAt)
}

/** Get the per-second olive cost for a model, or throw if missing. */
export function getOlivesPerSecond(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.olivesPerSecond === undefined) throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing olivesPerSecond`)
  return cost.olivesPerSecond
}

/**
 * Resolve per-second olive cost accounting for resolution tiers.
 * Returns the tier rate if outputHeight matches a tier, otherwise the default rate.
 */
export function resolveOlivesPerSecond(cost: ModelOliveCost, outputHeight?: number): number {
  const base = cost.olivesPerSecond ?? 0
  if (!cost.resolutionTiers || !outputHeight) return base
  for (const tier of cost.resolutionTiers) {
    if (outputHeight <= tier.maxHeight) return tier.olivesPerSecond
  }
  return base
}

/** Resolve the combined video-input/output rate for a selected resolution tier. */
export function resolveVideoInputOlivesPerSecond(cost: ModelOliveCost, outputHeight?: number): number {
  const outputRate = resolveOlivesPerSecond(cost, outputHeight)
  const base = cost.videoInputOlivesPerSecond ?? outputRate * (cost.videoInputDiscount ?? 1)
  if (!cost.resolutionTiers || !outputHeight) return base
  for (const tier of cost.resolutionTiers) {
    if (outputHeight <= tier.maxHeight) {
      return tier.videoInputOlivesPerSecond ?? tier.olivesPerSecond * (cost.videoInputDiscount ?? 1)
    }
  }
  return base
}

/** Resolve the audio surcharge for the selected video resolution tier. */
export function resolveAudioSurchargeOlives(cost: ModelOliveCost, outputHeight?: number): number {
  const base = cost.audioSurchargeOlives ?? 0
  if (!cost.resolutionTiers || !outputHeight) return base
  for (const tier of cost.resolutionTiers) {
    if (outputHeight <= tier.maxHeight) return tier.audioSurchargeOlives ?? base
  }
  return base
}

/**
 * Distinct reference images a request will actually be billed for.
 *
 * Mirrors what handlers send to the provider (see buildHailuo03References):
 * element frontals and loose image references collapse into one deduped list,
 * and a start-frame request strips references entirely — so a repeated URL or
 * a stripped list must never be charged. Both the backend charge and the UI
 * estimate call this same function; if the counts can drift, the quote lies.
 */
export function countBillableReferenceImages(input: {
  /** True when the request carries a start frame — reference-stripping mode. */
  startFramePresent: boolean
  imageReferenceUrls?: readonly string[] | null
  elementFrontalUrls?: readonly string[] | null
  maxImages?: number
}): number {
  if (input.startFramePresent) return 0
  const urls = [...(input.elementFrontalUrls ?? []), ...(input.imageReferenceUrls ?? [])].filter(Boolean)
  const distinct = new Set(urls).size
  return input.maxImages != null ? Math.min(distinct, input.maxImages) : distinct
}

export type ReferenceTokenTerms = NonNullable<ModelOliveCost['referenceTokenSurcharge']>

export interface ReferenceImageMeasurement {
  url: string
  width?: number
  height?: number
  processedWidth?: number
  processedHeight?: number
  tokens: number
  source: 'probed' | 'inline'
}

/** One reference audio clip as the provider tokenizes it: the seconds it receives, at the card's rate. */
export interface ReferenceAudioMeasurement {
  url: string
  seconds: number
  tokens: number
  /** `trim`: the request's trim window; `probed`: the source's length; `estimated`: the card's fallback. */
  source: 'trim' | 'probed' | 'estimated'
}

/** One reference video clip as the provider tokenizes it: the frames it receives, sampled at the card's rate. */
export interface ReferenceVideoMeasurement {
  url: string
  /** The window the worker sends, in seconds and in source frames. */
  seconds: number
  frames: number
  tokens: number
  /** `probed`: the source's own frame rate and length; `window`: the stamped trim at 24 fps; `estimated`: the card's fallback. */
  source: 'probed' | 'window' | 'estimated'
}

export interface ReferenceTokenBreakdown {
  includedTokens: number
  /** Image, audio and video tokens together; the allowance is shared. */
  measuredTokens: number
  billedTokens: number
  images: ReferenceImageMeasurement[]
  /** Present only when the request carries billable audio references. */
  audio?: ReferenceAudioMeasurement[]
  audioTokens?: number
  /** Present only when the request carries billable video references. */
  video?: ReferenceVideoMeasurement[]
  videoTokens?: number
}

/** Tighter than fal's accepted range so downstream rounding remains safe. */
export const FAL_AR_MIN_SAFE = 0.5
export const FAL_AR_MAX_SAFE = 2.0

export function paddedReferenceDimensions(dims: { width: number; height: number }) {
  const { width, height } = dims
  if (width / height < FAL_AR_MIN_SAFE) return { width: Math.ceil(height * FAL_AR_MIN_SAFE), height }
  if (width / height > FAL_AR_MAX_SAFE) return { width, height: Math.ceil(width / FAL_AR_MAX_SAFE) }
  return { width, height }
}

export function processedReferenceDimensions(dims: { width: number; height: number }, terms: ReferenceTokenTerms) {
  dims = paddedReferenceDimensions(dims)
  const scale = terms.processedShortEdge / Math.min(dims.width, dims.height)
  return {
    width: Math.round((dims.width * scale) / terms.dimensionStep) * terms.dimensionStep,
    height: Math.round((dims.height * scale) / terms.dimensionStep) * terms.dimensionStep,
  }
}

export function referenceImageTokens(
  dims: { width?: number; height?: number } | undefined,
  terms: ReferenceTokenTerms,
): number {
  if (
    !dims?.width ||
    !dims.height ||
    !Number.isFinite(dims.width) ||
    !Number.isFinite(dims.height) ||
    dims.width <= 0 ||
    dims.height <= 0
  )
    return terms.estimateTokensPerUnmeasuredImage
  const processed = processedReferenceDimensions({ width: dims.width, height: dims.height }, terms)
  return Math.ceil((processed.width * processed.height) / terms.pixelsPerToken)
}

/**
 * Tokens the provider bills for one reference audio clip. An unknown length is estimated at
 * the card's fallback (the model's combined cap), never at zero, and a card without an audio
 * rate bills none.
 */
export function referenceAudioTokens(seconds: number | undefined, terms: ReferenceTokenTerms): number {
  const rate = terms.tokensPerAudioSecond
  if (rate == null) return 0
  const clip =
    typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
      ? seconds
      : (terms.estimateSecondsPerUnmeasuredAudio ?? 0)
  return Math.ceil(clip * rate)
}

/**
 * Tokens the provider bills for one reference video clip: the frames it receives (a window in
 * seconds at the source frame rate when the count is not known), sampled at the card's rate,
 * each sample priced at the OUTPUT resolution's frame size. An unknown clip is estimated at
 * the card's cap, never at zero, and a card without a video rate bills none.
 */
export function referenceVideoTokens(
  clip: { frames?: number; seconds?: number; frameRate?: number },
  outputHeight: number | undefined,
  terms: ReferenceTokenTerms,
): number {
  const samplesPerFrame = terms.videoSamplesPerFrame
  const tiers = terms.videoTokensPerSampleTiers
  if (samplesPerFrame == null || !tiers?.length) return 0
  const positive = (value: number | undefined) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
  const fps = positive(clip.frameRate) ?? terms.estimateFramesPerSecondForUnmeasuredVideo ?? 24
  const frames = positive(clip.frames) ?? (positive(clip.seconds) ?? terms.estimateSecondsPerUnmeasuredVideo ?? 0) * fps
  const tier = (outputHeight != null && tiers.find(t => outputHeight <= t.maxHeight)) || tiers[tiers.length - 1]
  return Math.floor(frames * samplesPerFrame) * tier.tokensPerSample
}

/**
 * The reference images a token card bills: provider order, deduplicated, capped. A start frame
 * strips nothing here — the token-card models (the H3 Max family) send every reference
 * alongside a first frame, unlike the per-image card's {@link countBillableReferenceImages}.
 */
export function billableReferenceImageUrls(input: {
  imageReferenceUrls?: readonly string[] | null
  elementFrontalUrls?: readonly string[] | null
  maxImages?: number
}): string[] {
  return [...new Set([...(input.elementFrontalUrls ?? []), ...(input.imageReferenceUrls ?? [])].filter(Boolean))].slice(
    0,
    input.maxImages,
  )
}

/**
 * The list-card surcharge for a request's reference tokens: everything beyond the included
 * allowance at the card's per-1k rate, rounded to the 0.0001-olive charge step. Takes the
 * pooled measured total (images, audio and video, as `generationReferenceTokens` sums them)
 * or, for an image-only estimate, the image measurements capped at the card's slot limit.
 */
export function resolveReferenceTokenSurchargeOlives(
  cost: ModelOliveCost,
  measured: number | readonly Pick<ReferenceImageMeasurement, 'tokens'>[],
): number {
  const terms = cost.referenceTokenSurcharge
  if (!terms) return 0
  const tokens =
    typeof measured === 'number'
      ? measured
      : measured.slice(0, terms.maxImages).reduce((sum, image) => sum + image.tokens, 0)
  return Math.round(((Math.max(0, tokens - terms.includedTokens) * terms.olivesPer1kTokens) / 1000) * 10000) / 10000
}

/** Flat olives added for reference images beyond the model's included allowance. */
export function resolveReferenceImageSurchargeOlives(cost: ModelOliveCost, billableReferenceImages: number): number {
  const terms = cost.referenceImageSurcharge
  if (!terms || billableReferenceImages <= 0) return 0
  const capped = Math.min(billableReferenceImages, terms.maxImages)
  return Math.max(0, capped - terms.includedImages) * terms.olivesPerImage
}

/** Get the per-asset olive cost for a model, or throw if missing. */
export function getOlivesPerAsset(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.olivesPerAsset === undefined) throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing olivesPerAsset`)
  return cost.olivesPerAsset
}

/** Get the per-1,000-characters olive cost for a TTS model, or throw if missing. */
export function getOlivesPerThousandChars(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.olivesPerThousandChars === undefined)
    throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing olivesPerThousandChars`)
  return cost.olivesPerThousandChars
}

/**
 * Resolve per-asset olive cost accounting for duration tiers.
 * Returns the tier rate if duration matches a tier, otherwise the default olivesPerAsset.
 */
export function resolveOlivesPerAsset(cost: ModelOliveCost, duration?: number): number {
  const base = cost.olivesPerAsset ?? 0
  if (!cost.durationTiers || duration == null) return base
  for (const tier of cost.durationTiers) {
    if (duration <= tier.maxDuration) return tier.olivesPerAsset
  }
  return base
}

/**
 * Resolve per-asset olive cost accounting for output megapixel tiers.
 * Returns the tier rate if outputMP matches a tier, otherwise the default olivesPerAsset.
 */
export function resolveOlivesPerAssetMP(cost: ModelOliveCost, outputMP?: number): number {
  const base = cost.olivesPerAsset ?? 0
  if (!cost.megapixelTiers || outputMP == null) return base
  for (const tier of cost.megapixelTiers) {
    if (outputMP <= tier.maxMP) return tier.olivesPerAsset
  }
  return base
}

/**
 * Resolve olives for a duration-priced model.
 *
 * - `olivesPerMinuteCeil`: bills per output minute, rounded UP. 10 s → 1 min, 61 s → 2 min.
 * - `olivesPerAsset` + `durationTiers`: tier-based fixed cost (existing SFX shape).
 * - `olivesPerAsset` only: flat per-asset.
 *
 * When `durationSeconds` is omitted/null and the cost shape uses `olivesPerMinuteCeil`,
 * returns `minimumOlives ?? olivesPerMinuteCeil` as a lower-bound estimate. This is
 * not equivalent to passing `0`, which rounds up to 1 minute and applies
 * `Math.max(minimumOlives, rate)`.
 *
 * Returns 0 if the cost shape doesn't match any duration-aware billing rule.
 */
export function resolveOlivesForDuration(cost: ModelOliveCost, durationSeconds?: number): number {
  if (cost.olivesPerMinuteCeil !== undefined) {
    if (durationSeconds == null) {
      return cost.minimumOlives ?? cost.olivesPerMinuteCeil
    }
    const minutes = Math.max(1, Math.ceil(durationSeconds / 60))
    // Olives are priced to 0.1; snap the multiple so 5.4 × 3 bills 16.2, not 16.200000000000003.
    const total = Math.round(minutes * cost.olivesPerMinuteCeil * 10) / 10
    return cost.minimumOlives !== undefined ? Math.max(cost.minimumOlives, total) : total
  }
  if (cost.olivesPerAsset !== undefined) {
    return resolveOlivesPerAsset(cost, durationSeconds)
  }
  return 0
}

/**
 * Resolve olives for a per-character (TTS / voiceover) model from the input text
 * length. Cost = (charCount / 1000) × `olivesPerThousandChars`, floored at
 * `minimumOlives` (defaults to the standard 0.1-olive minimum) so a short line
 * never bills to ~0. Returns 0 when the model isn't per-character priced.
 */
export function resolveOlivesForChars(cost: ModelOliveCost, charCount: number): number {
  if (cost.olivesPerThousandChars === undefined) return 0
  const raw = (Math.max(0, charCount) / 1000) * cost.olivesPerThousandChars
  const floor = cost.minimumOlives ?? 0.1
  return Math.max(floor, Math.round(raw * 10000) / 10000)
}

// ---------------------------------------------------------------------------
// TTS dialogue (Gemini 3.8 TTS: two speakers in one take)
// ---------------------------------------------------------------------------

/** One voice in a TTS dialogue. `id` is the alias the turns refer to (e.g. "Ava"). */
export interface TtsDialogueSpeaker {
  id: string
  voice: string
}

/** One spoken line, with optional delivery direction that overrides the take's style. */
export interface TtsDialogueTurn {
  speakerId: string
  text: string
  styleInstructions?: string
}

export interface TtsDialogue {
  speakers: TtsDialogueSpeaker[]
  turns: TtsDialogueTurn[]
}

/**
 * The readable `Alias: line` transcript of a dialogue. It stands in for the
 * prompt wherever one is displayed or stored; the provider receives the turns.
 */
export function ttsDialogueTranscript(dialogue: TtsDialogue): string {
  return dialogue.turns.map(turn => `${turn.speakerId}: ${turn.text}`).join('\n')
}

/**
 * Billable characters of a TTS request: the spoken text of every turn for a
 * dialogue (aliases and style direction are free on both routes), else the
 * prompt.
 */
export function ttsBillableChars(params: { prompt?: string | null; dialogue?: TtsDialogue | null }): number {
  if (params.dialogue) return params.dialogue.turns.reduce((sum, turn) => sum + turn.text.length, 0)
  return params.prompt?.length ?? 0
}

/** Resolve per-image olive cost accounting for resolution tiers, megapixels, and quality. */
export function resolveImageOlivesPerAsset(
  cost: ModelOliveCost,
  opts: { resolution?: string | null; quality?: string | null; outputMP?: number },
): number {
  // Match the requested resolution to a tier; fall back to the first (base) tier when the
  // resolution is omitted OR doesn't match any tier. This keeps single-tier models (Reve,
  // Ideogram 4.0) and their quality pricing correct even if a stray resolution is passed.
  const imageTier = cost.imageResolutionTiers
    ? ((opts.resolution ? cost.imageResolutionTiers.find(tier => tier.resolution === opts.resolution) : undefined) ??
      cost.imageResolutionTiers[0])
    : null
  if (imageTier) {
    if (opts.quality && imageTier.qualityOlivesPerAsset?.[opts.quality] !== undefined) {
      return imageTier.qualityOlivesPerAsset[opts.quality]
    }
    return imageTier.olivesPerAsset
  }

  let resolved =
    cost.megapixelTiers && opts.outputMP != null
      ? resolveOlivesPerAssetMP(cost, opts.outputMP)
      : (cost.olivesPerAsset ?? 0)

  // Backward-compatible multiplier path for models that have not opted into explicit tiers.
  if (!cost.megapixelTiers && !cost.imageResolutionTiers) {
    if (opts.resolution === '2K') resolved *= 1.5
    if (opts.resolution === '4K') resolved *= 2
  }

  if (opts.quality && cost.qualityMultipliers?.[opts.quality] !== undefined) {
    resolved *= cost.qualityMultipliers[opts.quality]
  }

  return Math.round(resolved * 10000) / 10000
}

/** Get the audio surcharge olive cost for a model, or throw if missing. */
export function getAudioSurchargeOlives(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.audioSurchargeOlives === undefined)
    throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing audioSurchargeOlives`)
  return cost.audioSurchargeOlives
}

// ============================================
// Chat (AI Assistant) Models
// ============================================

export interface ChatSessionSummary {
  id: string
  title: string | null
  createdAt: string
  updatedAt: string
  /**
   * Every server response carries it. Absent only on a session the web client
   * builds locally from a bare id before the next list read (the desktop pane
   * never reads it); never fill it with a default.
   */
  allowance?: ChatAllowance
}

/** Per-thread olive budget for auto-approving generation tool calls (v1.3 mobile chat). */
export interface ChatAllowance {
  allowanceOlives: number
  allowanceSpent: number
  autoApprove: boolean
}

/** A chat thread as the phone's cross-project Chat tab lists it (GET /mobile/v1/chat/sessions). */
export interface MobileChatSessionSummary extends ChatSessionSummary {
  projectId: string
  projectName: string
  /** The last reply's text (markdown, at most 600 characters); null when the thread has none yet. */
  excerpt?: string | null
  /** The last asset the thread made, viewed or referenced; null when its latest turn touched none. */
  lastAssetId?: string | null
}

/** Who runs the weights. Decides whether a first-party key can route around fal. */
export type ChatModelVendor = 'anthropic' | 'openai' | 'xai' | 'minimax' | 'mock'

/**
 * Per-token chat pricing for the inline AI chat pane. LLM inference runs on
 * fal's `openrouter/router` endpoint (per-token pass-through billing) unless
 * the vendor's first-party key is set (see chat/provider.ts); olive rates are
 * the vendor list price at $0.10/olive either way — LLM inference is billed at
 * cost, no margin and no discount (founder decision 2026-09-13), per
 * docs/pricing-margins.md.
 */
export interface ChatModelConfig {
  vendor: ChatModelVendor
  /** Model slug passed to fal's openrouter/router endpoint. */
  providerModelId: string
  /**
   * Model id on the vendor's own API, used when that vendor's key is present
   * (ANTHROPIC_API_KEY / OPENAI_API_KEY). Null when only the fal route exists.
   */
  directModelId: string | null
  label: string
  /** Olives per 1M input (prompt) tokens. */
  olivesPerMInputTokens: number
  /** Olives per 1M cached input tokens read from the provider cache. */
  olivesPerMCacheReadInputTokens: number
  /** Olives per 1M input tokens written to the provider cache. */
  olivesPerMCacheWriteInputTokens: number
  /** Olives per 1M output (completion) tokens. */
  olivesPerMOutputTokens: number
  /**
   * Olives deducted upfront per user message and refunded down to actual
   * usage after the turn settles. This is the user-charge ceiling. Provider
   * execution has separate request, output-token, step, and best-effort
   * metered-budget stops because a completed provider step can overshoot.
   */
  holdOlives: number
  /** Whether this model accepts image FileUIParts in chat turns. */
  supportsImageInput: boolean
}

export const CHAT_MODELS = {
  'opus-5.5': {
    vendor: 'anthropic',
    providerModelId: 'anthropic/claude-opus-5.5',
    directModelId: 'claude-opus-5-5',
    label: 'Claude Opus 5.5',
    // $4/$20 list, 1M context, 128K output. Thinking is always on (default
    // effort medium) and forced tool choice is rejected; the chat loop sets
    // neither. Cache reads are 0.05× input, not Anthropic's usual 0.1×.
    olivesPerMInputTokens: 40, // $4.00/M list
    olivesPerMCacheReadInputTokens: 2, // $0.20/M list
    olivesPerMCacheWriteInputTokens: 50, // $5.00/M list (1.25× input)
    olivesPerMOutputTokens: 200, // $20.00/M list
    // Twice the standard hold keeps the same ~250K uncached-input headroom as
    // the $2/M models (see gpt-6-astra).
    holdOlives: 10,
    supportsImageInput: true,
  },
  'chat-gpt': {
    vendor: 'openai',
    providerModelId: 'openai/gpt-6.1-sol',
    directModelId: 'gpt-6.1-sol',
    label: 'GPT-6.1 Sol',
    // Sol is GPT-6's balanced tier at 1M context; 6.1 keeps 6 Sol's $2/$10 and
    // halves cached input to $0.10/M. The >272K-token tier (2× input, 1.5×
    // output) is not modeled; the chat context caps keep turns under it.
    olivesPerMInputTokens: 20, // $2.00/M list
    olivesPerMCacheReadInputTokens: 1, // $0.10/M list
    olivesPerMCacheWriteInputTokens: 25, // $2.50/M list (1.25× input)
    olivesPerMOutputTokens: 100, // $10.00/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'chat-sonnet': {
    vendor: 'anthropic',
    providerModelId: 'anthropic/claude-sonnet-5.5',
    directModelId: 'claude-sonnet-5-5',
    label: 'Claude Sonnet 5.5',
    // $2/$10 list, 1M context. Like Opus 5.5, cache reads are 0.05× input
    // ($0.10/M), not Anthropic's usual 0.1×.
    olivesPerMInputTokens: 20, // $2.00/M list
    olivesPerMCacheReadInputTokens: 1, // $0.10/M list
    olivesPerMCacheWriteInputTokens: 25, // $2.50/M list (5-minute writes, 1.25× input)
    olivesPerMOutputTokens: 100, // $10.00/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'gpt-6-astra': {
    vendor: 'openai',
    providerModelId: 'openai/gpt-6-astra',
    directModelId: 'gpt-6-astra',
    label: 'GPT-6 Astra',
    // OpenAI's flagship (released 2026-09-04): $10/$50 list, 1M context, 128K
    // output. Same uncached price as Fable 5.1; OpenAI's cache reads are 4×
    // Anthropic's. The >272K-token tier (2× input, 1.5× output) is not modeled.
    olivesPerMInputTokens: 100, // $10.00/M list
    olivesPerMCacheReadInputTokens: 10, // $1.00/M list
    olivesPerMCacheWriteInputTokens: 125, // $12.50/M list (1.25× input)
    olivesPerMOutputTokens: 500, // $50.00/M list
    // 4× the standard hold: at 100 olives/M input, 5 olives would stop a
    // tool loop after ~50K uncached input tokens.
    holdOlives: 20,
    supportsImageInput: true,
  },
  'fable-5.1': {
    vendor: 'anthropic',
    providerModelId: 'anthropic/claude-fable-5.1',
    directModelId: 'claude-fable-5-1',
    label: 'Claude Fable 5.1',
    // Anthropic's most capable model (released 2026-09-01): $10/$50 list, 1M
    // context, 128K output, thinking always on. Cache reads are $0.25/M
    // (0.025× input), cache writes 1.25× input.
    olivesPerMInputTokens: 100, // $10.00/M list
    olivesPerMCacheReadInputTokens: 2.5, // $0.25/M list
    olivesPerMCacheWriteInputTokens: 125, // $12.50/M list (1.25× input)
    olivesPerMOutputTokens: 500, // $50.00/M list
    holdOlives: 20, // see gpt-6-astra
    supportsImageInput: true,
  },
  'chat-grok': {
    vendor: 'xai',
    providerModelId: 'x-ai/grok-4.7',
    directModelId: null,
    label: 'Grok 4.7',
    // Grok 4.7 keeps 4.6's list rate: $2/$6, cached input $0.50/M.
    // The >200K-token long-context tier ($4/$12) is not modeled — turns stay well under it.
    olivesPerMInputTokens: 20, // $2.00/M list
    olivesPerMCacheReadInputTokens: 5, // $0.50/M list
    olivesPerMCacheWriteInputTokens: 20, // automatic cache population has no separate write surcharge
    olivesPerMOutputTokens: 60, // $6.00/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'chat-minimax': {
    vendor: 'minimax',
    providerModelId: 'minimax/minimax-m3',
    directModelId: null,
    label: 'MiniMax M3',
    // The budget rung: ~6× cheaper output than Grok, with a 1M context.
    // (Upstream it also accepts video, but chat attachments are image-only —
    // see CHAT_ATTACHMENT_POLICY — so that capability is unreachable from here.)
    olivesPerMInputTokens: 3, // $0.30/M list
    olivesPerMCacheReadInputTokens: 0.6, // $0.06/M list
    olivesPerMCacheWriteInputTokens: 3, // no separate write surcharge — falls back to the input rate
    olivesPerMOutputTokens: 12, // $1.20/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'chat-mock': {
    vendor: 'mock',
    providerModelId: 'mock',
    directModelId: null,
    label: 'Mock (dev)',
    olivesPerMInputTokens: 0,
    olivesPerMCacheReadInputTokens: 0,
    olivesPerMCacheWriteInputTokens: 0,
    olivesPerMOutputTokens: 0,
    holdOlives: 0,
    supportsImageInput: true,
  },
} as const satisfies Record<string, ChatModelConfig>

export type ChatModelId = keyof typeof CHAT_MODELS

/** Preselected in the chat pane, on new Action nodes, and by the MCP action tools. */
/**
 * Spending for non-generation work; generation budgets remain separate. This is what a
 * customer sees: one olive figure per resource kind. What the provider billed us stays
 * on the server (docs/agent-work-billing.md), in the engine's report.
 */
export interface AgentBillingSummary {
  sessionId: string
  policyVersion: string
  rateVersion: string
  budgetPolicy: string
  status: 'active' | 'funding_required' | 'reconciliation_required' | 'completed' | 'failed' | 'cancelled'
  estimatedOlives: number
  accruedOlives: number
  reservedOlives: number
  chargedOlives: number
  resources: Array<{
    kind: 'model' | 'compute' | 'tool'
    accruedOlives: number
    unmetered: boolean
  }>
}

/** The policy a session pinned before unified agent billing applied to it; its work was measured by the run itself. */
export const LEGACY_AGENT_BILLING_POLICY = 'legacy'

/** A session that bills the customer under the unified policy, as opposed to a legacy contract's placeholder. */
export function isUnifiedAgentBilling(
  billing: Pick<AgentBillingSummary, 'policyVersion'> | null | undefined,
): billing is AgentBillingSummary {
  return !!billing && billing.policyVersion !== LEGACY_AGENT_BILLING_POLICY
}

/**
 * What a customer has spent on agent work under a unified session: what has accrued
 * while the session is open (active, paused for funding, or flagged for
 * reconciliation) and the settled charge once it is terminal. Reserved olives are
 * holds, never spending. Every surface that shows a session's agent olives reads
 * this one figure, so run history, receipts, the public API and MCP agree.
 */
export function agentSpendOlives(
  billing: Pick<AgentBillingSummary, 'status' | 'accruedOlives' | 'chargedOlives'>,
): number {
  switch (billing.status) {
    case 'active':
    case 'funding_required':
    case 'reconciliation_required':
      return billing.accruedOlives
    case 'completed':
    case 'failed':
    case 'cancelled':
      return billing.chargedOlives
  }
}

/**
 * The agent olives a run shows: the session's spending under unified billing; the run's
 * own measurement (`agentCostOlives`) for a legacy contract or a run with no session.
 */
export function customerAgentOlives(
  billing: Pick<AgentBillingSummary, 'policyVersion' | 'status' | 'accruedOlives' | 'chargedOlives'> | null | undefined,
  measuredOlives: number,
): number {
  return isUnifiedAgentBilling(billing) ? agentSpendOlives(billing) : measuredOlives
}

export const DEFAULT_CHAT_MODEL_ID: ChatModelId = 'opus-5.5'

export function isChatModelId(value: unknown): value is ChatModelId {
  return typeof value === 'string' && value in CHAT_MODELS
}

/**
 * Checkpoint-1 Action agent ids, kept as silent aliases so stored Actions
 * keep running. New Actions use ChatModelId directly as agentId — the
 * Execution dropdown and the chat pane's model picker share one catalog.
 */
export const LEGACY_ACTION_AGENT_ALIASES: Record<string, ChatModelId> = {
  martini: 'chat-sonnet',
  'martini-fast': 'chat-grok',
  claude: 'chat-sonnet',
  grok: 'chat-grok',
  mock: 'chat-mock',
}

/** Resolve an Action agentId (chat model id or legacy alias) to a chat model. */
export function resolveActionAgentChatModel(agentId: string): ChatModelId | null {
  if (isChatModelId(agentId)) return agentId
  return LEGACY_ACTION_AGENT_ALIASES[agentId] ?? null
}

/** Minimum charge per chat message (platform 0.1-olive billing floor). */
export const CHAT_MIN_CHARGE_OLIVES = 0.1

export interface ChatTokenUsage {
  inputTokens: number
  inputNoCacheTokens?: number
  inputCacheReadTokens?: number
  inputCacheWriteTokens?: number
  outputTokens: number
}

export interface ChatCostBreakdown {
  uncachedInputOlives: number
  cacheReadInputOlives: number
  cacheWriteInputOlives: number
  outputOlives: number
  rawOlives: number
  chargedOlives: number
}

function normalizeChatInputBuckets(usage: ChatTokenUsage): {
  uncached: number
  cacheRead: number
  cacheWrite: number
} {
  const inputTokens = Math.max(0, usage.inputTokens)
  const hasDetails =
    usage.inputNoCacheTokens !== undefined ||
    usage.inputCacheReadTokens !== undefined ||
    usage.inputCacheWriteTokens !== undefined
  if (!hasDetails) return { uncached: inputTokens, cacheRead: 0, cacheWrite: 0 }

  const cacheRead = Math.min(inputTokens, Math.max(0, usage.inputCacheReadTokens ?? 0))
  const cacheWrite = Math.min(inputTokens - cacheRead, Math.max(0, usage.inputCacheWriteTokens ?? 0))
  // Any missing or inconsistent remainder is conservatively priced as normal
  // input so incomplete gateway details can never silently undercharge.
  return { uncached: inputTokens - cacheRead - cacheWrite, cacheRead, cacheWrite }
}

/**
 * Token counts are integers and rates have at most two decimals, so a bucket's
 * exact value has finitely many decimals; a real fraction of a tenth is never
 * smaller than this. Guards the ceil against float noise such as
 * 0.1 × 30 = 3.0000000000000004, which would otherwise bill 3.1 for 3.0.
 */
const CHAT_ROUNDING_EPSILON = 1e-9

/** tokens × rate first, then scale — keeps whole-number products exact. */
export function olivesForTokens(tokens: number, olivesPerMillion: number): number {
  return (tokens * olivesPerMillion) / 1_000_000
}

export function calculateChatCostBreakdown(modelId: ChatModelId, usage: ChatTokenUsage): ChatCostBreakdown {
  return calculateChatCostBreakdownAtRates(CHAT_MODELS[modelId], usage)
}

/** The four token rates a chat price is computed from. */
export type ChatTokenRates = Pick<
  ChatModelConfig,
  | 'olivesPerMInputTokens'
  | 'olivesPerMCacheReadInputTokens'
  | 'olivesPerMCacheWriteInputTokens'
  | 'olivesPerMOutputTokens'
>

const SONNET_5_PICKER_RATES: ChatTokenRates = {
  olivesPerMInputTokens: 30,
  olivesPerMCacheReadInputTokens: 3,
  olivesPerMCacheWriteInputTokens: 37.5,
  olivesPerMOutputTokens: 150,
}
const GPT_6_SOL_PICKER_RATES: ChatTokenRates = {
  olivesPerMInputTokens: 20,
  olivesPerMCacheReadInputTokens: 2,
  olivesPerMCacheWriteInputTokens: 25,
  olivesPerMOutputTokens: 100,
}

/**
 * Models that left the picker, at the rates the picker billed them, by direct and gateway id. Workflow runs,
 * recovered runs and the judge can still name them (WORKFLOW_CLAUDE_CODE_MODEL, a pinned run, the judge's
 * calibrated tier), so they keep the price they had instead of falling back to another model's.
 */
export const RETIRED_CHAT_MODEL_RATES: Readonly<Record<string, ChatTokenRates>> = {
  'claude-sonnet-5': SONNET_5_PICKER_RATES,
  'anthropic/claude-sonnet-5': SONNET_5_PICKER_RATES,
  'gpt-6-sol': GPT_6_SOL_PICKER_RATES,
  'openai/gpt-6-sol': GPT_6_SOL_PICKER_RATES,
}

/** The rates for a picker key, a current direct or gateway id, or a retired model; null when unknown. */
export function chatTokenRatesFor(model: string): ChatTokenRates | null {
  const id = (Object.keys(CHAT_MODELS) as ChatModelId[]).find(
    key => key === model || CHAT_MODELS[key].directModelId === model || CHAT_MODELS[key].providerModelId === model,
  )
  return id ? CHAT_MODELS[id] : (RETIRED_CHAT_MODEL_RATES[model] ?? null)
}

/** The chat turn price at explicit rates, for callers billing a model that is not (or no longer) in the picker. */
export function calculateChatCostBreakdownAtRates(cfg: ChatTokenRates, usage: ChatTokenUsage): ChatCostBreakdown {
  const input = normalizeChatInputBuckets(usage)
  const uncachedInputOlives = olivesForTokens(input.uncached, cfg.olivesPerMInputTokens)
  const cacheReadInputOlives = olivesForTokens(input.cacheRead, cfg.olivesPerMCacheReadInputTokens)
  const cacheWriteInputOlives = olivesForTokens(input.cacheWrite, cfg.olivesPerMCacheWriteInputTokens)
  const outputOlives = olivesForTokens(Math.max(0, usage.outputTokens), cfg.olivesPerMOutputTokens)
  const rawOlives = uncachedInputOlives + cacheReadInputOlives + cacheWriteInputOlives + outputOlives
  const chargedOlives =
    rawOlives <= 0 ? 0 : Math.max(CHAT_MIN_CHARGE_OLIVES, Math.ceil(rawOlives * 10 - CHAT_ROUNDING_EPSILON) / 10)
  return {
    uncachedInputOlives,
    cacheReadInputOlives,
    cacheWriteInputOlives,
    outputOlives,
    rawOlives,
    chargedOlives,
  }
}

/**
 * Metered olive cost for one chat turn, rounded UP to 0.1 olive with the
 * platform floor applied. Free models (all-zero rates) cost 0.
 */
export function calculateChatCostOlives(modelId: ChatModelId, usage: ChatTokenUsage): number {
  return calculateChatCostBreakdown(modelId, usage).chargedOlives
}

/**
 * A canvas node referenced in a chat message (dragged or @-mentioned into the
 * composer). Travels as a `data-node-ref` UIMessage part; the backend renders
 * it into a bracketed context block for the model, which resolves content via
 * MCP tools (get_asset, video inspection). Display fields (name, thumbnailUri)
 * are client-supplied UI hints — the backend treats them as untrusted and
 * truncates them.
 */
/**
 * Where an inline chat reference sits in the message's text part. UTF-16
 * offsets (`String.prototype.slice` semantics) into the final outgoing text,
 * covering the `@Name` mention the composer wrote there. Absent on messages
 * sent before inline ranges existed; those render their chips after the text.
 */
export interface ChatNodeRefTextRange {
  start: number
  end: number
}

/** Display metadata every chat reference carries, frozen at send time. */
interface ChatNodeRefBase {
  /** Display name at send time; sanitized and length-capped server-side. */
  name: string
  thumbnailUri?: string
  textRange?: ChatNodeRefTextRange
}

export type ChatNodeRef =
  | (ChatNodeRefBase & {
      kind: 'asset'
      assetId: string
      assetType: 'image' | 'video' | 'audio'
      /** Scrub position (seconds) captured at drag time — video assets only. */
      timestamp?: number
    })
  | (ChatNodeRefBase & { kind: 'element'; elementId: string })
  | (ChatNodeRefBase & { kind: 'variable'; variableId: string })
  | (ChatNodeRefBase & { kind: 'bin'; binId: string })
  | (ChatNodeRefBase & { kind: 'note'; noteId: string })
  /** A canvas Action card — its id is what update_actions / run_workflow take. */
  | (ChatNodeRefBase & { kind: 'action'; actionId: string })
  /** A generation model — referenced for its capabilities; never changes the chat model. */
  | (ChatNodeRefBase & { kind: 'model'; modelId: string })
  | (ChatNodeRefBase & { kind: 'library-collection'; collectionId: string })
  | (ChatNodeRefBase & {
      kind: 'library-asset'
      collectionId: string
      libraryAssetId: string
      assetKind: 'image' | 'video' | 'text'
    })

export type ChatNodeRefKind = ChatNodeRef['kind']

/** The stable id a chat reference points at, whatever its kind. */
export function chatNodeRefId(ref: ChatNodeRef): string {
  switch (ref.kind) {
    case 'asset':
      return ref.assetId
    case 'element':
      return ref.elementId
    case 'variable':
      return ref.variableId
    case 'bin':
      return ref.binId
    case 'note':
      return ref.noteId
    case 'action':
      return ref.actionId
    case 'model':
      return ref.modelId
    case 'library-collection':
      return ref.collectionId
    case 'library-asset':
      return ref.libraryAssetId
  }
}

/** Max length the backend keeps of client-supplied node-ref display strings. */
export const CHAT_NODE_REF_NAME_MAX_LENGTH = 120

// ============================================
// Chat request context bounds
// ============================================

/**
 * Hard cap on one POST /chat body. The backend refuses anything larger before
 * parsing it (see chat/requestLimits.ts), then selects the recent context
 * itself. It sits well above MAX_CHAT_CONTEXT_BYTES because shipped iOS builds
 * (martini-mobile before its client-side selection) send the whole raw thread:
 * at 512 KB, one ~800 KB board-overview turn made every later send in that
 * thread fail with 413.
 */
export const MAX_CHAT_REQUEST_BYTES = 8 * 1024 * 1024
export const MAX_CHAT_CONTEXT_MESSAGES = 64
export const MAX_CHAT_CONTEXT_PARTS = 256
export const MAX_CHAT_CONTEXT_TEXT_CHARS = 64_000
/**
 * Serialized-message budget for one provider request. Clients select to it
 * before sending and the backend re-selects whatever arrives.
 *
 * This is the bound the text-character cap cannot express: tool outputs
 * live in tool parts, not text parts, so a thread of tool-heavy turns passes
 * every other bound and still overflows the body cap. Observed live as a
 * thread whose every send failed with 413 once its history reached ~530 KB.
 */
export const MAX_CHAT_CONTEXT_BYTES = 480 * 1024

interface ChatMessageMeasure {
  parts: number
  textChars: number
  bytes: number
}

const chatMessageEncoder = new TextEncoder()

/** Cheap structural measure of an unvalidated message-shaped value. */
export function measureChatMessage(message: unknown): ChatMessageMeasure {
  const bytes = chatMessageEncoder.encode(JSON.stringify(message) ?? '').length
  const parts = typeof message === 'object' && message !== null ? (message as { parts?: unknown }).parts : undefined
  if (!Array.isArray(parts)) return { parts: 0, textChars: 0, bytes }

  let textChars = 0
  for (const part of parts) {
    const text = typeof part === 'object' && part !== null ? (part as { text?: unknown }).text : undefined
    if (typeof text === 'string') textChars += text.length
  }
  return { parts: parts.length, textChars, bytes }
}

/**
 * Select the newest bounded context for one provider request.
 *
 * The full UI thread stays loaded and durably persisted; only the model input
 * rolls over. The client applies this before sending and the backend applies
 * it again to whatever arrives, so both sides must share this one
 * implementation and its constants: a bound the client does not mirror is a
 * thread that dies on every send (retry re-sends the same history).
 *
 * The newest message is always retained, even when it alone exceeds a bound,
 * so validation can reject that single turn with actionable copy instead of
 * silently sending an empty prompt.
 */
export function selectRecentChatMessages<T>(messages: ReadonlyArray<T>): T[] {
  if (messages.length === 0) return []

  let start = messages.length
  let totalParts = 0
  let totalText = 0
  let totalBytes = 0
  let selected = 0

  for (let index = messages.length - 1; index >= 0 && selected < MAX_CHAT_CONTEXT_MESSAGES; index -= 1) {
    const measure = measureChatMessage(messages[index])
    const wouldOverflow =
      totalParts + measure.parts > MAX_CHAT_CONTEXT_PARTS ||
      totalText + measure.textChars > MAX_CHAT_CONTEXT_TEXT_CHARS ||
      totalBytes + measure.bytes > MAX_CHAT_CONTEXT_BYTES
    if (selected > 0 && wouldOverflow) break
    start = index
    selected += 1
    totalParts += measure.parts
    totalText += measure.textChars
    totalBytes += measure.bytes
  }

  const bounded = messages.slice(start)
  const firstUserIndex = bounded.findIndex(
    message => typeof message === 'object' && message !== null && (message as { role?: unknown }).role === 'user',
  )
  return firstUserIndex > 0 ? bounded.slice(firstUserIndex) : bounded
}

// ============================================
// V7 Set Pipeline Types
// ============================================

/** Character type — determines which FAL endpoint to use for 3D mesh reconstruction. */
export type CharacterType = 'person' | 'creature' | 'object'

/** Visual style detected from a set source image or selected for V10 render output. */
export type VisualStyle = 'photorealistic' | 'anime' | '3d_animation' | 'illustration' | 'painting' | 'sketch'

/** Ordered visual style values for validation and UI controls. */
export const VISUAL_STYLE_VALUES = [
  'photorealistic',
  'anime',
  '3d_animation',
  'illustration',
  'painting',
  'sketch',
] as const satisfies readonly VisualStyle[]

/** Labels for render style selectors. */
export const VISUAL_STYLE_OPTIONS: Array<{ value: VisualStyle; label: string }> = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'anime', label: 'Anime' },
  { value: '3d_animation', label: '3D Animation' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'painting', label: 'Painting' },
  { value: 'sketch', label: 'Sketch' },
]

/** How the set's 3D environment is sourced: single image, multiple images, or video. */
export type SetSourceType = 'image' | 'multi-image' | 'video'

/** Character color slot — maps to maximally distinct colors for SAM segmentation. */
export type CharacterSlotColor = 'red' | 'green' | 'blue' | 'yellow' | 'magenta'

/** Hex colors for rendering character meshes in the 3D viewer. */
export const CHARACTER_SLOT_COLORS: Record<CharacterSlotColor, string> = {
  red: '#FF0000',
  green: '#00FF00',
  blue: '#0000FF',
  yellow: '#FFFF00',
  magenta: '#FF00FF',
}

/** 3D vector for position, rotation, scale. */
export interface Vec3 {
  x: number
  y: number
  z: number
}

/** Normalized bounding box (0-1 range relative to image dimensions). */
export interface NormalizedBbox {
  x: number
  y: number
  w: number
  h: number
}

/** Character entity — extracted person from source image. */
export interface Character {
  id: string
  setId: string
  projectId: string
  slotIndex: number
  slotColor: CharacterSlotColor
  label: string
  sourceBbox: NormalizedBbox | null
  cutoutUrl: string | null
  characterSheetUrl: string | null
  headshotUrl: string | null
  meshUrl: string | null
  meshHeight: number | null
  characterType: CharacterType
  initialPosition: Vec3 | null
  initialRotation: Vec3 | null
  initialScale: Vec3 | null
  createdAt: string
  updatedAt: string
}

/** V7 set creation mode. */
export type SetCreationMode = 'fast' | 'quality'

/** V7 set creation pipeline phases (state machine). */
export type SetCreationPhase =
  'uploading' | 'segmenting' | 'processing_characters' | 'generating_environment' | 'complete' | 'failed'

/** V7 enhancement pipeline phases. */
export type EnhancementPhase =
  'pending' | 'estimating_divergence' | 'cleaning_world' | 'replacing_characters' | 'completed' | 'failed'

/** Character replacement progress within enhancement. */
export interface CharacterReplacementProgress {
  current: number
  total: number
  currentLabel: string
}

/** Character placement data sent with screenshot capture. */
export interface CharacterPlacement {
  characterId: string
  position: Vec3
  rotation: Vec3
  scale: Vec3
  screenBbox: NormalizedBbox
}

// =============================================================================
// Billing analytics
// =============================================================================

/** Pivot dimension for the team billing dashboard. */
export type SpendGroupBy =
  'project' | 'actor' | 'model' | 'actor_project' | 'actor_model' | 'user' | 'user_project' | 'user_model'

export type SpendActorType = 'user' | 'api_key' | 'unknown'

/** Actor option returned independently of the active dashboard pivot. */
export interface SpendActorFacetEntry {
  key: string
  label: string
  actorType: SpendActorType
  color?: string
}

/** Measure selected for chart/table emphasis. */
export type SpendMeasure = 'martini_olives' | 'byok_fal_usd' | 'generations'

/** Coarseness of the time axis in the analytics chart. */
export type SpendBucket = 'day' | 'week'

/** Source quality for BYOK FAL USD row costs. */
export interface ByokFalUsdSourceBreakdown {
  estimated: number
  reconciled: number
  missing: number
}

/** Single time bucket: total olives charged per pivot key in that bucket. */
export interface SpendTimeBucket {
  ts: string
  byKey: Record<string, number>
  byokFalUsdByKey: Record<string, number>
  countByKey: Record<string, number>
}

/** A single row in the analytics breakdown (one per pivot key). */
export interface SpendBreakdownEntry {
  key: string
  label: string
  totalOlives: number
  totalByokFalUsd: number
  txCount: number
  prevTotalOlives: number
  prevTotalByokFalUsd: number
  prevTxCount: number
  byokFalUsdSourceBreakdown: ByokFalUsdSourceBreakdown
  meta: {
    color?: string
    avatar?: string
    actorType?: SpendActorType
    actorId?: string
    actorLabel?: string
    apiKeyId?: string | null
    apiKeyLabel?: string | null
    auditUserId?: string | null
    auditUserAlias?: string | null
    projectId?: string
    projectName?: string
    userId?: string
    userAlias?: string
    model?: string
    modelLabel?: string
  }
  sparkline: number[]
  byokFalUsdSparkline: number[]
  countSparkline: number[]
}

/** Quick-glance KPI summary alongside the analytics payload. */
export interface SpendKpiHighlight {
  actorType?: SpendActorType
  actorId?: string
  actorLabel?: string
  apiKeyId?: string | null
  apiKeyLabel?: string | null
  auditUserId?: string | null
  auditUserAlias?: string | null
  userId?: string
  alias?: string
  color?: string
  model?: string
  modelLabel?: string
  totalOlives: number
  totalByokFalUsd: number
  txCount: number
}

/** The series of card payment fees in `SpendTimeBucket`: not a project, member or model, so never a breakdown row. */
export const SPEND_PAYMENT_FEE_KEY = '__payment_fee'

export interface SpendAnalyticsResponse {
  /** @deprecated Use totalOlives. */
  total: number
  /** @deprecated Use prevTotalOlives. */
  prevTotal: number
  totalOlives: number
  prevTotalOlives: number
  /** Card payment fees in the period, on top of `totalOlives`, which holds usage only like `breakdown`. */
  paymentFeeOlives: number
  prevPaymentFeeOlives: number
  totalByokFalUsd: number
  prevTotalByokFalUsd: number
  byokFalUsdSourceBreakdown: ByokFalUsdSourceBreakdown
  generationCount: number
  prevGenerationCount: number
  buckets: SpendTimeBucket[]
  bucket: SpendBucket
  actorFacet: SpendActorFacetEntry[]
  breakdown: SpendBreakdownEntry[]
  topUser: SpendKpiHighlight | null
  topModel: SpendKpiHighlight | null
  topUserByokFalUsd: SpendKpiHighlight | null
  topModelByokFalUsd: SpendKpiHighlight | null
  topUserByGenerationCount: SpendKpiHighlight | null
  topModelByGenerationCount: SpendKpiHighlight | null
  truncated: boolean
}

/**
 * Per-member spend cap row used by the team billing controls.
 * `monthSpendOlives` is live spend in the current calendar month (UTC).
 */
export interface MemberSpendCap {
  userId: string
  alias: string
  color: string
  email: string | null
  role: 'admin' | 'member'
  spendCapOlives: number | null
  spendAlertThresholdOlives: number | null
  monthSpendOlives: number
}

export interface MemberSpendCapsResponse {
  members: MemberSpendCap[]
  monthLabel: string
}

/**
 * Classified kind of AI agent talking to our MCP server.
 *
 * Detected from the HTTP user-agent (and, during MCP initialize, the
 * clientInfo.name field). Only kinds we have a brand asset for are rendered
 * in the collaborator avatar strip — others fall through to 'unknown' and
 * are ignored by the UI but still logged for analytics.
 */
export type McpAgentKind =
  | 'claude-code'
  | 'claude-desktop'
  | 'cursor'
  | 'windsurf'
  | 'cline'
  | 'continue'
  | 'zed'
  | 'gemini-cli'
  | 'codex'
  | 'chatgpt'
  | 'martini-chat'
  /** The first-party `martini` CLI (`X-Martini-Client: martini-cli/<version>`), on any host agent. */
  | 'martini-cli'
  /** Martini's own workflow agent, calling the registry in-process or from its sandbox. */
  | 'martini-workflow'
  /** Next Securities' orchestrator (`ncp-agent-worker/<version> (+nx-securities)`), an enterprise API/MCP client. */
  | 'ncp-agent-worker'
  | 'unknown'

/**
 * Ephemeral presence entry for an AI agent actively using the MCP on a
 * project. Lives in a backend in-memory registry with a sliding TTL and is
 * broadcast to the project's WebSocket room so the collaborator strip can
 * render a logo avatar alongside human peers.
 */
export interface McpAgentPresence {
  agentKind: McpAgentKind
  userId: string
  lastActiveAt: number
  expiresAt: number
}

/** WebSocket broadcast: current set of active MCP agents on a project. */
export interface McpAgentPresenceEvent {
  type: 'mcp_agent_presence'
  projectId: string
  agents: McpAgentPresence[]
}

// =============================================================================
// Subject Collections
// =============================================================================

export interface SubjectCollectionMember {
  subjectId: string
}

/**
 * First-class canvas object that groups existing SubjectCanvasNode records as
 * variants. Placement is stored separately in canvasPositions[`collection-${id}`].
 */
export interface SubjectCollection {
  id: string
  name: string
  /**
   * Optional MVP home scene used by the single-placement canvas UI.
   * Collection identity itself is project-scoped; placement lives in
   * canvasPositions[`collection-${id}`].
   */
  homeSceneId?: string
  createdAt: string
  createdBy: string
  /** Ordered subject members. Each referenced subject stores collectionId === id. */
  memberOrder: SubjectCollectionMember[]
}

// =============================================================================
// V10: Subject Poses
// =============================================================================

/**
 * A single 3D pose owned by a SubjectCanvasNode.
 *
 * Each pose bundles a mesh, character sheet, extracted panels, and cutout —
 * everything needed to place the subject in a V10 set and render it through
 * the V10 shot pipeline.  A subject can have multiple poses (e.g. from
 * different set pipelines or future manual creation).
 *
 * Stored inline on `SubjectCanvasNode.poses[]` in Yjs.  Only URL strings are
 * stored — binary assets live in R2.
 */
export interface SubjectPose {
  id: string
  name: string
  meshUrl: string
  meshHeight?: number
  characterType?: CharacterType
  cutoutUrl?: string
  cutoutThumbnailUrl?: string
  headshotUrl?: string
  headshotThumbnailUrl?: string
  /**
   * Set-level display image/thumbnail (a generated default used as the main when no
   * headshot exists). Persisted so the render/panel "is this a user swap?" detector
   * (`pickCustomMainImage`) recognises it as a generated default — matching the capture-time
   * `isGeneratedCharacterImageUrl` check, which already treats these as generated.
   */
  displayImageUrl?: string
  displayThumbnailUrl?: string
  characterSheetUrl?: string
  frontalPanelUrl?: string
  referencePanelUrls?: string[]
  portraitPanelUrl?: string
  /** Pre-rendered thumbnail of the 3D mesh (stored in R2). */
  meshVisualizationUrl?: string
  /** Set ID whose pipeline created this pose (for dedup on re-creation). */
  sourceSetId?: string
  createdAt: string
}

// =============================================================================
// SubjectCanvasNode (canonical — synced into frontend/src/types/shared.ts)
// =============================================================================

export type SubjectCategory = 'character' | 'animal' | 'prop' | 'costume' | 'scene' | 'effect' | 'other'

/** Structural mirror of the image-pipeline schema; kept dependency-free for shared-type sync. */
export interface ImageAdjustments {
  exposure: number
  temp: number
  tint: number
  highlights: number
  shadows: number
  whites: number
  blacks: number
  contrast: number
  saturation: number
  vibrance: number
  vignette: number
  /** Vignette shape (Lightroom post-crop vignette): midpoint and feather 0..100, roundness and offsets −100..100. */
  vignetteMidpoint: number
  vignetteFeather: number
  vignetteRoundness: number
  vignetteOffsetX: number
  vignetteOffsetY: number
  fade: number
  grain: number
  grainSeed: number
}

export interface ImageEdit {
  angle?: number
  crop?: SubjectCropRect
  adjustments?: ImageAdjustments
}

export interface EditRevision {
  revisionId: string
  edit: ImageEdit
  pipelineVersion: number
  uri: string
  createdAt: string
  /** Migrated crop revisions record the parent's source at promotion, not its historical file. */
  source: { uri: string; width: number; height: number }
}

/** Crop rectangle in natural (full-resolution) image pixels, stable across display scaling. */
export interface SubjectCropRect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Canonical SubjectCanvasNode shape — the Yjs record for a reusable visual
 * subject (character, animal, prop, costume, scene, effect, other).
 *
 * Both backend (board mutations, MCP, generation) and frontend (canvas,
 * inspector, store) consume this type. Keep field additions here; do not
 * re-declare in `backend/src/board/index.ts` or `frontend/src/types/index.ts`.
 *
 * When `category === 'character'`, the character-specific fields are
 * populated by the set pipeline (V10+) or by auto-generation. They are
 * internal — used under the hood for generation quality.
 */
export interface SubjectCanvasNode {
  id: string
  sceneId: string
  binId?: string
  /**
   * When set, the subject is a member of the named variation stack and is
   * hidden when the stack is collapsed. Mirrors `Asset.stackId` so stacks
   * can hold both assets and subjects with a uniform back-reference shape.
   * Mutually exclusive with `collectionId`.
   */
  stackId?: string
  /**
   * When set, this subject is a variant inside a SubjectCollection.
   * Mutually exclusive with `stackId`; collection membership is mirrored
   * by `subjectCollections[collectionId].memberOrder`.
   */
  collectionId?: string
  name: string
  shotNumber?: number
  description?: string
  category: SubjectCategory

  mainImageUrl?: string
  /** Canvas asset ID of the source image (for click-to-navigate). Null when uploaded directly. */
  mainImageAssetId?: string | null
  /** Pre-crop original — set when a new image is uploaded/dropped. Crop edits only update mainImageUrl. */
  originalMainImageUrl?: string
  /** Crop rect applied to originalMainImageUrl, in natural image pixels. Set when user crops; cleared on new upload. */
  mainImageCropRect?: SubjectCropRect
  referenceImageUrls?: string[]
  /** Canvas asset IDs parallel to referenceImageUrls (for click-to-navigate). Null entries = uploaded directly. */
  referenceImageAssetIds?: (string | null)[]
  /** Pre-crop originals — parallel to referenceImageUrls. Crop edits only update referenceImageUrls. */
  originalReferenceImageUrls?: string[]

  voiceId?: string
  voiceProvider?: string
  voiceSourceUrl?: string
  voiceLabel?: string

  thumbnailUrl?: string
  color?: string
  rating?: ShotRating
  starRating?: StarRating
  colorLabel?: ShotColorLabel

  createdAt: string
  createdBy: string
  /**
   * Variant property labels for collection variants (e.g. `{ pose: 'angry' }`).
   * Free-form for MVP; later phases may add property definitions.
   */
  variantProperties?: Record<string, string>

  // ---------------------------------------------------------------------------
  // Character-specific fields (populated when category === 'character')
  // ---------------------------------------------------------------------------

  /** NanoBanana 14-panel character sheet (hidden — used for panel extraction). */
  characterSheetUrl?: string
  /** Extracted frontal full-body panel from character sheet (hidden — best image for generation). */
  frontalPanelUrl?: string
  /** Extracted side/back panels from character sheet (hidden — generation angle refs). */
  referencePanelUrls?: string[]
  /** Extracted frontal portrait panel from character sheet (hidden). */
  portraitPanelUrl?: string

  /** SAM3D .glb mesh URL (hidden — used in 3D set viewer). */
  meshUrl?: string
  /** Mesh bounding-box height in meters (hidden — scale computation). */
  meshHeight?: number
  /** Character type — determines mesh reconstruction endpoint (hidden). */
  characterType?: 'person' | 'creature' | 'object'

  /** V10+ poses — each bundles a mesh, sheet, panels, cutout for set placement. */
  poses?: SubjectPose[]
}

/**
 * Lightweight binding between a subject and a V10 set.
 *
 * Stored in the Yjs `setSubjectPlacements` map keyed by `{setId}:{subjectId}`.
 * 3D transforms live separately in the `characterTransforms` map (composite
 * key `{setId}:{subjectId}`).  Mesh/panel URLs are resolved at read time by
 * looking up the subject's `poses` array via `poseId`.
 */
export interface SetSubjectPlacement {
  subjectId: string
  poseId: string
  name: string
  color: string
  slotIndex: number
  /** Initial 3D transform captured at set creation; characterTransforms overrides it after user edits. */
  initialPosition?: Vec3 | null
  initialRotation?: Vec3 | null
  initialScale?: Vec3 | null
  /** Tombstone used to remember that this subject was removed from the set. */
  removedAt?: string
}

/**
 * One mutation event from an MCP tool call. Broadcast over WebSocket to
 * the project room so the frontend activity log can render a receipt.
 *
 * Ephemeral by design — server holds no history; the log is whatever the
 * client buffered during this session. Read-only tools (`get_*`,
 * `list_*`) do not emit activity.
 */
export interface McpActivityEvent {
  type: 'mcp_activity'
  projectId: string
  /** Unique ID per tool invocation, lets the frontend update an entry in place. */
  toolCallId: string
  toolName: string
  agentKind: McpAgentKind
  userId: string
  status: 'completed' | 'failed'
  startedAt: number
  durationMs: number
  /** Scene the action targeted, when known. */
  sceneId?: string
  /** Primary entity acted on (assetId / nodeId / sceneId / subjectId). */
  targetId?: string
  /** All entities acted on by the tool call, when known. */
  targetIds?: string[]
  /**
   * Media kind affected by the call, when known. Lets the activity log
   * say "image" / "video" instead of generic "node". Pulled from the
   * tool result's asset record for create-style tools.
   */
  nodeKind?: 'image' | 'video' | 'set' | 'audio'
  /** Short human-readable description, e.g. "created node in Scene 2". */
  summary?: string
  errorMessage?: string
}

/** GET /organizations/:orgId/mcp-usage */
export interface McpUsageResponse {
  used: number
  limit: number
  /** Start of the current calendar month in UTC. */
  periodStart: string
}

// ============================================
// Actions (agent-run canvas cards; "workflow" names the chain/run)
// ============================================

export type ActionInputRef =
  | { type: 'bin'; id: string; label?: string }
  | { type: 'asset'; id: string; label?: string }
  | { type: 'note'; id: string; label?: string }
  | { type: 'variable'; id: string; label?: string }
  | { type: 'model'; id: string; label?: string }
  /**
   * A shared Instructions file (`project_files` path as the id): the method
   * several Actions follow, injected whole into the executor's prompt after
   * the brief. Not an input — it never appears in the resolved input context,
   * draws no edge, and the fingerprint hashes its path, not its content.
   */
  | { type: 'instructions'; id: string; label?: string }

export interface ActionPinnedSettings {
  modelId?: string
  duration?: number
  aspectRatio?: string
}

/**
 * A collaborative Action stored in the project's `actions` Yjs map.
 *
 * The brief (`instruction`) is the wiring: input and output bins are derived
 * from its @-refs, never configured separately. Cardinality (how many times an
 * Action runs) is decided by the plan pass reading the brief against current
 * bin contents — there is no per-input iterate/context configuration.
 */
export interface Action {
  id: string
  sceneId: string
  name: string
  instruction: string
  agentId: string
  /** Explicit execution order. Canvas x-position is presentation, not control flow. */
  order: number
  pinned?: ActionPinnedSettings
  inputRefs: ActionInputRef[]
  /**
   * Bins this Action may write into, derived from the brief's @-refs. An asset
   * belongs to exactly one bin, so several destinations mean *routing* — the
   * brief decides which takes land where ("keepers to Selects, the rest to
   * Retakes") — never copying one take into all of them.
   */
  outputBinIds?: string[]
  gate: 'auto' | 'approval'
  /**
   * The workflow this Action belongs to (a `workflows` map record). Actions
   * without one are grouped on the canvas by their wiring instead, and get a
   * record the first time someone names the group.
   */
  workflowId?: string
  /**
   * The project-file path of this Action's own Instructions (the how: steps,
   * commands, settings, workarounds with their reasons), injected whole after
   * the brief on every unit and rewritten by the harness after runs that
   * learned something. Set when the Action is authored (default from
   * `ownInstructionsPathFor`, made unique on the canvas) or when a person first
   * writes the file; never derived from names afterwards, so a rename moves
   * nothing, and carried verbatim through placement so every copy of a saved
   * workflow shares one file. Absent on Actions authored before this existed:
   * they have no own Instructions until someone writes the file (ADR-0008).
   */
  instructionsPath?: string
  /**
   * Wiring this Action lost because what it pointed at was deleted. Absent
   * when nothing is lost. See `ActionLostWiring`.
   */
  lostWiring?: ActionLostWiring[]
  createdAt: string
  updatedAt: string
}

/**
 * One piece of wiring an Action lost to a delete. The board still drops the
 * dead ref (an Action never points at a ghost) but records the loss here, so
 * it stays visible: the card says what went, get_canvas_children and
 * update_actions return it, and a run that finds its bin gone can say why.
 * Undoing the delete removes the record with the rest of the transaction. An
 * explicit rewire settles it (`settleActionLostWiring`). Never saved into a
 * workflow or carried to a placed copy.
 */
export interface ActionLostWiring {
  type: 'bin' | 'variable' | 'note'
  id: string
  /** The name when it was deleted: the record is gone, so this is the only label left. */
  name: string
  role: 'input' | 'output'
  /** When the delete happened (ISO). */
  at: string
}

/**
 * A named workflow on a canvas — the container the frame is drawn around.
 * Stored in the project's `workflows` Yjs map. Membership lives on the
 * Actions (`Action.workflowId`); the frame's bounds are derived from the
 * member cards and the bins they are wired to, never stored.
 */
export interface WorkflowKitCandidateIdentity {
  runId?: string
  candidateId?: string
  snapshotId: string
}

export interface ExternalKitCandidateProvenance {
  source: 'external'
  authorUserId: string
  idempotencyKey: string
  requestHash: string
  submissionId: string
  baseline: Pick<WorkflowToolBundle, 'id' | 'sha256' | 'snapshot'> | null
  reviewedWorkflowFingerprint: string
  reviewedFingerprints: Record<string, ActionFingerprint>
}

export interface WorkflowKitCandidate extends WorkflowKitCandidateIdentity {
  /** Explicit verification of this proposed fix; absent means not verified. */
  verification?: { passed: boolean }
  external?: ExternalKitCandidateProvenance
  at: string
  files: Array<{
    path: string
    change: 'added' | 'changed' | 'deleted'
    bytes: number
    beforeExecutable?: boolean
    afterExecutable?: boolean
  }>
  summary: string
}

export interface Workflow {
  /** Serializes saves of this placement until release or expiry. */
  saveLease?: { token: string; expiresAt: number }
  /** Publication authority, checked atomically with candidate mutations by the board. */
  candidateLeases?: Record<string, { token: string; expiresAt: number }>
  candidates?: WorkflowKitCandidate[]
  /** Opaque candidate-collection revision prevents compare-and-set ABA races. */
  candidateRevision?: string
  id: string
  sceneId: string
  name: string
  /**
   * The saved workflow this placed workflow was placed from or last saved as.
   * The storage key predates the workflows/runs vocabulary (renaming a live
   * Yjs key needs a read-both shim); the `workflows` row mirrors it as
   * `sourceWorkflowId`.
   */
  definitionId?: string
  /** Actual immutable base of this editable Yjs draft; absent on legacy copies. */
  definitionRevisionId?: string
  /** Immutable tools inherited from a saved/copy/exported workflow. Runs keep their own pinned reference. */
  toolBundle?: WorkflowToolBundle
  /**
   * What a run does when the compiled kit is stale or missing: recompile
   * first (`auto`), or refuse and let the person decide (`manual`). Absent on
   * records written before the field — read as `auto`.
   */
  kitPolicy?: WorkflowKitPolicy
  /** Saved/imported copies may prepare a missing kit once; any existing kit still follows kitPolicy. */
  allowInitialKitSetup?: boolean
  createdAt: string
  updatedAt: string
}

/** Run-start policy for a stale or missing kit, stored on the placed workflow. */
export type WorkflowKitPolicy = 'auto' | 'manual'

/**
 * Optional UI barriers checked before the run door writes anything.
 *
 * The canvas UI sends the Actions it holds (`expectedActions`) and the backend
 * hashes both sides with one build of `workflowFingerprintSource` — a browser
 * never computes the fingerprint itself. A tab left open across a deploy runs
 * an older bundle, and an older hashing scheme against the current backend
 * refused every run as "still syncing" (2026-09-11). `expectedActionsFingerprint`
 * is the pre-2026-09-11 door: still accepted so tabs built before the switch
 * are no worse off; remove once the stale-build prompt has been live a while.
 */
export interface WorkflowRunEditBarriers {
  expectedActions?: Action[]
  expectedActionsFingerprint?: string
  expectedKitPolicy?: WorkflowKitPolicy
}

/**
 * Why a barrier refused: which Actions differ between what the client sent and
 * what the canvas read holds, by field name only — never content. Field names
 * are the keys of `actionMachinery`. Rides on the 409 so the client can attach
 * it to its report when the retries give up; the backend itself stays quiet,
 * because every genuinely syncing click produces a few of these on the way in.
 */
export interface WorkflowActionsMismatch {
  changed: Array<{ id: string; fields: string[] }>
  missingOnCanvas: string[]
  extraOnCanvas: string[]
}

/** Bins an Action reads, in tag order — the normalized wiring (brief tags merged with stored refs), like the run resolves it. */
export function actionInputBinIds(action: Pick<Action, 'instruction' | 'inputRefs'>): string[] {
  return normalizeActionInputRefs(action)
    .filter(ref => ref.type === 'bin')
    .map(ref => ref.id)
}

/** Where an Action's takes may land, deduped. Empty means "leave them on the canvas". */
export function resolveActionOutputBinIds(action: Pick<Action, 'outputBinIds'>): string[] {
  return [...new Set(action.outputBinIds?.filter(id => id.length > 0) ?? [])]
}

/**
 * Bins that are both read and written by the same Action.
 *
 * MCP uses these overlaps for warnings and the UI for hints; reading and
 * writing the same bin is allowed for generate-then-review Actions.
 */
export function actionBinConflicts(action: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds'>): string[] {
  const inputs = new Set(actionInputBinIds(action))
  return resolveActionOutputBinIds(action).filter(id => inputs.has(id))
}

/** A board that keeps churning must not grow an Action record without bound. */
const ACTION_LOST_WIRING_LIMIT = 20

const lostWiringKey = (entry: Pick<ActionLostWiring, 'type' | 'id' | 'role'>) =>
  `${entry.type}:${entry.id}:${entry.role}`

/** Add newly lost wiring to an Action's record; an entry already recorded keeps its first time. */
export function recordActionLostWiring(
  existing: readonly ActionLostWiring[] | undefined,
  lost: readonly ActionLostWiring[],
): ActionLostWiring[] {
  const merged = [...(existing ?? [])]
  const seen = new Set(merged.map(lostWiringKey))
  for (const entry of lost) {
    if (seen.has(lostWiringKey(entry))) continue
    seen.add(lostWiringKey(entry))
    merged.push(entry)
  }
  return merged.slice(-ACTION_LOST_WIRING_LIMIT)
}

/** The bins, variables and notes an Action reads, as `type:id` keys. */
function actionWiredInputKeys(action: Pick<Action, 'instruction' | 'inputRefs'>): Set<string> {
  return new Set(
    normalizeActionInputRefs(action)
      .filter(ref => ref.type === 'bin' || ref.type === 'variable' || ref.type === 'note')
      .map(ref => `${ref.type}:${ref.id}`),
  )
}

const sameKeys = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every(key => b.has(key))

/**
 * The lost-wiring record after an edit: changing the output bins settles the
 * lost outputs, changing what the Action reads settles the lost inputs — the
 * person or agent rewired it. Compares the wiring itself, not which fields a
 * write carried, so a rename or an autosave of an unchanged brief settles
 * nothing. Undefined when nothing is left.
 */
export function settleActionLostWiring(
  before: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds'>,
  after: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds' | 'lostWiring'>,
): ActionLostWiring[] | undefined {
  if (!after.lostWiring?.length) return undefined
  const outputsChanged = !sameKeys(
    new Set(resolveActionOutputBinIds(before)),
    new Set(resolveActionOutputBinIds(after)),
  )
  const inputsChanged = !sameKeys(actionWiredInputKeys(before), actionWiredInputKeys(after))
  const rest = after.lostWiring.filter(entry => (entry.role === 'output' ? !outputsChanged : !inputsChanged))
  return rest.length > 0 ? rest : undefined
}

/** The Action with its lost-wiring record replaced; an empty record removes the key. */
export function withActionLostWiring<T extends Pick<Action, 'lostWiring'>>(
  action: T,
  lostWiring: readonly ActionLostWiring[] | undefined,
): T {
  const { lostWiring: _previous, ...rest } = action
  return (lostWiring?.length ? { ...rest, lostWiring: [...lostWiring] } : rest) as T
}

/**
 * `@[Kind:Label](id)` — the tag grammar Actions use inside briefs. Kept
 * byte-identical with backend/src/workflows/tags.ts and
 * frontend/src/lib/workflowTags.ts: the brief is the wiring, so every side
 * must parse the same tokens.
 */
export const ACTION_TAG_PATTERN_SOURCE = String.raw`@\[(Asset|Bin|Model|Variable|Note|Instructions):([^\]]+)\]\(([^)]+)\)`

/** The refs a brief tags, in brief order, one per (type, id), with their labels. */
export function actionBriefTagRefs(instruction: string): ActionInputRef[] {
  const refs: ActionInputRef[] = []
  const seen = new Set<string>()
  for (const match of instruction.matchAll(new RegExp(ACTION_TAG_PATTERN_SOURCE, 'g'))) {
    const type = match[1].toLowerCase() as ActionInputRef['type']
    const id = match[3]
    const key = `${type}:${id}`
    if (seen.has(key)) continue
    seen.add(key)
    refs.push({ type, id, label: match[2] })
  }
  return refs
}

/**
 * The refs an Action actually reads: the brief's tags merged with the stored
 * refs, one per (type, id), the tag's label winning over a stored one. The
 * brief is the wiring, so a tag the stored refs never carried (observed live
 * 2026-09-08: briefs tagging a script variable and source assets over
 * `inputRefs: []`) is still an input; a stored ref the brief does not tag (an
 * MCP caller passing refs without tags) stays one. Fingerprints, run
 * snapshots, pricing and input resolution all read this one definition, so
 * they can never disagree about what an Action is wired to.
 */
export function normalizeActionInputRefs(action: Pick<Action, 'instruction' | 'inputRefs'>): ActionInputRef[] {
  // A record authored before the brief was required, or a partial object, has no brief to parse.
  return dedupeActionInputRefs([...actionBriefTagRefs(action.instruction ?? ''), ...(action.inputRefs ?? [])])
}

/** One ref per (type, id) — the same tag twice in a brief is still one connection. */
export function dedupeActionInputRefs(refs: readonly ActionInputRef[]): ActionInputRef[] {
  const seen = new Set<string>()
  const result: ActionInputRef[] = []
  for (const ref of refs) {
    const key = `${ref.type}:${ref.id}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(ref)
  }
  return result
}

/**
 * The refs that draw as edges and ports: every input except models and
 * Instructions (which connect nothing), deduped. Bins the Action also writes
 * are excluded because a self-loop is not a dependency edge; their contents
 * are still read as inputs.
 */
export function actionEdgeRefs(action: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds'>): ActionInputRef[] {
  const outputs = new Set(resolveActionOutputBinIds(action))
  return normalizeActionInputRefs(action).filter(
    ref => ref.type !== 'model' && ref.type !== 'instructions' && !(ref.type === 'bin' && outputs.has(ref.id)),
  )
}

/** Shared Instructions files an Action's brief tags, in tag order (the paths, deduped). */
export function actionInstructionsRefs(action: Pick<Action, 'inputRefs'>): ActionInputRef[] {
  return dedupeActionInputRefs(action.inputRefs).filter(ref => ref.type === 'instructions')
}

/** Immutable JSON bytes. Only this descriptor travels through collaboration and run records. */
export interface JsonFileSnapshot {
  id: string
  storageAssetId: string
  filename: string
  uri: string
  checksum: string
  sizeBytes: number
}

/** A constraint on an existing brief-tagged input bin. Defaults are explicit immutable revisions. */
export interface FileInputContract {
  key: string
  format: 'json'
  cardinality: 'one' | 'many'
  defaultFiles?: JsonFileSnapshot[]
}

export type WorkflowFileSource = { json: unknown; filename?: string } | { fileId: string }
export type WorkflowFileInputs = Record<string, WorkflowFileSource | WorkflowFileSource[]>

/** A wired bin or a descendant of an explicitly included preset input. */
export interface WorkflowDefinitionBin {
  collapsed?: boolean
  includeContents?: boolean
  parentSourceId?: string
  childOrder?: string[]
  fileInput?: FileInputContract
  sourceId: string
  name: string
  layoutMode?: 'freeform' | 'grid' | 'row' | 'columns'
  layoutWidth?: number
  gridColumns?: number
}

/** Common wired-bin snapshot for the run door and the live kit status key. */
export function snapshotKitBin(bin: Omit<WorkflowDefinitionBin, 'sourceId'> & { id: string }): WorkflowDefinitionBin {
  return {
    sourceId: bin.id,
    name: bin.name,
    ...(bin.collapsed !== undefined ? { collapsed: bin.collapsed } : {}),
    ...(bin.fileInput ? { fileInput: bin.fileInput } : {}),
    ...(bin.layoutMode ? { layoutMode: bin.layoutMode } : {}),
    ...(bin.gridColumns ? { gridColumns: bin.gridColumns } : {}),
    ...(bin.layoutWidth ? { layoutWidth: bin.layoutWidth } : {}),
  }
}

/** A note a brief references directly; instantiating writes a placeholder with this text. */
export interface WorkflowDefinitionNote {
  /** Source id of a bin carried by this definition. */
  binId?: string
  sourceId: string
  text: string
}

/** A variable a brief references directly; instantiating writes one with this name and value. */
export interface WorkflowDefinitionVariable {
  /** Source id of a bin carried by this definition. */
  binId?: string
  sourceId: string
  name: string
  value: string
  color?: string
}

/**
 * An Instructions file the workflow's Actions follow: the Action's own file
 * (found by convention at `ownInstructionsPathFor`, machine-maintained) or a
 * shared file the brief tags `@[Instructions:Label](path)`. Paths are stable
 * across instantiation (they derive from names, never from ids), so the same
 * file is found in every copy. Instantiation writes the file only where none
 * exists; an existing file always wins. On a run, `definition_snapshot.files`
 * holds the content the units ran with — read from the project at run start,
 * never copied from a saved definition.
 */
export interface WorkflowDefinitionFile {
  path: string
  content: string
  /** No writer at all until a human unlocks it in the inspector. */
  locked?: boolean
}

/**
 * The carried asset record: the source `BoardAssetRecord` minus its id, lineage
 * (parent/stack/version group), placement (scene/bin/shot number), and
 * source-project pointers. Media URLs point at the same immutable R2 objects.
 */
export interface WorkflowDefinitionAssetRecord {
  uri: string
  prompt: string
  duration: number
  mimeType: string
  checksum: string
  status: 'completed'
  proxyUri?: string
  thumbnailUri?: string
  assetType?: string
  aspectRatio?: string
  width?: number
  height?: number
  modelId?: string
  [key: string]: unknown
}

/**
 * An asset a brief references directly (a pinned character sheet, a style
 * reference); instantiating writes a new asset record on the same R2 objects.
 */
export interface WorkflowDefinitionAsset {
  sourceId: string
  label: string
  record: WorkflowDefinitionAssetRecord
  displayName?: string
}

/** Component hashes of an Action's compiled machinery. */
export interface ActionFingerprint {
  brief: string
  instructions: string
  wiring: string
  pinned: string
  action: string
}

/** Parsed tools/kit.json, with schema defaults applied. */
export interface Kit {
  version: 1
  /** Runtime semantics, distinct from the file format. Missing means legacy-unverified. */
  runtimeContract?: 'martini-kit-v1'
  driver: 'agent' | 'engine'
  /**
   * Omitted means serial; only agent-driven execution can opt in. `streaming-inputs` is env-gated
   * (`WORKFLOW_STREAMING_INPUTS`, docs/workflows/operational-toggles.md): off, it runs as settled-inputs.
   */
  concurrency?: 'serial' | 'settled-inputs' | 'streaming-inputs'
  /**
   * Opts the kit in to named generate families (`generate.<name>.json`) and submit-only applies
   * (`wait: false`, `--no-wait`); both also need `WORKFLOW_GENERATE_FAMILIES=1`. Omitted, a kit that
   * declares `concurrency: 'streaming-inputs'` is opted in and any other is not; `false` opts out.
   */
  generateFamilies?: boolean
  /** The step agent's model; omitted means the engine default (Fable 5.1, standard speed). Fast requires claude-opus-5-5. */
  agent?: { model: 'claude-fable-5-1' | 'claude-opus-5-5'; speed: 'standard' | 'fast' }
  workflowFingerprint?: string
  compiledAt?: string
  compiledByRunId?: string
  entrypoint?: string
  actions: Record<
    string,
    {
      step: string
      fingerprint: ActionFingerprint
      files: string[]
      decisionPoints: Array<{ id: string; when: string; whatTheModelJudges: string }>
      mode: 'script' | 'script+model' | 'judgement'
      /** Kit-owned judge configuration, preserved during placement and publication. */
      judges?: Array<Record<string, unknown>>
      /** Per-step round limits; omitted kinds use the operator default. */
      retakeRounds?: { generate?: number; judge?: number }
    }
  >
  tests?: {
    command: string
    lastRun?: { at: string; passed: number; failed: number }
  }
  status: 'valid' | 'invalid'
  invalidReason?: string
}

export type Freshness = 'fresh' | 'stale' | 'missing'

export interface KitFreshness {
  overall: Freshness
  removed: string[]
  actions: Array<{
    actionId: string
    name: string
    step: string
    freshness: Freshness
    changed: Array<'brief' | 'instructions' | 'wiring' | 'pinned'>
  }>
}

/** The placed workflow's Implementation inspection response. */
export interface KitStatusResponse {
  candidates?: WorkflowKitCandidate[]
  kit: Kit | null
  freshness: KitFreshness
  lastCompileRun: {
    id: string
    status: WorkflowRunStatusValue
    createdAt: string
    completedAt: string | null
    errorMessage: string | null
    runLog?: string | null
    /** Historical canvas log, retained across independent frontend/backend deployments. */
    runLogNoteId?: string | null
  } | null
  kitPolicy: WorkflowKitPolicy
  /** Agent skills the published kit ships (see WorkflowToolBundle.kit.skills). */
  skills?: WorkflowKitSkill[]
}

/** One skill folder inside a kit: its SKILL.md path and the frontmatter that names it. */
export interface WorkflowKitSkill {
  name: string
  description: string
  /** SKILL.md path relative to tools/; the folder around it is the whole skill. */
  path: string
}

/** A portable capability for immutable tool files; bytes and manifest live in R2, never in Yjs. */
export interface WorkflowToolBundle {
  /** Compile metadata carried with the immutable library; absent on older bundles. */
  kit?: {
    sha256: string
    workflowFingerprint?: string
    compiledAt?: string
    compiledByRunId?: string
    status: 'valid' | 'invalid'
    tests?: Kit['tests']
    /** Agent skills the kit ships (every SKILL.md with a valid frontmatter), discovered at publish time. */
    skills?: WorkflowKitSkill[]
  }
  id: string
  sha256: string
  /** Trusted storage URL used when a workflow file crosses Martini environments. */
  manifestUrl?: string
  /** Published Daytona snapshot required by the tools; null for a host-only library. */
  snapshot: string | null
  fileCount: number
  bytes: number
}

/**
 * A workflow as machinery: the Actions with their briefs, the bins they are
 * wired to as empty containers, and the notes, variables, and assets the
 * briefs reference directly (a directly tagged asset is part of the program;
 * it travels as a new record on the same R2 objects). Bin CONTENTS are
 * material and never travel.
 *
 * Every id is in the SOURCE canvas's id space (action ids, `sceneId`,
 * `workflowId`, bin/note/variable/asset ids inside briefs and refs).
 * Instantiating onto a canvas mints fresh ids for all of it and rewrites the
 * briefs. `definition_snapshot` on a run stores the same shape.
 */
export interface WorkflowDefinition {
  name: string
  actions: Action[]
  bins?: WorkflowDefinitionBin[]
  presetBinContents?: WorkflowPackageBinContents[]
  notes?: WorkflowDefinitionNote[]
  variables?: WorkflowDefinitionVariable[]
  assets?: WorkflowDefinitionAsset[]
  toolBundle?: WorkflowToolBundle
  files?: WorkflowDefinitionFile[]
  /** Saved canvas arrangement in source-id space, relative to the workflow’s top-left corner. */
  layout?: WorkflowPackageLayout
  schemaVersion?: 1
}

/**
 * Instructions files are injected whole into every unit's prompt, so they stay
 * a bounded slice of it: per file, and per Action (own plus tagged files).
 * Measured in UTF-8 bytes (Korean is three bytes a character, so fewer
 * characters fit; the token count comes out similar). 48 KiB is roughly 12k
 * tokens of English, a few percent of the smallest action-agent context
 * window, and it rides the prompt cache after the first unit. Enforced with
 * one rule set (`instructionsCapViolation`) wherever a definition is built:
 * run start and resume, saving a workflow, exporting one; the inspector shows
 * the same numbers live. Over the cap refuses with the path and size named,
 * never truncates. Independent of the saved-definition size cap.
 */
export const INSTRUCTIONS_FILE_MAX_BYTES = 16_384
export const INSTRUCTIONS_PER_ACTION_MAX_BYTES = 49_152

/** UTF-8 byte length: the unit every Instructions cap is measured in, browser and Node alike. */
export function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length
}

/**
 * The first Instructions cap one Action breaks, as the sentence shown to the
 * person, or null when its files fit. `files` are the ones the Action reads:
 * its own file plus each tagged shared file, missing ones left out.
 */
/**
 * A short, stable key for an Instructions file's text: what a frozen run's
 * baseline records per path (`WorkflowRunOptions.instructionsBaseline`) so a
 * restart can tell an edit from a lock toggle or a save that changed nothing
 * (both bump `updatedAt`). Synchronous and identical in the browser and the
 * backend (53-bit cyrb53 plus the length); not a security hash.
 */
export function instructionsContentKey(content: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < content.length; i++) {
    const code = content.charCodeAt(i)
    h1 = Math.imul(h1 ^ code, 2654435761)
    h2 = Math.imul(h2 ^ code, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return `${content.length.toString(16)}-${(4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)}`
}

export function instructionsCapViolation(
  actionName: string,
  files: ReadonlyArray<{ path: string; bytes: number }>,
): string | null {
  for (const file of files) {
    if (file.bytes > INSTRUCTIONS_FILE_MAX_BYTES) {
      return `The Instructions file "${file.path}" is ${file.bytes} bytes; the limit is ${INSTRUCTIONS_FILE_MAX_BYTES} bytes per file. Shorten it or split it.`
    }
  }
  const total = files.reduce((sum, file) => sum + file.bytes, 0)
  if (total > INSTRUCTIONS_PER_ACTION_MAX_BYTES) {
    return `The Instructions of "${actionName}" add up to ${total} bytes; the limit is ${INSTRUCTIONS_PER_ACTION_MAX_BYTES} bytes per Action (own file plus tagged files). Shorten or untag some.`
  }
  return null
}

/** Portable content, independent of its source account, project, or transport. */
export interface ContentPackage<T> {
  format: 'martini-content'
  version: 1
  name: string
  content: T
}

/**
 * Source geometry a workflow file carries so an import lands looking like the
 * sender's canvas. Keyed like `canvasPositions` (`action-<id>`, `bin-<id>`,
 * `annotation-<id>`, `variable-<id>`, `asset-<id>`) in the SOURCE id space.
 * Root entries are relative to the cluster's top-left corner; entries for bin
 * contents are bin-relative, as stored. Sizes ride along where the source
 * stored one (bins, notes).
 */
export interface WorkflowPackageLayout {
  positions: Record<string, { x: number; y: number; width?: number; height?: number }>
}

/**
 * Material the sender chose to send along inside one of the workflow's bins.
 * Used by explicitly included saved presets and by selected material in a workflow file.
 */
export interface WorkflowPackageBinContents {
  sourceBinId: string
  assets: Array<{ sourceId: string; record: WorkflowDefinitionAssetRecord; displayName?: string }>
}

export type WorkflowContentPackage = ContentPackage<{
  type: 'workflow'
  definition: WorkflowDefinition
  layout?: WorkflowPackageLayout
  binContents?: WorkflowPackageBinContents[]
}>

// ---------------------------------------------------------------------------
// Project files — the project's memory filesystem (`project_files`)
// ---------------------------------------------------------------------------

/** Hard cap per file (the table's check constraint). */
export const PROJECT_FILE_MAX_BYTES = 1_048_576
export const PROJECT_FILE_PATH_MAX_LENGTH = 200
/** Files per project; memory is a notebook, not a bucket. */
export const PROJECT_FILES_PER_PROJECT_CAP = 2_000

export interface ProjectFileRecord {
  id: string
  projectId: string
  /** Relative slash-separated path, unique per project (e.g. `project.md`, `workflows/dailies/experience.md`). */
  path: string
  content: string
  /** Locked means no writer at all (people, agents, the harness) until a human unlocks it in the inspector. */
  locked: boolean
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
}

/** One row of a project's file listing — no content, so a listing is cheap to put in agent context. */
export interface ProjectFileListing {
  path: string
  bytes: number
  updatedAt: string
  locked: boolean
}

/** A path-safe slug for a name ("EP01 Dailies — Cut!" → "ep01-dailies-cut"). */
export function projectFileSlug(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '')
  return slug || 'untitled'
}

/** Where a workflow keeps its files: `workflows/<slug>/`. */
export function workflowFilesDirFor(workflowName: string): string {
  return `workflows/${projectFileSlug(workflowName)}`
}

/** What earlier runs of a workflow learned, appended by units and distilled after each Action. */
export function experiencePathFor(workflowName: string): string {
  return `${workflowFilesDirFor(workflowName)}/experience.md`
}

/**
 * The default path for an Action's own Instructions when it is authored:
 * `workflows/<workflow-slug>/instructions/<action-slug>.md`. A default only —
 * the path that counts is `Action.instructionsPath`, set once and never
 * recomputed from names.
 */
export function ownInstructionsPathFor(workflowName: string, actionName: string): string {
  return `${workflowFilesDirFor(workflowName)}/instructions/${projectFileSlug(actionName)}.md`
}

/**
 * A path no other Action uses: the candidate itself, or `<stem>-2.md`,
 * `<stem>-3.md`, … (two Actions named "Color Grade" and "Color-Grade" slug
 * alike; the second gets its own file).
 */
export function uniqueInstructionsPath(candidate: string, taken: ReadonlySet<string>): string {
  if (!taken.has(candidate)) return candidate
  const match = /^(.*?)(\.[A-Za-z0-9]+)?$/.exec(candidate)
  const stem = match?.[1] ?? candidate
  const extension = match?.[2] ?? ''
  for (let n = 2; ; n++) {
    const next = `${stem}-${n}${extension}`
    if (!taken.has(next)) return next
  }
}

/** The two kinds of workflow, one `workflows` table (ADR-0007). */
export type WorkflowKind = 'saved' | 'placed'

/**
 * An organization's library entry: reusable, versioned machinery. Runnable
 * directly (each run stamps a fresh placed copy) and placeable onto a canvas.
 */
/**
 * An operator pin on a saved workflow: every run of it — any door, any placed
 * copy linked to it — is driven by this engine, whatever the request asked
 * for. `oliveBudget` is what a pinned run gets when the request carries none
 * (Workflow Beta refuses a run without one); a budget in the request wins.
 * Set by hand on `workflows.run_engine` / `run_olive_budget`, like `feature_allowlist`.
 */
export interface WorkflowRunEnginePin {
  engine: 'fable'
  oliveBudget: number
}

export interface SavedWorkflowRecord {
  id: string
  kind: 'saved'
  organizationId: string
  name: string
  description: string
  /** Workspace folder name; null when unfiled. */
  folder: string | null
  /** Retired grouping label kept for compatibility; read-only and always null for new data. */
  versionOf: string | null
  version: number
  /** Immutable current save. Absent on pre-version-control records. */
  revisionId?: string | null
  versionLabel?: string
  releaseMode?: 'legacy' | 'managed'
  productionRevisionId?: string | null
  releaseGeneration?: number
  definition: WorkflowDefinition
  /** The placed workflow this was last saved from (provenance only). */
  sourceWorkflowId: string | null
  /** Operator pin on the engine of every run of this workflow; null when runs follow the request. */
  enginePin: WorkflowRunEnginePin | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/** A complete immutable save, distinct from a run-item repair/revision. */
export interface SavedWorkflowRevision {
  id: string
  workflowId: string
  /** The actual edited base; may belong to another workflow for a fork. */
  parentRevisionId: string | null
  /** Read-time, permission-checked ancestry; null hides an inaccessible source. */
  parent: {
    revisionId: string
    workflowId: string
    organizationId: string
    name: string
    versionLabel: string
    deleted: boolean
  } | null
  /** Backwards-compatible integer save counter, not the dotted label. */
  version: number
  major: number
  minor: number
  patch: number
  versionLabel: string
  definition: WorkflowDefinition
  fingerprint: string
  createdBy: string | null
  source: 'human' | 'agent' | 'automatic' | 'baseline'
  changeNote: string
  causalRunId: string | null
  causalFeedbackRef: string | null
  createdAt: string
}

export type SavedWorkflowRevisionSummary = Omit<SavedWorkflowRevision, 'definition'>

/** Graph metadata never loads executable definitions or their computed fingerprints. */
export type WorkflowLineageRevision = Omit<SavedWorkflowRevision, 'definition' | 'fingerprint'>

export interface WorkflowLineage {
  workflows: Array<{ id: string; name: string; organizationId: string; deleted: boolean }>
  revisions: WorkflowLineageRevision[]
  /** History or the immediate-child candidate window exceeded the requested limit. */
  truncated: boolean
}

export interface WorkflowReleaseEvent {
  id: string
  workflowId: string
  generation: number
  action: 'enable' | 'disable' | 'promote' | 'rollback'
  fromRevisionId: string | null
  toRevisionId: string | null
  releaseMode: 'legacy' | 'managed'
  createdBy: string | null
  changeNote: string
  createdAt: string
}

export interface WorkflowProductionState {
  releaseMode: 'legacy' | 'managed'
  productionRevisionId: string | null
  generation: number
  /** Newest 100 events at or before generation, in descending generation order. */
  events: WorkflowReleaseEvent[]
  /** True when older production audit events were omitted. */
  eventsTruncated: boolean
}

export interface WorkflowRevisionSelection {
  revisionId?: string
  production?: true
}

/**
 * One copy of a workflow on a canvas. The Yjs `workflows` record is the
 * truth (id == record id); the row mirrors it for the Workflows page and
 * links it to the saved workflow it was placed from or last saved as.
 */
export interface PlacedWorkflowRecord {
  id: string
  kind: 'placed'
  organizationId: string
  name: string
  /** The linked saved workflow, or null for a workflow authored on a canvas and never saved. */
  sourceWorkflowId: string | null
  /** The saved version it was placed from or last saved as; null when never linked. */
  version: number | null
  projectId: string
  sceneId: string
  canvasName: string
  /** A copy stamped for one API/MCP run of a saved workflow: hidden from listings, its runs roll up under the saved workflow. */
  hidden: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export type WorkflowRecord = SavedWorkflowRecord | PlacedWorkflowRecord

/** How the plan pass read the brief: fan out once per item of one input, or run once. */
export type ActionPlanMode = 'per_item' | 'single'

/** One approved execution unit, expanded mechanically by the harness from bin membership. */
export interface ActionPlanUnit {
  index: number
  /** Yjs asset UUIDs this unit consumes (the iterated item; context refs resolve at run time). */
  inputAssetIds: string[]
}

/**
 * The frozen contract between the plan pass and execution, stored on
 * `workflow_run_actions.plan`. The LLM authors only the *reading* of the brief
 * (`mode`, `iteratedRefId`, `outputsPerUnit`, `modelId`, `summary`,
 * `ambiguity`); the harness expands units, computes estimates, and hashes the
 * result — the model never authors numbers or UUIDs the gate relies on.
 */
/**
 * The plan pass's reading of the brief's judge clause. Guidance is the brief's
 * own acceptance language verbatim — strictness is never a code knob: the
 * judge model weighs it holistically and the harness branches only on `pass`.
 */
export interface ActionPlanJudge {
  guidance: string
  /** Total takes per unit, including the first (default 3). */
  maxAttempts: number
  /** Where gave-up takes route, ONLY when the brief names a destination; null = leave in place. */
  rejectBinId: string | null
}

/**
 * Why a plan re-parked after its units ran: the batch the agent built priced
 * (server-side, exact) above what was left on the run's rail, so nothing was
 * generated. `estOlives` is raised to the cost, so the next Go arms the rail
 * over it; the numbers let the gate say so instead of "needs input".
 */
export interface ActionPlanOverBudget {
  costOlives: number
  remainingOlives: number
}

export interface ActionPlan {
  mode: ActionPlanMode
  /** The input ref the Action fans out over; null when mode is 'single'. */
  iteratedRefId: string | null
  units: ActionPlanUnit[]
  outputsPerUnit: number
  totalPlannedOutputs: number
  modelId: string | null
  estOlives: number
  /** Set by the harness when the built batch was refused for exceeding the remaining budget. */
  overBudget?: ActionPlanOverBudget | null
  /** One human sentence of the plan's reading, rendered above the Go button. */
  summary: string
  /** The agent's question when the brief has more than one plausible reading. Parks, never fails. */
  ambiguity: string | null
  /** Present when the brief asks outputs to be judged; the harness owns the verdict/retake loop. */
  judge: ActionPlanJudge | null
  /** Server-computed. Approval stamps it into `review` so consent never carries to a re-derived plan. */
  planHash: string
}

/**
 * `needs_revision`: the run stopped because every unit of an Action asked for
 * its inputs to change (flag_for_revision, or a `DECLINED:` reply). Terminal
 * and resumable like `failed`, but the agent's decision, not a failure.
 */
export type WorkflowRunStatusValue =
  'pending' | 'awaiting_approval' | 'running' | 'paused' | 'completed' | 'failed' | 'needs_revision' | 'cancelled'

export type WorkflowRunActionStatusValue =
  | 'pending'
  | 'awaiting_approval'
  | 'running'
  | 'completed'
  | 'failed'
  /** Every unit asked for the inputs to change and none broke; see WorkflowRunStatusValue. */
  | 'needs_revision'
  | 'cancelled'
  | 'skipped'

export type WorkflowRunItemStatusValue =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'rejected'
  | 'superseded'
  /** A retake holds this take: it is the source of a rerun in flight and reverts or supersedes when that settles. */
  | 'rerunning'
  /**
   * The agent chose not to generate for this unit because the brief's inputs
   * fail a precondition it sets (flag_for_revision, or a `DECLINED:` reply):
   * `inputPayload.needsRevision` carries the request, or errorMessage the
   * reason when only the marker was used. Not a crash: the inputs need to
   * change, then the run is resumed or run again.
   */
  | 'needs_revision'

/** Compact plan block on the mirror so the canvas can render the gate without HTTP. */
export interface WorkflowRunStatusPlan {
  unitCount: number
  totalPlannedOutputs: number
  estOlives: number
  summary: string
  ambiguity: string | null
  overBudget?: ActionPlanOverBudget | null
}

/**
 * One action's slot on the mirror: enough for the canvas to label queued and
 * finished actions ("waiting" / "done · 2m 15s") and for the inspector to find
 * every run an action took part in — no HTTP round trip.
 */
export interface WorkflowRunStatusAction {
  actionId: string
  status: WorkflowRunActionStatusValue
  startedAt: string | null
  completedAt: string | null
}

export type WorkflowProgressStage =
  'starting' | 'planning' | 'executing' | 'generating' | 'reviewing' | 'recovering' | 'finalizing'

/** Bounded execution activity, independent of lease renewals and the diagnostic trace. */
export interface WorkflowRunProgress {
  stage: WorkflowProgressStage
  operation: string
  lastActivityAt: string
  actionId: string | null
  executorModel: string | null
  activeUnitCount: number
  queuedUnitCount: number
  /** Known outputs still executing, settling or awaiting review. */
  pendingOutputCount: number
  /** Concurrent sessions; step counters belong to one unit/take, never the whole run. */
  units: Array<{
    unitIndex: number
    take: number
    stage: WorkflowProgressStage
    operation: string
    stepsCompleted: number
    stepLimit: number | null
  }>
  recentActivity: Array<{ at: string; operation: string }>
}

/** A non-blocking caveat. Warnings never change lifecycle status or clear errors. */
export interface WorkflowWarning {
  code: string
  message: string
}

export interface WorkflowRunWarning extends WorkflowWarning {
  /** Null for a warning about the whole run, such as an applied budget default. */
  actionId: string | null
}

/** Includes warnings from earlier attempts, in Action order. Legacy records have none. */
export function workflowRunWarnings(
  actions: Pick<WorkflowRunActionRecord, 'actionId' | 'warnings'>[],
  runWarnings: WorkflowWarning[] = [],
): WorkflowRunWarning[] {
  return [
    ...runWarnings.map(warning => ({ ...warning, actionId: null })),
    ...actions.flatMap(action => (action.warnings ?? []).map(warning => ({ ...warning, actionId: action.actionId }))),
  ]
}

/** Small read model shared by the run inspector and compact API/MCP polls. */
export interface WorkflowRunStatusSummary {
  /** Optional for older servers and persisted records. Empty means no reported caveats. */
  warnings?: WorkflowRunWarning[]
  runId: string
  status: WorkflowRunStatusValue
  phase: string | null
  progress: WorkflowRunProgress | null
  currentAction: { id: string; name: string; index: number } | null
  actions: { completed: number; pending: number; total: number }
  outputs: {
    delivered: number
    accepted: number
    rejected: number
    failed: number
    /** Units the agent ended on purpose because the inputs need to change (see WorkflowRunStatusValue). */
    needsRevision: number
    pending: number
    /** Sum of plans derived so far, not a required-shot completeness guarantee. */
    planned: number
    plannedActions: number
  }
  olives: { budget: number | null; generation: number; agent: number }
  errorMessage: string | null
  /** A failed run's named cause, for code; null otherwise. Optional for older servers. */
  errorCode?: WorkflowRunErrorCode | null
  /** Which engine drives the run. Optional for older servers. */
  engine?: 'fable' | 'harness'
  /** True for a script build (Workflow Beta), which authors and tests the kit and generates no media. Absent on other runs. */
  compile?: boolean
  /**
   * Present only on a build that chains into an execution run: null until the handoff, then the
   * execution run's id — read that run for media results. Absent on build-only requests and on runs.
   */
  thenRunId?: string | null
  /** Compiles and promotions: how the kit tests went, with the failed output tail; absent on other runs and before verification. */
  kitVerification?: WorkflowRunKitVerification
  /** The kit's run artifacts copied into the project's files (`runs/<runId>/artifacts/`); absent when the kit declares none. */
  artifacts?: WorkflowRunArtifacts
}

/** Lightweight backend-owned mirror for live canvas progress. Keyed by run ID. */
export interface WorkflowRunStatus {
  /** Optional for older servers and persisted records. Empty means no reported caveats. */
  warnings?: WorkflowRunWarning[]
  runId: string
  projectId: string
  sceneId: string
  status: WorkflowRunStatusValue
  currentActionId: string | null
  currentActionIndex: number
  actionCount: number
  /** Run start, ISO; null until the harness claims the run. Optional: statuses mirrored before this field was added lack it. */
  startedAt?: string | null
  /** Every action in the run, in run order. Optional for the same reason as `startedAt`. */
  actions?: WorkflowRunStatusAction[]
  /**
   * The bins this run's Actions read or write, from its definition snapshot
   * (what the run executes, not the live canvas). The canvas keeps them from
   * being deleted while the run is live, even if an Action is rewired
   * mid-run. Absent on build runs, which touch no bins.
   */
  binIds?: string[]
  completedItemCount: number
  totalItemCount: number
  failedItemCount: number
  generationCostOlives: number
  agentCostOlives: number
  oliveBudget: number | null
  /** Present while a plan awaits approval (and kept for the run's duration once approved). */
  plan: WorkflowRunStatusPlan | null
  /** What the harness is doing right now ("unit 3/7 · waiting on generations"); null when idle/terminal. */
  phase: string | null
  errorMessage: string | null
  updatedAt: string
  /** Which door started the run. Optional: statuses mirrored before this field was added lack it. */
  origin?: WorkflowRunOrigin
  /** Set when the run is driven by Workflow Beta (one autonomous session); absent for the per-Action harness and on older mirrors. */
  engine?: WorkflowRunEngineValue
  /**
   * Set on a Workflow Beta compile run: it builds the workflow's scripts and executes no Action, so its
   * per-action slots track the build, never an Action's outcome. The run it chains into carries the outcomes.
   */
  build?: true
  /** Run creation, ISO — the stable order between two live runs on one canvas (`updatedAt` flips every sync). */
  createdAt?: string
  /** Who started the run. On the mirror so every collaborator's canvas can say whose run it is without HTTP. */
  startedBy?: WorkflowRunPerson | null
  /** The workspace whose olives this run spends: the starter's billing organization, whoever presses Go. */
  payer?: WorkflowRunPayer | null
  /** Who pressed Go on this plan, or authorized the run's budget when the plan auto-approved. */
  approvedBy?: WorkflowRunPerson | null
  cancelledBy?: WorkflowRunPerson | null
}

/** A person named on a run's mirror: the starter, the approver, the canceller. */
export interface WorkflowRunPerson {
  id: string
  /** The profile alias at mirror time. */
  name: string
}

export interface WorkflowRunPayer {
  organizationId: string
  name: string
}

/**
 * `workflow_run_actions.review`: the gate decision for one action's plan.
 * Approval binds to `planHash`; `approvedBy` is the person who pressed Go
 * (absent on auto-approved plans and on reviews written before it existed).
 */
export interface WorkflowRunActionReview {
  approved: boolean
  autoApproved?: boolean
  approvedAt: string
  planHash: string
  approvedBy?: string
}

export type WorkflowRunOrigin = 'ui' | 'chat' | 'mcp' | 'api'

/**
 * Who chose the run's olive rail. `explicit`: the caller passed a budget (or,
 * for canvas runs, a Go on the approval card arms it). `auto`: the run was
 * created without one and the harness arms the rail at each action's gate
 * with leeway over that action's estimate.
 */
export type WorkflowRunBudgetSource = 'explicit' | 'auto'

/**
 * What happens when spend crosses the budget. `strict`: canvas runs park for
 * a Go, API runs fail at the gate with the estimate. `soft`: runs driven over
 * MCP or chat, and canvas runs with run-level budget consent, keep going. The
 * budget is a marker `get_run` reports spend against. A trace notes crossings;
 * the step cap and repeated-error guard bound a runaway unit instead. Derived from the run's
 * origin and durable consent (workflows/runBudget.ts), never stored.
 */
export type WorkflowRunBudgetPolicy = 'strict' | 'soft'

/**
 * One variable value pinned for a single run. The value replaces the canvas
 * variable's value in every Action that reads it, for this run only — the
 * canvas node is never written, so concurrent runs of the same instance can
 * carry different values.
 */
export interface WorkflowRunVariable {
  /** The canvas variable node id. */
  id: string
  /** The variable's name at the time the run started. */
  name: string
  value: string
}

/**
 * One input bin's contents pinned for a single run: these project assets
 * stand in for the bin's canvas members in every Action that reads it. The
 * bin itself is never written.
 */
export interface WorkflowRunBinInput {
  /** Internal snapshots of Files in ordinary bins retain their existing live-note behavior. */
  includeCanvasNotes?: boolean
  /** The canvas bin id. */
  id: string
  /** The bin's name at the time the run started. */
  name: string
  /** Project asset ids, in the order the Action should read them. */
  assetIds: string[]
}

/**
 * A build's write-back of its verified kit to the saved workflow its placed copy is linked to
 * (`retainSavedKit.ts`). A hidden run copy always writes back; a visible copy only when its
 * promotion was started with `retainToSaved: true`, because a visible copy is where kit authors
 * publish test builds and the saved workflow is what clients run.
 */
export interface WorkflowSavedKitRetention {
  /** The promotion was started with `retainToSaved: true`. */
  requested: boolean
  /**
   * When the outcome was recorded. Absent while only the request or the attempt is: the outcome lags
   * the run's terminal status.
   */
  at?: string
  savedWorkflowId?: string
  /** The kind of placed copy the build ran on, once it was read. */
  copy?: 'hidden' | 'visible'
  /** On a write: the saved workflow's version before and after it. An attempt has only `fromVersion`. */
  fromVersion?: number
  toVersion?: number
  /** On a write or an attempt: the bundle written onto the saved workflow. */
  toolBundle?: Pick<WorkflowToolBundle, 'id' | 'sha256'>
  /**
   * On an attempt: the bundles earlier attempts tried to write, oldest first (at most seven). A twin
   * replaying the same handoff, or a retry after an error, rebinds its own bundle (ids are random),
   * and any of those writes may be the one that landed, so restore checks `toolBundle` and these.
   */
  attemptedToolBundles?: Array<Pick<WorkflowToolBundle, 'id' | 'sha256'>>
  /**
   * On a write or an attempt: the saved workflow's bundle before it, null when it had none. Bundles
   * are immutable (ADR-0009), so this is what `restore_saved_workflow_kit` commits back. The attempt
   * lands before the saved workflow is written and survives a later skip, so a write whose outcome
   * was lost still restores while the saved workflow carries the attempted `toolBundle`.
   */
  previousToolBundle?: WorkflowToolBundle | null
  /** Why nothing was written. */
  skipped?: WorkflowSavedKitRetentionSkip
}

/**
 * Why a build did not write the saved workflow: `not-linked` (no saved workflow, or it is gone),
 * `not-verified` (no passing published kit), `copy-mismatch` (the copy moved or changed workspace),
 * `visible-copy` (a visible copy without `retainToSaved: true`), `version-moved` (saved since the
 * run started), `no-access`, `definition-differs` (the copy's Actions no longer match the saved
 * ones), `not-portable` (the kit could not be rebound), `stale-kit`, `save-raced` (a save landed
 * during the write), `error`.
 */
export type WorkflowSavedKitRetentionSkip =
  | 'not-linked'
  | 'not-verified'
  | 'copy-mismatch'
  | 'visible-copy'
  | 'version-moved'
  | 'no-access'
  | 'definition-differs'
  | 'not-portable'
  | 'stale-kit'
  | 'save-raced'
  | 'error'

/**
 * How the caller asked the run to be driven; rides beside the pinned inputs
 * in `workflow_runs.input` so a run option needs no column of its own.
 */
export interface WorkflowRunOptions {
  /** Host workspace affinity; absent on legacy and portable Daytona runs. */
  workspaceRoot?: string
  candidate?: WorkflowKitCandidateIdentity
  /** Engine-owned: whether this build may write its kit to the saved workflow its copy is linked to. */
  retainSaved?: WorkflowSavedKitRetention
  consumedCandidate?: WorkflowKitCandidateIdentity & { applied: string[] }
  candidateBaseline?: Record<string, string>
  /** Workflow Beta only: author and dry-test kit scripts without generating media. */
  compile?: boolean
  /**
   * The run's Instructions are frozen from the saved workflow's carried files
   * (a saved-workflow run copy; see `CreateCanvasWorkflowRunInput.instructionsFiles`):
   * the agent reads the snapshot's text, never the project's file at the same
   * fixed kit path, which an older version or a sibling run may hold. Absence is
   * frozen too: a path the saved version carries no file for reads as missing.
   * A restart keeps the snapshot except where `instructionsBaseline` says a file
   * changed.
   */
  frozenInstructions?: boolean
  /**
   * A visible run copy's project files as the run last read them: the content
   * key (`instructionsContentKey`) per Instructions path, null where the project
   * had none; recorded at run start. A restart (a classic resume or rehearsal
   * Go; Workflow Beta runs do not restart) takes the project's file for a path
   * whose content no longer matches — someone edited the copy since — keeps the
   * frozen snapshot everywhere else, and writes the keys it read back as the
   * next baseline in the same compare-and-set as the snapshot, so reverting an
   * adopted edit counts as an edit too. A lock toggle or an unchanged save never
   * brings the older bytes back. Absent on hidden copies: nobody edits them.
   */
  instructionsBaseline?: Record<string, string | null>
  /** Original JSON run request, started only after the compiled kit has been saved and this run completes. */
  thenRun?: Record<string, unknown>
  thenRunId?: string
  /**
   * Engine-owned, stamped on the run an automatic compile chained into: the compile that rebuilt the
   * scripts first and its clock, so the run can show the build as its first step and time it.
   */
  compiledBy?: WorkflowRunCompileStep
  /** Engine-owned durable compile checkpoints, never caller-authored. */
  chain?: { status: 'pending' | 'handoff' | 'done'; key: string }
  compileBaseline?: {
    ownership?: Record<string, string[]>
    freshness?: KitFreshness
    /** Only files owned by originally fresh Actions; missing files have an empty hash. */
    hashes: Record<string, string>
    sha256?: string
  }
  compilePhase?: 'verified' | 'published'
  /**
   * Written with `compilePhase: 'published'` when the kit's head swap landed but its canvas pin did not
   * follow: the durable record of a mirror failure (the run warning beside it is best effort). Operator copy.
   */
  compileMirror?: { error: string; at: string }
  compileVerdict?: { failure?: string }
  /** Engine-stamped clock of the compile's selftest, so the run can show the tests as a step of their own. */
  compileTests?: { startedAt: string; completedAt?: string; failed?: boolean }
  verifiedKit?: WorkflowToolBundle
  compilePublication?: { predecessorVersion: string | null; epoch: string }
  testsPassedByRunId?: string
  /** Engine-recorded verification environment, never trusted from uploaded kit metadata. */
  kitVerification?: WorkflowRunKitVerification
  /** Engine-measured wall clock of a promotion's stages; written at publication or failure, absent on other runs. */
  promotionTimings?: WorkflowPromotionTimings
  /** Where and when the Workflow Beta runner first admitted this run (queue wait behind the per-machine cap). */
  admission?: WorkflowRunAdmission
  /** Engine-authored totals and findings; the full evidence lives in the project post-mortem file. */
  postmortem?: WorkflowRunPostmortem
  /** The kit-declared run artifacts (kit.json `artifacts`) copied into the project's files at run end; absent when the kit declares none. */
  artifacts?: WorkflowRunArtifacts
  /**
   * Why the engine failed the run, for operators: written by the Workflow Beta runner beside the user-facing
   * `errorMessage` when an internal error ends the run, so a generic "unexpected error" is diagnosable from the
   * row (`get_run`, Backstage) without the machine's log buffer. Operator copy, never rendered to users.
   */
  failure?: WorkflowRunFailure
  /**
   * Rehearsal (ADR-0008 amendment): every Action parks for a Go before its
   * plan executes, never auto-approved, and its Instructions are re-read from
   * the project when it proceeds, so the operator can fix the method between
   * Actions. An authoring mode, not the default.
   */
  rehearsal?: boolean
  /**
   * Ignored since 2026-09-11: how a sandbox session reaches Martini is the deployment's `WORKFLOW_CLI_MODE`
   * (`cli-first`, the default, or the `mcp` rollback), resolved at session start; a stored per-run preference
   * never overrides it. Kept only so rows written by the earlier per-run override still parse.
   */
  cliMode?: 'complement' | 'cli-first'
  /**
   * Which engine drives the run: the per-Action harness (default) or the Workflow Beta engine — one autonomous
   * session for the whole workflow. `'claude-code'` is the pre-rename spelling of the same engine: read for rows
   * written before 2026-09-09, never written.
   */
  engine?: WorkflowRunEngineValue
  /** Engine session id, persisted by the Workflow Beta runner so a recovered run can resume its transcript. */
  claudeCodeSessionId?: string
  /** The step agent's model and speed, fixed at the first launch; a recovered run reuses it rather than re-reading kit.json. */
  claudeCodeAgent?: { model: string; speed: 'standard' | 'fast' }
  /**
   * The kit's named-generate-family opt-in, fixed at the first launch while WORKFLOW_GENERATE_FAMILIES is on; a
   * recovered run reuses it rather than re-reading kit.json.
   */
  generateFamiliesKitOptIn?: boolean
  /**
   * Written by the Workflow Beta runner when a backend shutdown hands the run back mid-session: when, which tool
   * was executing, and how far into its turn the agent was. The owner that relaunches the agent reads it for the
   * `workflow_agent_relaunched` event and clears it; a recovered run without one lost its owner without a handback.
   */
  agentInterrupted?: WorkflowAgentInterruption
  /**
   * The container agent this run launched and may still be running: what a later owner needs to attach to it
   * after a deploy handed the run over (WORKFLOW_CONTAINER_AGENT_ADOPT). Written at launch, cursor advanced with
   * the usage flush, removed when the agent exits.
   */
  containerAgent?: WorkflowContainerAgentLaunch
  /** Engine-owned Actions, Notes and Run report regions, shown in the workflow panel. */
  runLog?: string
  /** Canvas log written by historical runs. */
  runLogNoteId?: string
}

/**
 * How a compile or promotion's kit tests went, as the engine ran them in the protected verification
 * view. Counts are the enforcement evidence; `blamed` and `outputTail` are for the author reading a
 * failure without the sandbox, which is torn down with the run.
 */
export interface WorkflowRunKitVerification {
  at: string
  runtimeContract: string | null
  formatVersion: 1
  snapshot: string | null
  host: 'local' | 'daytona'
  backendRevision: string | null
  cliVersion: string | null
  passed: number
  failed: number
  /** The test command's exit status; null when it was killed at its timeout. Absent on rows written before it was recorded. */
  exitCode?: number | null
  /** The test command ran past its timeout and was killed. */
  timedOut?: boolean
  /** Wall clock of the test command, ms. */
  durationMs?: number
  /** Tools-relative files the test command named on `test failed: <path>` lines; absent on rows written before it was recorded. */
  blamed?: string[]
  /**
   * The last part of the failed test command's stdout+stderr, bounded (see `KIT_TEST_OUTPUT_TAIL_CHARS`),
   * with a leading `…[N chars omitted]` marker when cut. Only on a failure; authoring-agent copy — file
   * paths and stack traces are expected — never rendered as end-user UI.
   */
  outputTail?: string
  /**
   * The project file holding the failed test command's full stdout+stderr (`runs/<runId>/kit-tests.log`,
   * up to 1 MiB with a truncation marker). Only on a failure, and only once the file was written.
   */
  logPath?: string
}

export type WorkflowRunArtifactSkipReason =
  | 'path'
  | 'not_text'
  | 'binary'
  | 'too_large'
  | 'file_limit'
  | 'total_limit'
  | 'project_near_limit'
  | 'timed_out'
  /** The write was still in flight when the budget ran out; the file may still land in the project. */
  | 'write_unsettled'
  | 'write_failed'

/**
 * What the engine kept of a kit's declared run artifacts at run end. `kept` paths are relative to
 * the run's scratch and to `dir`, the project file folder they were copied to; `skipped` holds at
 * most 20 entries and `skippedMore` counts the rest.
 */
export interface WorkflowRunArtifacts {
  dir: string
  kept: Array<{ path: string; bytes: number }>
  skipped: Array<{ path: string; reason: WorkflowRunArtifactSkipReason }>
  skippedMore?: number
  /** The scratch could not be listed, so nothing was copied. */
  error?: 'list_failed'
}

/**
 * Where a promotion's wall clock went, in ms. Each stage is set when it ends, so a failed run
 * carries the stages it reached; `totalMs` runs from the claim to publication or failure.
 */
export interface WorkflowPromotionTimings {
  /**
   * How the sandbox came up; `host` for the host-mode environment. `prewarmed` and `pooled` appear
   * only on promotions recorded before the run prewarm and the standing sandbox pool were removed.
   */
  boot?: 'prewarmed' | 'pooled' | 'cold' | 'resumed' | 'host'
  /** Opening the sandbox, including the tools library materialization. */
  sandboxOpenMs?: number
  /** Inside `sandboxOpenMs`: waiting for the environment lease. Daytona only. */
  leaseMs?: number
  /** Inside `sandboxOpenMs`: the sandbox create-or-start and workspace setup. Daytona only. */
  sandboxBootMs?: number
  /** Inside `sandboxOpenMs`: materializing tools/ from object storage. Daytona only. */
  toolsSyncMs?: number
  /** Reading the candidate, the baseline fence and the kit hashes (verifying the kit). */
  baselineMs?: number
  /** Writing the protected verification view and running the kit's tests.command. */
  testsMs?: number
  /** Freezing the verified kit into an immutable bundle. */
  freezeMs?: number
  /** Publishing the head and mirroring the canvas pin. */
  publishMs?: number
  totalMs?: number
}

/** The engine's own account of a failed Workflow Beta run: a stable code, the internal message, and when. */
export interface WorkflowRunFailure {
  /** A guard's stable identifier (`COMPILE_BASELINE_MISSING`, …) or the thrown error's class name. */
  code: string
  /** The internal error message as thrown — operator copy. */
  message: string
  /** ISO timestamp of the failure. */
  at: string
}

/**
 * The engine failure codes (`WorkflowRunFailure.code`) published to callers, by public name. Every
 * other failure stays unnamed: its code is a class name or a guard id, operator detail.
 */
export const PUBLIC_WORKFLOW_RUN_FAILURE_CODES = {
  /** The model provider kept failing or could not be reached: not the workflow; running again is the fix. */
  MODEL_PROVIDER_UNAVAILABLE: 'model_provider_unavailable',
} as const

/** Why a failed run failed, for code (the API's `errorCode`, the Workflows page's badge). */
export type WorkflowRunErrorCode =
  (typeof PUBLIC_WORKFLOW_RUN_FAILURE_CODES)[keyof typeof PUBLIC_WORKFLOW_RUN_FAILURE_CODES]

/**
 * The public code of a failed run's cause; null for any other status and for a cause without one.
 * Reads only the failure's code, so a run listing that narrows its input still answers.
 */
export function workflowRunErrorCode(run: {
  status: WorkflowRunStatusValue
  input: { options?: { failure?: Pick<WorkflowRunFailure, 'code'> } }
}): WorkflowRunErrorCode | null {
  const code = run.status === 'failed' ? run.input.options?.failure?.code : undefined
  return code && Object.hasOwn(PUBLIC_WORKFLOW_RUN_FAILURE_CODES, code)
    ? PUBLIC_WORKFLOW_RUN_FAILURE_CODES[code as keyof typeof PUBLIC_WORKFLOW_RUN_FAILURE_CODES]
    : null
}

/**
 * How a Workflow Beta run was admitted onto its first machine: written once, with the pending → running
 * transition, so the completion event and the time report can say where the run executed and how long it sat
 * behind the per-machine run cap. Re-claims after a handback keep the first admission.
 */
export interface WorkflowRunAdmission {
  /** The machine (Fly machine id, `SERVER_INSTANCE_ID`) whose runner admitted the run. */
  machine: string
  /** Milliseconds between the run's creation and its admission: the queue behind the run cap plus the claim. */
  queueWaitMs: number
  /** The admitting machine's lane load right after this run took its slot, and the lane's cap. */
  laneActive: number
  laneCap: number
  /** ISO timestamp of the admission. */
  at: string
}

/** The moment a shutdown handed a Workflow Beta run back: what the interrupted agent turn was doing. */
export interface WorkflowAgentInterruption {
  /** Epoch ms of the handback. */
  at: number
  /** The tool executing, or the last one invoked, when the owner stopped; absent before the first tool. */
  tool?: string
  /** How far into its turn the agent was, in ms — the work the relaunch redoes. */
  turnMs: number
  /**
   * Written while no session was live (the sandbox was opening, or the run was past its session in
   * settle or delivery): the run is a deploy handback, but no model turn was lost, and recovery must
   * not read it as evidence that a session ran.
   */
  outsideSession?: true
  /**
   * Epoch ms of the claim that relaunched this handback. The claim stamps it so no machine's 30 s
   * sweep lists the row again: one durable relaunch per deploy handback, across replicas. A later
   * shutdown writes a fresh marker without it.
   */
  sweptAt?: number
}

/** A running container agent's launch record: the environment session and command, and how far its output was read. */
export interface WorkflowContainerAgentLaunch {
  v: 1
  /** The run-token owner minted for this launch; a later owner accepts it beside its own. */
  launchOwner: string
  sessionId: string
  cmdId: string
  /** Epoch ms. */
  launchedAt: number
  /** The last stream record dispatched in order; an attach drops records at or below it. */
  seq: number
}

/** The automatic compile that ran before a Workflow Beta run: which run built the scripts, and when. */
export interface WorkflowRunCompileStep {
  runId: string
  /** The compile run's own clock, ISO; `startedAt` is null only for a compile that never claimed a worker. */
  startedAt: string | null
  completedAt: string | null
}

export interface WorkflowPostmortemFinding {
  scope: 'engine' | 'workflow' | 'provider'
  category: string
  summary: string
  evidence: {
    actionId?: string
    step?: string
    spans: string[]
    ms?: number
    log?: string
    assetIds: string[]
  }
  suggestedFix?: string
  confidence: 'low' | 'medium' | 'high'
  source: 'engine' | 'agent' | 'script'
}

export interface WorkflowRunPostmortem {
  /** Manifest judge calls are agent usage, never session or decision turns. */
  judgeCalls?: Array<{
    actionId: string
    model: string
    tier: 'fast' | 'careful'
    items: string[]
    ms: number
    inputTokens: number
    outputTokens: number
    inputCacheReadTokens?: number
    inputCacheWriteTokens?: number
    olives: number
    usd: number
    error?: string
  }>

  repairs?: Array<{
    kind: 'script' | 'manifest' | 'bible' | 'other'
    path: string
    bytes: number
    actionId: string
    action: string
    published?: boolean
    reason?: string
  }>
  totals: {
    wallMs: number
    modelMs: number
    toolMs: number
    stepMs: number
    providerWaitMs: number
    idleMs: number
    turns: number
    /** Absent on reports recorded before fast-path turn accounting. */
    decisionTurns?: number
    otherTurns?: number
    apiMs: number | null
    sdkRoundTrips: number | null
    generationOlives: number
    agentOlives: number
  }
  findings: WorkflowPostmortemFinding[]
}

/** The stored engine marker: the client-facing `'fable'`, or the pre-rename `'claude-code'` still on older rows. */
export type WorkflowRunEngineValue = 'fable' | 'claude-code'

/** One definition of "this run is on Workflow Beta", shared by the backend doors and the panel. */
export function isWorkflowBetaEngine(engine: string | null | undefined): engine is WorkflowRunEngineValue {
  return engine === 'fable' || engine === 'claude-code'
}

/** Pinned run inputs and server-owned authorization (`workflow_runs.input`). */
export interface WorkflowRunInput {
  /** Frozen selection and semantic request identity, retained across compile handoffs and retries. */
  savedRevision?: { workflowId: string; revisionId: string; versionLabel: string }
  /**
   * Explicit source association retained by a historical import, NOT an execution pin.
   * The imported run is associated with this saved version; its exact executed definition is unknown.
   * Original source identifiers/name are provenance only. Never inferred from timestamps or version counters.
   */
  importedRevision?: {
    workflowId: string
    revisionId: string
    versionLabel: string
    sourceWorkflowId: string
    sourceName: string
  }
  savedRunRequestHash?: string
  /** Server-authored caveats recorded at creation, retained across builds and retries. */
  warnings?: WorkflowWarning[]
  /** Server-written by Go: budget consent for this entire run, retained across resumes and replans. */
  budgetApproval?: { approvedAt: string; approvedBy?: string }
  fileRequestIdentity?: string
  variables?: WorkflowRunVariable[]
  bins?: WorkflowRunBinInput[]
  files?: JsonFileSnapshot[]
  options?: WorkflowRunOptions
}

/** The inputs a workflow accepts, read from its Actions. */
export interface WorkflowInputs {
  /** Variables the Actions read, with their current canvas values. */
  variables: Array<{ id: string; name: string; value: string }>
  /**
   * Bins the Actions read but no Action writes. assetCount counts runnable assets, notes and variables
   * throughout the bin's subtree on this canvas. It describes contents, not whether a run is allowed.
   */
  bins: Array<{ id: string; name: string; assetCount: number; fileInput?: FileInputContract }>
}

export interface WorkflowRunRecord {
  id: string
  projectId: string
  sceneId: string
  createdBy: string | null
  /** Who cancelled the run, when a person did (anyone with project write access may). Absent on records built before the column. */
  cancelledBy?: string | null
  /** Which door started the run: the canvas (`ui`), the project assistant (`chat`), an external agent (`mcp`), or the public API (`api`). */
  origin: WorkflowRunOrigin
  budgetSource: WorkflowRunBudgetSource
  status: WorkflowRunStatusValue
  /** The placed workflow (Yjs `workflows` record / `workflows.id`) this run executed; null for runs older than placed rows. */
  workflowId: string | null
  /** The saved workflow the placed copy was linked to when the run started, and its version then. */
  savedWorkflowId: string | null
  savedWorkflowVersion: number | null
  definitionSnapshot: WorkflowDefinition
  input: WorkflowRunInput
  currentActionIndex: number
  oliveBudget: number | null
  generationCostOlives: number
  agentCostOlives: number
  errorMessage: string | null
  /**
   * The harness's live "what it is doing" line ("unit 3/7 · waiting on
   * generations"), written per phase change while the run executes. Display
   * copy, meaningful only while the run is `running`.
   */
  phase: string | null
  /** Absent on older records; lifecycle status remains authoritative when work stops. */
  progress?: WorkflowRunProgress | null
  createdAt: string
  /**
   * The row's `updated_at`, which a trigger bumps on every UPDATE (lease renewals included): the
   * fence for a read-modify-write of the row (`updateRunIfUnchanged`). Absent on records built
   * without the column.
   */
  updatedAt?: string
  startedAt: string | null
  completedAt: string | null
}

/**
 * One line of the per-action diagnostics trail on `workflow_run_actions.trace`:
 * the plan pass, every tool call (with wall time and serialized result size —
 * the context-cost driver), per-unit outcomes with token totals, and sweeps.
 * Written by the harness; read by the canvas inspector and the public API.
 */
export interface ActionTraceEvent {
  /** Wrapper owner; pre-action means its invocation started no Action. */
  actionId?: string
  integrity?: 'changed'
  admittedSha256?: string
  afterSha256?: string | null
  /** Milliseconds since this action's execution began (gate and pause waits excluded). */
  t: number
  /** Wall clock of the event (epoch ms), so a run timeline can place it; spans start at `at - ms`. */
  at?: number
  /** Stable span id <actionIndex>.<seq>; findings reference this rather than copying spans. */
  id?: string
  /** The unit (0-based) and take this event belongs to; absent for action-level events. */
  unit?: number
  take?: number
  /** Turn classification is measured once; CLI judgements may mark it later. */
  decisionPoint?: boolean
  firstTool?: string
  kind: 'plan' | 'tool' | 'unit' | 'revision' | 'settle' | 'judge' | 'sweep' | 'note' | 'turn'
  name?: string
  ms?: number
  chars?: number
  outputAssetIds?: string[]
  inputTokens?: number
  outputTokens?: number
  /** Model steps the session took (unit events from the real executor). */
  steps?: number
  /** Input tokens the provider served from its prompt cache, when it reported them. */
  cacheReadTokens?: number
  /**
   * tool: how much of `ms` is already counted by the spans nested inside this one
   * (generation waits, tool calls). Subtracted from the span's own total so a
   * compiled step phase — or a `martini` call from the sandbox — and the waits it
   * contains are never both charged.
   */
  nestedMs?: number
  /** settle: when the first output landed, ms after the wait began (the rest is the provider's batch spread). */
  firstMs?: number
  /** settle: board reads the wait took. */
  polls?: number
  note?: string
  /** note: the producer's own verdict, so a label's wording can never colour a span. */
  status?: 'error'
  /**
   * tool · a `martini` CLI call: how the engine answered when it was neither done nor pending.
   * `note: 'error'` still counts every one of these in the rollup; `outcome` is what lets a
   * reader tell a refusal (the engine will not do this) from an error or a cancelled call.
   */
  outcome?: 'refused' | 'error' | 'cancelled'
  /** Call summary for computer tools (the exec command / written path). */
  detail?: string
  /** note · trace truncated: spans the stored window dropped. Rollup totals still count them. */
  dropped?: number
}

export interface WorkflowRunActionRecord {
  /** Optional for older servers and persisted records. Empty means no reported caveats. */
  warnings?: WorkflowWarning[]
  id: string
  runId: string
  actionId: string
  actionIndex: number
  definition: Action
  agentId: string
  status: WorkflowRunActionStatusValue
  attempt: number
  plan: ActionPlan | null
  /** Diagnostics trail (plan pass, tool calls, unit outcomes); shape is backend-owned. */
  trace: unknown[] | null
  inputTokens: number
  outputTokens: number
  agentCostOlives: number
  review: Record<string, unknown> | null
  errorMessage: string | null
  startedAt: string | null
  completedAt: string | null
}

export interface WorkflowRunItemRecord {
  id: string
  runId: string
  runActionId: string
  actionId: string
  inputAssetIds: string[]
  inputPayload: Record<string, unknown> | null
  outputAssetId: string | null
  generationId: string | null
  replacesItemId: string | null
  status: WorkflowRunItemStatusValue
  attempt: number
  costOlives: number
  agentCostOlives: number
  feedback: string | null
  verdict: Record<string, unknown> | null
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
}

/**
 * The settled result of an item or Action, beside its lifecycle `status` (the
 * check-run status/conclusion split). `generated` for completed work, `failed`
 * for a broken one, `needs_revision` when the agent ended the unit on purpose
 * with flag_for_revision because the brief's inputs failed a precondition.
 */
export type WorkflowRunItemOutcome = 'generated' | 'failed' | 'needs_revision'

/** Written to `workflow_run_items.input_payload.needsRevision` when the agent ended the unit with flag_for_revision. */
export interface WorkflowRunRevisionRequest {
  /** The headline: what fails and what must change, a few sentences at most. */
  reason: string
  /** The agent's longer working-out, when it gave one; rendered collapsed. */
  detail?: string
  /** Ids or names of the variables, assets, or bins the agent says need changing; empty when it named none. */
  inputRefs: string[]
  /** Root ids of comment threads the agent left during the unit (add_comment), for the canvas link. */
  commentThreadIds: string[]
}

function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
}

/**
 * Defensive read of a `needs_revision` item's request. Two records exist: the
 * jsonb payload carries the flag_for_revision request (full reason, named
 * inputs, comment threads), or — when the agent only answered with the
 * `DECLINED:` marker — the error message is the reason (no named inputs).
 * Non-string refs and thread ids are dropped. Null for every other item.
 */
export function workflowRunRevisionRequestOf(
  item: Pick<WorkflowRunItemRecord, 'status' | 'inputPayload' | 'errorMessage'>,
): WorkflowRunRevisionRequest | null {
  if (item.status !== 'needs_revision') return null
  const raw = item.inputPayload?.needsRevision
  if (typeof raw !== 'object' || raw === null) {
    const reason = item.errorMessage?.trim()
    return reason ? { reason, inputRefs: [], commentThreadIds: [] } : null
  }
  const { reason, detail, inputRefs, commentThreadIds } = raw as Record<string, unknown>
  if (typeof reason !== 'string' || reason.trim() === '') return null
  return {
    reason,
    ...(typeof detail === 'string' && detail.trim() !== '' ? { detail } : {}),
    inputRefs: stringsOf(inputRefs),
    commentThreadIds: stringsOf(commentThreadIds),
  }
}

/** Items: completed → generated; needs_revision → needs_revision; failed → failed; anything else (still open) → null. */
export function workflowRunItemOutcome(item: Pick<WorkflowRunItemRecord, 'status'>): WorkflowRunItemOutcome | null {
  if (item.status === 'completed') return 'generated'
  if (item.status === 'needs_revision') return 'needs_revision'
  return item.status === 'failed' ? 'failed' : null
}

/** Actions: the same reading as items; anything still open → null. */
export function workflowRunActionOutcome(status: WorkflowRunActionStatusValue): WorkflowRunItemOutcome | null {
  if (status === 'completed') return 'generated'
  if (status === 'needs_revision') return 'needs_revision'
  return status === 'failed' ? 'failed' : null
}

// ---------------------------------------------------------------------------
// Workflows payloads. Built by the backend from `workflows` + `workflow_runs`
// so no page touches Yjs docs. Four readers, each sized for what it shows:
// GET /workflows/overview (the Workflows page: statistics per workflow, no
// runs), GET /workflows/runs (one workflow's runs, paged), GET
// /workflows/index (the canvas picker: headers and derived summaries) and
// GET /workflows/saved (MCP's full listing with copies and runs). The numbers
// the detail view shows over runs (average cost per settled run, success
// rate, average duration) are computed client-side from run rows; the
// overview's statistics are the same definitions summed by the backend.
// ---------------------------------------------------------------------------

export type WorkflowRefKind = 'bin' | 'variable' | 'note' | 'asset'
/** Saved input-bin defaults. Omitted bins use these contents; explicit overrides replace them. */
export interface SavedWorkflowBinDefaults {
  required: boolean
  defaultSource: 'saved_preset' | 'saved_files' | null
}

export interface WorkflowRef extends Partial<SavedWorkflowBinDefaults> {
  kind: WorkflowRefKind
  name: string
}

/** Run states as the page sees them — the run's own status union. */
export type WorkflowRunState = WorkflowRunStatusValue

export interface WorkflowStepSummary {
  name: string
  agent: string
}

export interface WorkflowRunStep {
  name: string
  state: WorkflowRunActionStatusValue
  durationMs: number | null
  /** When the step began; the page clocks elapsed from this while it is running. */
  startedAt: string | null
}

export interface WorkflowRunSummary {
  id: string
  /** Exact revision frozen at execution; absent on unpinned runs, including imported history. */
  savedRevision?: WorkflowRunInput['savedRevision']
  /** Historical source association only; savedRevision takes precedence for run attribution. */
  importedRevision?: WorkflowRunInput['importedRevision']
  /** The saved workflow the run's placed copy was linked to, when it was. */
  savedWorkflowId: string | null
  /** Current saved workflow name; null when the saved row is unavailable. */
  savedWorkflowName: string | null
  /** Retired grouping label kept for compatibility; read-only and always null for new data. */
  versionOf: string | null
  /** Name captured when this version ran. */
  name: string
  projectId: string
  projectName: string
  canvasId: string
  canvasName: string
  completedAt: string | null
  phase: string | null
  /** Run error truncated to 160 characters; null when absent. */
  errorSummary: string | null
  /** The placed workflow that ran. */
  workflowId: string
  state: WorkflowRunState
  /** When the run got a slot and began; a run still queued carries its creation time here, as the list's sort key. */
  startedAt: string
  /** When the run was asked for: the start of its queue wait. */
  createdAt: string
  /**
   * How long the run waited for a slot before it started; null while it is still queued.
   * Never part of `durationMs`, which starts at `startedAt`.
   */
  queueWaitMs: number | null
  /** Start to end, queue wait excluded. */
  durationMs: number | null
  /** Generation + agent olives. */
  costOlives: number
  costSplit: { generationOlives: number; agentOlives: number }
  trigger: WorkflowRunOrigin
  /** Set when the run was driven by Workflow Beta; absent for the per-Action harness. */
  engine?: WorkflowRunEngineValue
  /**
   * Set when the run built the workflow's scripts instead of generating media.
   * `chained` when the engine built them on its own ahead of the run that was
   * asked for (the `auto` kit policy); false for an explicit Compile.
   */
  compile?: { chained: boolean }
  /** Profile alias of who started the run; null when the account is gone. */
  startedBy: string | null
  /** Takes the run landed in its output bins. */
  outputs: number
  steps: WorkflowRunStep[]
  /** Backend-authored copy for failed runs. */
  note?: string
  /** A failed run's named cause, when it has one (`workflowRunErrorCode`). */
  errorCode?: WorkflowRunErrorCode
  /**
   * The run's delivered output on GET /workflows/runs: null when it has no visual output,
   * absent when it was not resolved in the listing's time budget (or on other reads).
   */
  output?: WorkflowRunOutput | null
}

/** A run's final output as a thumbnail shows it. */
export type WorkflowRunOutput = Omit<
  WorkflowOutputSummary,
  'runId' | 'savedWorkflowId' | 'savedWorkflowName' | 'completedAt'
>

/** One placed workflow (a copy on a canvas) with its runs. */
export interface PlacedWorkflowSummary {
  id: string
  kind: 'placed'
  /** The saved workflow it was placed from or last saved as, when linked. */
  sourceWorkflowId: string | null
  version: number | null
  name: string
  projectId: string
  projectName: string
  canvasId: string
  canvasName: string
  /** A run copy stamped for one API/MCP run; listed only under its saved workflow. */
  hidden: boolean
  placedAt: string
  runs: WorkflowRunSummary[]
}

export interface SavedWorkflowSummary {
  revisionId?: string | null
  versionLabel?: string
  releaseMode?: 'legacy' | 'managed'
  productionRevisionId?: string | null
  headRevisionId?: string | null
  headVersion?: number
  headVersionLabel?: string
  id: string
  kind: 'saved'
  name: string
  description: string
  /** Workspace folder name; null when unfiled. */
  folder: string | null
  /** Retired grouping label kept for compatibility; read-only and always null for new data. */
  versionOf: string | null
  /** Latest completed output URL, resolved only for the overview. */
  frameUrl: string | null
  /** Completion time and media type of this preview, independent of the saved row's creation date. */
  frameCompletedAt?: string
  frameMediaKind?: 'image' | 'video'
  /** Pixel size of the frame's asset when the board records it; absent when unknown. */
  frameWidth?: number
  frameHeight?: number
  version: number
  /** Content identity of the machinery (see backend/src/workflows/fingerprint.ts). */
  fingerprint: string
  steps: WorkflowStepSummary[]
  inputs: WorkflowRef[]
  outputs: WorkflowRef[]
  /** The doors it has been run through. */
  runsVia: WorkflowRunOrigin[]
  /** Agent skills the saved kit ships; absent when it ships none (or predates skill discovery). */
  skills?: WorkflowKitSkill[]
  /** Operator pin: every run of this workflow is driven by this engine (see SavedWorkflowRecord.enginePin). */
  enginePin?: WorkflowRunEnginePin | null
  /** Stable paging key for MCP listings: an edit or rename never moves it. */
  createdAt: string
  updatedAt: string
  /** Copies on canvases, hidden run copies included (their runs are the API runs). */
  placed: PlacedWorkflowSummary[]
}

export interface WorkflowsListResponse {
  saved: SavedWorkflowSummary[]
  /** Non-hidden placed workflows with no listed saved workflow (authored on a canvas, or whose saved workflow is gone). */
  placed: PlacedWorkflowSummary[]
}

/**
 * Run statistics for one placed copy, summed by the backend over every run
 * it has (`runStatsOf` in backend/src/workflows/savedWorkflows.ts, over the
 * same run summaries the detail view shows, receipts included). The
 * Workflows page's numbers are defined by these fields the way
 * `frontend/src/lib/workflowSummaries.ts` defines them over runs: settled =
 * completed, failed or needs revision, builds excluded; success rate =
 * completed / settled; average cost = settledCostOlives / settled; total
 * cost counts every run, builds included.
 */
export interface WorkflowRunStats {
  /** Every run, builds included. */
  runs: number
  /** Running, awaiting approval, or pending. */
  live: number
  /** The newest run still going, for the row's state word; null when none is. */
  liveRun: Pick<WorkflowRunSummary, 'state' | 'compile' | 'startedAt'> | null
  /** Ended with a verdict, builds excluded: the cohort of the success rate and the average cost. */
  settled: number
  /** Completed among the settled. */
  completed: number
  /** Olives over every run, builds included. */
  totalCostOlives: number
  /** Olives over the settled cohort. */
  settledCostOlives: number
  /** Newest start (creation, for a run not yet started); null with no runs. */
  lastRunAt: string | null
  /** The doors runs came through. */
  origins: WorkflowRunOrigin[]
}

/** A placed copy on the Workflows overview: its run statistics instead of its runs. */
export interface PlacedWorkflowOverview extends Omit<PlacedWorkflowSummary, 'runs'> {
  stats: WorkflowRunStats
}

/** A saved workflow on the Workflows overview: copies with statistics, and the statistics summed over them. */
export interface SavedWorkflowOverview extends Omit<SavedWorkflowSummary, 'placed' | 'frameUrl'> {
  /** Omitted while unresolved; null confirms no visual output; a URL is a usable cover. */
  frameUrl?: string | null
  /** Local review fixture's original head name; presentation only, never an execution selector. */
  sourceVersionName?: string
  placed: PlacedWorkflowOverview[]
  stats: WorkflowRunStats
}

/**
 * GET /workflows/overview — the Workflows page in one request: what the
 * overview shows, sized by the number of workflows rather than the number of
 * runs. Runs themselves come from GET /workflows/runs when one workflow is
 * opened.
 */
export interface WorkflowsOverviewResponse {
  orgId: string
  /** Over every run under the listed workflows and copies — not a page of them. */
  totals: { runs: number; live: number; costOlives: number }
  saved: SavedWorkflowOverview[]
  /** Non-hidden placed workflows with no listed saved workflow. */
  placed: PlacedWorkflowOverview[]
}

/** A completed run's final visual output, resolved from its project board. */
export interface WorkflowOutputSummary {
  runId: string
  savedWorkflowId: string | null
  savedWorkflowName: string | null
  /** The label of the version the run pinned ("1.2.0"); absent or null for a run that recorded none. */
  versionLabel?: string | null
  completedAt: string
  assetUrl: string
  mediaKind: 'video' | 'image'
  width?: number
  height?: number
  durationMs?: number
  canvasHref?: string
  /** A step output's place among its run's shots at that step, from 1; a final has none. */
  shot?: number
}

export interface WorkflowOutputsResponse {
  /** Incomplete candidate search or output resolution; available outputs are not authoritative. */
  partial?: boolean
  outputs: WorkflowOutputSummary[]
}

/** One step of a run as its expanded row shows it: GET /workflows/runs/:runId?steps=1 → `steps`. */
export interface WorkflowRunStepDetail {
  actionId: string
  /** Position in the run's definition, from 0. */
  index: number
  /** The action's name; `Step N` when it has none. */
  name: string
  state: WorkflowRunActionStatusValue
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  /** The step's items' generation cost plus the action's agent cost. */
  costOlives: number
  /** Retakes that replaced an item are not counted. */
  items: { total: number; completed: number; failed: number }
  /** Some output candidates could not be resolved. Keep available previews and allow retry; do not cache as complete. */
  partial?: boolean
  /** Up to 4 of the step's outputs, newest first. */
  outputs: WorkflowOutputSummary[]
  /** A failed step's error, or a needs-revision step's feedback. */
  error: string | null
}

/** One step across runs, matched by position and name. */
export interface WorkflowStepStats {
  /** Stable read-model key; legacy clients may still filter by name. */
  key?: string
  index?: number
  name: string
  /** Runs whose step started. */
  reached: number
  /** Runs whose step completed. */
  completed: number
  /** Failed runs whose failing step is this one. */
  failedHere: number
  /** Over completed steps with both stamps. */
  avgDurationMs: number | null
  /** Step cost per run that reached it. */
  avgCostOlives: number | null
  costOlives: number
  /** Up to 4 of the step's newest outputs. */
  outputs: WorkflowOutputSummary[]
  /** Image and video outputs the step landed over the sample; `outputs` is its newest few. */
  outputCount: number
}

/** GET /workflows/runs/steps: the steps of the newest runs in scope, in definition order. */
export interface WorkflowStepsSummary {
  /** Some output reads were unresolved. Available previews and step statistics are retained; retry to recover. */
  partial?: boolean
  steps: WorkflowStepStats[]
  /** Runs read: the newest in scope, up to 500. */
  sampleSize: number
  /** More runs matched than were read. */
  truncated: boolean
  /** Failed runs in the sample. */
  failedRuns: number
  /** Sum of the steps' cost: the base of each step's share. */
  totalCostOlives: number
}

/** One generation model across a scope's newest runs. */
export interface WorkflowModelSpend {
  model: string
  kind: 'image' | 'video' | 'audio' | 'other'
  /** Items whose output matched a generation of this model. */
  calls: number
  olives: number
}

/** One agent across a scope's newest runs: its actions’ recorded agent costs, including judge calls. */
export interface WorkflowAgentSpend {
  agentId: string
  /** Steps that started. */
  steps: number
  inputTokens: number
  outputTokens: number
  olives: number
}

/** GET /workflows/runs/spend: where a scope's newest runs spent, by model and by agent. */
export interface WorkflowSpendSummary {
  /** Olives desc. */
  models: WorkflowModelSpend[]
  /** Olives desc. */
  agents: WorkflowAgentSpend[]
  /** Items' generation cost, attributed or not: the base of each model's share. */
  generationOlives: number
  /** Actions' recorded agent cost: the base of each agent's share. */
  agentOlives: number
  /** Generation cost on items with no generation record. */
  unattributedOlives: number
  /** Runs read: the newest in scope, up to 500. */
  sampleSize: number
  truncated: boolean
}

/**
 * GET /workflows/runs/summary. Today and all day buckets use UTC; the 14
 * buckets include today. Costs are attributed to the run's creation day,
 * completions/failures to completedAt. failed24h and olives7d are rolling
 * windows. Settled metrics are limited to runs created in the last 14 days.
 */
export interface WorkflowRunsSummary {
  runningNow: number
  failed24h: number
  completedToday: number
  olivesToday: number
  olives7d: number
  runsPerDay: Array<{ day: string; completed: number; failed: number; olives: number }>
}

/** Exact filtered counts with costs, durations and UTC buckets sampled from the newest 2,000 runs. */
export interface WorkflowRunsDashboard extends WorkflowRunsSummary {
  window: { since: string; until: string; bucket: 'hour' | 'day' }
  tiles: {
    runs: number
    /** Pending, awaiting approval, running or paused within this window. */
    running: number
    /** Completed + failed; cancelled and needs_revision are not settled. */
    settled: number
    completed: number
    failed: number
    successRate: number | null
    /** Generation + agent cost averaged over sampled runs; null when there are no runs. */
    avgCostOlives: number | null
    totalCostOlives: number
    /** The generation and agent parts of totalCostOlives. */
    generationCostOlives: number
    agentCostOlives: number
    /** Finished runs with valid startedAt/completedAt, including cancelled runs. */
    avgDurationMs: number | null
    medianDurationMs: number | null
  }
  /** At most eight series including Other. Only occupied buckets are returned, ascending by t. */
  series: Array<{
    /** Stable namespaced series identity; synthetic buckets use all, canvas or other. */
    key: string
    label: string
    points: Array<{
      t: string
      runs: number
      completed: number
      /** Settled delivery runs, including needs_revision and excluding compile runs. */
      settled?: number
      failed: number
      olives: number
      avgDurationMs: number | null
      /** Median of the bucket's finished-run durations; the chart draws this, not the average. */
      medianDurationMs: number | null
      /** Finished runs with valid timestamps behind avg/median. */
      timedRuns: number
    }>
  }>
  /** Number of newest matching runs used for costs, durations and series. Counts and successRate are exact. */
  sampleSize: number
  /** The sample hit 2,000 rows; cost/duration metrics and series may omit older matches. */
  truncated?: boolean
}

/** GET /workflows/runs sort keys; defaults to started descending. */
export type WorkflowRunsSortKey = 'started' | 'completed' | 'duration' | 'cost' | 'state' | 'workflow' | 'origin'

/** GET /workflows/runs — workspace, workflow or group runs, sorted and paged. */
export interface WorkflowRunsPage {
  runs: WorkflowRunSummary[]
  /**
   * Opaque base64url JSON; pass back unchanged with the same target, filters, sort and dir.
   * Stored keys: {sort, dir, value, createdAt, id, bucket?}; value is the stored sort value
   * (started = created_at, completed = completed_at, state = status, origin = origin).
   * Order: value in dir (nulls last), created_at DESC, id DESC. bucket is live/settled
   * when pinLive=1, so live runs precede settled runs without gaps across page boundaries.
   * Computed keys: {sort, dir, offset, filterHash}; sort the newest 2,000 filtered rows,
   * fetched in narrow pages and cached for 15 seconds per workspace/target/filter set.
   * duration = completed_at - started_at (nulls last); cost = generation + agent olives;
   * workflow = saved group/name, then saved version name (run name when unsaved).
   * Null on the last page. Restart paging when filters/sort change or the cache expires.
   */
  nextCursor: string | null
  /** Computed sorting hit the cap: sorted within the newest 2,000 matching runs. */
  truncated?: boolean
}

/**
 * A saved workflow as a chooser lists it: the row and what its definition
 * derives (steps, inputs, outputs, fingerprint, skills), without copies or
 * run history. Enough to show a list and place one on a canvas.
 */
export type SavedWorkflowIndexEntry = Omit<SavedWorkflowSummary, 'placed' | 'runsVia'>

/**
 * GET /workflows/index — every saved workflow in a workspace as an index
 * entry, most recently updated first. The canvas picker's request: it reads
 * headers and the derived-summary cache, never projects, copies or runs.
 */
export interface WorkflowsIndexResponse {
  orgId: string
  saved: SavedWorkflowIndexEntry[]
}

// ============================================
// Lineage (provenance / copyright / compliance trail)
// ============================================

/**
 * Mirrors the Postgres `generation_capability` enum (see 04_generations.sql).
 */
export type GenerationCapability =
  | 'generate-video'
  | 'extend-video'
  | 'edit-video'
  | 'generate-still'
  | 'upscale-image'
  | 'upscale-video'
  | 'generate-set-3d'
  | 'generate-audio'
  | 'inpaint-audio'

/**
 * Mirrors the Postgres `generation_status` enum (see 04_generations.sql).
 */
export type GenerationStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

/** How a reference asset was fed into a generation. */
export type LineageReferenceKind = 'startFrame' | 'endFrame' | 'styleRef' | 'character' | 'video' | 'element' | 'audio'

/**
 * How the parent generation was resolved.
 * `yjs-parent` = walked `asset.parentId` in the Yjs doc.
 * `input-videoAssetId` / `input-sourceAssetId` = read `generation.input` JSONB.
 * `root` = no parent (upload, prompt-only t2v, etc.).
 */
export type LineageDerivationSource = 'yjs-parent' | 'input-videoAssetId' | 'input-sourceAssetId' | 'root'

/** One input asset consumed by a generation. */
export interface LineageTakeReference {
  kind: LineageReferenceKind
  assetId: string | null
  url: string
  originalFilename: string | null
}

/** One generation in the lineage, with everything a legal reviewer needs. */
export interface LineageTake {
  generationId: string
  assetId: string | null // null if the generation failed before producing an asset
  jobId: string | null
  operation: AssetOperation
  capability: GenerationCapability
  prompt: string // post-substitution, as actually sent to the model
  userPrompt: string | null // original, may contain @[uuid:Name] element tags
  variableSnapshot: Record<string, { name: string; value: string; color?: string }> | null
  model: string
  modelVersion: string | null
  status: GenerationStatus
  params: Record<string, unknown>
  references: LineageTakeReference[]
  parentAssetId: string | null
  inferredParentGenerationId: string | null
  derivationSource: LineageDerivationSource
  shotNumber: number | null // null for orphaned takes (never placed on a shot)
  siblingIndex: number | null
  variationNumber: number
  createdAt: string
  completedAt: string | null
  authorId: string | null
  authorName: string | null
  outputUrl: string | null
  thumbnailUrl: string | null
  errorCode: string | null
  errorMessage: string | null
}

/** All takes belonging to one shot (selected + variations + extensions + offshoots). */
export interface LineageShot {
  shotNumber: number
  selectedAssetId: string | null
  takes: LineageTake[]
}

/** A scene and the shots inside it. */
export interface LineageScene {
  id: string
  name: string
  description: string | null
  shots: LineageShot[]
}

/** An uploaded or generated reference asset used as input anywhere in the project. */
export interface LineageReferenceAsset {
  assetId: string
  url: string
  mimeType: string
  originalFilename: string | null
  source: 'uploaded' | 'generated'
  uploadedBy: string | null
  uploadedAt: string
  usedInShots: number[]
}

/** High-level counts shown in the summary card. */
export interface LineageSummary {
  totalScenes: number
  totalShots: number
  totalTakes: number
  orphanTakes: number
  modelCounts: Record<string, number>
  dateRange: { from: string; to: string } | null
  statusCounts: Partial<Record<GenerationStatus, number>>
  /** Rows in `derivations`, by evidence. Absent on bundles exported before the ledger existed. */
  derivationCounts?: Partial<Record<LineageEvidence, number>>
}

/**
 * Canonical lineage graph for a project.
 * `contentHash` is SHA-256 over the canonical JSON form of this object,
 * with `contentHash` itself held at the empty string during hashing.
 */
export interface LineageTree {
  projectId: string
  projectName: string
  projectSlug: string
  organizationName: string | null
  generatedAt: string
  generatedByUserId: string
  generatedByUserName: string
  contentHash: string
  scenes: LineageScene[]
  orphanTakes: LineageTake[]
  references: LineageReferenceAsset[]
  summary: LineageSummary
  /**
   * The project's derivation ledger (docs/asset-lineage-design.md §10.1), oldest
   * first, ordered by (createdAt, id) so the canonical hash is byte-stable.
   * Absent on bundles exported before the ledger existed.
   */
  derivations?: Derivation[]
  /** Display names for the `actor.userId`s in `derivations`, for the audit CSV. */
  derivationActors?: Record<string, string>
  /** Only on the redacted export: every redaction of the project, so a reviewer sees what was hidden and why. */
  redactions?: DerivationRedaction[]
  /**
   * The reworked ledger (ADR-0021), present when the tree was built with
   * `LINEAGE_V2_READS` on — then `derivations` and `derivationActors` are
   * absent: the switch chooses the table. Left out of the Lineage page's tree,
   * whose Placements tab reads it through its own route.
   */
  ledger?: LineageLedgerExport
}

/** What portion of the project a lineage export covers. */
export type LineageExportScope =
  { kind: 'full' } | { kind: 'scenes'; sceneIds: string[] } | { kind: 'dateRange'; from: string; to: string }

/** Export artifact format. */
export type LineageExportFormat = 'bundle' | 'html' | 'manifest'

/** Export job lifecycle status surfaced to the frontend. */
export type LineageExportStatus = 'pending' | 'assembling' | 'bundling' | 'uploading' | 'completed' | 'failed'

/**
 * A finished report export printed to PDF on request (`POST …/lineage/export/:exportId/pdf`).
 * The PDF is rendered once from the stored report and kept beside it; later requests return the
 * same file. `sizeBytes` is null when the size of a kept file could not be read.
 */
export interface LineageExportPdfAnswer {
  url: string
  sizeBytes: number | null
  expiresAt: string
}

/** Row returned from the lineage export status endpoint. */
export interface LineageExportRecord {
  id: string
  projectId: string
  requestedByUserId: string | null
  scope: LineageExportScope
  format: LineageExportFormat
  includeTakeVideos: boolean
  /** The redacted export: rows and inputs hidden by a redaction are included, flagged. Project owners only. */
  includeRedacted: boolean
  status: LineageExportStatus
  generationCount: number | null
  contentHash: string | null
  bundleUrl: string | null
  bundleSizeBytes: number | null
  expiresAt: string | null
  createdAt: string
  errorMessage: string | null
}

// ============================================
// Billing Invoices
// ============================================

/**
 * Normalized invoice status — derived from Stripe's invoice.status.
 * Stored as a string union so the frontend can switch on it without importing Stripe types.
 */
export type InvoiceStatus = 'paid' | 'open' | 'void' | 'uncollectible' | 'draft'

/**
 * Invoice row returned to the frontend. Source data is Autumn's CustomerInvoice
 * (itself backed by Stripe), normalized for display.
 */
export interface NormalizedInvoice {
  /** Stripe invoice ID (used for download lookup). */
  stripeId: string
  /** Unix ms timestamp. */
  createdAt: number
  /** Human-readable description, e.g. "Pro plan — April 2026" or "Top-up". */
  description: string
  /** Amount in major currency units (dollars), matching Autumn's invoice shape. */
  amount: number
  /** ISO 4217 currency code, lowercase (matches Stripe). */
  currency: string
  /** Normalized status. */
  status: InvoiceStatus
  /** Stripe-hosted invoice page. Used as fallback link. */
  hostedUrl: string | null
}

// ============================================
// Organization Membership
// ============================================

/**
 * Organization member role. Mirrors the `organization_role` Postgres enum.
 * - `admin`: full management (members, settings, billing, credits)
 * - `member`: can use credits on team projects
 * - `billing`: billing-page access only — view invoices and top up credits.
 *   Enterprise-gated (finance / AP users).
 */
export type OrganizationRole = 'admin' | 'member' | 'billing'

export type OrganizationSecurityEventActorType = 'user' | 'backstage' | 'service' | 'anonymous'
/**
 * Facet used to filter the team security log. The table is append-only, so a
 * miscategorized event can never be corrected — see
 * `docs/security/security-event-taxonomy.md` for the decision ladder, the
 * boundaries that are easy to guess wrong (`permission` vs `data_modification`,
 * `security` vs `administration`), and what adding a new category requires.
 */
export type OrganizationSecurityEventCategory =
  | 'authentication'
  | 'administration'
  | 'account'
  | 'permission'
  | 'data_access'
  | 'data_modification'
  | 'data_deletion'
  | 'security'
export type OrganizationSecurityEventOutcome = 'success' | 'failure' | 'denied'
export type OrganizationSecurityEventSource = 'application' | 'supabase_auth' | 'partykit' | 'backstage'

/**
 * `source` is stored raw but never shown raw: `supabase_auth`, `partykit`, and `backstage` name
 * our vendors and internal tooling, which we don't disclose to team admins. Every read surface
 * (log table, filter, CSV export) renders through this map, so the customer only ever sees which
 * broad tier an event came from.
 */
export const SECURITY_EVENT_SOURCE_LABELS: Record<OrganizationSecurityEventSource, string> = {
  application: 'Martini app',
  supabase_auth: 'Backend database',
  partykit: 'Backend services',
  backstage: 'Backend services',
}

/**
 * The filter buckets an admin picks from. Several stored sources collapse into one bucket, so the
 * read API accepts a comma-separated `source` list rather than a single value.
 */
export interface OrganizationSecurityEventSourceGroup {
  value: string
  label: string
  sources: OrganizationSecurityEventSource[]
}

export const SECURITY_EVENT_SOURCE_GROUPS: OrganizationSecurityEventSourceGroup[] = [
  { value: 'martini_app', label: 'Martini app', sources: ['application'] },
  { value: 'backend_database', label: 'Backend database', sources: ['supabase_auth'] },
  { value: 'backend_services', label: 'Backend services', sources: ['partykit', 'backstage'] },
]

export interface OrganizationSecurityEvent {
  id: string
  occurredAt: string
  organizationId: string
  actorId: string | null
  actorType: OrganizationSecurityEventActorType
  actorLabel: string | null
  category: OrganizationSecurityEventCategory
  eventType: string
  outcome: OrganizationSecurityEventOutcome
  source: OrganizationSecurityEventSource
  resourceType: string | null
  resourceId: string | null
  projectId: string | null
  ip: string | null
  userAgent: string | null
  requestMethod: string | null
  requestPath: string | null
  statusCode: number | null
  metadata: Record<string, unknown>
}

export interface OrganizationSecurityEventPage {
  items: OrganizationSecurityEvent[]
  nextCursor: string | null
}

/**
 * Roles authorized for billing operations: viewing invoices and
 * purchasing credit top-ups. Plan lifecycle (cancel / resubscribe / upgrade)
 * remains admin-only.
 */
export const BILLING_ROLES: readonly OrganizationRole[] = ['admin', 'billing']

// ============================================
// Translator Route Coverage
// ============================================

/**
 * One route entry returned by `GET /i18n/route-keys`. Locale-agnostic
 * `hashes` come from `i18n_route_keys` (freshness-filtered); `completed`
 * fields come from `i18n_route_completion` for the requested locale.
 *
 * Frontend merges with bundle + active overrides to compute the
 * "translated/total" badge in the picker.
 */
export interface I18nRouteEntry {
  /** Normalized static route (locale prefix stripped, dynamic segments collapsed to `:id`). */
  route: string
  /** Message-key hashes seen on this route within the last 30 days. */
  hashes: string[]
  /** True iff `i18n_route_completion` has a row for `(locale, route)`. */
  completed: boolean
  /** Alias of the user who marked it complete, if available. */
  completedBy: string | null
  /** ISO timestamp of completion, if set. */
  completedAt: string | null
}

// ============================================
// Onboarding V2
// ============================================
// Single source of truth for the onboarding intent + one-tap survey answer
// values. The literal strings MUST stay identical to the SQL CHECK constraints
// in supabase/schemas/24_onboarding_state.sql and the PostHog/Loops property
// values (contracts C1/C3/C4/C9 in docs/plans/onboarding-v2-contracts.md).
// Intent set is founder-final (2026-06-10) — reshaped pre-remote-apply; see
// the contracts-doc amendment. 'other' added 2026-06-10 (founder dogfood):
// free-typing a brief without picking a chip auto-selects it, so Draft Shots
// can enable. Survey answers remain frozen at wave 0.

/** Intent chips offered on the brief step (S1), in display order. */
export const ONBOARDING_INTENTS = ['movie_trailer', 'short_drama', 'film', 'commercial', 'social', 'other'] as const
export type OnboardingIntent = (typeof ONBOARDING_INTENTS)[number]

/** Survey answer: where the user works. */
export const ONBOARDING_WORKPLACES = ['agency', 'studio', 'freelance', 'personal', 'school'] as const
export type OnboardingWorkplace = (typeof ONBOARDING_WORKPLACES)[number]

/** Survey answer: team size. */
export const ONBOARDING_TEAM_SIZES = ['solo', '2_10', '11_50', '50_plus'] as const
export type OnboardingTeamSize = (typeof ONBOARDING_TEAM_SIZES)[number]

/** Survey answer: prior AI video experience. */
export const ONBOARDING_AI_EXPERIENCES = ['first_time', 'tried_few', 'power_user'] as const
export type OnboardingAiExperience = (typeof ONBOARDING_AI_EXPERIENCES)[number]

/**
 * Survey answer: how the user heard about Martini. Multi-select (2026-06-10):
 * the column is text[] with a per-element CHECK against these literals; the
 * PostHog/Loops mirrors flatten the array to a comma-joined string.
 */
export const ONBOARDING_REFERRAL_CHANNELS = ['tiktok_ig', 'youtube', 'x', 'friend', 'search', 'other'] as const
export type OnboardingReferralChannel = (typeof ONBOARDING_REFERRAL_CHANNELS)[number]

export type OnboardingPresetMoveId = 'stopmotion-couple' | 'garden-friends' | 'portrait-closeup'

export interface CameraMoveFingerprintInput {
  keyframes: Array<{
    timeSeconds: number
    pose: {
      position: { x: number; y: number; z: number }
      quaternion: { x: number; y: number; z: number; w: number }
      fov: number
    }
    ease?: { preset?: string }
    tension?: number
  }>
  aspectRatio?: string
  speedId?: string
  mode?: string
  defaults?: { tension?: number }
}

/**
 * Stable identity for a curated camera move. Includes ordered keyframe timing,
 * pose, easing, and effective tension plus the path's framing and playback
 * mode. Keyframe ids and source labels are deliberately excluded because the
 * editor may regenerate them.
 */
export function fingerprintCameraMove(input: CameraMoveFingerprintInput): string {
  const rounded = (value: number): string => {
    const normalized = Math.round(value * 1000) / 1000
    return (Object.is(normalized, -0) ? 0 : normalized).toFixed(3)
  }
  const defaultTension = input.defaults?.tension ?? 0
  return [
    input.aspectRatio ?? '16:9',
    input.speedId ?? 'ease-in-out',
    input.mode === 'smooth' ? 'smooth' : 'exact',
    input.keyframes
      .map(keyframe => {
        const { position, quaternion, fov } = keyframe.pose
        return [
          rounded(keyframe.timeSeconds),
          [position.x, position.y, position.z].map(rounded).join(','),
          [quaternion.x, quaternion.y, quaternion.z, quaternion.w].map(rounded).join(','),
          rounded(fov),
          keyframe.ease?.preset ?? '',
          rounded(keyframe.tension ?? defaultTension),
        ].join('~')
      })
      .join(';'),
  ].join('|')
}

/**
 * KEEP IN SYNC with MOTION_PRESET_PATHS. The frontend node test fails when a
 * curated seed changes without updating its server-side fingerprint.
 */
export const ONBOARDING_PRESET_MOVE_FINGERPRINTS: Record<OnboardingPresetMoveId, string> = {
  'stopmotion-couple':
    '16:9|ease-in-out|exact|0.000~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~custom~0.000;0.685~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~ease-in-out~0.000;5.000~0.089,-0.044,-0.291~0.115,0.251,-0.030,0.961~27.839~~0.000',
  'garden-friends':
    '16:9|ease-in-out|exact|0.000~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~custom~0.000;0.685~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~ease-in-out~0.000;5.000~0.089,-0.044,-0.291~0.115,0.251,-0.030,0.961~27.839~~0.000',
  'portrait-closeup':
    '16:9|ease-in-out|exact|0.000~0.000,0.000,0.000~0.000,0.000,0.000,1.000~42.151~ease-in-out~0.000;5.000~0.081,-0.004,-0.294~0.025,0.066,-0.002,0.998~37.800~~0.000',
}

// ============================================
// Affiliate Program
// ============================================
// Status literals are the single source of truth and must stay identical to
// the column comments in supabase/schemas/29_affiliates.sql. Scheme (locked):
// 25% of net subscription revenue (30% at >=25 active referrals), 12-month
// reward window per referred customer. Cash uses Dub's 60-day holdback and
// monthly payouts; designated partners receive manual monthly Martini olive
// credits. Subscriptions only — rewards never attach to olive top-ups,
// signups, or clicks.

export const AFFILIATE_STATUSES = ['applied', 'approved', 'suspended', 'terminated'] as const
export type AffiliateStatus = (typeof AFFILIATE_STATUSES)[number]

export const AFFILIATE_REFERRAL_STATUSES = ['attributed', 'active', 'expired', 'voided'] as const
export type AffiliateReferralStatus = (typeof AFFILIATE_REFERRAL_STATUSES)[number]

export const AFFILIATE_VOID_REASONS = ['self_referral', 'fraud', 'refund_window'] as const
export type AffiliateVoidReason = (typeof AFFILIATE_VOID_REASONS)[number]

export const AFFILIATE_REWARD_TYPES = ['cash', 'olives'] as const
export type AffiliateRewardType = (typeof AFFILIATE_REWARD_TYPES)[number]

/** Default commission rate: 25% of net subscription revenue. */
export const AFFILIATE_DEFAULT_COMMISSION_BPS = 2500
/** Performance tier: 30% once an affiliate has >= AFFILIATE_TIER_THRESHOLD active referrals. */
export const AFFILIATE_TIER_COMMISSION_BPS = 3000
export const AFFILIATE_TIER_THRESHOLD = 25
/** Reward window per referred customer, in months (capped, not lifetime). */
export const AFFILIATE_REWARD_WINDOW_MONTHS = 12

/** Body for the public application form (POST /affiliates/apply). */
export interface AffiliateApplicationInput {
  displayName: string
  contactEmail: string
  channelUrls: string[]
  applicationNote?: string
  /** Must be true — the form requires accepting the program agreement. */
  agreementAccepted: boolean
}

/** What a logged-in user sees about their own partner standing (Settings → Partner program). */
/**
 * Net commission reported to Dub, from our own audit mirror (clawbacks are
 * negative rows, so these are already net of refunds). NOT a payout balance —
 * Dub owns the 60-day holdback, the balance, and the payout schedule.
 */
export interface AffiliateCashEarnings {
  type: 'cash'
  lifetimeCents: number
  last30dCents: number
}

export interface AffiliateOliveEarnings {
  type: 'olives'
  earnedOlives: number
  availableOlives: number
  creditedOlives: number
  /** Cash commissions earned before the one-way cutover remain in Dub. */
  hasHistoricalCashEarnings: boolean
}

export type AffiliateEarnings = AffiliateCashEarnings | AffiliateOliveEarnings

export interface AffiliateSelfView {
  status: AffiliateStatus
  rewardType: AffiliateRewardType
  slug: string | null
  appliedAt: string
  approvedAt: string | null
  commissionRateBps: number
  /** Filmmakers bound to this partner (active = currently subscribed referrals). */
  referralCounts: { total: number; active: number }
  /**
   * null = the ledger read failed, or the partner isn't approved yet. Render
   * "unavailable" — never a zero, which would read as "you've earned nothing".
   */
  earnings: AffiliateEarnings | null
}

/** Admin list/detail view of an affiliate (backstage queue). */
export interface AffiliateAdminView {
  id: string
  displayName: string
  contactEmail: string
  channelUrls: string[]
  status: AffiliateStatus
  applicationNote: string | null
  promoCampaignId: string | null
  promoCampaignSlug: string | null
  dubPartnerId: string | null
  commissionRateBps: number
  rewardType: AffiliateRewardType
  rewardOrganizationId: string | null
  rewardTypeEffectiveAt: string | null
  rewardTermsAcceptedAt: string | null
  agreementAcceptedAt: string | null
  approvedAt: string | null
  notes: string | null
  createdAt: string
  referralCounts: { total: number; active: number; voided: number }
}

export interface AffiliateRewardDestinationCandidate {
  organizationId: string
  organizationName: string
  userId: string
  email: string | null
  source: 'linked_user' | 'contact_email'
}

/** Admin view of one referred organization. */
export interface AffiliateReferralAdminView {
  id: string
  affiliateId: string
  organizationId: string
  attributedAt: string
  firstPaidAt: string | null
  rewardWindowEndsAt: string | null
  status: AffiliateReferralStatus
  voidReason: AffiliateVoidReason | null
}

/** Admin view of one commission-log entry (our audit mirror of Dub). */
export interface AffiliateCommissionLogEntry {
  id: string
  affiliateId: string
  referralId: string
  invoiceId: string
  kind: 'sale' | 'clawback'
  amountCents: number
  commissionCents: number
  commissionRateBps: number
  rewardType: AffiliateRewardType
  rewardOlives: number | null
  invoiceSettledAt: string | null
  olivePayoutKey: string | null
  olivePayoutClaimedAt: string | null
  oliveGrantedAt: string | null
  status: 'pending' | 'reported' | 'available' | 'payout_pending' | 'payout_review' | 'credited' | 'absorbed' | 'voided'
  voidReason: string | null
  reportedToDubAt: string | null
  createdAt: string
}

export interface AffiliateOlivePayoutBatch {
  payoutKey: string
  status: 'payout_pending' | 'payout_review' | 'credited'
  olives: number
  rowCount: number
  claimedAt: string | null
  grantedAt: string | null
  stale: boolean
  reviewReason:
    | 'definite_rejection_release_failed'
    | 'unknown_grant_outcome'
    | 'autumn_granted_audit_write_failed'
    | 'credited_in_autumn_ledger_finalize_failed'
    | null
  providerConfirmedGranted: boolean
}

export interface AffiliateAbsorbedRefundEvent {
  invoiceId: string
  type: 'refund' | 'chargeback'
  date: string
  olives: number
  amountCents: number
}

export interface AffiliateOliveSummary {
  cutoff: string
  earnedOlives: number
  availableOlives: number
  availableRowCount: number
  creditedOlives: number
  reviewRequiredOlives: number
  absorbedOlives: number
  absorbedAmountCents: number
  absorbedEventCount: number
  historicalCashCents: number
  payoutBatches: AffiliateOlivePayoutBatch[]
  absorbedEvents: AffiliateAbsorbedRefundEvent[]
}

export interface AffiliateOliveProgramSummary {
  absorbedOlives: number
  absorbedAmountCents: number
  absorbedEventCount: number
}

// ============================================================================
// Library (Collections) — wire contract
// ============================================================================
// Shared 1:1 with frontend/src/store/libraryStore.ts (which re-exports these).
// Ids are client-generated short text ids (plus deterministic 'smp-*' sample
// ids) — unique per workspace only. Timestamps are unix ms on the wire;
// Postgres stores timestamptz (converted in backend/src/library/serialize.ts).
// See spec/48-library-data-architecture.md and supabase/schemas/30_library.sql.

export type LibraryAssetKind = 'image' | 'video' | 'model3d' | 'audio' | 'text'
export type LibrarySourceKind = 'upload' | 'import' | 'generation'
export type LibraryVersionSource = 'initial' | 'replace' | 'restore' | 'generation' | 'upload' | 'import'

/** One immutable entry in an asset's append-only media history. */
export type LibAssetVersion = {
  id: string
  assetId: string
  versionNumber: number
  kind: LibraryAssetKind
  src: string
  srcLarge?: string
  posterSrc?: string
  r2Key?: string
  posterR2Key?: string
  originalFilename?: string
  width?: number
  height?: number
  sizeBytes?: number
  /** The prompt-fragment body for kind='text' versions (src is '' for those). */
  textValue?: string
  source: LibraryVersionSource
  generationId?: string
  createdBy?: string
  createdAt: number
}

/** What a generation referenced from the library — captured into library_links. */
export type LibraryRef = {
  collectionId: string
  assetId?: string
  /** The asset's currentVersionId at reference time — what makes staleness computable. */
  versionId?: string
}

export type LibAsset = {
  id: string
  collectionId: string
  kind: LibraryAssetKind
  /** https URL (persisted) or `idb:<assetId>` (upload in flight, browser-local). */
  src: string
  /** Uploader-private IndexedDB blob pointer; irrelevant once src is https. */
  blobKey?: string
  /** Native-aspect variant when it differs from src (sample imagery). */
  srcLarge?: string
  /** Display still for non-image media (video poster) — grids render <img>. */
  posterSrc?: string
  /** R2 object key for library uploads (org-prefixed; null for captured/sample). */
  r2Key?: string
  posterR2Key?: string
  name: string
  originalFilename?: string
  width?: number
  height?: number
  sizeBytes?: number
  sourceKind?: LibrarySourceKind
  /**
   * propertyKey → typed value, keyed by the property's stable `key`. Shape
   * depends on the property `type`:
   *   select    → optionId (string; label resolves via the vocabulary)
   *   text      → string (literal)
   *   number    → number
   *   checkbox  → boolean
   * Absent key = unset. Select still stores option IDS (never labels) so an
   * option rename is a one-row edit (spec 48 §4).
   */
  attrs: Record<string, AttrValue>
  /** Prompt-fragment body for kind='text' assets (src is '' for those). */
  textValue?: string
  /** Where the pixels were captured FROM (canvas capture only). */
  origin?: { projectId: string; sceneId?: string; assetId?: string }
  /** The version the flat media fields mirror. '<id>-v1' is the history root. */
  currentVersionId?: string
  rev: number
  createdBy?: string
  updatedBy?: string
  createdAt: number
  updatedAt: number
  deletedAt?: number
}

export type LibCollection = {
  id: string
  name: string
  /** Stable kebab slug — the future SQL/table name (spec §7 naming contract). */
  slug: string
  /** Folder path: 'Night Shift' or 'Night Shift/Cast'. null = unfiled. */
  folder: string | null
  tags: string[]
  heroAssetId: string | null
  /** Free-form notes about the collection, shown under its title. */
  description?: string
  /** Sample-catalog collections keep mock usage; user ones don't. */
  sample?: boolean
  usedIn: number
  film?: string
  rev: number
  createdBy?: string
  updatedBy?: string
  createdAt: number
  updatedAt: number
  deletedAt?: number
}

export type LibraryPropertyOption = {
  id: string
  /** Display label ('Happy'). Renaming touches only this row. */
  value: string
  color?: string
}

/** The user-visible field types (spec 49). Widening this is an additive
    migration (a Postgres check-constraint + this union) — multi_select, date,
    rating etc. are later phases. */
export type LibraryPropertyType = 'select' | 'text' | 'number' | 'checkbox'

/**
 * A single asset attribute value. The runtime shape is dictated by the owning
 * property's `type` (see LibAsset.attrs). `null`/absent means unset.
 *   string  → select optionId, text
 *   number  → number
 *   boolean → checkbox
 */
export type AttrValue = string | number | boolean

/** Type-specific property settings (spec 49). Only the keys relevant to the
    property's `type` are read; unknown keys are ignored. */
export type LibraryPropertyConfig = {
  /** text: render as a multi-line textarea. */
  multiline?: boolean
  /** number: fixed decimal places (0 = integer). */
  precision?: number
}

export type LibraryProperty = {
  id: string
  /** Stable slug key ('mood') — the attrs key; never renamed. */
  key: string
  /** The user's word ('Mood'); renamed freely. */
  label: string
  type: LibraryPropertyType
  /** Option vocabulary — only populated for select / multi_select. */
  options: LibraryPropertyOption[]
  /** Type-specific settings; absent = defaults. */
  config?: LibraryPropertyConfig
  sortOrder: number
  deletedAt?: number
}

export type LibraryViewLayout = 'gallery' | 'table' | 'matrix'

/** A saved way of looking: serializable, rename-proof (property keys + option ids). */
export type LibraryViewDefinition = {
  /** Collection scope (v1 views are per-collection). */
  collectionId: string
  layout: LibraryViewLayout
  /** propertyKey → allowed optionIds (empty/absent = no filter). */
  filters?: Record<string, string[]>
  groupBy?: string
  sort?: { by: 'name' | 'createdAt' | string; dir: 'asc' | 'desc' }
  /** Matrix axes: two property keys. */
  axes?: { x: string; y: string }
}

export type LibView = {
  id: string
  name: string
  definition: LibraryViewDefinition
  rev: number
  createdAt: number
  updatedAt: number
  deletedAt?: number
}

export type LibraryAssetUpdatePatch = Partial<{
  src: string
  blobKey: string | null
  srcLarge: string | null
  posterSrc: string | null
  r2Key: string | null
  posterR2Key: string | null
  kind: LibraryAssetKind
  originalFilename: string | null
  width: number | null
  height: number | null
  sizeBytes: number | null
  sourceKind: LibrarySourceKind | null
  textValue: string | null
}>

/** The command seam — mirrors the store executor 1:1. */
export type LibraryCommand =
  | { type: 'collection.create'; collection: LibCollection }
  | { type: 'collection.rename'; id: string; name: string }
  | { type: 'collection.delete'; id: string }
  | { type: 'collection.restore'; id: string }
  | { type: 'collection.move'; id: string; folder: string | null }
  | { type: 'collection.setTags'; id: string; tags: string[] }
  | { type: 'collection.setHero'; id: string; assetId: string | null }
  | { type: 'collection.setDescription'; id: string; description: string }
  | { type: 'folder.create'; path: string }
  | { type: 'folder.rename'; path: string; next: string }
  | { type: 'folder.delete'; path: string; restoreCollections?: Record<string, string | null> }
  | { type: 'asset.create'; asset: LibAsset }
  | { type: 'asset.rename'; id: string; name: string }
  | { type: 'asset.move'; ids: string[]; collectionId: string; perAssetTarget?: Record<string, string> }
  | { type: 'asset.delete'; ids: string[] }
  | { type: 'asset.restore'; ids: string[] }
  | { type: 'asset.setValue'; id: string; key: string; value: AttrValue | null }
  | {
      type: 'asset.update'
      id: string
      patch: LibraryAssetUpdatePatch
      prev?: Record<string, unknown>
      /** Present when the patch replaces media: the client-minted id of the
          version this write appends (idempotent retries; executor parity). */
      versionId?: string
    }
  /** Move the current pointer to an existing version (history restore / undo
      of replace-media). Copies that version's media onto the flat columns —
      never appends, never rewrites history. */
  | { type: 'asset.setCurrentVersion'; id: string; versionId: string }
  | { type: 'property.create'; property: LibraryProperty }
  | { type: 'property.rename'; key: string; label: string }
  /** Change a property's field type in place. Stored values are KEPT and
      re-interpreted (readers are shape-tolerant; mismatched leftovers display
      raw until edited); the option vocabulary stays dormant off-select. */
  | { type: 'property.setType'; key: string; propertyType: LibraryPropertyType; config?: LibraryPropertyConfig }
  | { type: 'property.delete'; key: string }
  | { type: 'property.restore'; key: string }
  | { type: 'property.addOption'; key: string; option: LibraryPropertyOption }
  | { type: 'property.removeOption'; key: string; optionId: string }
  | { type: 'property.renameOption'; key: string; optionId: string; value: string }
  | { type: 'tag.addToVocab'; tags: string[] }
  | { type: 'view.save'; view: LibView }
  | { type: 'view.update'; id: string; name?: string; definition?: LibraryViewDefinition }
  | { type: 'view.delete'; id: string }
  | { type: 'link.setProject'; projectId: string; collectionIds: string[] }
  | { type: 'batch'; label: string; commands: LibraryCommand[] }

/** GET /library/:orgId response. Includes soft-deleted rows (stable-id revive). */
export type LibrarySnapshot = {
  /** Echo of the workspace alias the client asked for ('personal' | orgId). */
  workspaceId: string
  /** The resolved org uuid. */
  orgId: string
  collections: LibCollection[]
  assets: LibAsset[]
  /** Append-only media history for every asset (mostly one row each). */
  versions: LibAssetVersion[]
  folders: string[]
  tagVocab: string[]
  properties: LibraryProperty[]
  views: LibView[]
  projectLinks: Record<string, string[]>
  /** created_by/updated_by uuid → profile alias, for attribution rows. */
  members?: Record<string, string>
}

/**
 * GET /library/community/snapshot — the published community library: a live,
 * scrubbed snapshot of the source workspace (COMMUNITY_LIBRARY_ORG_ID env).
 * Recipients copy it into their own workspace as sample rows; assets keep
 * their public src URLs but never the source org's R2 keys, canvas origins,
 * or author ids.
 */
export type CommunityLibrarySnapshot = {
  collections: LibCollection[]
  assets: LibAsset[]
  folders: string[]
  tagVocab: string[]
  properties: LibraryProperty[]
}

/** POST /library/:orgId/uploads — presigned PUT init, one entry per file. */
export type LibraryUploadFileRequest = {
  assetId: string
  filename: string
  contentType: string
  sizeBytes: number
  /** Client-captured poster still (video first frame), uploaded alongside. */
  poster?: { contentType: string; sizeBytes: number }
}
export type LibraryUploadFileInit = {
  assetId: string
  key: string
  /** null when the backend runs without R2 (local dev) — use /uploads/direct. */
  uploadUrl: string | null
  publicUrl: string
  directUploadToken?: string
  poster?: { key: string; uploadUrl: string | null; publicUrl: string; directUploadToken?: string }
  directUpload: boolean
}
export type LibraryUploadsResponse = { files: LibraryUploadFileInit[] }

// =============================================================================
// Comments (canvas + timeline review threads)
// =============================================================================

/**
 * What a comment is about — never a resolved position. Pins re-resolve from
 * the live doc every render (node moved → pin follows; clip trimmed → marker
 * rides). When the referent disappears the UI freezes the last projection and
 * badges the thread (node) or falls back to `fallbackTicks` (clip).
 */
export type CommentAnchor =
  /** Pinned to a spot on the board (world coordinates). */
  | { type: 'canvas'; sceneId: string; x: number; y: number }
  /**
   * Pinned to a node; u/v are normalized offsets within the node bounds so the
   * pin survives resizes. fx/fy capture the world position at creation — the
   * frozen-pin fallback when the node is later deleted.
   */
  | { type: 'node'; sceneId: string; nodeId: string; u: number; v: number; fx?: number; fy?: number }
  /**
   * Pinned to a moment inside a clip (offset from clip start, in ticks).
   * Rides trims/moves/reorders because clip ids are stable; `fallbackTicks`
   * (absolute sequence time at creation) + `assetId` let the UI degrade
   * gracefully when the clip is deleted or the timeline is rebuilt.
   */
  | {
      type: 'clip'
      sequenceId: string
      clipId: string
      offsetTicks: number
      fallbackTicks: number
      assetId: string
      /** Range comments: duration of the marked span, in ticks. */
      rangeTicks?: number
    }
  /** Pinned to absolute sequence time (about the cut/moment, not any clip). */
  | { type: 'time'; sequenceId: string; ticks: number; rangeTicks?: number }

/**
 * Mention target stored on a comment. Only people are mentionable — agents act
 * on behalf of the user they authed as, so you @ the person and their connected
 * agent picks the work up as them (get_comments mentions:'me' matches by userId).
 */
export type CommentMention = { kind: 'user'; userId: string }

/**
 * A single comment. Threads are flat: the root has threadId === id and owns
 * the anchor + resolved state; replies share the root's threadId and carry
 * neither. Canonical store is Supabase; the project Yjs doc holds a mirror
 * (written only by the backend) for real-time delivery.
 */
export interface CommentRecord {
  id: string
  projectId: string
  threadId: string
  authorUserId: string | null
  /** Engine-owned run identity; mutually exclusive with a human author. */
  authorRunId?: string | null
  /** MCP client name (e.g. 'claude-code') when authored via an agent; null from the app. */
  authorClient?: string | null
  body: string
  mentions: CommentMention[]
  /** Root comments only; replies inherit the thread root's anchor. */
  anchor?: CommentAnchor | null
  resolvedAt?: string | null
  resolvedBy?: string | null
  createdAt: string
  editedAt?: string | null
  /** Mirror tombstone: soft-deleted comments keep the record, lose the body. */
  deleted?: boolean
}

/** POST /projects/:projectId/comments */
export interface CreateCommentRequest {
  /** Client-generated UUID for optimistic render + idempotent retry; server mints one if absent. */
  id?: string
  /** Present = reply to that thread; absent = new thread (anchor required). */
  threadId?: string
  body: string
  /** @deprecated Put user references in body; accepted for older clients. */
  mentions?: CommentMention[]
  anchor?: CommentAnchor
}

export const COMMENT_REF_KINDS = ['user', 'asset', 'subject', 'bin', 'annotation', 'variable'] as const
export type CommentRefKind = (typeof COMMENT_REF_KINDS)[number]
/** Hard cap per comment — bounds notifier fan-out and render work. */
export const COMMENT_REF_MAX = 50

export interface CommentRef {
  kind: CommentRefKind
  id: string
  label: string
  /** The exact token text, e.g. '@[asset-…:Shot 004]'. */
  raw: string
  start: number
  end: number
}
export type CommentSegment = { type: 'text'; text: string } | { type: 'ref'; ref: CommentRef }

const COMMENT_REF_RE =
  /@\[(?:(user|asset|subject|bin|annotation|variable)-([A-Za-z0-9_-]{1,64})|([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})):([^\]\r\n]{1,120})\]/gi
const COMMENT_REF_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Tokenise a comment body. Unknown kinds and malformed tokens stay text. Parses at most COMMENT_REF_MAX refs. */
export function parseCommentBody(body: string): CommentSegment[] {
  const segments: CommentSegment[] = []
  let cursor = 0
  let count = 0
  for (const match of body.matchAll(COMMENT_REF_RE)) {
    if (count >= COMMENT_REF_MAX) break
    const start = match.index
    const end = start + match[0].length
    if (start > cursor) segments.push({ type: 'text', text: body.slice(cursor, start) })
    segments.push({
      type: 'ref',
      ref: {
        kind: match[1] ? (match[1].toLowerCase() as CommentRefKind) : 'asset',
        id: match[2] ?? match[3],
        label: match[4],
        raw: match[0],
        start,
        end,
      },
    })
    cursor = end
    count += 1
  }
  if (cursor < body.length) segments.push({ type: 'text', text: body.slice(cursor) })
  return segments
}

export function commentRefsOf(body: string): CommentRef[] {
  return parseCommentBody(body).flatMap(segment => (segment.type === 'ref' ? [segment.ref] : []))
}

/**
 * Timeline time base — the ONE definition. The frontend's `lib/constants.ts`
 * and the MCP timeline tools derive from these; nothing else may restate them.
 */
export const TIMELINE_TICKS_PER_SECOND = 48000
export const TIMELINE_FPS = 24
export const TIMELINE_TICKS_PER_FRAME = TIMELINE_TICKS_PER_SECOND / TIMELINE_FPS

/**
 * Read-only timeline projection for the phone (GET /mobile/v1/sequences/:id).
 * Nested sequences are flattened server-side; all times are timeline ticks.
 */
/** Wire shape of a clip volume point — mirrors VolumePoint in types/timeline-editing.ts. */
export interface MobileVolumePoint {
  time: number
  gainDb: number | null
  curve?: 'linear' | 'hold'
}
export type MobileSequenceClipKind = 'video' | 'image' | 'audio'
export interface MobileSequenceTrack {
  id: string
  index: number
  type: 'video' | 'audio'
  name: string
  muted: boolean
  solo: boolean
  /** False when the editor hid the track (video `isVisible: false`); the phone skips its clips. */
  visible: boolean
  /**
   * The phone must not edit this track's clips. True for either of two reasons: the editor locked the
   * track (an edit is refused with TRACK_LOCKED), or the track is a nested sequence's sound shown
   * through this sequence (`id` like `<clip>/track-<n>`): it is unlocked in its own sequence and edited
   * there, never through the parent.
   */
  locked: boolean
  gainDb?: number
}
export interface MobileSequenceClip {
  id: string
  trackId: string
  trackIndex: number
  kind: MobileSequenceClipKind
  assetId: string | null
  startTicks: number
  durationTicks: number
  trimInTicks: number
  trimOutTicks: number
  speed: number
  gainDb?: number
  fadeInTicks?: number
  fadeOutTicks?: number
  volumePoints?: MobileVolumePoint[]
  loop?: boolean
  preservePitch?: boolean
  /** Ids of the nested-sequence clips this clip was flattened through (outermost first); absent for root clips. */
  nestedPath?: string[]
  /** Root clips only: echo it in an edit's `base.clips` so the edit is refused if this clip changed meanwhile. */
  version?: string
  /** Root clips only: shared by a picture and its sound. Re-creating the clip should re-create the sound. */
  linkGroupId?: string
}
export interface MobileSequenceAsset {
  id: string
  uri: string
  proxyUri?: string
  thumbUri?: string
  mimeType: string
  assetType?: string
  durationSeconds?: number
  width?: number
  height?: number
  shotNumber?: number
  displayName?: string
}
export interface MobileSequenceComment {
  id: string
  threadId: string
  clipId: string | null
  ticks: number
  rangeTicks?: number
  resolved: boolean
  authorUserId: string | null
  createdAt: string
  preview: string
}
export interface MobileSequenceListItem {
  id: string
  name: string
  durationTicks: number
  clipCount: number
  revision: string
}

export interface MobileSequenceList {
  sequences: MobileSequenceListItem[]
}

export interface MobileSequencePayload {
  sequence: {
    id: string
    name: string
    durationTicks: number
    fps: number
    ticksPerSecond: number
    frame?: { width: number; height: number }
  }
  tracks: MobileSequenceTrack[]
  clips: MobileSequenceClip[]
  assets: Record<string, MobileSequenceAsset>
  comments: MobileSequenceComment[]
  revision: string
}

/**
 * Workflow runs on the phone, for people with Actions on the project (as on the web). A run makes many
 * outputs (Next Securities: ~108 per run); the phone groups them into one card led by the run's finals.
 * Asset ids are board asset ids; only assets the board lists (completed, with a file) are named.
 */
/**
 * `final`: an output of the run's last Action that produced media. `piece`: any other output.
 * `replaced`: an output a later take replaced; the phone hides it.
 */
export type MobileWorkflowRunRole = 'final' | 'piece' | 'replaced'

/** On a board asset (GET /mobile/v1/projects/:projectId/board) that a run produced. */
export interface MobileWorkflowRunTag {
  runId: string
  role: MobileWorkflowRunRole
  /** 0-based index of the Action that made it. */
  actionIndex: number
}

export interface MobileWorkflowRunSummary {
  runId: string
  /** The saved workflow's name (the run's own snapshot name when it has none). */
  name: string
  /** The canvas the run made for itself (a saved workflow run from chat, MCP or the API), so parallel runs read apart. */
  canvasName?: string
  status: WorkflowRunStatusValue
  origin: WorkflowRunOrigin
  createdAt: string
  completedAt?: string
  actionCount: number
  /**
   * 0-based Action cursor, using the same base as `steps[].index`. Can equal `actionCount` after the
   * last Action completes; passed through from the run without clamping to the last step.
   */
  currentActionIndex: number
  /** Board asset ids of the finals, in the order they were made. */
  finals: string[]
  /** Outputs on the board that no later take replaced, finals included. */
  outputCount: number
  olives: { generation: number; agent: number }
}

/** GET /mobile/v1/projects/:projectId/runs — newest first. */
export interface MobileWorkflowRunList {
  runs: MobileWorkflowRunSummary[]
  /** Pass as `?cursor=` for the next page; null on the last page. */
  nextCursor: string | null
}

export interface MobileWorkflowRunStep {
  /** 0-based Action index. */
  index: number
  name: string
  status: WorkflowRunActionStatusValue
  /** Board asset ids this Action made that no later take replaced. */
  outputs: string[]
  /** Board asset ids this Action made that a later take replaced. */
  replaced: string[]
  /**
   * Replaced asset id → the board asset id that took its place (from `replaces_item_id`, followed to the
   * take that stands now). Absent when no replaced output names its replacement.
   */
  replacedBy?: Record<string, string>
}

/** GET /mobile/v1/runs/:runId */
export interface MobileWorkflowRunDetail {
  run: MobileWorkflowRunSummary
  steps: MobileWorkflowRunStep[]
}

/**
 * The phone's Workflows tab: a workspace's workflows grouped into families (every saved version of one
 * workflow, e.g. "Style 1 … - v1.5.5D" and "… - v1.6.9"), with run statistics. For workspace members whose
 * workspace holds Actions, as on the web's Workflows page.
 */
/** What the caller may do to a run now; each is POST /mobile/v1/runs/:runId/<action>. */
export type MobileWorkflowRunAction = 'approve' | 'resume' | 'cancel'

/** The newest final of a family's newest completed run. */
export interface MobileWorkflowFamilyFinal {
  /** Board asset id in `projectId`. */
  assetId: string
  projectId: string
  thumbnailUri?: string
  durationSeconds?: number
}

export interface MobileWorkflowFamily {
  /** The saved name without its version suffix, case-folded: the key in /workflows/:familyKey/runs. */
  familyKey: string
  /** The newest saved version's name without its version suffix (a run's name when nothing is saved). */
  name: string
  /** The newest saved version's version label ("v1.6.9"); null when its name carries none. */
  latestVersion: string | null
  /** Live saved workflows in the family, most recently saved first. */
  savedWorkflowIds: string[]
  runCount: number
  lastRunAt: string | null
  /** Runs that are failed, need revision, await approval or are paused. */
  needsYou: number
  /** Absent when no completed run has a final on its board, or it was not read in time. */
  latestFinal?: MobileWorkflowFamilyFinal
  /** Test and internal workflows ("(internal)", "Perf …", "Stress test", "sanity check"). */
  internal: boolean
}

/** GET /mobile/v1/workspaces/:orgId/workflows */
export interface MobileWorkflowFamilies {
  /** Most recently run first; families that never ran last, by name. */
  families: MobileWorkflowFamily[]
  stats: {
    /** Runs started in the last 7 days. */
    runs7d: number
    /** Of those, the ones that completed. */
    completed7d: number
    /** Olives those runs spent (generation and agent). */
    olives7d: number
    /** 14 UTC days, oldest first, today last: runs started each day. */
    runsPerDay: Array<{ day: string; count: number }>
  }
  /** True when the run history could not be read in time: counts cover the newest runs read. */
  partial: boolean
}

/** A run in a family: the run summary plus where it ran and what the caller may do to it. */
export interface MobileWorkflowFamilyRun extends MobileWorkflowRunSummary {
  projectId: string
  projectName: string
  actions: MobileWorkflowRunAction[]
}

/** GET /mobile/v1/workspaces/:orgId/workflows/:familyKey/runs */
export interface MobileWorkflowFamilyRuns {
  family: MobileWorkflowFamily
  stats: {
    /** Completed over settled (completed, failed, needs revision); null before any settled. */
    completionRate: number | null
    /** Median start-to-finish of completed runs; null before any completed. */
    medianRunSeconds: number | null
    /** Mean olives per settled run; null before any settled. */
    olivesPerRun: number | null
    /** 8 UTC weeks (Monday starts), oldest first, this week last. */
    runsPerWeek: Array<{ weekStart: string; count: number }>
  }
  /** The family's runs that need the caller, newest first (first page only; empty on later pages). */
  needsYou: MobileWorkflowFamilyRun[]
  /** Newest first, 50 a page. */
  runs: MobileWorkflowFamilyRun[]
  /** Pass as `?cursor=` for the next page; null on the last page. */
  nextCursor: string | null
  /** The family's saved versions, most recently saved first. */
  versions: Array<{ id: string; name: string; version: string | null; savedAt: string }>
  /** True when the run history could not be read in time. */
  partial: boolean
}

/** POST /mobile/v1/runs/:runId/approve | resume | cancel */
export interface MobileWorkflowRunActionResult {
  runId: string
  /** The run's status after the action. */
  status: WorkflowRunStatusValue
}

/** GET /mobile/v1/projects: GET /board/projects plus what the phone shows of each project. */
export interface MobileProjectSummary {
  id: string
  name: string
  createdAt: string
  lastActivityAt: string
  visibility: string
  teamId: string | null
  teamName?: string
  role: string
  joinedAt: string
  /**
   * Made by workflow runs: started over the API or MCP (no canvas or chat runs) and every asset on its
   * board is a run's output. False when that could not be checked in time; the next read fills it in.
   */
  runMade: boolean
}

export interface MobileProjectList {
  projects: MobileProjectSummary[]
}

export interface CommentTimecode {
  raw: string
  start: number
  end: number
  ticks: number
}

/** Find timecodes in a comment body. Never inside @[…] ref tokens. */
export function parseCommentTimecodes(body: string): CommentTimecode[] {
  const excluded = [
    ...commentRefsOf(body),
    ...Array.from(body.matchAll(/\b(?:[a-z][a-z\d+.-]*:\/\/|www\.)[^\s<>]+/gi), match => ({
      start: match.index,
      end: match.index + match[0].length,
    })),
  ]
  const timecodes: CommentTimecode[] = []
  // Consume whole numeric candidates before validating so malformed fields
  // cannot turn into shorter, valid timecodes by regex backtracking.
  for (const match of body.matchAll(/(?<![\w:.])(?:\d+(?::\d+){1,3}(?:\.\d+)?|\d+\.\d+s)(?![\w:])/g)) {
    const raw = match[0]
    const start = match.index
    const end = start + raw.length
    if (/^\s*(?:am|pm)\b/i.test(body.slice(end))) continue
    if (excluded.some(span => start < span.end && end > span.start)) continue

    let seconds: number
    let frames = 0
    if (raw.endsWith('s')) {
      if (!/^\d+\.\d{1,3}s$/.test(raw)) continue
      seconds = Number(raw.slice(0, -1))
    } else {
      const fields = raw.split(':')
      const values = fields.map(Number)
      if (!/^\d{1,2}$/.test(fields[0])) continue
      if (fields.length === 2) {
        if (!/^\d{2}(?:\.\d{1,3})?$/.test(fields[1]) || values[0] > 59 || values[1] >= 60) continue
        seconds = values[0] * 60 + values[1]
      } else {
        if (fields.slice(1).some(field => !/^\d{2}$/.test(field))) continue
        if (fields.length === 3) {
          // Three fields are always MM:SS:FF (what the ruler prints). An out-of-range
          // frame count is not a timecode — never a silent H:MM:SS reinterpretation.
          if (values[0] > 59 || values[1] > 59 || values[2] >= TIMELINE_FPS) continue
          seconds = values[0] * 60 + values[1]
          frames = values[2]
        } else {
          if (values[1] > 59 || values[2] > 59 || values[3] >= TIMELINE_FPS) continue
          seconds = values[0] * 3600 + values[1] * 60 + values[2]
          frames = values[3]
        }
      }
    }
    const ticks = Math.round(seconds * TIMELINE_TICKS_PER_SECOND) + frames * TIMELINE_TICKS_PER_FRAME
    if (!Number.isSafeInteger(ticks)) continue
    timecodes.push({ raw, start, end, ticks })
    if (timecodes.length === 100) break
  }
  return timecodes
}

/** MM:SS:FF at the timeline frame rate — the one timecode formatter (the frontend's formatTimecodeFromTicks delegates here). */
export function formatCommentTimecode(ticks: number): string {
  const totalFrames = Math.round(ticks / TIMELINE_TICKS_PER_FRAME)
  const frames = totalFrames % TIMELINE_FPS
  const totalSeconds = Math.floor(totalFrames / TIMELINE_FPS)
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60)
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(frames).padStart(2, '0')}`
}

/** Write validation must detect the first ref beyond the read-time parsing cap. */
export function hasTooManyCommentRefs(body: string): boolean {
  const matches = body.matchAll(COMMENT_REF_RE)
  for (let count = 0; count <= COMMENT_REF_MAX; count += 1) {
    if (matches.next().done) return false
  }
  return true
}

/** Build a token with a single-line label, capped at 120 chars. Empty labels fall back to the id. */
export function formatCommentRef(kind: CommentRefKind, id: string, label: string): string {
  const safeLabel = label.replace(/\s+/g, ' ').replace(/\]/g, ')').trim().slice(0, 120) || id
  return `@[${kind}-${id}:${safeLabel}]`
}

/** Tokens → '@Label' for email, notifications, previews. */
export function stripCommentRefs(body: string): string {
  return parseCommentBody(body)
    .map(segment => (segment.type === 'ref' ? `@${segment.ref.label}` : segment.text))
    .join('')
}

/** Kinds that look like tokens but aren't allowed, deduped and lowercase. */
export function unknownCommentRefKinds(body: string): string[] {
  const unknown = new Set<string>()
  for (const match of body.matchAll(/@\[([a-z]+)-([A-Za-z0-9_-]{1,64}):[^\]\r\n]{1,120}\]/gi)) {
    const kind = match[1].toLowerCase()
    // A legacy bare UUID can start with only letters (e.g. deadbeef-…).
    if (COMMENT_REF_UUID_RE.test(`${match[1]}-${match[2]}`)) continue
    if (!(COMMENT_REF_KINDS as readonly string[]).includes(kind)) unknown.add(kind)
  }
  return [...unknown]
}

// =============================================================================
// Topaz model limits
// =============================================================================

/**
 * Constraints Topaz enforces server-side and rejects the whole request for.
 *
 * Every entry here is backed by an observed production rejection — we do not
 * guess limits, because an invented ceiling blocks work that would have
 * succeeded. The comment on each records the error Topaz returned.
 *
 * Checked in the upscale modal (so the user sees it before spending time and
 * olives) and re-checked in the handler (so presets, reused settings and MCP
 * callers can't bypass it).
 */

/** Starlight video models. Shared so the limit table and the UI agree on the family. */
export const STARLIGHT_VIDEO_MODEL_CODES = [
  'sl-1',
  'slp-2',
  'slp-2.5',
  'slhq-1',
  'slm-1',
  'wonder-1',
  'slf-1',
  'slf-2',
] as const

export function isStarlightVideoModel(model: string): boolean {
  return (STARLIGHT_VIDEO_MODEL_CODES as readonly string[]).includes(model)
}

/**
 * Hyperion (SDR→HDR) video models. Shared so the 4K output cap, "HDR by model"
 * labeling, and pricing all agree on the family.
 */
export const HYPERION_VIDEO_MODEL_CODES = ['hyp-1', 'hyp-2', 'hyp-2.5'] as const

export function isHyperionVideoModel(model: string): boolean {
  return (HYPERION_VIDEO_MODEL_CODES as readonly string[]).includes(model)
}

/**
 * Hyperion models Topaz bills per frame (~13 frames/credit at 1080p, ~6 at 4K) —
 * an order of magnitude above Hyperion 1's duration rate — priced on the
 * `topaz-video-enhance-hyperion` tier.
 */
export const HYPERION_PER_FRAME_MODEL_CODES = ['hyp-2', 'hyp-2.5'] as const

export function isHyperionPerFrameModel(model: string): boolean {
  return (HYPERION_PER_FRAME_MODEL_CODES as readonly string[]).includes(model)
}

/**
 * `grain` range. The enhancement models reject above 0.1 with
 * "grain must be a float between 0 and 0.1"; Starlight accepts the full 0-1
 * range (16 completed jobs at 0.5 on slp-2.5 / sl-1, no grain 400 ever).
 */
export const TOPAZ_GRAIN_MAX_ENHANCE = 0.1
export const TOPAZ_GRAIN_MAX_STARLIGHT = 1

export function getTopazGrainMax(model: string): number {
  return isStarlightVideoModel(model) ? TOPAZ_GRAIN_MAX_STARLIGHT : TOPAZ_GRAIN_MAX_ENHANCE
}

/**
 * Max input frames for Starlight.
 *
 * Observed: "input_frame_count (1328) needs to be <= 450 for slp-2 model".
 * Topaz named slp-2, but no Starlight job above 450 frames has ever completed
 * (best observed: slf-2 and slp-2 at ~363), so the cap is applied family-wide.
 * If a specific model turns out to allow more, give it an entry here.
 */
export const TOPAZ_STARLIGHT_MAX_INPUT_FRAMES = 450

/**
 * Image upscale factor range.
 * Observed: "Filter 1 (Wonder 3) invalid upscale factor: 0.658…, should be in
 * range: [1, 6]". Topaz image models never downscale.
 */
export const TOPAZ_IMAGE_SCALE_MIN = 1
export const TOPAZ_IMAGE_SCALE_MAX = 6

/**
 * Max input megapixels, per image model.
 * Observed: "input image dimensions (43.53 MP) exceed maximum allowed of
 * 24.00 MP for \"Recovery V2\"". The message is explicitly per-model, so only
 * the model we have evidence for is listed.
 */
export const TOPAZ_IMAGE_MAX_INPUT_MEGAPIXELS: Record<string, number> = {
  'Recovery V2': 24,
}

/** A limit the request would be rejected for, with copy safe to show a user. */
export interface TopazLimitViolation {
  field: 'frameCount' | 'scaleFactor' | 'inputMegapixels' | 'outputSize' | 'tilingModel' | 'aspectRatio'
  message: string
}

/**
 * Hard caps published on the FAL endpoint for `blackforestlabs/flux-video-upscale`.
 * These are provider-enforced: exceeding them fails the submit outright, so both the
 * modal and the handler gate on them before a job is created.
 */
/**
 * The provider cap is 20s, but it validates the MP4 container duration, and a re-encoded
 * 20.000s trim lands at ~20.02–20.05s once AAC priming/padding is muxed in — FAL then
 * rejects the clip Martini just cut for it ("Clip too long"). Holding the window at 19.5s
 * keeps every trim under the cap with headroom for frame rounding too.
 */
export const FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS = 19.5
export const FLUX_VIDEO_UPSCALE_MAX_SOURCE_BYTES = 50 * 1024 * 1024
export const FLUX_VIDEO_UPSCALE_SCALE_MIN = 1.5
export const FLUX_VIDEO_UPSCALE_SCALE_MAX = 3

/** Creativity switch exposed to users; maps to the provider's 0/1 `creativity` field. */
export type FluxUpscaleCreativity = 'precise' | 'creative'

export const FLUX_UPSCALE_CREATIVITY_VALUES: Record<FluxUpscaleCreativity, number> = {
  precise: 0,
  creative: 1,
}

/** A Flux Video Upscale limit the request would be rejected for, with user-safe copy. */
export interface FluxVideoUpscaleLimitViolation {
  field: 'sourceDuration' | 'sourceBytes' | 'scaleFactor'
  message: string
  /** True when trimming the clip would bring the job back inside the limit. */
  trimmable: boolean
}

/**
 * Returns the limit this Flux upscale would be rejected for, or null when it is
 * within every published constraint. Unknown duration/size are treated as fine:
 * asset metadata is not always populated, and the provider is the backstop.
 */
export function checkFluxVideoUpscaleLimits(params: {
  sourceDurationSec?: number | null
  sourceBytes?: number | null
  scaleFactor?: number | null
}): FluxVideoUpscaleLimitViolation | null {
  const { sourceDurationSec, sourceBytes, scaleFactor } = params

  if (
    typeof sourceDurationSec === 'number' &&
    Number.isFinite(sourceDurationSec) &&
    sourceDurationSec > FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS + 0.05
  ) {
    return {
      field: 'sourceDuration',
      message: `Flux Video Upscale takes clips up to ${FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS}s — this one is ${sourceDurationSec.toFixed(1)}s. Trim it to ${FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS}s or shorter, or pick a Topaz model.`,
      trimmable: true,
    }
  }

  if (
    typeof sourceBytes === 'number' &&
    Number.isFinite(sourceBytes) &&
    sourceBytes > FLUX_VIDEO_UPSCALE_MAX_SOURCE_BYTES
  ) {
    return {
      field: 'sourceBytes',
      message: `Flux Video Upscale takes files up to 50 MB — this one is ${(sourceBytes / (1024 * 1024)).toFixed(0)} MB. Trimming the clip shorter usually gets under the cap, or pick a Topaz model.`,
      // A shorter window means fewer bytes, so trimming is a real fix here too —
      // just not a guaranteed one for a high-bitrate source.
      trimmable: true,
    }
  }

  if (typeof scaleFactor === 'number' && Number.isFinite(scaleFactor)) {
    if (scaleFactor < FLUX_VIDEO_UPSCALE_SCALE_MIN || scaleFactor > FLUX_VIDEO_UPSCALE_SCALE_MAX) {
      return {
        field: 'scaleFactor',
        message: `Flux Video Upscale supports ${FLUX_VIDEO_UPSCALE_SCALE_MIN}x-${FLUX_VIDEO_UPSCALE_SCALE_MAX}x — this one is ${scaleFactor.toFixed(2)}x.`,
        trimmable: false,
      }
    }
  }

  return null
}

/**
 * Returns the limit this video upscale would be rejected for, or null when it
 * is within every constraint we know about.
 */
export function checkTopazVideoLimits(params: {
  model: string
  /** Source frames the job will feed Topaz. Skipped when unknown. */
  frameCount?: number | null
}): TopazLimitViolation | null {
  const { model, frameCount } = params
  if (
    isStarlightVideoModel(model) &&
    typeof frameCount === 'number' &&
    Number.isFinite(frameCount) &&
    frameCount > TOPAZ_STARLIGHT_MAX_INPUT_FRAMES
  ) {
    return {
      field: 'frameCount',
      message: `Starlight models handle up to ${TOPAZ_STARLIGHT_MAX_INPUT_FRAMES} frames per job — this clip is ${Math.round(frameCount)}. Trim it shorter, or pick a non-Starlight model.`,
    }
  }
  return null
}

/**
 * Returns the limit this image upscale would be rejected for, or null when it
 * is within every constraint we know about.
 */
export function checkTopazImageLimits(params: {
  model: string
  scaleFactor?: number | null
  sourceWidth?: number | null
  sourceHeight?: number | null
  /** Requested output size. Above {@link TOPAZ_IMAGE_TILE_MAX_EDGE} it runs tiled. */
  outputWidth?: number | null
  outputHeight?: number | null
}): TopazLimitViolation | null {
  const { model, scaleFactor, sourceWidth, sourceHeight, outputWidth, outputHeight } = params

  if (typeof scaleFactor === 'number' && Number.isFinite(scaleFactor)) {
    if (scaleFactor < TOPAZ_IMAGE_SCALE_MIN || scaleFactor > TOPAZ_IMAGE_SCALE_MAX) {
      return {
        field: 'scaleFactor',
        message: `Upscale factor must be between ${TOPAZ_IMAGE_SCALE_MIN}x and ${TOPAZ_IMAGE_SCALE_MAX}x — this one is ${scaleFactor.toFixed(2)}x. Topaz image models don't downscale.`,
      }
    }
  }

  const maxMegapixels = TOPAZ_IMAGE_MAX_INPUT_MEGAPIXELS[model]
  if (
    maxMegapixels != null &&
    typeof sourceWidth === 'number' &&
    typeof sourceHeight === 'number' &&
    sourceWidth > 0 &&
    sourceHeight > 0
  ) {
    const megapixels = (sourceWidth * sourceHeight) / 1_000_000
    if (megapixels > maxMegapixels) {
      return {
        field: 'inputMegapixels',
        message: `${model} accepts images up to ${maxMegapixels} MP — this one is ${megapixels.toFixed(1)} MP (${sourceWidth}x${sourceHeight}). Pick a different model, or upscale a smaller version.`,
      }
    }
  }

  if (
    typeof outputWidth === 'number' &&
    typeof outputHeight === 'number' &&
    needsTopazImageTiling(outputWidth, outputHeight)
  ) {
    if (!isTopazImageTilingModel(model)) {
      return {
        field: 'tilingModel',
        message: `${model} invents new detail in each section, so it can't make images larger than ${formatPixels(TOPAZ_IMAGE_TILE_MAX_EDGE)} px. Pick ${TOPAZ_IMAGE_TILING_MODEL_LABEL} for larger sizes.`,
      }
    }
    // Tiles share one scale, so a tiled output keeps the source's shape:
    // changing it is outpainting, not an upscale. This also bounds the height
    // scale, which the scale check above only sees through the width.
    if (
      typeof sourceWidth === 'number' &&
      typeof sourceHeight === 'number' &&
      sourceWidth > 0 &&
      sourceHeight > 0 &&
      Math.abs((outputHeight / sourceHeight) * (sourceWidth / outputWidth) - 1) > TOPAZ_IMAGE_TILED_ASPECT_TOLERANCE
    ) {
      const matchingHeight = normalizeTopazImageTiledSize(
        outputWidth,
        (outputWidth * sourceHeight) / sourceWidth,
      ).height
      return {
        field: 'aspectRatio',
        message: `Sizes above ${formatPixels(TOPAZ_IMAGE_TILE_MAX_EDGE)} px keep the source's shape. For a width of ${formatPixels(outputWidth)} px, use a height of ${formatPixels(matchingHeight)} px.`,
      }
    }
    if (exceedsTopazImageTiledMax(outputWidth, outputHeight)) {
      return {
        field: 'outputSize',
        message: `Upscales go up to ${formatPixels(TOPAZ_IMAGE_TILED_MAX_EDGE)} px on the long edge and ${TOPAZ_IMAGE_TILED_MAX_MEGAPIXELS} megapixels. This one is ${formatPixels(outputWidth)} × ${formatPixels(outputHeight)} (${Math.round((outputWidth * outputHeight) / 1_000_000)} MP).`,
      }
    }
  }

  return null
}

/**
 * Longest edge one Topaz image job may output. Larger upscales are cut into
 * overlapping tiles that each stay at or under it, upscaled one job per tile,
 * and blended back into one image (ENG-1091).
 */
export const TOPAZ_IMAGE_TILE_MAX_EDGE = 7680

/**
 * Minimum overlap between neighbouring tiles, as a fraction of the tile's
 * length along that axis. The hand-run proof (6,336 × 1,296 to 16,896 × 3,456
 * in three tiles) used about this much and showed no seam at 100%.
 */
export const TOPAZ_IMAGE_TILE_MIN_OVERLAP = 0.06

/**
 * Largest tiled output. The generation worker blends it in memory (150 MP of
 * RGB is ~450 MB raw), and finalization re-reads it with sharp, whose default
 * decode limit is 268 MP.
 */
export const TOPAZ_IMAGE_TILED_MAX_EDGE = 32768
export const TOPAZ_IMAGE_TILED_MAX_MEGAPIXELS = 150

/**
 * Edge an interior tile is planned to. Each tile's source crop is widened to
 * whole source pixels, which adds up to one source pixel (6 output px at the
 * 6x maximum) per side, so the job it becomes still fits the per-job cap.
 */
const TOPAZ_IMAGE_TILE_PLAN_MAX_EDGE = TOPAZ_IMAGE_TILE_MAX_EDGE - 2 * TOPAZ_IMAGE_SCALE_MAX

/**
 * Largest tile, in output megapixels. GAN models cost one Topaz credit per
 * 24 MP and every tile past 9 MP bills 2 olives, so a bigger tile would cost
 * Topaz three credits and lose money (docs/pricing-margins.md §3).
 */
export const TOPAZ_IMAGE_TILE_MAX_MEGAPIXELS = 48

/** How far a tiled output's shape may drift from the source's (even-pixel rounding stays far inside it). */
export const TOPAZ_IMAGE_TILED_ASPECT_TOLERANCE = 0.01

/** Topaz model used when an image upscale request doesn't name one. */
export const DEFAULT_TOPAZ_IMAGE_MODEL = 'Standard V2'

/** User-facing list of the models that can run tiled, for copy. */
export const TOPAZ_IMAGE_TILING_MODEL_LABEL = 'Standard V2, High Fidelity V2, Low Resolution V2 or CGI'

/** One run of tiles along an axis, in output pixels. Both values are even. */
export interface TopazImageTileSpan {
  start: number
  length: number
}

/** One tile of a tiled upscale, in output pixels. */
export interface TopazImageTile {
  column: number
  row: number
  x: number
  y: number
  width: number
  height: number
}

export interface TopazImageTilePlan {
  /** Output size after rounding to even pixels. */
  outputWidth: number
  outputHeight: number
  columns: TopazImageTileSpan[]
  rows: TopazImageTileSpan[]
  /** Row-major: every column of row 0, then row 1. */
  tiles: TopazImageTile[]
}

function formatPixels(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

/**
 * Whether a Topaz image model can run tiled. Generative models invent
 * different detail in each tile, so their tiles would not line up; detail-
 * preserving (GAN) models reproduce the source and blend cleanly.
 */
export function isTopazImageTilingModel(model: string): boolean {
  return !TOPAZ_GENERATIVE_IMAGE_MODELS.includes(model)
}

/** Whether this output is larger than one Topaz job can make. */
export function needsTopazImageTiling(outputWidth: number, outputHeight: number): boolean {
  return Math.max(outputWidth, outputHeight) > TOPAZ_IMAGE_TILE_MAX_EDGE
}

/** Tiled outputs are always even on both axes, which LED processors and video tools expect. */
export function normalizeTopazImageTiledSize(width: number, height: number): { width: number; height: number } {
  return {
    width: Math.max(2, Math.round(width / 2) * 2),
    height: Math.max(2, Math.round(height / 2) * 2),
  }
}

export function exceedsTopazImageTiledMax(outputWidth: number, outputHeight: number): boolean {
  const { width, height } = normalizeTopazImageTiledSize(outputWidth, outputHeight)
  return (
    Math.max(width, height) > TOPAZ_IMAGE_TILED_MAX_EDGE ||
    width * height > TOPAZ_IMAGE_TILED_MAX_MEGAPIXELS * 1_000_000
  )
}

/**
 * Splits one axis into exactly `count` evenly spaced tiles of one length, the
 * shortest that still overlaps each neighbour by at least
 * {@link TOPAZ_IMAGE_TILE_MIN_OVERLAP} of the tile. Short tiles keep the
 * overlap near the minimum: Topaz bills by output megapixels, and only
 * neighbours overlap (never three tiles at one point), which keeps the blend a
 * simple two-way feather. Null when that tile would not fit one job.
 */
function planTopazImageTileAxis(length: number, count: number): TopazImageTileSpan[] | null {
  if (count === 1) return length <= TOPAZ_IMAGE_TILE_MAX_EDGE ? [{ start: 0, length }] : null

  // Planned in 2 px units, so every tile edge and size is even.
  const units = length / 2
  const overlapUnits = (tileUnits: number) => Math.ceil(TOPAZ_IMAGE_TILE_MIN_OVERLAP * tileUnits)
  let tileUnits = Math.ceil(units / (count - (count - 1) * TOPAZ_IMAGE_TILE_MIN_OVERLAP))
  while (count * tileUnits - (count - 1) * overlapUnits(tileUnits) < units) tileUnits++
  if (tileUnits * 2 > TOPAZ_IMAGE_TILE_PLAN_MAX_EDGE) return null

  // Covering `units` with this tile length guarantees the spacing between
  // starts is at most tileUnits - overlapUnits, and rounding each start can't
  // push two neighbours further apart than that whole number.
  const stride = (units - tileUnits) / (count - 1)
  return Array.from({ length: count }, (_, index) => ({
    start: (index === count - 1 ? units - tileUnits : Math.round(index * stride)) * 2,
    length: tileUnits * 2,
  }))
}

/** Fewest tiles along an axis that fit one job's edge. */
function minimumTopazImageTileCount(length: number): number {
  for (let count = 1; count <= 64; count++) {
    if (planTopazImageTileAxis(length, count)) return count
  }
  throw new Error(`Tile planning gave up on a ${length} px axis`)
}

/**
 * Whether one tile's job stays within {@link TOPAZ_IMAGE_TILE_MAX_MEGAPIXELS},
 * counting the up-to-one-source-pixel widening per side at the 6x maximum.
 */
function fitsTopazImageTileArea(width: number, height: number): boolean {
  const margin = 2 * TOPAZ_IMAGE_SCALE_MAX
  return (width + margin) * (height + margin) <= TOPAZ_IMAGE_TILE_MAX_MEGAPIXELS * 1_000_000
}

/**
 * The tile grid for a tiled Topaz image upscale: the fewest tiles that each
 * fit one job's edge and area (fewer, larger tiles when counts tie, which
 * means more overlap to blend across). Depends only on the output size, so the
 * dialog's quote, the MCP quote and the charge at /generate all come from the
 * same plan the worker runs.
 */
export function planTopazImageTiles(outputWidth: number, outputHeight: number): TopazImageTilePlan {
  const { width, height } = normalizeTopazImageTiledSize(outputWidth, outputHeight)
  let best: { columns: TopazImageTileSpan[]; rows: TopazImageTileSpan[] } | null = null
  const firstColumnCount = minimumTopazImageTileCount(width)
  const firstRowCount = minimumTopazImageTileCount(height)
  // Splitting one axis finer can let the other stay whole, so try a few
  // column counts and keep the cheapest grid.
  for (let columnCount = firstColumnCount; columnCount <= firstColumnCount + 8; columnCount++) {
    const columns = planTopazImageTileAxis(width, columnCount)
    if (!columns) continue
    for (let rowCount = firstRowCount; rowCount <= 64; rowCount++) {
      const rows = planTopazImageTileAxis(height, rowCount)
      if (!rows || !fitsTopazImageTileArea(columns[0].length, rows[0].length)) continue
      const count = columnCount * rowCount
      const bestCount = best ? best.columns.length * best.rows.length : Infinity
      if (count < bestCount) best = { columns, rows }
      break
    }
  }
  if (!best) throw new Error(`Tile planning gave up on ${width}x${height}`)

  const { columns, rows } = best
  const tiles = rows.flatMap((rowSpan, row) =>
    columns.map((columnSpan, column) => ({
      column,
      row,
      x: columnSpan.start,
      y: rowSpan.start,
      width: columnSpan.length,
      height: rowSpan.length,
    })),
  )
  return { outputWidth: width, outputHeight: height, columns, rows, tiles }
}

/**
 * The output size a Topaz image upscale will request: explicit dimensions win,
 * otherwise source × scale. Billing and the handler both read it, so a caller
 * that sends only a scale factor is priced for the size it actually gets.
 */
export function resolveTopazImageOutputSize(params: {
  outputWidth?: number | null
  outputHeight?: number | null
  sourceWidth?: number | null
  sourceHeight?: number | null
  scaleFactor?: number | null
}): { width: number; height: number } | null {
  if (params.outputWidth && params.outputHeight) {
    return { width: params.outputWidth, height: params.outputHeight }
  }
  if (params.sourceWidth && params.sourceHeight && params.scaleFactor) {
    return { width: params.sourceWidth * params.scaleFactor, height: params.sourceHeight * params.scaleFactor }
  }
  return null
}

/** A tiled upscale costs what its tiles would cost as separate upscales. */
export function calculateTopazImageTiledOlives(cost: ModelOliveCost, plan: TopazImageTilePlan): number {
  const total = plan.tiles.reduce(
    (sum, tile) => sum + resolveOlivesPerAssetMP(cost, (tile.width * tile.height) / 1_000_000),
    0,
  )
  return Math.round(total * 10000) / 10000
}

// ---------------------------------------------------------------------------
// Image extend (outpaint), ENG-1105
// ---------------------------------------------------------------------------

/**
 * The extend operation: Bria Expand run over a padded canvas in passes, with the
 * source laid back after each. A contextual model (Extend Image in the canvas,
 * `extend_image` for agents), never a picker entry.
 */
export const IMAGE_EXTEND_MODEL_ID = 'bria-expand-extend'
/** Dev/E2E twin that fills locally, so the whole pass pipeline runs without fal. */
export const MOCK_IMAGE_EXTEND_MODEL_ID = 'mock-extend-image'

export function isImageExtendModel(modelId: string | null | undefined): boolean {
  return modelId === IMAGE_EXTEND_MODEL_ID || modelId === MOCK_IMAGE_EXTEND_MODEL_ID
}

/**
 * Bria Expand's canvas limit: the expanded image must be under 5000 × 5000 in
 * area. A larger output runs its passes at a working size inside this and is
 * scaled up after, with the source laid back at full size (see `workScale`).
 */
export const IMAGE_EXTEND_PASS_MAX_PIXELS = 24_000_000
/**
 * The most one pass may grow the picture's area. In real runs Bria Expand kept
 * the frame coherent up to about 2.4× per call: a 1:1 product still widened to
 * 4.89:1 in one call came back with a second copy of the product, and in two
 * calls it did not. FLUX Fill, before it, invented people past about 2×.
 */
export const IMAGE_EXTEND_PASS_MAX_GROWTH = 2.4
export const IMAGE_EXTEND_MAX_EDGE = 20_000
export const IMAGE_EXTEND_MAX_MEGAPIXELS = 64
export const IMAGE_EXTEND_MIN_SOURCE_EDGE = 64
/** The generation pipeline fetches a source frame up to 50 MB. */
export const IMAGE_EXTEND_MAX_SOURCE_BYTES = 50 * 1024 * 1024

export type ImageExtendSide = 'left' | 'right' | 'top' | 'bottom'
export const IMAGE_EXTEND_SIDES: readonly ImageExtendSide[] = ['left', 'right', 'top', 'bottom']

/** Pixels added on each side of the source. */
export interface ImageExtendPadding {
  left: number
  right: number
  top: number
  bottom: number
}

/** Where the source sits on a larger target canvas. */
export const IMAGE_EXTEND_ANCHORS = [
  'center',
  'left',
  'right',
  'top',
  'bottom',
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
] as const
export type ImageExtendAnchor = (typeof IMAGE_EXTEND_ANCHORS)[number]

/** A rectangle on a canvas, in that canvas's pixels. */
export interface ImageExtendRect {
  x: number
  y: number
  width: number
  height: number
}

/** One Bria Expand call: the canvas it paints, in working pixels. */
export interface ImageExtendPass {
  index: number
  width: number
  height: number
  /** Where the picture so far sits on this canvas: the working-size source for the first pass, else the previous pass. */
  placement: ImageExtendRect
  /** Where the source sits on this canvas; its own pixels are laid back there after the call. */
  source: ImageExtendRect
}

export interface ImageExtendPlan {
  sourceWidth: number
  sourceHeight: number
  padding: ImageExtendPadding
  outputWidth: number
  outputHeight: number
  /** Where the source sits on the output canvas. */
  source: ImageExtendRect
  /**
   * Working size over output size: 1 unless the output is past
   * {@link IMAGE_EXTEND_PASS_MAX_PIXELS}, when the passes run smaller and the
   * last one is scaled up to the output with the full-size source laid over it.
   */
  workScale: number
  /** The source at working size, the first pass's input. */
  workSource: { width: number; height: number }
  /** In run order; each pass grows the previous one's picture, the last one to the output's shape. */
  passes: ImageExtendPass[]
}

export interface ImageExtendPlanIssue {
  code:
    | 'IMAGE_EXTEND_INVALID_PADDING'
    | 'IMAGE_EXTEND_NO_PADDING'
    | 'IMAGE_EXTEND_SOURCE_TOO_SMALL'
    | 'IMAGE_EXTEND_TOO_LARGE'
    | 'IMAGE_EXTEND_TARGET_SMALLER'
  message: string
}

function isImageExtendPixelCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

/** A padding object with whole, non-negative sides, or null. */
export function parseImageExtendPadding(value: unknown): ImageExtendPadding | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const padding = {
    left: record.left ?? 0,
    right: record.right ?? 0,
    top: record.top ?? 0,
    bottom: record.bottom ?? 0,
  }
  if (!IMAGE_EXTEND_SIDES.every(side => isImageExtendPixelCount(padding[side]))) return null
  return padding as ImageExtendPadding
}

/**
 * Per-side padding that places the source on a `targetWidth` × `targetHeight`
 * canvas at `anchor`. Centring splits an odd difference with the extra pixel
 * on the right or bottom. Null when the target is smaller than the source.
 */
export function imageExtendPaddingForTarget(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
  anchor: ImageExtendAnchor = 'center',
): ImageExtendPadding | null {
  const dx = targetWidth - sourceWidth
  const dy = targetHeight - sourceHeight
  if (dx < 0 || dy < 0) return null
  const horizontal = anchor.includes('left') ? 'start' : anchor.includes('right') ? 'end' : 'center'
  const vertical = anchor.includes('top') ? 'start' : anchor.includes('bottom') ? 'end' : 'center'
  const split = (total: number, position: 'start' | 'end' | 'center'): [number, number] =>
    position === 'start' ? [0, total] : position === 'end' ? [total, 0] : [Math.floor(total / 2), Math.ceil(total / 2)]
  const [left, right] = split(dx, horizontal)
  const [top, bottom] = split(dy, vertical)
  return { left, right, top, bottom }
}

/**
 * Padding that widens or heightens the source to `aspectRatio` (width / height)
 * without cropping it: a wider target adds width, a taller one adds height.
 */
export function imageExtendPaddingForAspect(
  sourceWidth: number,
  sourceHeight: number,
  aspectRatio: number,
  anchor: ImageExtendAnchor = 'center',
): ImageExtendPadding {
  const current = sourceWidth / sourceHeight
  const targetWidth = aspectRatio > current ? Math.round(sourceHeight * aspectRatio) : sourceWidth
  const targetHeight = aspectRatio < current ? Math.round(sourceWidth / aspectRatio) : sourceHeight
  return (
    imageExtendPaddingForTarget(sourceWidth, sourceHeight, targetWidth, targetHeight, anchor) ?? {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    }
  )
}

/**
 * The working-size layout along one axis: the source's length, the canvas's,
 * and the source's offset on it. A side that adds pixels keeps at least one at
 * working size, and a side that adds none keeps none, so the anchor holds.
 */
function imageExtendWorkAxis(
  sourceLength: number,
  before: number,
  after: number,
  scale: number,
): { source: number; canvas: number; offset: number } {
  const source = Math.max(1, Math.round(sourceLength * scale))
  const canvas = Math.max(
    Math.floor((sourceLength + before + after) * scale),
    source + (before > 0 ? 1 : 0) + (after > 0 ? 1 : 0),
  )
  const room = canvas - source
  const offset = before === 0 ? 0 : after === 0 ? room : Math.min(room - 1, Math.max(1, Math.round(before * scale)))
  return { source, canvas, offset }
}

/**
 * The pass plan for extending a `sourceWidth` × `sourceHeight` image: as few
 * Bria Expand calls as keep each one's growth in area within
 * {@link IMAGE_EXTEND_PASS_MAX_GROWTH}, growing both axes geometrically so
 * every pass adds about the same share of new picture, with the source kept on
 * its anchor throughout. A pure function of its inputs: the MCP quote, the
 * dialog's quote, the charge at /generate and the worker all run it.
 */
export function planImageExtend(
  sourceWidth: number,
  sourceHeight: number,
  padding: ImageExtendPadding,
): ImageExtendPlan {
  const outputWidth = sourceWidth + padding.left + padding.right
  const outputHeight = sourceHeight + padding.top + padding.bottom
  const source = { x: padding.left, y: padding.top, width: sourceWidth, height: sourceHeight }
  const workScale = Math.min(1, Math.sqrt(IMAGE_EXTEND_PASS_MAX_PIXELS / (outputWidth * outputHeight)))
  const across = imageExtendWorkAxis(sourceWidth, padding.left, padding.right, workScale)
  const down = imageExtendWorkAxis(sourceHeight, padding.top, padding.bottom, workScale)
  const growth = (across.canvas * down.canvas) / (across.source * down.source)
  // The epsilon keeps a growth of exactly 2.4^n at n passes despite float error.
  const count = Math.max(1, Math.ceil(Math.log(growth) / Math.log(IMAGE_EXTEND_PASS_MAX_GROWTH) - 1e-9))

  /** Canvas length and source offset after pass `k` of `count`, on one axis. */
  const step = (axis: { source: number; canvas: number; offset: number }, k: number) => {
    if (k === count) return { length: axis.canvas, offset: axis.offset }
    const length = Math.round(axis.source * (axis.canvas / axis.source) ** (k / count))
    // The offset grows in proportion to the added length, so the anchor holds at every pass.
    const added = axis.canvas - axis.source
    const offset = added === 0 ? 0 : Math.round((axis.offset * (length - axis.source)) / added)
    return { length, offset }
  }

  const passes: ImageExtendPass[] = []
  let previous = { x: step(across, 0), y: step(down, 0) }
  for (let k = 1; k <= count; k++) {
    const x = step(across, k)
    const y = step(down, k)
    passes.push({
      index: k - 1,
      width: x.length,
      height: y.length,
      placement: {
        x: x.offset - previous.x.offset,
        y: y.offset - previous.y.offset,
        width: previous.x.length,
        height: previous.y.length,
      },
      source: { x: x.offset, y: y.offset, width: across.source, height: down.source },
    })
    previous = { x, y }
  }
  return {
    sourceWidth,
    sourceHeight,
    padding,
    outputWidth,
    outputHeight,
    source,
    workScale,
    workSource: { width: across.source, height: down.source },
    passes,
  }
}

/**
 * Why this extension can't run, or null. Checked by the quote, /generate and
 * the handler alike, so a refusal names the same limit everywhere.
 */
export function imageExtendPlanIssue(
  sourceWidth: number,
  sourceHeight: number,
  padding: ImageExtendPadding,
): ImageExtendPlanIssue | null {
  if (!parseImageExtendPadding(padding)) {
    return { code: 'IMAGE_EXTEND_INVALID_PADDING', message: 'Each side must be a whole number of pixels, 0 or more.' }
  }
  if (IMAGE_EXTEND_SIDES.every(side => padding[side] === 0)) {
    return { code: 'IMAGE_EXTEND_NO_PADDING', message: 'Add pixels to at least one side.' }
  }
  if (Math.min(sourceWidth, sourceHeight) < IMAGE_EXTEND_MIN_SOURCE_EDGE) {
    return {
      code: 'IMAGE_EXTEND_SOURCE_TOO_SMALL',
      message: `The source must be at least ${IMAGE_EXTEND_MIN_SOURCE_EDGE} px on each side.`,
    }
  }
  const outputWidth = sourceWidth + padding.left + padding.right
  const outputHeight = sourceHeight + padding.top + padding.bottom
  if (
    Math.max(outputWidth, outputHeight) > IMAGE_EXTEND_MAX_EDGE ||
    outputWidth * outputHeight > IMAGE_EXTEND_MAX_MEGAPIXELS * 1_000_000
  ) {
    return {
      code: 'IMAGE_EXTEND_TOO_LARGE',
      message: `Extended images go up to ${formatPixels(IMAGE_EXTEND_MAX_EDGE)} px on the long edge and ${IMAGE_EXTEND_MAX_MEGAPIXELS} MP. This one would be ${formatPixels(outputWidth)} × ${formatPixels(outputHeight)}. Extend less, then upscale.`,
    }
  }
  return null
}

/** An extension costs one Bria Expand call per pass, at a flat price each. */
export function calculateImageExtendOlives(cost: ModelOliveCost, plan: ImageExtendPlan): number {
  return Math.round((cost.olivesPerAsset ?? 0) * plan.passes.length * 10000) / 10000
}

/** Collaboration deployment selected by projects.collab_backend. */
export type CollabBackend = 'legacy' | 'cf'

/**
 * Detector version stamped on ShotDetectionMetadata. Bump when the algorithm
 * or tuning changes so cached results are recomputed instead of trusted.
 * v2: TransNetV2 became the primary detector (ENG-777 benchmark: F1 94.2 vs
 * 66.7 for the v1 ffmpeg config), with ffmpeg as the no-dependency fallback.
 */
export const SHOT_DETECTION_VERSION = 2

/** Which engine produced a ShotDetectionMetadata result. */
export type ShotDetector = 'transnetv2-v1' | 'ffmpeg-scene-v1'

/** Automatic shot-cut detection result stored on a video asset. */
export interface ShotDetectionMetadata {
  /** Must equal SHOT_DETECTION_VERSION to be trusted; older results are recomputed. */
  version: number
  /** Cut positions in source ticks (48kHz), ascending, exclusive of 0 and the asset duration. */
  cutsTicks: number[]
  detectedAt: string
  /** Engine that produced this result (fallbacks mean it can vary per asset). */
  detector: ShotDetector
}

/** Sequential execution order, shared by the runner and canvas. Null means a dependency cycle. */
export function resolveActionExecutionOrder<
  T extends Pick<Action, 'id' | 'order' | 'instruction' | 'inputRefs' | 'outputBinIds'>,
>(actions: readonly T[]): T[] | null {
  const byOrder = [...actions].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
  const writerByBin = new Map<string, string>()
  for (const action of byOrder) {
    for (const binId of resolveActionOutputBinIds(action)) writerByBin.set(binId, action.id)
  }
  const upstream = new Map<string, string[]>()
  for (const action of byOrder) {
    const writers = new Set<string>()
    for (const ref of normalizeActionInputRefs(action)) {
      if (ref.type !== 'bin') continue
      const writer = writerByBin.get(ref.id)
      if (writer && writer !== action.id) writers.add(writer)
    }
    upstream.set(action.id, [...writers])
  }

  const result: T[] = []
  const placed = new Set<string>()
  let remaining = byOrder
  while (remaining.length > 0) {
    const next = remaining.find(action =>
      upstream.get(action.id)!.every(id => placed.has(id) || !remaining.some(r => r.id === id)),
    )
    if (!next) {
      return null
    }
    result.push(next)
    placed.add(next.id)
    remaining = remaining.filter(action => action.id !== next.id)
  }
  return result
}

/** JSON with object keys sorted at every level, so key order never changes the hash. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

/** Presentation only: scripts do not consume canvas geometry, colour or layout.
 * Keep this deny-list explicit: every new snapshot field is machinery by default.
 */
const KIT_BIN_PRESENTATION_FIELDS = new Set([
  'position',
  'size',
  'color',
  'colour',
  'collapsed',
  'layoutMode',
  'layoutWidth',
  'gridColumns',
])

/** Shared raw inputs for backend component hashes and live client invalidation. */
export function actionFingerprintInputs(
  action: Action,
  files: readonly WorkflowDefinitionFile[],
  bins: readonly WorkflowDefinitionBin[],
) {
  const refs = normalizeActionInputRefs(action)
  const paths = new Set(refs.filter(ref => ref.type === 'instructions').map(ref => ref.id))
  if (action.instructionsPath) paths.add(action.instructionsPath)
  const byPath = new Map(files.map(file => [file.path, file.content]))
  const inputs = refs
    .filter(ref => ref.type !== 'instructions')
    .map(({ type, id, label }) => ({ type, id, label: label ?? null }))
  const outputs = resolveActionOutputBinIds(action)
  const binIds = new Set([...inputs.filter(ref => ref.type === 'bin').map(ref => ref.id), ...outputs])
  return {
    brief: action.instruction,
    instructions: [...paths].sort().map(path => ({ path, content: byPath.get(path) ?? null })),
    wiring: {
      inputs,
      outputs,
      bins: bins
        .filter(bin => binIds.has(bin.sourceId))
        .sort((a, b) => (a.sourceId < b.sourceId ? -1 : a.sourceId > b.sourceId ? 1 : 0))
        .map(bin => Object.fromEntries(Object.entries(bin).filter(([key]) => !KIT_BIN_PRESENTATION_FIELDS.has(key)))),
    },
    pinned: { pinned: action.pinned ?? null, agentId: action.agentId, gate: action.gate },
  }
}

/**
 * One Action's machinery as the workflow fingerprint hashes it. The single
 * projection behind `workflowFingerprintSource` and `describeActionsMismatch`,
 * so "what the hash covers" and "which field differs" can never disagree.
 * Changing it changes every fingerprint: update the golden hashes in
 * fingerprint.test.ts deliberately, and remember open tabs hash nothing —
 * they send their Actions (WorkflowRunEditBarriers).
 */
export function actionMachinery(action: Action) {
  return {
    id: action.id,
    instruction: action.instruction,
    agentId: action.agentId,
    order: action.order,
    gate: action.gate,
    pinned: action.pinned ?? null,
    // The brief's tags merged with the stored refs (normalizeActionInputRefs):
    // a placed workflow, its run snapshot and its saved definition all hash
    // the same wiring, whatever the stored refs happened to carry.
    inputRefs: normalizeActionInputRefs(action).map(ref => ({
      type: ref.type,
      id: ref.id,
      label: ref.label ?? null,
    })),
    outputBinIds: resolveActionOutputBinIds(action),
    // The own Instructions PATH is machinery like a tag's path; its content
    // is not (ADR-0008). Only present when set, so Actions without one keep
    // the fingerprint they had.
    ...(action.instructionsPath ? { instructionsPath: action.instructionsPath } : {}),
  }
}

/**
 * Field-level account of why two Action sets hash differently: the Actions on
 * both sides whose machinery differs (with the differing field names), the ids
 * the client sent that the canvas read lacks, and the ids it has that the
 * client did not send. Names only — the content stays where it is.
 */
export function describeActionsMismatch(
  expected: readonly Action[],
  canvas: readonly Action[],
): WorkflowActionsMismatch {
  const project = (actions: readonly Action[]) =>
    new Map(actions.map(action => [action.id, actionMachinery(action) as Record<string, unknown>]))
  const sent = project(expected)
  const held = project(canvas)
  const changed: WorkflowActionsMismatch['changed'] = []
  for (const [id, mine] of sent) {
    const theirs = held.get(id)
    if (!theirs) continue
    const fields = [...new Set([...Object.keys(mine), ...Object.keys(theirs)])]
      .filter(field => canonicalJson(mine[field]) !== canonicalJson(theirs[field]))
      .sort()
    if (fields.length) changed.push({ id, fields })
  }
  return {
    changed,
    missingOnCanvas: [...sent.keys()].filter(id => !held.has(id)),
    extraOnCanvas: [...held.keys()].filter(id => !sent.has(id)),
  }
}

export function workflowFingerprintSource(
  definition: Pick<WorkflowDefinition, 'actions' | 'bins' | 'toolBundle' | 'presetBinContents'>,
): string {
  const actions = [...definition.actions]
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .map(actionMachinery)
  const bins = [...(definition.bins ?? [])]
    .sort((a, b) => a.sourceId.localeCompare(b.sourceId))
    .map(bin => ({
      sourceId: bin.sourceId,
      name: bin.name,
      layoutMode: bin.layoutMode ?? null,
      gridColumns: bin.gridColumns ?? null,
      ...(bin.fileInput ? { fileInput: bin.fileInput } : {}),
      ...(bin.includeContents ? { includeContents: true } : {}),
      ...(bin.parentSourceId ? { parentSourceId: bin.parentSourceId } : {}),
      ...(bin.childOrder ? { childOrder: bin.childOrder } : {}),
    }))
  return canonicalJson({
    actions,
    bins,
    ...(definition.toolBundle
      ? { toolBundle: { sha256: definition.toolBundle.sha256, snapshot: definition.toolBundle.snapshot } }
      : {}),
    // Saved defaults are execution inputs reviewed with this version. Keep asset
    // order, but canonicalize bin order; absent presets retain their legacy hash.
    ...(definition.presetBinContents?.length
      ? {
          presetBinContents: [...definition.presetBinContents].sort((a, b) =>
            a.sourceBinId.localeCompare(b.sourceBinId),
          ),
        }
      : {}),
  })
}

/** A new team's monthly checkout price, in major currency units, before location-dependent taxes. */
export interface TeamCheckoutQuote {
  planId: string
  promotionCode?: string
  amount: number
  regularAmount: number
  currency: string
  /** 0 = no discount, null = forever; otherwise the number of discounted months. */
  discountMonths: number | null
}

/** What happens to the org's stored fal key once a plan switch applies. */
export type FalKeyAfterSwitch =
  /** The key runs generations (a fal-key plan, or a manual fal grant). */
  | 'active'
  /** The key stays stored but olive Pro and Studio do not use it; generations pay in olives. */
  | 'paused'
  /** No fal key is stored. */
  | 'none'

/**
 * A plan switch with a fal-key plan on either side, as Autumn prices it with
 * the terms /billing/subscribe will attach (POST /billing/subscribe/preview).
 * Amounts are in major units of `currency`.
 */
export interface PlanSwitchPreview {
  planId: string
  /** The paid plan the switch replaces; null when the org holds none (free or complimentary). */
  fromPlanId: string | null
  /** `now`, or at the end of the current billing period. */
  effectiveAt: 'now' | 'period_end'
  /** ISO time the new plan starts. */
  effectiveDate: string
  /** fal-key plans: whether this checkout carries the 30-day trial, and when it ends. Null for an olive plan. */
  trial: { available: boolean; endsAt: string | null } | null
  /**
   * Olives that stay in the account through the switch, as of now. Scheduled:
   * bought, granted and rolled-over olives (what is left of the current plan's
   * allowance is `endingOlives`). Immediate: every olive, the allowance's
   * remainder kept as a dated balance. Null when Autumn shows no olive balance.
   */
  carriedOlives: number | null
  /**
   * On a scheduled switch, what is left of the current plan's allowance, as of
   * now: Autumn ends that allowance at the switch and the backend grants what
   * is left of it back right after, dated 11 months after the switch
   * (oliveRegrant.ts). 0 on an immediate switch, which carries it with every
   * other olive. Null when unknown.
   */
  endingOlives: number | null
  currency: string
  /** Charged today. Negative when unused time on the current plan comes back as account credit. */
  chargeToday: number
  /** The first charge after today. */
  nextCharge: { amount: number; date: string } | null
  falKeyAfterSwitch: FalKeyAfterSwitch
  /** The switch goes through a Stripe page (no saved card). */
  checkoutRequired: boolean
}

/** A plan switch Autumn holds for the end of the current billing period (GET /billing). */
export interface ScheduledPlanSwitch {
  planId: string
  /** ISO time the plan starts. */
  startsAt: string | null
}

/** POST /billing/subscribe/preview: the price quote, plus the switch for one that involves a fal-key plan. */
export interface PlanCheckoutPreview extends TeamCheckoutQuote {
  switch: PlanSwitchPreview | null
}

export type ChatRunCodeOutput =
  | { ok: true; exitCode: number; stdout: string; stderr: string; durationMs: number; truncated: boolean }
  | { ok: false; error: 'busy' | 'timeout' | 'failed'; message: string }

/**
 * Blocking error screens the browser reports to the broken-user detector
 * (`POST /health/blocked-screens`, docs/health-alerts.md). A closed list keeps
 * the Redis key space and the Slack copy bounded.
 */
export const BLOCKED_SCREEN_SURFACES = ['auth_init', 'projects_load', 'project_open'] as const
export type BlockedScreenSurface = (typeof BLOCKED_SCREEN_SURFACES)[number]

export function isBlockedScreenSurface(value: unknown): value is BlockedScreenSurface {
  return typeof value === 'string' && (BLOCKED_SCREEN_SURFACES as readonly string[]).includes(value)
}

/**
 * The fal setup's "Connect your app" screens (frontend/src/components/onboarding/fal/ConnectAppFlow.tsx): the
 * chooser, the plan with the person's agent, and the app's key and prompt. The page reports its beats on them to
 * `POST /onboarding/plan-sessions/:id/events` (backend/src/onboarding/planEvents.ts) and to PostHog.
 */
export const FAL_CONNECT_SCREENS = ['chooser', 'plan', 'app_setup'] as const
export type FalConnectScreen = (typeof FAL_CONNECT_SCREENS)[number]

/**
 * What can happen on a connect screen: it came up, the job list folded under the plan was opened, the prompt was copied
 * or shown, progress from the agent or the app was seen (`step` says which), the person finished, skipped, went back,
 * or said what stopped them.
 */
export const FAL_CONNECT_BEATS = [
  'screen_viewed',
  'jobs_shown',
  'prompt_copied',
  'prompt_shown',
  'progress_seen',
  'done',
  'skipped',
  'back',
  'leave_reason',
] as const
export type FalConnectBeat = (typeof FAL_CONNECT_BEATS)[number]

/** The fixed answers to "What stopped you?" on a skip; `other` carries a short line of the person's own. */
export const FAL_CONNECT_LEAVE_REASONS = ['no_agent', 'unclear_value', 'no_time', 'did_not_work', 'other'] as const
export type FalConnectLeaveReason = (typeof FAL_CONNECT_LEAVE_REASONS)[number]

/**
 * One request an app made with its API key, as `GET /organizations/:orgId/generation-api-keys/:keyId/requests`
 * lists the last few (backend/src/generationApi/requestFeed.ts): the request line and when, never a body.
 */
export interface ApiKeyRequest {
  method: string
  path: string
  status: number
  /** When the request arrived, ISO 8601. */
  at: string
}

/** Mutable workspace vocabulary attached to immutable saved workflow revisions. */
export interface WorkflowVersionLabel {
  /** Hex color without the leading #. Older catalogs may omit it. */
  color?: string
  id: string
  organizationId: string
  name: string
  createdAt: string
}
